import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';
import { Phone, FileText, CheckCircle, MessageSquare, Upload, Calendar, CalendarClock, Search, ArrowLeft, ArrowRight, Activity, Mail, Send, Copy, Image, ExternalLink, Share2, AlertCircle, LayoutGrid, ChevronRight, Share, MapPin, PhoneCall, Bed, CheckCircle2, AlertTriangle, HelpCircle, Building2, UserPlus, Stethoscope, Trash2, X, XCircle, RefreshCcw, RotateCcw, FastForward, Download, ShieldCheck, Lock, UserCheck, Clock, Circle, Plus, Edit, ClipboardList, Star, Wallet, CreditCard, Trophy, Settings, Loader2, Calculator, Sparkles, Save, Receipt, PhoneOff, PhoneMissed } from 'lucide-react';
import SearchableSelect from '../../Components/common/SearchableSelect';
import { PatientDetailsSkeleton } from '../../Components/common/Skeleton';
import { useSocket } from '../../hooks/useSocket';
import { generateDealInvoicePDF } from '../../utils/generateDealInvoice';
import { getEncryptedLeadId } from '../../utils/routeEncryption';
import { DoctorListPopup, HospitalListPopup, AvailabilityPopup } from './components/QuickListModals';
import TimePicker12, { formatTime12 } from '../../Components/common/TimePicker12';
import { initiateCall as exotelInitiateCall, getCallLogs as exotelGetCallLogs, saveCallDisposition } from '../../services/exotelApi';
import CallHistoryPanel, { getEffectiveDisposition } from './components/CallHistoryPanel';

const getLightStatusBadge = (status) => {
  switch (status) {
    case 'call-successful':
    case 'completed':
    case 'answered':
      return {
        bg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        dot: 'bg-emerald-500',
        label: 'Call was successful'
      };
    case 'no-answer':
    case 'no-user-answered':
    case 'missed':
      return {
        bg: 'bg-red-50 text-red-700 border-red-200',
        dot: 'bg-red-500',
        label: 'No user answered (Missed)'
      };
    case 'client-hungup-during-call':
      return {
        bg: 'bg-rose-50 text-rose-700 border-rose-200',
        dot: 'bg-rose-500',
        label: 'Client hung-up during call'
      };
    case 'client-hungup-before-connecting':
    case 'cancelled':
    case 'call-fatal':
      return {
        bg: 'bg-amber-50 text-amber-700 border-amber-200',
        dot: 'bg-amber-500',
        label: 'Client hung-up before connecting'
      };
    case 'client-unanswered':
      return {
        bg: 'bg-amber-50 text-amber-700 border-amber-200',
        dot: 'bg-amber-500',
        label: 'Client unanswered'
      };
    case 'busy':
      return {
        bg: 'bg-orange-50 text-orange-700 border-orange-200',
        dot: 'bg-orange-500',
        label: 'User was busy'
      };
    case 'ringing':
      return {
        bg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        dot: 'bg-indigo-500 animate-ping',
        label: 'Ringing'
      };
    case 'in-progress':
      return {
        bg: 'bg-blue-50 text-blue-700 border-blue-200',
        dot: 'bg-blue-500 animate-pulse',
        label: 'Call in progress'
      };
    default:
      return {
        bg: 'bg-slate-50 text-slate-700 border-slate-200',
        dot: 'bg-slate-400',
        label: status || 'Unknown'
      };
  }
};

const getLightDispositionBadge = (dispKey) => {
  switch (dispKey) {
    case 'callback_completed':
    case 'resolved':
      return {
        bg: 'bg-emerald-50 text-emerald-800 border-emerald-300',
        dot: 'bg-emerald-500',
        label: 'Callback Completed'
      };
    case 'follow_up_needed':
      return {
        bg: 'bg-blue-50 text-blue-800 border-blue-300',
        dot: 'bg-blue-500',
        label: 'Follow-up Needed'
      };
    case 'appointment_booked':
      return {
        bg: 'bg-purple-50 text-purple-800 border-purple-300',
        dot: 'bg-purple-500',
        label: 'Appointment Booked'
      };
    case 'not_interested':
      return {
        bg: 'bg-slate-100 text-slate-700 border-slate-300',
        dot: 'bg-slate-400',
        label: 'Not Interested'
      };
    case 'invalid_number':
      return {
        bg: 'bg-rose-50 text-rose-800 border-rose-300',
        dot: 'bg-rose-500',
        label: 'Invalid Number'
      };
    case 'pending':
    default:
      return {
        bg: 'bg-amber-50 text-amber-800 border-amber-300',
        dot: 'bg-amber-500',
        label: 'Pending Callback'
      };
  }
};


const LeadDetail = () => {
   const { id: routeId } = useParams();
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const { user } = useAuth();
   const [lead, setLead] = useState(null);
   const id = lead?._id || routeId;

   useEffect(() => {
      if (searchParams.get('payment_sync') === 'true') {
         handleSyncPaymentStatus(true);
      }
   }, [searchParams]);
   // canManagePipeline: true for system roles OR custom role users with lead:update/surgery:update_status
   // For stage-specific action buttons, use canUpdateStage(stageKey) instead, which enforces pipelinePermissions
   const canManagePipeline = ["admin", "customer_care", "cc_head", "accountant"].includes(user?.role) ||
      (user?.roleDetails?.permissions && Array.isArray(user.roleDetails.permissions) &&
         user.roleDetails.permissions.some(p => {
            const name = typeof p === 'object' && p !== null ? p.name : p;
            return ['lead:update', 'surgery:update_status', 'payout:approve', 'payout:process'].includes(name);
         }));
   const isAdmin = user?.role === "admin";
   const canMarkAsLost = ["admin", "customer_care", "cc_head"].includes(user?.role);

   // ── RBAC: Call Permissions ────────────────────────────────────────────────
   // System roles allowed to initiate outbound calls via Exotel
   const CALL_INITIATE_ROLES = ['admin', 'customer_care', 'cc_head', 'receptionist', 'marketing_agent'];
   const canInitiateCalls = user && (
      CALL_INITIATE_ROLES.includes(user?.role) ||
      (Array.isArray(user?.roleDetails?.permissions) &&
         user.roleDetails.permissions.some(p => {
            const name = typeof p === 'object' && p !== null ? p.name : p;
            return name === 'call:initiate';
         }))
   );



   const todayDateStr = useMemo(() => {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
   }, []);

   const [viewedStatus, setViewedStatus] = useState(null);
   const [loading, setLoading] = useState(true);
   const [updating, setUpdating] = useState(false);
   const [isLockingTeam, setIsLockingTeam] = useState(false);
   const [stakeholderNote, setStakeholderNote] = useState('');
   const [addingNote, setAddingNote] = useState(false);
   const [showDocListModal, setShowDocListModal] = useState(false);
   const [showHospListModal, setShowHospListModal] = useState(false);
   const [showAvailModal, setShowAvailModal] = useState(false);
   const [isEditingVault, setIsEditingVault] = useState(false);

   const [isCallingPatient, setIsCallingPatient] = useState(false);
   const [callHistoryRefresh, setCallHistoryRefresh] = useState(0);
   const [showCallHistory, setShowCallHistory] = useState(false);
   const [latestCall, setLatestCall] = useState(null);
   const [showQuickDispModal, setShowQuickDispModal] = useState(false);
   const [quickDispForm, setQuickDispForm] = useState({
      disposition: 'callback_completed',
      dispositionNotes: '',
      callReason: ''
   });
   const [isSavingQuickDisp, setIsSavingQuickDisp] = useState(false);

   useEffect(() => {
      if (id) {
         exotelGetCallLogs(id, 1)
            .then(logs => {
               if (logs && logs.length > 0) setLatestCall(logs[0]);
               else setLatestCall(null);
            })
            .catch(() => {});
      }
   }, [id, callHistoryRefresh]);

   const handleOpenQuickDispModal = () => {
      if (!latestCall) return;
      const eff = getEffectiveDisposition(latestCall);
      setQuickDispForm({
         disposition: eff === 'pending' ? 'callback_completed' : eff,
         dispositionNotes: latestCall.dispositionNotes || latestCall.notes || '',
         callReason: latestCall.callReason || ''
      });
      setShowQuickDispModal(true);
   };

   const handleSaveQuickDisp = async (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (!latestCall?._id) return;
      setIsSavingQuickDisp(true);
      try {
         const res = await saveCallDisposition(latestCall._id, {
            disposition: quickDispForm.disposition,
            dispositionNotes: quickDispForm.dispositionNotes.trim(),
            notes: quickDispForm.dispositionNotes.trim(),
            callReason: quickDispForm.callReason.trim()
         });
         const updated = res?.data || {
            ...latestCall,
            disposition: quickDispForm.disposition,
            dispositionNotes: quickDispForm.dispositionNotes.trim(),
            notes: quickDispForm.dispositionNotes.trim(),
            callReason: quickDispForm.callReason.trim(),
            dispositionUpdatedAt: new Date().toISOString()
         };
         setLatestCall(updated);
         setCallHistoryRefresh(prev => prev + 1);
         toast.success('Follow-up status updated');
         setShowQuickDispModal(false);
      } catch (err) {
         toast.error(err?.response?.data?.message || 'Failed to update status');
      } finally {
         setIsSavingQuickDisp(false);
      }
   };

   const handleExotelCall = async () => {
      if (!lead?.patientPhone) return toast.error('No patient phone number available.');
      if (!user?.phone) return toast.error('Your profile has no phone number. Update your profile to make calls.');
      setIsCallingPatient(true);
      try {
         const result = await exotelInitiateCall(id, lead.patientPhone);
         if (result.success) {
            toast.success('📞 Your phone will ring first — then connect to the patient!', { duration: 6000 });
            setShowCallHistory(true);
            setTimeout(() => setCallHistoryRefresh(prev => prev + 1), 5000);
         }
      } catch (err) {
         toast.error(err?.response?.data?.message || 'Failed to initiate call. Check Exotel configuration.');
      } finally {
         setIsCallingPatient(false);
      }
   };

   // Manual payment states
   const [showManualPaymentUpload, setShowManualPaymentUpload] = useState(false);
   const [manualPaymentAmount, setManualPaymentAmount] = useState('');
   const [manualPaymentReceipt, setManualPaymentReceipt] = useState(null);
   const [uploadingReceipt, setUploadingReceipt] = useState(false);

   const handleManualPaymentSubmit = async () => {
      if (!manualPaymentAmount) {
         return toast.error('Please enter the payment amount.');
      }
      if (!manualPaymentReceipt) {
         return toast.error('Please upload the payment receipt.');
      }

      setUploadingReceipt(true);
      try {
         // 1. Upload receipt to S3
         const fileData = new FormData();
         fileData.append('documents', manualPaymentReceipt);

         const uploadRes = await api.post('/upload', fileData, {
            headers: {
               'Content-Type': 'multipart/form-data'
            }
         });

         const uploadedFile = uploadRes.data?.data?.files?.[0];
         if (!uploadedFile || !uploadedFile.url) {
            throw new Error('Upload response did not contain file URL');
         }

         // 2. Prepare payload
         const payload = {
            pastPaymentProof: {
               url: uploadedFile.url,
               name: uploadedFile.filename || 'payment_receipt'
            },
            billingDetails: {
               ...lead.billingDetails,
               totalPayableAmount: Number(manualPaymentAmount)
            },
            razorpayDetails: {
               ...lead.razorpayDetails,
               status: 'paid',
               paymentId: 'MANUAL_SETTLEMENT'
            },
            notes: 'Manual override: Payment receipt uploaded by Customer Care.'
         };

         // 3. Move stage to paid
         await handleStageTransition('paid', payload);
         setShowManualPaymentUpload(false);
      } catch (err) {
         console.error('Manual payment confirmation failed:', err);
         toast.error(err.response?.data?.message || err.message || 'Failed to confirm manual payment');
      } finally {
         setUploadingReceipt(false);
      }
   };

   const handleAddNote = async () => {
      if (!stakeholderNote.trim()) return;
      setAddingNote(true);
      try {
         const response = await api.post(`/leads/${id}/notes`, { content: stakeholderNote });
         setLead(response.data.data);
         setStakeholderNote('');
         toast.success('Note added to timeline');
      } catch (error) {
         toast.error('Failed to add note');
      } finally {
         setAddingNote(false);
      }
   };

   // Dynamic Form States for 10-Stage Pipeline
   const [screeningForm, setScreeningForm] = useState({
      eligibility: true, conditionSuitable: true, complications: '', patientWilling: true, timeline: 'Immediate', followUpDate: '', followUpNotes: ''
   });
   const [clinicalForm, setClinicalForm] = useState({
      doctorReviewDone: true,
      surgeryFeasible: true,
      anaesthesiaClearance: true,
      riskLevel: 'Low',
      isFit: true,
      reviewingDoctorId: ''
   });
   const [showSurgeonKyc, setShowSurgeonKyc] = useState(false);
   const [showAnesKyc, setShowAnesKyc] = useState(false);

   const [assignmentForm, setAssignmentForm] = useState({
      assignedSurgeon: '',
      surgeonType: 'partner',
      assignedSurgeonOtherName: '',
      assignedSurgeonOtherOCMR: '',
      assignedSurgeonOtherPhone: '',
      assignedSurgeonOtherEmail: '',
      assignedSurgeonOtherAadhar: '',
      assignedSurgeonOtherPan: '',
      assignedSurgeonOtherBankName: '',
      assignedSurgeonOtherAccountNo: '',
      assignedSurgeonOtherIfsc: '',
      assignedSurgeonOtherAccountHolder: '',
      assignedanesthesiologist: '',
      anesthesiologistType: 'partner',
      assignedanesthesiologistOtherName: '',
      assignedanesthesiologistOtherOCMR: '',
      assignedanesthesiologistOtherPhone: '',
      assignedanesthesiologistOtherEmail: '',
      assignedanesthesiologistOtherAadhar: '',
      assignedanesthesiologistOtherPan: '',
      assignedanesthesiologistOtherBankName: '',
      assignedanesthesiologistOtherAccountNo: '',
      assignedanesthesiologistOtherIfsc: '',
      assignedanesthesiologistOtherAccountHolder: '',
      assignedHospital: ''
   });

   useEffect(() => {
      if (lead) {
         const initialSurgeon = lead.assignedSurgeon?._id || (typeof lead.assignedSurgeon === 'string' ? lead.assignedSurgeon : '');
         const initialanesthesiologist = lead.assignedAnesthesiologist?._id || (typeof lead.assignedAnesthesiologist === 'string' ? lead.assignedAnesthesiologist : '') || lead.assignedanesthesiologist?._id || (typeof lead.assignedanesthesiologist === 'string' ? lead.assignedanesthesiologist : '');

         const isSurgeonOther = lead.surgeonType === 'other' || (!initialSurgeon && Boolean(lead.assignedSurgeonOtherName || lead.assignedSurgeonOtherPhone || lead.assignedSurgeonOtherEmail));
         const isAnesthesiologistOther = lead.anesthesiologistType === 'other' || (!initialanesthesiologist && Boolean(lead.assignedanesthesiologistOtherName || lead.assignedanesthesiologistOtherPhone || lead.assignedanesthesiologistOtherEmail));

         setShowSurgeonKyc(Boolean(lead.assignedSurgeonOtherAadhar || lead.assignedSurgeonOtherPan || lead.assignedSurgeonOtherAccountNo || lead.assignedSurgeonOtherBankName));
         setShowAnesKyc(Boolean(lead.assignedanesthesiologistOtherAadhar || lead.assignedanesthesiologistOtherPan || lead.assignedanesthesiologistOtherAccountNo || lead.assignedanesthesiologistOtherBankName));

         setAssignmentForm({
            assignedSurgeon: initialSurgeon,
            surgeonType: isSurgeonOther ? 'other' : 'partner',
            assignedSurgeonOtherName: lead.assignedSurgeonOtherName || '',
            assignedSurgeonOtherOCMR: lead.assignedSurgeonOtherOCMR || '',
            assignedSurgeonOtherPhone: lead.assignedSurgeonOtherPhone || '',
            assignedSurgeonOtherEmail: lead.assignedSurgeonOtherEmail || '',
            assignedSurgeonOtherAadhar: lead.assignedSurgeonOtherAadhar || '',
            assignedSurgeonOtherPan: lead.assignedSurgeonOtherPan || '',
            assignedSurgeonOtherBankName: lead.assignedSurgeonOtherBankName || '',
            assignedSurgeonOtherAccountNo: lead.assignedSurgeonOtherAccountNo || '',
            assignedSurgeonOtherIfsc: lead.assignedSurgeonOtherIfsc || '',
            assignedSurgeonOtherAccountHolder: lead.assignedSurgeonOtherAccountHolder || '',
            assignedanesthesiologist: initialanesthesiologist,
            anesthesiologistType: isAnesthesiologistOther ? 'other' : 'partner',
            assignedanesthesiologistOtherName: lead.assignedanesthesiologistOtherName || '',
            assignedanesthesiologistOtherOCMR: lead.assignedanesthesiologistOtherOCMR || '',
            assignedanesthesiologistOtherPhone: lead.assignedanesthesiologistOtherPhone || '',
            assignedanesthesiologistOtherEmail: lead.assignedanesthesiologistOtherEmail || '',
            assignedanesthesiologistOtherAadhar: lead.assignedanesthesiologistOtherAadhar || '',
            assignedanesthesiologistOtherPan: lead.assignedanesthesiologistOtherPan || '',
            assignedanesthesiologistOtherBankName: lead.assignedanesthesiologistOtherBankName || '',
            assignedanesthesiologistOtherAccountNo: lead.assignedanesthesiologistOtherAccountNo || '',
            assignedanesthesiologistOtherIfsc: lead.assignedanesthesiologistOtherIfsc || '',
            assignedanesthesiologistOtherAccountHolder: lead.assignedanesthesiologistOtherAccountHolder || '',
            assignedHospital: lead.assignedHospital?._id || lead.assignedHospital || lead.leadProviderHospitalId?._id || lead.leadProviderHospitalId || ''
         });
      }
   }, [lead]);
   const [schedulingForm, setSchedulingForm] = useState({ surgeryDate: '', otSlot: '', surgeonConfirmed: true, anaesthetistConfirmed: true });
   const [intakeStep, setIntakeStep] = useState(1);

   const workflowRef = useRef(null);

   useEffect(() => {
      if (workflowRef.current && !loading && lead) {
         workflowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
   }, [intakeStep, lead?.status, loading]);
   const [intakeForm, setIntakeForm] = useState({
      insurance: { hasAyushmanCard: 'No' },
      reportType: '',
      readinessDate: '',
      medicalDetails: {
         history: { currentIllness: '', pastDiseases: '', previousSurgeries: '', familyHistory: '' },
         medications: { currentMedicines: '', bloodThinners: '', steroids: '', otherTreatments: '' },
         allergies: { drugAllergy: '', foodAllergy: '', anesthesiaAllergy: '' },
         vitals: { bp: '', sugarLevel: '', weight: '', height: '', oxygenLevel: '', heartCondition: '' }
      }
   });

   const [paymentForm, setPaymentForm] = useState({
      procedureAmount: '',
      implantCost: '',
      gstPercent: 18,
      platformFee: 35,
      gstAmount: 0,
      platformFeeAmount: 0,
      totalPayableAmount: 0,
      alternateEmail: '',
      alternateWhatsapp: '',
      useAlternate: false,
      description: ''
   });

   const [platformFeeType, setPlatformFeeType] = useState('percent'); // 'percent' or 'amount'
   const [platformFeeEnabled, setPlatformFeeEnabled] = useState(true);
   const [gstEnabled, setGstEnabled] = useState(true);
   const prevBillingLeadIdRef = useRef(null);

   const updateBillingState = (overrides = {}, syncBackend = true) => {
      setPaymentForm(prevForm => {
         const nextProcAmtStr = overrides.procedureAmount !== undefined ? overrides.procedureAmount.toString() : (prevForm.procedureAmount !== undefined ? prevForm.procedureAmount.toString() : '');
         const procVal = nextProcAmtStr === '' ? 0 : parseInt(nextProcAmtStr);

         const nextFeeEnabled = overrides.platformFeeEnabled !== undefined ? overrides.platformFeeEnabled : (prevForm.platformFeeEnabled !== undefined ? prevForm.platformFeeEnabled : platformFeeEnabled);
         const nextFeeType = overrides.platformFeeType !== undefined ? overrides.platformFeeType : (prevForm.platformFeeType || platformFeeType || 'percent');
         const nextGstEnabled = overrides.gstEnabled !== undefined ? overrides.gstEnabled : (prevForm.gstEnabled !== undefined ? prevForm.gstEnabled : gstEnabled);
         const nextGstPercentStr = overrides.gstPercent !== undefined ? overrides.gstPercent.toString() : (prevForm.gstPercent !== undefined && prevForm.gstPercent !== '' ? prevForm.gstPercent.toString() : '18');
         const gstPct = nextGstPercentStr === '' ? 18 : Number(nextGstPercentStr);

         let nextFeeStr = overrides.platformFee !== undefined ? overrides.platformFee.toString() : (prevForm.platformFee !== undefined && prevForm.platformFee !== '' ? prevForm.platformFee.toString() : '35');
         let pfAmount = 0;

         if (!nextFeeEnabled) {
            pfAmount = 0;
         } else if (nextFeeType === 'amount') {
            pfAmount = overrides.platformFeeAmount !== undefined ? Number(overrides.platformFeeAmount) : (Number(prevForm.platformFeeAmount) || 0);
            const feePct = (procVal > 0 && pfAmount > 0) ? Math.round((pfAmount / procVal) * 100) : (Number(nextFeeStr) > 100 ? 35 : Number(nextFeeStr) || 35);
            nextFeeStr = feePct.toString();
         } else {
            let feeNum = nextFeeStr === '' ? 35 : Number(nextFeeStr);
            if (feeNum > 100) feeNum = 35;
            nextFeeStr = feeNum.toString();
            pfAmount = procVal === 0 ? 0 : Math.round(procVal * (feeNum / 100));
         }

         let gAmount = 0;
         let total = 0;

         if (nextGstEnabled) {
            if (nextFeeEnabled) {
               gAmount = pfAmount === 0 ? 0 : Math.round(pfAmount * (gstPct / 100));
               total = pfAmount + gAmount;
            } else {
               gAmount = procVal === 0 ? 0 : Math.round(procVal * (gstPct / 100));
               total = procVal + gAmount;
            }
         } else {
            gAmount = 0;
            total = nextFeeEnabled ? pfAmount : procVal;
         }

         const updated = {
            ...prevForm,
            procedureAmount: nextProcAmtStr,
            platformFee: nextFeeStr,
            platformFeeAmount: pfAmount,
            platformFeeEnabled: nextFeeEnabled,
            platformFeeType: nextFeeType,
            gstEnabled: nextGstEnabled,
            gstPercent: nextGstPercentStr,
            gstAmount: gAmount,
            totalPayableAmount: total,
            calculatorBaseItems: overrides.calculatorBaseItems !== undefined ? overrides.calculatorBaseItems : (prevForm.calculatorBaseItems || []),
            calculatorDeductedItems: overrides.calculatorDeductedItems !== undefined ? overrides.calculatorDeductedItems : (prevForm.calculatorDeductedItems || []),
            manualCalcItems: overrides.manualCalcItems !== undefined ? overrides.manualCalcItems : (prevForm.manualCalcItems || []),
            ...overrides
         };

         const targetLeadId = id || lead?._id;
         if (syncBackend && targetLeadId) {
            const billingPayload = {
               ...(lead?.billingDetails || {}),
               procedureAmount: procVal,
               platformFeeEnabled: nextFeeEnabled,
               platformFeeType: nextFeeType,
               platformFee: Number(nextFeeStr) || 35,
               platformFeeAmount: pfAmount,
               gstEnabled: nextGstEnabled,
               gstPercent: gstPct,
               gstAmount: gAmount,
               totalPayableAmount: total,
               calculatorBaseItems: updated.calculatorBaseItems,
               calculatorDeductedItems: updated.calculatorDeductedItems,
               manualCalcItems: updated.manualCalcItems,
               description: updated.description || lead?.billingDetails?.description || ''
            };

            const altPayload = {
               email: updated.alternateEmail || '',
               whatsapp: updated.alternateWhatsapp || '',
               useAlternate: Boolean(updated.useAlternate)
            };

            if (lead) {
               lead.billingDetails = billingPayload;
               lead.hospitalAlternateContact = altPayload;
            }

            api.patch(`/leads/${targetLeadId}`, {
               billingDetails: billingPayload,
               hospitalAlternateContact: altPayload
            }).catch(err => {
               console.error("Failed to sync billing details:", err);
            });
         }

         return updated;
      });
   };

   const handleUpdateAlternateContact = (changes = {}, syncBackend = true) => {
      setPaymentForm(prevForm => {
         const updated = {
            ...prevForm,
            ...changes
         };

         const targetLeadId = id || lead?._id;
         const altPayload = {
            email: updated.alternateEmail !== undefined ? updated.alternateEmail : (prevForm.alternateEmail || ''),
            whatsapp: updated.alternateWhatsapp !== undefined ? updated.alternateWhatsapp : (prevForm.alternateWhatsapp || ''),
            useAlternate: updated.useAlternate !== undefined ? Boolean(updated.useAlternate) : Boolean(prevForm.useAlternate)
         };

         if (lead) {
            lead.hospitalAlternateContact = altPayload;
         }

         if (syncBackend && targetLeadId) {
            api.patch(`/leads/${targetLeadId}`, {
               hospitalAlternateContact: altPayload
            }).catch(err => {
               console.error("Failed to sync hospital alternate contact:", err);
            });
         }

         return updated;
      });
   };

   const [payments, setPayments] = useState([]);

   // Lookups
   const [doctors, setDoctors] = useState([]);
   const [hospitals, setHospitals] = useState([]);
   const [selectedFiles, setSelectedFiles] = useState([]);
   const [agents, setAgents] = useState([]);
   const [showCCReassignModal, setShowCCReassignModal] = useState(false);
   const fileInputRef = useRef(null);

   // Follow-up system state
   const [hoveredStageInfo, setHoveredStageInfo] = useState(null);
   const [notInterestedReason, setNotInterestedReason] = useState('');
   const [showNotInterestedModal, setShowNotInterestedModal] = useState(false);
   const [showRevertModal, setShowRevertModal] = useState(false);
   const [followUpAction, setFollowUpAction] = useState(null); // 'not_ready'
   const [rescheduleDate, setRescheduleDate] = useState('');
   const [rescheduleNotes, setRescheduleNotes] = useState('');
   const [showReadyModal, setShowReadyModal] = useState(false);
   const [readyFormData, setReadyFormData] = useState({ readinessDate: '', notes: '', confirmed: false, hasAyushmanCard: 'No' });
   const [ayushmanFile, setAyushmanFile] = useState(null);
   const [shareForm, setShareForm] = useState({ email: '', whatsapp: '' });
   const [callStatus, setCallStatus] = useState('initial'); // 'initial', 'documents_yes', 'documents_no'
   const [sharingChannel, setSharingChannel] = useState(null); // 'patient', 'hospital'
   const [hospitalProcess, setHospitalProcess] = useState('assignment'); // 'assignment', 'bed_check', 'tests_ongoing'
   const [showHospitalChange, setShowHospitalChange] = useState(false);
   const [previewFile, setPreviewFile] = useState(null);
   const [isPastSurgeryMode, setIsPastSurgeryMode] = useState(false);
   const intakeRef = useRef(null);

   const isPastSurgeryCase = useMemo(() => {
      return Boolean(
         isPastSurgeryMode ||
         lead?.isManualPastEntry ||
         lead?.isManualEntry ||
         lead?.source === 'past_surgery' ||
         lead?.leadGeneratedByType === 'past_surgery'
      );
   }, [isPastSurgeryMode, lead]);

   useEffect(() => {
      if (lead) {
         if (lead.isManualPastEntry || lead.isManualEntry || lead.source === 'past_surgery' || lead.leadGeneratedByType === 'past_surgery') {
            setIsPastSurgeryMode(true);
         }
      }
   }, [lead]);

   const [sendingManualNotif, setSendingManualNotif] = useState(false);
   const [isSendingPayoutNotif, setIsSendingPayoutNotif] = useState(false);

   const handleSendManualNotification = async () => {
      if (!id) return;
      setSendingManualNotif(true);
      try {
         const docId = clinicalForm.reviewingDoctorId ||
            lead?.clinicalData?.reviewingDoctorId?._id ||
            (typeof lead?.clinicalData?.reviewingDoctorId === 'string' ? lead?.clinicalData?.reviewingDoctorId : null) ||
            lead?.assignedSurgeon?._id ||
            (typeof lead?.assignedSurgeon === 'string' ? lead?.assignedSurgeon : null);

         const res = await api.post(`/leads/${id}/manual-notify`, {
            event: `stage_${lead?.status || 'general'}`,
            notes: `Manual stage notification triggered by CC/Admin for past surgery lead`,
            doctorId: docId,
            assignedSurgeon: assignmentForm.assignedSurgeon || lead?.assignedSurgeon?._id || lead?.assignedSurgeon,
            assignedAnesthesiologist: assignmentForm.assignedAnesthesiologist || assignmentForm.assignedanesthesiologist || lead?.assignedAnesthesiologist?._id || lead?.assignedAnesthesiologist || lead?.assignedanesthesiologist?._id || lead?.assignedanesthesiologist,
            assignedHospital: assignmentForm.assignedHospital || lead?.assignedHospital?._id || lead?.assignedHospital
         });
         const msg = typeof res.data?.message === 'string' ? res.data.message : 'Notification sent to assigned doctors & hospital successfully!';
         toast.success(msg);
      } catch (error) {
         const err = typeof error.response?.data?.message === 'string' ? error.response.data.message : 'Failed to send notification';
         toast.error(err);
      } finally {
         setSendingManualNotif(false);
      }
   };

   const handleSendPayoutNotification = async () => {
      if (!id) return;
      setIsSendingPayoutNotif(true);
      try {
         const res = await api.post(`/leads/${id}/notify-payout`);
         const msg = typeof res.data?.message === 'string' ? res.data.message : 'Payout notification email sent to doctors & hospital successfully!';
         toast.success(msg);
      } catch (error) {
         const err = typeof error.response?.data?.message === 'string' ? error.response.data.message : 'Failed to send payout notification';
         toast.error(err);
      } finally {
         setIsSendingPayoutNotif(false);
      }
   };

   const { socket } = useSocket();

   useEffect(() => {
      if (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') {
         fetchDoctorsAndHospitals();
      }
   }, [id, user?.role]);

   // 📡 REAL-TIME: Listen for patient document submissions
   useEffect(() => {
      if (!socket || !id) return;

      socket.on('lead_updated', (data) => {


         if (data.leadId === id || data.entityId === id) {
            // 1. Trigger Refresh via centralized system
            setRefreshTrigger(prev => prev + 1);

            // 2. Specialized Feedback based on event type
            if (data.type === 'document_uploaded' || data.documentCount !== undefined) {
               setIntakeStep(6);
               toast.success(`Documents Received: ${data.patientName} uploaded reports!`);
            } else if (data.type === 'doctor_review_submitted') {
               toast.success(`Doctor Review: ${data.patientName || 'Patient'} is ${data.reviewDecision}!`, {
                  duration: 6000,
                  icon: data.reviewDecision === 'Surgery Possible' ? '✅' : '⚠️'
               });
               if (data.reviewDecision === 'Additional Investigation Required') {
                  setIntakeStep(5);
               }
            } else {
               // Generic catch-all for status moves, assignments, etc.
               if (data.action || data.description) {
                  toast.success(data.description || `Lead updated: ${data.action?.replace(/_/g, ' ')}`, {
                     icon: '🔔',
                     duration: 4000
                  });
               }
            }
         }
      });

      return () => {
         socket.off('lead_updated');
      };
   }, [socket, id]);

   // 💾 PERSISTENCE: Save/Load Draft from SessionStorage to prevent data loss on reload
   useEffect(() => {
      const saved = sessionStorage.getItem(`intake_draft_${id}`);
      if (saved) {
         try {
            const parsed = JSON.parse(saved);
            if (parsed.form) setIntakeForm(parsed.form);
            if (parsed.step) setIntakeStep(parsed.step);
         } catch (e) {
            console.error("Draft recovery failed", e);
         }
      }
   }, [id]);

   useEffect(() => {
      if (intakeForm && id) {
         sessionStorage.setItem(`intake_draft_${id}`, JSON.stringify({ form: intakeForm, step: intakeStep }));
      }
   }, [intakeForm, intakeStep, id]);

   const [showAddMonitoringLogModal, setShowAddMonitoringLogModal] = useState(false);
   const [monitoringLog, setMonitoringLog] = useState({ visitDate: '', startTime: '', endTime: '', notes: '', logId: null });
   const [isSubmittingLog, setIsSubmittingLog] = useState(false);
   const [isSubmittingPayout, setIsSubmittingPayout] = useState(false);
   const [isFinalizingDeal, setIsFinalizingDeal] = useState(false);
   const [showFinalizeModal, setShowFinalizeModal] = useState(false);
   const [payoutForm, setPayoutForm] = useState({
      surgeonAmount: 0,
      anesthesiologistAmount: 0,
      surgeonPayoutDoc: null,
      anesthesiologistPayoutDoc: null,
      refundDetails: {
         amount: 0,
         paymentMode: 'Bank Transfer',
         transactionId: '',
         notes: ''
      },
      payoutNotes: ''
   });

   const [payoutConfig, setPayoutConfig] = useState({
      baseAmount: 0,
      surgeon: 23,
      surgeonTds: 10,
      surgeonIncentive: 0,
      anesthesiologist: 4,
      anesthesiologistTds: 10,
      anesthesiologistIncentive: 0,
      doxezRetention: 8,
      marketingPartner: 1,
      leadPartner: 2
   });

   useEffect(() => {
      if (lead?.billingDetails?.procedureAmount) {
         setPayoutConfig(prev => ({ ...prev, baseAmount: Number(lead.billingDetails.procedureAmount) }));
      }
   }, [lead?.billingDetails?.procedureAmount]);

   useEffect(() => {
      const surgicalCost = payoutConfig.baseAmount || 0;
      const isSurgeonPartner = (lead?.surgeonType || (lead?.assignedSurgeonOtherName ? 'other' : 'partner')) === 'partner';
      const isAnesthesiologistPartner = (lead?.anesthesiologistType || (lead?.assignedanesthesiologistOtherName ? 'other' : 'partner')) === 'partner';

      const existingSurgeonObj = lead?.payoutData?.doctorPayouts?.find(p => p.role === 'surgeon');
      const existingAnesthesiologistObj = lead?.payoutData?.doctorPayouts?.find(p => p.role === 'anesthesiologist');

      const existingSurgeonAmount = existingSurgeonObj?.amount;
      const existingAnesthesiologistAmount = existingAnesthesiologistObj?.amount;

      setPayoutForm(prev => {
         const surgeonAmount = prev._isSurgeonManual
            ? prev.surgeonAmount
            : (existingSurgeonAmount !== undefined && existingSurgeonAmount !== null
               ? existingSurgeonAmount
               : (isSurgeonPartner
                  ? Math.round(surgicalCost * (payoutConfig.surgeon / 100)) + Number(payoutConfig.surgeonIncentive || 0)
                  : 0));

         const anesthesiologistAmount = prev._isAnesthesiologistManual
            ? prev.anesthesiologistAmount
            : (existingAnesthesiologistAmount !== undefined && existingAnesthesiologistAmount !== null
               ? existingAnesthesiologistAmount
               : (isAnesthesiologistPartner
                  ? Math.round(surgicalCost * (payoutConfig.anesthesiologist / 100)) + Number(payoutConfig.anesthesiologistIncentive || 0)
                  : 0));

         return {
            ...prev,
            surgeonAmount,
            anesthesiologistAmount,
            disableSurgeonTds: prev._isSurgeonTdsManual ? prev.disableSurgeonTds : Boolean(existingSurgeonObj?.disableTds),
            disableAnesthesiologistTds: prev._isAnesthesiologistTdsManual ? prev.disableAnesthesiologistTds : Boolean(existingAnesthesiologistObj?.disableTds)
         };
      });
   }, [payoutConfig, lead]);

   const handleFinalizePostOp = async () => {
      if (!id) return;
      setUpdating(true);
      try {
         const res = await api.post(`/leads/${id}/finalize-post-op`);
         toast.success(res.data?.message || "Monitoring documentation finalized. Moving to next stage.");
         if (res.data?.data) setLead(res.data.data);
         fetchLead();
      } catch (error) {
         toast.error(error.response?.data?.message || "Failed to finalize monitoring");
      } finally {
         setUpdating(false);
      }
   };

   const handleAddMonitoringLog = async () => {
      if (!monitoringLog.visitDate || !monitoringLog.startTime || !monitoringLog.endTime) {
         toast.error("Please provide date, start time and end time");
         return;
      }

      // Local duplicate check (Allow same day if time is different)
      const targetDate = new Date(monitoringLog.visitDate).toDateString();
      const alreadyExists = lead.surgicalData?.postOpMonitoring?.logs?.some(l =>
         new Date(l.visitDate).toDateString() === targetDate &&
         l.startTime === monitoringLog.startTime &&
         l._id !== monitoringLog.logId
      );
      if (alreadyExists) {
         toast.error(`A visit for ${targetDate} at ${monitoringLog.startTime} is already recorded.`);
         return;
      }

      setIsSubmittingLog(true);
      try {
         const res = await api.post(`/leads/${id}/post-op-monitoring`, {
            visitDate: monitoringLog.visitDate,
            startTime: monitoringLog.startTime,
            endTime: monitoringLog.endTime,
            notes: monitoringLog.notes,
            logId: monitoringLog.logId
         });

         if (res.data.success) {
            toast.success(monitoringLog.logId ? "Monitoring visit updated!" : "Monitoring visit logged!");

            setShowAddMonitoringLogModal(false);
            setMonitoringLog({ visitDate: '', startTime: '', endTime: '', notes: '', logId: null });
            setLead(res.data.data);
            fetchLead();
         }
      } catch (error) {
         toast.error(error.response?.data?.message || "Failed to save visit log");
      } finally {
         setIsSubmittingLog(false);
      }
   };

   const handleUpdatePayout = async () => {
      setIsSubmittingPayout(true);
      try {
         // 1. Upload Surgeon Proof if selected
         let surgeonProofUrl = lead.payoutData?.doctorPayouts?.find(p => p.role === 'surgeon')?.payoutProofUrl || '';
         if (payoutForm.surgeonPayoutDoc && typeof payoutForm.surgeonPayoutDoc !== 'string') {
            const fd = new FormData();
            fd.append('documents', payoutForm.surgeonPayoutDoc);
            const upRes = await api.post('/upload', fd);
            surgeonProofUrl = upRes.data.data.files[0].url;
         }

         // 2. Upload anesthesiologist Proof if selected
         let anesthesiologistProofUrl = lead.payoutData?.doctorPayouts?.find(p => p.role === 'anesthesiologist')?.payoutProofUrl || '';
         if (payoutForm.anesthesiologistPayoutDoc && typeof payoutForm.anesthesiologistPayoutDoc !== 'string') {
            const fd = new FormData();
            fd.append('documents', payoutForm.anesthesiologistPayoutDoc);
            const upRes = await api.post('/upload', fd);
            anesthesiologistProofUrl = upRes.data.data.files[0].url;
         }

         const payload = {
            doctorPayouts: [
               {
                  role: 'surgeon',
                  amount: parseFloat(payoutForm.surgeonAmount) || 0,
                  disableTds: Boolean(payoutForm.disableSurgeonTds),
                  payoutProofUrl: surgeonProofUrl,
                  doctorId: lead.assignedSurgeon?._id || lead.assignedSurgeon || null,
                  doctorName: lead.surgeonType === 'other' ? (lead.assignedSurgeonOtherName || 'Surgeon') : (lead.assignedSurgeon?.user?.fullName || lead.assignedSurgeon?.name || 'Surgeon')
               },
               {
                  role: 'anesthesiologist',
                  amount: parseFloat(payoutForm.anesthesiologistAmount) || 0,
                  disableTds: Boolean(payoutForm.disableAnesthesiologistTds),
                  payoutProofUrl: anesthesiologistProofUrl,
                  doctorId: lead.assignedanesthesiologist?._id || lead.assignedanesthesiologist || null,
                  doctorName: lead.anesthesiologistType === 'other' ? (lead.assignedanesthesiologistOtherName || 'Anesthesiologist') : (lead.assignedanesthesiologist?.user?.fullName || lead.assignedanesthesiologist?.name || 'Anesthesiologist')
               }
            ],
            refundDetails: payoutForm.refundDetails,
            payoutNotes: payoutForm.payoutNotes
         };

         const res = await api.patch(`/leads/${id}/payout`, payload);
         if (res.data.success) {
            toast.success("Payout details updated successfully!");
            setLead(res.data.data);
            fetchLead();
            setPayoutForm(prev => ({
               ...prev,
               surgeonPayoutDoc: null,
               anesthesiologistPayoutDoc: null,
               _isSurgeonManual: false,
               _isAnesthesiologistManual: false,
               _isSurgeonTdsManual: false,
               _isAnesthesiologistTdsManual: false
            }));
         }
      } catch (error) {
         toast.error(error.response?.data?.message || "Failed to update payouts");
      } finally {
         setIsSubmittingPayout(false);
      }
   };

   const handleFinalizeDeal = async () => {
      setIsFinalizingDeal(true);
      try {
         const res = await api.post(`/leads/${id}/finalize-deal`);
         if (res.data.success) {
            toast.success("Deal finalized successfully!");
            setLead(res.data.data);
            setShowFinalizeModal(false);
         }
      } catch (error) {
         toast.error(error.response?.data?.message || "Failed to finalize deal");
      } finally {
         setIsFinalizingDeal(false);
      }
   };

   const handleDeleteMonitoringLog = async (logId) => {
      if (!window.confirm("Are you sure you want to delete this log?")) return;
      try {
         const res = await api.delete(`/leads/${id}/post-op-monitoring/${logId}`);
         if (res.data.success) {
            toast.success("Monitoring visit deleted successfully.");
            setLead(res.data.data);
            fetchLead();
         }
      } catch (error) {
         toast.error(error.response?.data?.message || "Failed to delete visit log");
      }
   };

   const [showManualVerifyModal, setShowManualVerifyModal] = useState(false);
   const [manualVerifyForm, setManualVerifyForm] = useState({
      surgeonArrivalTime: '',
      anesthesiologistArrivalTime: '',
      startTime: '',
      endTime: '',
      notes: ''
   });

   const handleManualDoctorVerify = async () => {
      const hasAnesthesiologist = Boolean(lead?.assignedanesthesiologist || lead?.assignedanesthesiologistOtherName || lead?.anesthesiologistType === 'other');

      if (!manualVerifyForm.surgeonArrivalTime) return toast.error('Please enter Operating Surgeon arrival time');
      if (hasAnesthesiologist && !manualVerifyForm.anesthesiologistArrivalTime) return toast.error('Please enter Anesthesiologist arrival time');
      if (!manualVerifyForm.startTime) return toast.error('Please enter surgery start time');
      if (!manualVerifyForm.endTime) return toast.error('Please enter surgery end time');

      setUpdating(true);
      try {
         const res = await api.post(`/leads/${id}/manual-verify-doctor`, manualVerifyForm);
         if (res.data?.success) {
            toast.success(res.data.message || 'Doctors verified manually!');
            setShowManualVerifyModal(false);
            setViewedStatus(null);
            fetchLead();
         }
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to complete manual verification');
      } finally {
         setUpdating(false);
      }
   };

   const [showRescheduleModal, setShowRescheduleModal] = useState(false);
   const [rescheduleForm, setRescheduleForm] = useState({
      faultBy: '',
      penaltyAmount: '',
      notes: '',
      isAborted: false,
      abortionFault: '',
      abortionPenaltyPercentage: 0,
      useAlternate: false,
      alternateEmail: '',
      alternateWhatsapp: '',
      isAbortingView: false,
      penaltyPercentage: 0,
      abortReason: '',
      abortFault: '',
      newSurgeryDate: lead?.schedulingData?.surgeryDate ? new Date(lead.schedulingData.surgeryDate).toISOString().split('T')[0] : ''
   });
   const [isRescheduling, setIsRescheduling] = useState(false);

   const [showCalculatorModal, setShowCalculatorModal] = useState(false);
   const [calcBaseItems, setCalcBaseItems] = useState([
      { name: 'OT Charges', rate: '', days: '', hours: '', amount: '' },
      { name: 'Surgeon Charges', rate: '', days: '', hours: '', amount: '' },
      { name: 'Anesthesiologist Charges', rate: '', days: '', hours: '', amount: '' },
      { name: 'Bed Charges', rate: '', days: '', hours: '', amount: '' },
      { name: 'Fooding Charges', rate: '', days: '', hours: '', amount: '' },
      { name: 'Drain Remove', rate: '', days: '', hours: '', amount: '' }
   ]);
   const [calcDeductedItems, setCalcDeductedItems] = useState([]);

   const [showManualCalculatorModal, setShowManualCalculatorModal] = useState(false);
   const [hospitalTemplate, setHospitalTemplate] = useState(null);
   const [savingTemplate, setSavingTemplate] = useState(false);
   const [manualCalcItems, setManualCalcItems] = useState([]);

   const getOfficialRateInfo = (itemName) => {
      if (!itemName || !itemName.trim()) return null;
      const t = hospitalTemplate || lead?.assignedHospital?.standardBillingTemplate;
      if (!t || (!t.baseItems && !t.manualItems)) return null;
      const cleanName = itemName.trim().toLowerCase();
      const baseMatch = (t.baseItems || []).find(i => i.name && i.name.trim().toLowerCase() === cleanName);
      if (baseMatch) {
         return {
            rate: baseMatch.rate || baseMatch.amount || 0,
            unitType: baseMatch.unitType || 'Fixed Amount'
         };
      }
      const manualMatch = (t.manualItems || []).find(i => i.name && i.name.trim().toLowerCase() === cleanName);
      if (manualMatch) {
         return {
            rate: manualMatch.amount || 0,
            unitType: 'Fixed Amount'
         };
      }
      return null;
   };

   useEffect(() => {
      const t = hospitalTemplate || lead?.assignedHospital?.standardBillingTemplate;
      if (t && t.baseItems && t.baseItems.length > 0) {
         const hasSavedItems = Boolean(lead?.billingDetails?.calculatorBaseItems && lead.billingDetails.calculatorBaseItems.length > 0);
         if (!hasSavedItems) {
            setCalcBaseItems(t.baseItems.map(i => ({
               name: i.name,
               rate: '',
               days: '',
               hours: '',
               amount: ''
            })));
         }
      }
   }, [hospitalTemplate, lead]);

   const addManualCalcItem = () => {
      setManualCalcItems(prev => [
         ...prev,
         { id: Date.now(), name: '', type: 'add', amount: '' }
      ]);
   };

   const removeManualCalcItem = (id) => {
      setManualCalcItems(prev => prev.filter(i => i.id !== id));
   };

   const updateManualCalcItem = (id, field, value) => {
      setManualCalcItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
   };

   // 🏥 HOSPITAL STANDARD BILLING TEMPLATE HANDLERS
   const handleSaveAsHospitalTemplate = async () => {
      const hId = lead?.assignedHospital?._id || (typeof lead?.assignedHospital === 'string' ? lead?.assignedHospital : null) ||
         lead?.leadProviderHospitalId?._id || (typeof lead?.leadProviderHospitalId === 'string' ? lead?.leadProviderHospitalId : null);

      if (!hId) {
         return toast.error("No assigned hospital found to save billing template.");
      }

      const hospName = lead?.assignedHospital?.hospitalName || lead?.assignedHospital?.name || lead?.assignedHospital?.user?.fullName || "Hospital";
      setSavingTemplate(true);

      try {
         const templateData = {
            baseItems: calcBaseItems.map(i => ({ name: i.name, rate: Number(i.rate) || 0, days: Number(i.days) || 1, hours: Number(i.hours) || 1, amount: Number(i.amount) || 0 })),
            deductedItems: calcDeductedItems.map(i => ({ name: i.name, rate: Number(i.rate) || 0, days: Number(i.days) || 1, hours: Number(i.hours) || 1, amount: Number(i.amount) || 0 })),
            manualItems: manualCalcItems.map(i => ({ name: i.name || 'Item', type: i.type, amount: Number(i.amount) || 0 })),
            platformFeePercent: Number(paymentForm.platformFee) || 35,
            platformFeeType: platformFeeType || 'percent',
            platformFeeEnabled: platformFeeEnabled,
            gstPercent: Number(paymentForm.gstPercent) || 18,
            updatedAt: new Date()
         };

         const res = await api.put(`/hospitals/${hId}`, { standardBillingTemplate: templateData });
         setHospitalTemplate(templateData);
         toast.success(`Saved standard billing template for ${hospName}!`);
      } catch (err) {
         console.error("Failed to save hospital billing template:", err);
         toast.error(err.response?.data?.message || "Failed to save hospital billing template.");
      } finally {
         setSavingTemplate(false);
      }
   };

   const handleApplyHospitalTemplate = () => {
      const t = hospitalTemplate || lead?.assignedHospital?.standardBillingTemplate;
      if (!t) return toast.error("No standard billing template saved for this hospital.");

      const hospName = lead?.assignedHospital?.hospitalName || lead?.assignedHospital?.name || lead?.assignedHospital?.user?.fullName || "Hospital";

      if (t.baseItems && t.baseItems.length > 0) {
         setCalcBaseItems(t.baseItems.map(i => ({
            name: i.name,
            rate: i.rate === 0 ? '' : i.rate.toString(),
            days: (!i.days || i.days <= 1) ? '' : i.days.toString(),
            hours: (!i.hours || i.hours <= 1) ? '' : i.hours.toString(),
            amount: i.amount === 0 ? '' : i.amount.toString()
         })));
      }

      if (t.deductedItems && t.deductedItems.length > 0) {
         setCalcDeductedItems(t.deductedItems.map(i => ({
            name: i.name,
            rate: i.rate === 0 ? '' : i.rate.toString(),
            days: (!i.days || i.days <= 1) ? '' : i.days.toString(),
            hours: (!i.hours || i.hours <= 1) ? '' : i.hours.toString(),
            amount: i.amount === 0 ? '' : i.amount.toString()
         })));
      }

      if (t.manualItems && t.manualItems.length > 0) {
         setManualCalcItems(t.manualItems.map((item, idx) => ({
            id: idx + 1,
            name: item.name,
            type: item.type || 'add',
            amount: item.amount === 0 ? '' : item.amount.toString()
         })));
      }

      if (t.platformFeeEnabled !== undefined) {
         setPlatformFeeEnabled(t.platformFeeEnabled);
      }

      if (t.platformFeeType) {
         setPlatformFeeType(t.platformFeeType);
      }

      toast.success(`Loaded standard billing template for ${hospName}!`);
   };

   const handleApplyManualCalculator = async () => {
      const netAmount = manualCalcItems.reduce((acc, item) => {
         const val = Number(item.amount) || 0;
         return item.type === 'deduct' ? acc - val : acc + val;
      }, 0);

      const val = Math.max(0, netAmount);
      // By default disable platform fee when manual calculation is applied
      setPlatformFeeEnabled(false);

      const pfAmount = 0;
      const gst = paymentForm.gstPercent === '' ? 18 : Number(paymentForm.gstPercent);
      const gAmount = val === 0 ? 0 : Math.round(val * (gst / 100));
      const total = val + gAmount;

      const formattedItems = manualCalcItems.filter(i => i.name.trim() || i.amount).map(i => ({
         name: i.name || 'Item',
         type: i.type,
         amount: Number(i.amount) || 0
      }));

      setPaymentForm(prev => ({
         ...prev,
         procedureAmount: val.toString(),
         platformFeeAmount: 0,
         gstAmount: gAmount,
         totalPayableAmount: total,
         manualCalcItems: formattedItems,
         calculatorBaseItems: [],
         calculatorDeductedItems: []
      }));
      setCalcBaseItems([]);
      setCalcDeductedItems([]);
      setShowManualCalculatorModal(false);

      try {
         const newBillingDetails = {
            ...(lead?.billingDetails || {}),
            platformFeeEnabled: false,
            procedureAmount: val,
            gstPercent: gst,
            platformFeeAmount: 0,
            gstAmount: gAmount,
            totalPayableAmount: total,
            manualCalcItems: formattedItems,
            calculatorBaseItems: [],
            calculatorDeductedItems: []
         };
         await api.patch(`/leads/${id}`, { billingDetails: newBillingDetails });
         toast.success(`Applied manual calculation: ₹${val.toLocaleString()} (+ ₹${gAmount.toLocaleString()} GST)`);
         fetchLead();
      } catch (err) {
         console.error("Failed to save manual calculator data:", err);
         toast.success(`Applied manual calculation: ₹${val.toLocaleString()}`);
      }
   };

   const handleApplyCalculator = async (finalAmount) => {
      const val = finalAmount;
      const isAmountMode = platformFeeType === 'amount';
      let pfAmount = 0;
      let feeVal = paymentForm.platformFee === '' ? 0 : Number(paymentForm.platformFee);

      if (!platformFeeEnabled) {
         pfAmount = 0;
      } else if (isAmountMode) {
         pfAmount = Number(paymentForm.platformFeeAmount) || 0;
         feeVal = val === 0 ? 0 : Math.round((pfAmount / val) * 100);
      } else {
         pfAmount = val === 0 ? 0 : Math.round(val * (feeVal / 100));
      }

      const gst = paymentForm.gstPercent === '' ? 18 : Number(paymentForm.gstPercent);
      const gAmount = (!platformFeeEnabled || pfAmount === 0) ? 0 : Math.round(pfAmount * (gst / 100));
      const total = (!platformFeeEnabled || pfAmount === 0) ? 0 : gAmount + pfAmount;

      const formattedBaseItems = calcBaseItems
         .filter(i => i.name && i.name.trim() !== '' && (Number(i.rate) > 0 || Number(i.amount) > 0))
         .map(i => ({ name: i.name, rate: Number(i.rate) || 0, days: Number(i.days) || 1, hours: Number(i.hours) || 1, amount: Number(i.amount) || 0 }));
      const formattedDeductedItems = calcDeductedItems
         .filter(i => i.name && i.name.trim() !== '' && (Number(i.rate) > 0 || Number(i.amount) > 0))
         .map(i => ({ name: i.name, rate: Number(i.rate) || 0, days: Number(i.days) || 1, hours: Number(i.hours) || 1, amount: Number(i.amount) || 0 }));

      setManualCalcItems([]);
      setPaymentForm(prev => ({
         ...prev,
         procedureAmount: val.toString(),
         platformFee: feeVal.toString(),
         gstAmount: gAmount,
         platformFeeAmount: pfAmount,
         totalPayableAmount: total,
         calculatorBaseItems: formattedBaseItems,
         calculatorDeductedItems: formattedDeductedItems,
         manualCalcItems: []
      }));
      setShowCalculatorModal(false);

      try {
         const newBillingDetails = {
            ...(lead?.billingDetails || {}),
            procedureAmount: val,
            gstPercent: gst,
            platformFee: feeVal,
            platformFeeType: platformFeeType || 'percent',
            gstAmount: gAmount,
            platformFeeAmount: pfAmount,
            totalPayableAmount: total,
            calculatorBaseItems: formattedBaseItems,
            calculatorDeductedItems: formattedDeductedItems,
            manualCalcItems: []
         };
         await api.patch(`/leads/${id}`, { billingDetails: newBillingDetails });
         toast.success(`Applied & saved calculated amount: ₹${val.toLocaleString()}`);
         fetchLead();
      } catch (err) {
         console.error("Failed to auto-save cost calculator data:", err);
         toast.success(`Applied calculated amount: ₹${val.toLocaleString()}`);
      }
   };

   const handleRemoveCalculator = async () => {
      const defaultBase = [
         { name: 'OT Charges', rate: '', days: '', hours: '', amount: '' },
         { name: 'Surgeon Charges', rate: '', days: '', hours: '', amount: '' },
         { name: 'Anesthesiologist Charges', rate: '', days: '', hours: '', amount: '' },
         { name: 'Bed Charges', rate: '', days: '', hours: '', amount: '' },
         { name: 'Fooding Charges', rate: '', days: '', hours: '', amount: '' },
         { name: 'Drain Remove', rate: '', days: '', hours: '', amount: '' }
      ];

      const t = hospitalTemplate || lead?.assignedHospital?.standardBillingTemplate;
      if (t && t.baseItems && t.baseItems.length > 0) {
         setCalcBaseItems(t.baseItems.map(i => ({
            name: i.name,
            rate: '',
            days: '',
            hours: '',
            amount: ''
         })));
      } else {
         setCalcBaseItems(defaultBase);
      }

      setCalcDeductedItems([]);
      setManualCalcItems([]);

      setPaymentForm(prev => ({
         ...prev,
         procedureAmount: '',
         platformFeeAmount: 0,
         gstAmount: 0,
         totalPayableAmount: 0,
         calculatorBaseItems: [],
         calculatorDeductedItems: [],
         manualCalcItems: []
      }));
      setShowCalculatorModal(false);

      try {
         const newBillingDetails = {
            ...(lead?.billingDetails || {}),
            procedureAmount: 0,
            platformFeeAmount: 0,
            gstAmount: 0,
            totalPayableAmount: 0,
            calculatorBaseItems: [],
            calculatorDeductedItems: [],
            manualCalcItems: []
         };
         await api.patch(`/leads/${id}`, { billingDetails: newBillingDetails });
         toast.success("Applied calculation removed successfully!");
         fetchLead();
      } catch (err) {
         console.error("Failed to remove cost calculator data:", err);
         toast.success("Calculation removed locally.");
      }
   };

   const handleRemoveManualCalculator = async () => {
      setManualCalcItems([]);
      setPaymentForm(prev => ({
         ...prev,
         procedureAmount: '',
         platformFeeAmount: 0,
         gstAmount: 0,
         totalPayableAmount: 0,
         manualCalcItems: [],
         calculatorBaseItems: [],
         calculatorDeductedItems: []
      }));
      setShowManualCalculatorModal(false);

      try {
         const newBillingDetails = {
            ...(lead?.billingDetails || {}),
            procedureAmount: 0,
            platformFeeAmount: 0,
            gstAmount: 0,
            totalPayableAmount: 0,
            manualCalcItems: [],
            calculatorBaseItems: [],
            calculatorDeductedItems: []
         };
         await api.patch(`/leads/${id}`, { billingDetails: newBillingDetails });
         toast.success("Applied manual calculation removed successfully!");
         fetchLead();
      } catch (err) {
         console.error("Failed to remove manual calculator data:", err);
         toast.success("Manual calculation removed locally.");
      }
   };

   const handleAbortSurgery = async () => {
      if (!rescheduleForm.abortFault) {
         toast.error("Please select abortion fault");
         return;
      }
      if (!rescheduleForm.abortReason) {
         toast.error("Please provide a reason");
         return;
      }

      setIsRescheduling(true);
      try {
         const payload = {
            isAbort: true,
            faultBy: rescheduleForm.abortFault,
            notes: rescheduleForm.abortReason,
            penaltyPercentage: rescheduleForm.penaltyPercentage
         };
         const res = await api.post(`/leads/${id}/reschedule-surgery`, payload);
         if (res.data.success) {
            toast.success(res.data.message);
            setShowRescheduleModal(false);
            setTimeout(() => {
               fetchLead();
            }, 500);
         }
      } catch (error) {
         toast.error(error.response?.data?.message || "Failed to abort");
      } finally {
         setIsRescheduling(false);
      }
   };

   const handleRescheduleSurgery = async () => {
      if (!rescheduleForm.faultBy) {
         toast.error("Please select who is at fault");
         return;
      }



      setIsRescheduling(true);
      try {
         const payload = {
            faultBy: rescheduleForm.faultBy,
            penaltyAmount: parseFloat(rescheduleForm.penaltyAmount) || 0,
            notes: rescheduleForm.notes,
            isAbort: false,
            newSurgeryDate: rescheduleForm.newSurgeryDate,
            hospitalAlternateContact: rescheduleForm.useAlternate ? {
               email: rescheduleForm.alternateEmail,
               whatsapp: rescheduleForm.alternateWhatsapp,
               useAlternate: true
            } : null
         };
         const res = await api.post(`/leads/${id}/reschedule-surgery`, payload);
         if (res.data.success) {
            toast.success(res.data.message || "Reschedule processed!");
            setShowRescheduleModal(false);
            setTimeout(() => {
               fetchLead();
            }, 500);
            // 🛡️ REVISION: No auto-redirect. Link is sent via WhatsApp/Email by backend.
         }
      } catch (error) {
         toast.error(error.response?.data?.message || "Failed to reschedule surgery");
      } finally {
         setIsRescheduling(false);
      }
   };

   useEffect(() => {
      if (lead) {
         // 🛡️ REVISION: Intelligent Defaulting based on Lead Type & Partner Role
         let initialSurgeon = lead.assignedSurgeon?._id || lead.assignedSurgeon || '';
         let initialanesthesiologist = lead.assignedAnesthesiologist?._id || lead.assignedAnesthesiologist || lead.assignedanesthesiologist?._id || lead.assignedanesthesiologist || '';

         // Extract provider info with fallbacks (String-safe)
         const providerId = (lead.leadProviderDoctorId?._id || lead.leadProviderDoctorId || lead.leadGeneratedById)?.toString();

         if (lead.leadType === 'surgery' && lead.leadGeneratedByType === 'doctor' && providerId) {
            // Find doctor object for specialization info (String-safe find)
            let doctorObj = lead.leadProviderDoctorId && typeof lead.leadProviderDoctorId === 'object' ? lead.leadProviderDoctorId : null;
            if (!doctorObj && doctors.length > 0) {
               doctorObj = doctors.find(d => d._id?.toString() === providerId);
            }

            const spec = doctorObj?.specialization?.toLowerCase() || '';
            const isanesthesiologist = spec.includes('anesthe') || spec.includes('anaesthe');

            if (isanesthesiologist) {
               if (!initialanesthesiologist) initialanesthesiologist = providerId;
            } else {
               if (!initialSurgeon) initialSurgeon = providerId;
            }
         } else if (lead.leadGeneratedByType === 'doctor' && !initialSurgeon && !initialanesthesiologist) {
            // Standard fallback if no specific surgical intent detected but doctor is provider
            initialSurgeon = providerId || '';
         }

         const isSurgeonOther = lead.surgeonType === 'other' || (!initialSurgeon && Boolean(lead.assignedSurgeonOtherName || lead.assignedSurgeonOtherPhone || lead.assignedSurgeonOtherEmail));
         const isAnesthesiologistOther = lead.anesthesiologistType === 'other' || (!initialanesthesiologist && Boolean(lead.assignedanesthesiologistOtherName || lead.assignedanesthesiologistOtherPhone || lead.assignedanesthesiologistOtherEmail));

         setAssignmentForm({
            assignedSurgeon: initialSurgeon,
            surgeonType: isSurgeonOther ? 'other' : 'partner',
            assignedSurgeonOtherName: lead.assignedSurgeonOtherName || '',
            assignedSurgeonOtherOCMR: lead.assignedSurgeonOtherOCMR || '',
            assignedSurgeonOtherPhone: lead.assignedSurgeonOtherPhone || '',
            assignedSurgeonOtherEmail: lead.assignedSurgeonOtherEmail || '',
            assignedanesthesiologist: initialanesthesiologist,
            anesthesiologistType: isAnesthesiologistOther ? 'other' : 'partner',
            assignedanesthesiologistOtherName: lead.assignedanesthesiologistOtherName || '',
            assignedanesthesiologistOtherOCMR: lead.assignedanesthesiologistOtherOCMR || '',
            assignedanesthesiologistOtherPhone: lead.assignedanesthesiologistOtherPhone || '',
            assignedanesthesiologistOtherEmail: lead.assignedanesthesiologistOtherEmail || '',
            assignedHospital: lead.assignedHospital?._id || lead.assignedHospital || lead.leadProviderHospitalId?._id || lead.leadProviderHospitalId || ''
         });
         const rawOtSlot = lead.schedulingData?.otSlot || '';
         const parsedSlotType = lead.schedulingData?.slotType || (rawOtSlot.includes('@') ? rawOtSlot.split('@')?.[0]?.trim() : rawOtSlot) || '';
         const parsedSpecificTime = lead.schedulingData?.specificTime || (rawOtSlot.includes('@') ? rawOtSlot.split('@')?.[1]?.trim() : '') || '';

         const hasRescheduleState = Boolean(lead.surgicalData?.surgicalReschedule || lead.surgicalReschedule);

         setSchedulingForm({
            surgeryDate: hasRescheduleState ? '' : (lead.schedulingData?.surgeryDate ? new Date(lead.schedulingData.surgeryDate).toISOString().split('T')[0] : ''),
            otSlot: hasRescheduleState ? '' : rawOtSlot,
            slotType: hasRescheduleState ? '' : parsedSlotType,
            specificTime: hasRescheduleState ? '' : parsedSpecificTime,
            surgeonConfirmed: true,
            anaesthetistConfirmed: true
         });
         const hId = lead.assignedHospital?._id || lead.assignedHospital ||
            lead.leadProviderHospitalId?._id || lead.leadProviderHospitalId ||
            (lead.leadGeneratedByType === 'hospital' ? lead.leadGeneratedById : null);

         if (hId) {
            setHospitalProcess('bed_check');
            if (lead.assignedHospital && typeof lead.assignedHospital === 'object' && lead.assignedHospital.standardBillingTemplate) {
               setHospitalTemplate(lead.assignedHospital.standardBillingTemplate);
            } else {
               api.get(`/hospitals/${hId}`).then(res => {
                  const hData = res.data?.data || res.data;
                  if (hData && hData.standardBillingTemplate) {
                     setHospitalTemplate(hData.standardBillingTemplate);
                  }
               }).catch(() => { });
            }
         }

         // 🔄 PERSISTENCE Part A: Resume from last saved intake step if applicable
         if (lead.registrationStep && intakeStep === 1) {
            setIntakeStep(lead.registrationStep);
         }

         // 🔄 PERSISTENCE Part B: Load saved form data into local state (Auto-filling Chief Complaints & Past Medical History)
         if (lead.clinicalIntake) {
            setIntakeForm(prev => ({
               ...prev,
               ...lead.clinicalIntake,
               medicalDetails: {
                  ...prev.medicalDetails,
                  ...(lead.clinicalIntake.medicalDetails || {}),
                  history: {
                     ...prev.medicalDetails?.history,
                     ...(lead.clinicalIntake.medicalDetails?.history || {}),
                     currentIllness: lead.chiefComplaints || lead.disease || lead.treatmentRequired || lead.clinicalIntake?.medicalDetails?.history?.currentIllness || '',
                     pastDiseases: lead.pastMedicalHistory || lead.comorbidities || lead.clinicalIntake?.medicalDetails?.history?.pastDiseases || ''
                  }
               },
               insurance: {
                  ...prev.insurance,
                  ...(lead.clinicalIntake?.insurance || {})
               }
            }));
         } else if (lead.chiefComplaints || lead.pastMedicalHistory || lead.disease || lead.treatmentRequired || lead.comorbidities) {
            setIntakeForm(prev => ({
               ...prev,
               medicalDetails: {
                  ...prev.medicalDetails,
                  history: {
                     ...prev.medicalDetails?.history,
                     currentIllness: lead.chiefComplaints || lead.disease || lead.treatmentRequired || '',
                     pastDiseases: lead.pastMedicalHistory || lead.comorbidities || ''
                  }
               }
            }));
         }

         // 🔄 PERSISTENCE Part C: Load Billing & Payment Data
         const leadKey = lead._id || id;
         const isInitialLeadLoad = prevBillingLeadIdRef.current !== leadKey;
         if (isInitialLeadLoad) {
            prevBillingLeadIdRef.current = leadKey;
            if (lead.billingDetails) {
               const savedFeeType = lead.billingDetails.platformFeeType || 'percent';
               setPlatformFeeType(savedFeeType);

               const savedProcAmt = lead.billingDetails.procedureAmount !== undefined && lead.billingDetails.procedureAmount !== null ? lead.billingDetails.procedureAmount.toString() : '';
               const procVal = savedProcAmt === '' ? 0 : parseInt(savedProcAmt);

               let savedFee = lead.billingDetails.platformFee !== undefined && lead.billingDetails.platformFee !== null ? lead.billingDetails.platformFee.toString() : '';
               // Sanitize: If feeType is 'percent' and savedFee > 100 (e.g. legacy default 500), fall back to '35'
               if (savedFeeType === 'percent' && (savedFee === '' || Number(savedFee) > 100)) {
                  savedFee = '35';
               }

               const savedFeeEnabled = lead.billingDetails.platformFeeEnabled !== false;
               setPlatformFeeEnabled(savedFeeEnabled);
               const savedGstEnabled = lead.billingDetails.gstEnabled !== false;
               setGstEnabled(savedGstEnabled);

               const savedGstPct = lead.billingDetails.gstPercent !== undefined ? lead.billingDetails.gstPercent : 18;
               let savedFeeAmt = 0;
               let savedGstAmt = 0;
               let savedTotal = 0;

               const savedGstEnabledForCalc = lead.billingDetails.gstEnabled !== false;
               if (savedFeeEnabled) {
                  if (savedFeeType === 'amount') {
                     savedFeeAmt = lead.billingDetails.platformFeeAmount !== undefined ? Number(lead.billingDetails.platformFeeAmount) : 0;
                  } else {
                     savedFeeAmt = procVal === 0 ? 0 : Math.round(procVal * ((Number(savedFee) || 0) / 100));
                  }
                  savedGstAmt = (!savedGstEnabledForCalc || savedFeeAmt === 0) ? 0 : Math.round(savedFeeAmt * (savedGstPct / 100));
                  savedTotal = savedFeeAmt + savedGstAmt;
               } else {
                  savedFeeAmt = 0;
                  savedGstAmt = !savedGstEnabledForCalc ? 0 : (procVal === 0 ? 0 : Math.round(procVal * (savedGstPct / 100)));
                  savedTotal = procVal + savedGstAmt;
               }

               setPaymentForm(prev => ({
                  ...prev,
                  procedureAmount: savedProcAmt,
                  implantCost: lead.billingDetails.implantCost || 0,
                  gstPercent: savedGstPct,
                  platformFee: savedFee,
                  gstAmount: savedGstAmt,
                  platformFeeAmount: savedFeeAmt,
                  totalPayableAmount: savedTotal,
                  calculatorBaseItems: lead.billingDetails.calculatorBaseItems || [],
                  calculatorDeductedItems: lead.billingDetails.calculatorDeductedItems || [],
                  manualCalcItems: lead.billingDetails.manualCalcItems || [],
                  description: lead.billingDetails.description || lead.billingDetails.remarks || ''
               }));
               if (lead.billingDetails.manualCalcItems && lead.billingDetails.manualCalcItems.length > 0) {
                  setManualCalcItems(lead.billingDetails.manualCalcItems.map((item, idx) => ({
                     id: idx + 1,
                     name: item.name,
                     type: item.type || 'add',
                     amount: item.amount === 0 ? '' : item.amount.toString()
                  })));
               }
               if (lead.billingDetails.calculatorBaseItems && lead.billingDetails.calculatorBaseItems.length > 0) {
                  setCalcBaseItems(lead.billingDetails.calculatorBaseItems.map(item => ({
                     name: item.name,
                     rate: item.rate === 0 ? '' : item.rate.toString(),
                     days: (!item.days || item.days <= 1) ? '' : item.days.toString(),
                     hours: (!item.hours || item.hours <= 1) ? '' : item.hours.toString(),
                     amount: item.amount === 0 ? '' : item.amount.toString()
                  })));
               }
               if (lead.billingDetails.calculatorDeductedItems && lead.billingDetails.calculatorDeductedItems.length > 0) {
                  setCalcDeductedItems(lead.billingDetails.calculatorDeductedItems.map(item => ({
                     name: item.name,
                     rate: item.rate === 0 ? '' : item.rate.toString(),
                     days: (!item.days || item.days <= 1) ? '' : item.days.toString(),
                     hours: (!item.hours || item.hours <= 1) ? '' : item.hours.toString(),
                     amount: item.amount === 0 ? '' : item.amount.toString()
                  })));
               }
            }
         }

         if (lead.hospitalAlternateContact) {
            setPaymentForm(prev => ({
               ...prev,
               alternateEmail: lead.hospitalAlternateContact.email !== undefined ? lead.hospitalAlternateContact.email : (prev.alternateEmail || ''),
               alternateWhatsapp: lead.hospitalAlternateContact.whatsapp !== undefined ? lead.hospitalAlternateContact.whatsapp : (prev.alternateWhatsapp || ''),
               useAlternate: lead.hospitalAlternateContact.useAlternate !== undefined ? lead.hospitalAlternateContact.useAlternate : Boolean(prev.useAlternate)
            }));
         }

         // 🔄 PERSISTENCE Part D: Load Payout Data
         if (lead.payoutData) {
            setPayoutForm(prev => ({
               ...prev,
               refundDetails: lead.payoutData.refundDetails || prev.refundDetails,
               payoutNotes: lead.payoutData.payoutNotes || ''
            }));
         }
      }
   }, [lead]);

   const handleSyncPaymentStatus = async (silent = false) => {
      if (!silent) setUpdating(true);
      try {
         const response = await api.post(`/payments/sync`, { leadId: id });
         if (response.data.success) {
            if (!silent) {
               toast.success(response.data.message || 'Payment confirmed! Pipeline updated.');
            }
            await fetchLead();
         } else if (!silent) {
            toast.error(response.data.message || 'Payment still pending.');
         }
      } catch (error) {
         if (!silent) toast.error(error.response?.data?.message || 'Failed to sync payment status');
      } finally {
         if (!silent) setUpdating(false);
      }
   };

   const handleConfirmPenaltyManually = async () => {
      setUpdating(true);
      try {
         const response = await api.post(`/leads/${id}/confirm-penalty-manual`);
         if (response.data.success) {
            toast.success('Penalty marked as paid manually! Case restored to Stage 6: OT Scheduling.');
            await fetchLead();
         } else {
            toast.error(response.data.message || 'Failed to confirm penalty');
         }
      } catch (err) {
         toast.error(err.response?.data?.message || 'Failed to confirm penalty manually');
      } finally {
         setUpdating(false);
      }
   };

   const [surgeonCompletionInput, setSurgeonCompletionInput] = useState('');
   const [anesthesiologistCompletionInput, setanesthesiologistCompletionInput] = useState('');

   const handleVerifyCompletionOtp = async (role, directOtp) => {
      const otp = directOtp || (role === 'surgeon' ? surgeonCompletionInput : anesthesiologistCompletionInput);
      if (!otp || otp.length < 6) return; // Don't toast on auto-verify until 6 digits

      setUpdating(true);
      try {
         const res = await api.post(`/leads/public/verify-completion-otp/${id}`, {
            otp,
            role
         });

         if (res.data.success) {
            toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} completion verified!`);
            if (role === 'surgeon') setSurgeonCompletionInput('');
            else setanesthesiologistCompletionInput('');
            fetchLead();
         }
      } catch (error) {
         toast.error(error.response?.data?.message || "Verification failed. Please check the code.");
      } finally {
         setUpdating(false);
      }
   };

   const handleTriggerCompletionProtocol = async () => {
      if (!window.confirm("Trigger clinical completion codes immediately? This will bypass the 10-minute automated window.")) return;
      setUpdating(true);
      try {
         await api.post(`/leads/${id}/trigger-completion-protocol`);
         toast.success('Work completion protocol deployed immediately!');
         fetchLead();
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to trigger protocol');
      } finally {
         setUpdating(false);
      }
   };

   const handleVerifyAndReleaseOTPs = async () => {
      setUpdating(true);
      try {
         const isHistorical = isPastSurgeryCase || lead?.razorpayDetails?.lastPaymentId === 'MANUAL_SETTLEMENT';
         if (isHistorical) {
            await api.patch(`/leads/${id}/status`, {
               status: 'closure',
               notes: 'Past surgery case OTPs bypassed -> transitioned to post-op'
            });
            toast.success('Past Surgery Case Moved to Post-Op Stage!');
            fetchLead();
            return;
         }

         await api.post(`/leads/${id}/verify-release-otps`, { sendOtp: false });
         toast.success('Moved to Live Surgery stage for manual doctor verification.');
         setViewedStatus(null);
         fetchLead();
      } catch (error) {
         console.error('[VERIFY_RELEASE_OTPS_ERR]', error);
         toast.error(error.response?.data?.message || 'Failed to process case update');
      } finally {
         setUpdating(false);
      }
   };

   const handleGeneratePaymentLink = async () => {
      setUpdating(true);



      try {
         // Trigger payment generation with latest billing data to avoid race conditions
         const payload = {
            billingDetails: {
               procedureAmount: paymentForm.procedureAmount,
               implantCost: 0,
               platformFee: paymentForm.platformFee,
               platformFeeType: platformFeeType,
               gstPercent: paymentForm.gstPercent,
               gstAmount: paymentForm.gstAmount,
               platformFeeAmount: paymentForm.platformFeeAmount,
               totalPayableAmount: paymentForm.totalPayableAmount,
               platformFeeEnabled: platformFeeEnabled,
               gstEnabled: gstEnabled,
               manualCalcItems: paymentForm.manualCalcItems || [],
               description: paymentForm.description || '',
               calculatorBaseItems: calcBaseItems.map(i => ({ name: i.name, rate: Number(i.rate) || 0, days: Number(i.days) || 1, hours: Number(i.hours) || 1, amount: Number(i.amount) || 0 })),
               calculatorDeductedItems: calcDeductedItems.map(i => ({ name: i.name, rate: Number(i.rate) || 0, days: Number(i.days) || 1, hours: Number(i.hours) || 1, amount: Number(i.amount) || 0 }))
            },
            hospitalAlternateContact: {
               email: paymentForm.alternateEmail,
               whatsapp: paymentForm.alternateWhatsapp,
               useAlternate: paymentForm.useAlternate
            }
         };


         await api.post(`/leads/${id}/notify-payment`, payload);
         toast.success('Billing saved and Razorpay link generated!');
         fetchLead();
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to generate payment link');
      } finally {
         setUpdating(false);
      }
   };

   const [refreshTrigger, setRefreshTrigger] = useState(0);

   const fetchLead = async () => {
      try {
         let leadId = routeId;

         if (window.location.pathname.startsWith('/surgeries')) {
            try {
               const surgRes = await api.get(`/surgeries/${routeId}?t=${Date.now()}`);
               if (surgRes.data?.success && surgRes.data?.data) {
                  const surgeryLeadId = surgRes.data.data.lead?._id || surgRes.data.data.lead;
                  if (surgeryLeadId) {
                     leadId = surgeryLeadId;
                  } else {
                     // Surgery found but has no linked lead — nothing we can do
                     console.error('[fetchLead] Surgery has no linked lead ID:', surgRes.data.data);
                     toast.error('This surgery has no linked patient lead.');
                     navigate('/surgeries');
                     return;
                  }
               }
            } catch (surgErr) {
               // Surgery not found by this ID — cannot resolve lead
               console.error('[fetchLead] Surgery not found for routeId:', routeId, surgErr?.response?.data?.message || surgErr.message);
               toast.error('Surgery record not found.');
               navigate('/surgeries');
               return;
            }
         }

         const response = await api.get(`/leads/${leadId}?t=${Date.now()}`);
         setLead(response.data.data);

         try {
            const payRes = await api.get(`/payments?leadId=${leadId}&limit=100`);
            if (payRes.data?.success && payRes.data?.data?.payments) {
               setPayments(payRes.data.data.payments);
            }
         } catch (payErr) {
            console.error('Error fetching payments for lead:', payErr);
         }
      } catch (error) {
         console.error('[fetchLead] Failed to load lead:', error?.response?.data?.message || error.message);
         toast.error(error?.response?.data?.message || 'Failed to load details');
         navigate('/surgeries');
      } finally {
         setLoading(false);
      }
   };


   // 🔄 REFRESH SYSTEM: Centralized data fetcher
   useEffect(() => {
      if (routeId) fetchLead();
   }, [routeId, refreshTrigger]);

   // 🏥 RESOLVE ASSIGNED HOSPITAL
   const resolvedHospital = useMemo(() => {
      if (!lead) return null;

      const hId = (lead.assignedHospital?._id || lead.assignedHospital ||
         lead.leadProviderHospitalId?._id || lead.leadProviderHospitalId ||
         (lead.leadGeneratedByType === 'hospital' ? lead.leadGeneratedById : null));

      // ✅ PRIORITY 1: Resolve from local hospitals list using IDs (to get full fields like gstPercent)
      if (hId && hospitals.length > 0) {
         const found = hospitals.find(h =>
            h._id?.toString() === hId?.toString() ||
            h.id?.toString() === hId?.toString() ||
            h._id?.toString() === hId?.$oid?.toString()
         );
         if (found) return found;
      }

      // ✅ PRIORITY 2: Fallback to backend populated object
      if (lead.assignedHospital?.hospitalName) return lead.assignedHospital;
      if (lead.leadProviderHospitalId?.hospitalName) return lead.leadProviderHospitalId;

      return null;
   }, [lead, hospitals]);

   // 💰 INITIALIZE PAYMENT FORM FOR NEW LEADS
   useEffect(() => {
      if (lead && resolvedHospital) {
         setPaymentForm(prev => {
            // 🛡️ CRITICAL: If billing details were already saved in database for this lead, PRESERVE THEM!
            if (lead.billingDetails && (lead.billingDetails.totalPayableAmount > 0 || lead.billingDetails.platformFeeAmount > 0 || lead.billingDetails.procedureAmount > 0 || lead.billingDetails.platformFeeType)) {
               return prev;
            }

            let gst = resolvedHospital.gstPercent !== undefined ? resolvedHospital.gstPercent : 18;
            let fee = resolvedHospital.platformFeePercent !== undefined ? resolvedHospital.platformFeePercent : 5;

            if (fee > 100) fee = 5;

            const procedureAmount = prev.procedureAmount || '';
            const val = procedureAmount === '' ? 0 : parseInt(procedureAmount);

            const pfAmount = val === 0 ? 0 : Math.round(val * (fee / 100));
            const gAmount = pfAmount === 0 ? 0 : Math.round(pfAmount * (gst / 100));
            const total = val === 0 ? 0 : gAmount + pfAmount;

            return {
               ...prev,
               gstPercent: gst,
               platformFee: fee.toString(),
               procedureAmount: procedureAmount,
               gstAmount: gAmount,
               platformFeeAmount: pfAmount,
               totalPayableAmount: total,
               description: prev.description || lead.billingDetails?.description || lead.billingDetails?.remarks || ''
            };
         });
      }
   }, [lead, resolvedHospital]);

   const sortedMonitoringLogs = useMemo(() => {
      if (!lead?.surgicalData?.postOpMonitoring?.logs) return [];
      return [...lead.surgicalData.postOpMonitoring.logs].sort((a, b) =>
         new Date(a.visitDate) - new Date(b.visitDate) || a.startTime.localeCompare(b.startTime)
      );
   }, [lead]);

   const unifiedTimeline = useMemo(() => {
      if (!lead) return [];

      const formatStageName = (status) => {
         if (!status) return 'Unknown Status';
         const mappings = {
            'new': 'Intake',
            'screening': 'Screening',
            'pre_screening': 'Screening',
            'patient_ready': 'Patient Readiness',
            'doc_verification': 'Doctor Review',
            'doctor_assignment': 'Doctor Assignment',
            'surgery_ready': 'Surgery Ready',
            'team_deployment': 'OT Schedule',
            'scheduling': 'Payment Links',
            'payment_links': 'Payment Links',
            'paid': 'Paid / Confirmed',
            'surgery': 'Live Surgery',
            'closure': 'Post-Op',
            'payout_processing': 'Payout Processing',
            'completed': 'Finalized'
         };
         return mappings[status] || status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      };

      const rawHistory = (lead.statusHistory || []).map(h => ({
         ...h,
         type: 'status',
         date: new Date(h.changedAt || h.createdAt),
         title: formatStageName(h.status || h.to),
         notes: h.notes
      }));

      // Deduplicate history to prevent double-logging bugs from the backend
      const history = rawHistory.filter((item, index, self) =>
         index === self.findIndex((t) => (
            t.title === item.title && Math.abs(t.date - item.date) < 60000
         ))
      );

      const monitoring = (lead.surgicalData?.postOpMonitoring?.logs || []).map(l => ({
         ...l,
         type: 'monitoring',
         date: new Date(l.createdAt || l.visitDate),
         title: `Visit Logged: ${new Date(l.visitDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`,
         notes: `Duration: ${l.startTime} - ${l.endTime}`
      }));

      return [...history, ...monitoring].sort((a, b) => b.date - a.date);
   }, [lead]);

   // ─── Pipeline Journey: enriched per-stage data for Audit Trail UI ───────────
   const pipelineJourney = useMemo(() => {
      if (!lead) return [];
      const stages = [];
      const hist = lead.statusHistory || [];
      const tsFor = (status) => {
         const e = hist.find(h => h.status === status || h.to === status);
         return e?.changedAt || e?.createdAt || null;
      };
      const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
      const fmtDT = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null;

      const surgeon = (lead.assignedSurgeon?.user && typeof lead.assignedSurgeon.user === 'object' && (lead.assignedSurgeon.user.firstName || lead.assignedSurgeon.user.lastName))
         ? `${lead.assignedSurgeon.user.firstName || ''} ${lead.assignedSurgeon.user.lastName || ''}`.trim()
         : (lead.assignedSurgeon?.name || lead.surgeonName);
      const surgeonSpec = lead.assignedSurgeon?.specialization;
      const anesthesiologist = (lead.assignedAnesthesiologist?.user && typeof lead.assignedAnesthesiologist.user === 'object' && (lead.assignedAnesthesiologist.user.firstName || lead.assignedAnesthesiologist.user.lastName))
         ? `${lead.assignedAnesthesiologist.user.firstName || ''} ${lead.assignedAnesthesiologist.user.lastName || ''}`.trim()
         : (lead.assignedAnesthesiologist?.name || lead.anesthesiologistName);
      const hospital = lead.assignedHospital?.hospitalName || lead.hospitalName;
      const hospitalCity = lead.assignedHospital?.address?.city;

      const assignmentDetails = [
         { label: 'Surgeon', value: surgeon ? (surgeonSpec ? `${surgeon} (${surgeonSpec})` : surgeon) : null },
         { label: 'anesthesiologist', value: anesthesiologist },
         { label: 'Hospital', value: hospital ? (hospitalCity ? `${hospital} — ${hospitalCity}` : hospital) : null },
      ].filter(d => d.value);

      // Stage 1: Lead Created
      stages.push({
         stage: 1, key: 'created', label: 'Lead Created', color: 'blue',
         timestamp: lead.createdAt,
         details: [
            { label: 'Source', value: lead.leadGeneratedByName ? `${lead.leadGeneratedByName} (${(lead.source || lead.leadSource || '').replace(/_/g, ' ').replace(/\\b\\w/g, l => l.toUpperCase())})` : (lead.source || lead.leadSource)?.replace(/_/g, ' ').replace(/\\b\\w/g, l => l.toUpperCase()) },
            { label: 'Service', value: lead.subServiceId?.name },
            { label: 'Website Lead', value: lead.isWebsiteLead ? 'Yes' : null },
            { label: 'Assigned CC', value: lead.assignedTo?.name || lead.assignedTo?.email },
            { label: 'Created On', value: fmtDT(lead.createdAt) },
            ...assignmentDetails,
         ].filter(d => d.value),
      });

      // Stage 2: Pre-Screening
      const preScreenTs = tsFor('pre_screening') || tsFor('screening');
      stages.push({
         stage: 2, key: 'pre_screening', label: 'Pre-Screening', color: 'indigo',
         timestamp: preScreenTs,
         details: [
            { label: 'Eligibility', value: lead.screeningData?.eligibility !== false ? 'Eligible' : 'Not Eligible' },
            { label: 'Condition Suitable', value: lead.screeningData?.conditionSuitable !== false ? 'Yes' : 'No' },
            { label: 'Patient Willing', value: lead.screeningData?.patientWilling !== false ? 'Yes' : 'No' },
            { label: 'Timeline', value: lead.screeningData?.timeline },
            { label: 'Complications', value: lead.screeningData?.complications },
            { label: 'Follow-up Date', value: fmtD(lead.screeningData?.followUpDate) },
            { label: 'Screened On', value: fmtDT(preScreenTs) },
         ].filter(d => d.value),
      });

      // Stage 3: Doc Verification
      const docVerTs = tsFor('doc_verification');
      stages.push({
         stage: 3, key: 'doc_verification', label: 'Document Verification', color: 'purple',
         timestamp: docVerTs,
         details: [
            { label: 'Verified On', value: fmtDT(docVerTs) },
            { label: 'Documents', value: lead.documents?.length > 0 ? `${lead.documents.length} file(s) uploaded` : null },
            ...assignmentDetails,
         ].filter(d => d.value),
      });

      // Stage 4: Clinical Assessment
      const clinicalTs = tsFor('clinical_assessment');
      stages.push({
         stage: 4, key: 'clinical_assessment', label: 'Clinical Assessment', color: 'amber',
         timestamp: clinicalTs,
         details: [
            { label: 'Reviewing Doctor', value: lead.reviewingDoctor?.name || lead.clinicalData?.reviewingDoctorName },
            { label: 'Surgery Feasible', value: lead.clinicalData?.surgeryFeasible !== false ? 'Yes' : 'No' },
            { label: 'Anaesthesia Clearance', value: lead.clinicalData?.anaesthesiaClearance !== false ? 'Yes' : 'No' },
            { label: 'Risk Level', value: lead.clinicalData?.riskLevel },
            { label: 'Fit for Surgery', value: lead.clinicalData?.isFit !== false ? 'Yes' : 'No' },
            { label: 'Assessed On', value: fmtDT(clinicalTs) },
         ].filter(d => d.value),
      });

      // Stage 5: Doctor Assignment
      const assignTs = tsFor('doctor_assignment');
      stages.push({
         stage: 5, key: 'doctor_assignment', label: 'Doctor Assignment', color: 'emerald',
         timestamp: assignTs,
         details: [
            ...assignmentDetails,
            { label: 'Assigned On', value: fmtDT(assignTs) },
         ].filter(d => d.value),
      });

      // Stage 6: Surgery Scheduling
      const scheduleTs = tsFor('scheduling');
      const surgeryDate = lead.scheduledSurgeryDate || lead.surgeryDate;
      stages.push({
         stage: 6, key: 'scheduling', label: 'Surgery Scheduling', color: 'teal',
         timestamp: scheduleTs,
         details: [
            { label: 'Surgery Date', value: fmtD(surgeryDate) },
            { label: 'OT Slot', value: lead.otSlot },
            { label: 'Surgeon Confirmed', value: lead.surgeonConfirmed ? 'Yes' : 'No' },
            { label: 'Anaesthetist Confirmed', value: lead.anaesthetistConfirmed ? 'Yes' : 'No' },
            { label: 'Scheduled On', value: fmtDT(scheduleTs) },
         ].filter(d => d.value),
      });

      // Stage 7: Patient Intake
      const intakeTs = tsFor('patient_intake');
      const med = lead.intakeData?.medicalDetails || lead.medicalDetails || {};
      const hist2 = med.history || {};
      const vitals = med.vitals || {};
      const allergies = med.allergies || {};
      stages.push({
         stage: 7, key: 'patient_intake', label: 'Patient Intake', color: 'rose',
         timestamp: intakeTs,
         details: [
            { label: 'Readiness Date', value: fmtD(lead.intakeData?.readinessDate) },
            { label: 'Ayushman Card', value: lead.intakeData?.insurance?.hasAyushmanCard },
            { label: 'Current Illness', value: hist2.currentIllness },
            { label: 'Past Diseases', value: hist2.pastDiseases },
            { label: 'Previous Surgeries', value: hist2.previousSurgeries },
            { label: 'Current Medicines', value: med.medications?.currentMedicines },
            { label: 'Drug Allergy', value: allergies.drugAllergy },
            { label: 'Blood Pressure', value: vitals.bp },
            { label: 'Sugar Level', value: vitals.sugarLevel },
            { label: 'Weight / Height', value: (vitals.weight || vitals.height) ? `${vitals.weight || '—'} kg / ${vitals.height || '—'} cm` : null },
            { label: 'Intake Completed', value: fmtDT(intakeTs) },
         ].filter(d => d.value),
      });

      // Stage 8: OT Monitoring
      const monLogs = lead.monitoringLogs || lead.surgicalData?.postOpMonitoring?.logs || [];
      const otTs = tsFor('ot_monitoring');
      stages.push({
         stage: 8, key: 'ot_monitoring', label: 'OT / Post-Op Monitoring', color: 'cyan',
         timestamp: otTs,
         details: [
            { label: 'Total Visits', value: monLogs.length > 0 ? `${monLogs.length} visit(s)` : '0' },
            ...monLogs.slice(0, 3).map((l, i) => ({
               label: `Visit ${i + 1}`,
               value: `${l.visitDate ? new Date(l.visitDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '?'} — ${l.startTime || '?'} to ${l.endTime || '?'}${l.notes ? ` (${l.notes.substring(0, 40)})` : ''}`,
            })),
         ].filter(d => d.value),
      });

      // Stage 9: Payout Processing
      const payoutTs = tsFor('payout_processing');
      const pData = lead.payoutData || {};
      stages.push({
         stage: 9, key: 'payout_processing', label: 'Payout Processing', color: 'violet',
         timestamp: payoutTs,
         details: [
            { label: 'Status', value: (lead.paymentStatus || pData.status || 'Paid').toUpperCase() },
            { label: 'Payment Mode', value: pData.paymentMode || pData.paymentMethod },
            { label: 'Transaction ID', value: pData.transactionId || pData.razorpayPaymentId },
            { label: 'Processed On', value: fmtDT(payoutTs) },
         ].filter(d => d.value),
      });

      // Stage 10: Finalized
      const finalizedAt = pData.finalizedAt || (lead.status === 'completed' ? lead.updatedAt : null);
      stages.push({
         stage: 10, key: 'completed', label: 'Deal Finalized', color: 'green',
         timestamp: finalizedAt,
         details: [
            { label: 'Finalized On', value: fmtDT(finalizedAt) },
            { label: 'Booking ID', value: lead.bookingId || lead.leadId },
         ].filter(d => d.value),
      });

      // Only return stages that have been reached (have a timestamp OR are the current/past status)
      const reachedStatuses = new Set(hist.map(h => h.status || h.to));
      reachedStatuses.add(lead.status);
      if (lead.status === 'completed') reachedStatuses.add('completed');
      return stages.filter(s => reachedStatuses.has(s.key) || s.timestamp);
   }, [lead]);

   const inHouseDoctors = useMemo(() => {
      return doctors.filter(d => d.employmentStatus === 'Full-time');
   }, [doctors]);

   const surgeonDoctors = useMemo(() => {
      return doctors.filter(d =>
         (/surgeon/i.test(d.doctorType || '') || /surgeon/i.test(d.specialization || ''))
      );
   }, [doctors]);

   const anesthesiologistDoctors = useMemo(() => {
      return doctors.filter(d =>
         (/anes|anaes|anesthesiologist/i.test(d.doctorType || '') || /anes|anaes|anesthesiologist/i.test(d.specialization || ''))
      );
   }, [doctors]);

   useEffect(() => {
      if (resolvedHospital) {
         setHospitalProcess('bed_check');
      }
   }, [resolvedHospital]);

   const fetchDoctorsAndHospitals = async () => {
      try {
         const promises = [
            api.get('/doctors?limit=100'),
            api.get('/hospitals?limit=100')
         ];

         if (user?.role === 'admin' || user?.role === 'cc_head') {
            promises.push(api.get('/users?role=customer_care&limit=100'));
         }

         const [doctorsRes, hospitalsRes, agentsRes] = await Promise.all(promises);
         setDoctors(doctorsRes.data.data.doctors || []);
         setHospitals(hospitalsRes.data.data.hospitals || []);
         if (agentsRes) setAgents(agentsRes.data.data.users || []);
      } catch (error) {
         console.error('Failed to load lists');
      }
   };

   const handleReassignCC = async (agentId) => {
      if (!window.confirm("Are you sure you want to reassign this lead to another agent?")) return;
      setUpdating(true);
      try {
         await api.patch(`/leads/${id}`, { assignedCustomerCare: agentId });
         toast.success('Lead reassigned successfully');
         setShowCCReassignModal(false);
         fetchLead();
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to reassign lead');
      } finally {
         setUpdating(false);
      }
   };

   const handleStageTransition = async (nextStatus, payload) => {
      setUpdating(true);
      try {
         if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            try {
               await api.patch(`/leads/${id}`, payload);
            } catch (pErr) {
               console.error("Auto-patch payload error:", pErr);
            }
         }
         await api.put(`/leads/${id}/status`, {
            status: nextStatus,
            payload
         });
         toast.success(`Pipeline advanced to: ${nextStatus.replace(/_/g, ' ')}`);
         setViewedStatus(null);
         fetchLead();
      } catch (err) {
         toast.error(err.response?.data?.message || 'Transition Fault: Please check checklist items.');
      } finally {
         setUpdating(false);
      }
   };

   const handleAdvanceStageOne = () => {
      if (!screeningForm.timeline) {
         toast.error('Please select a timeline (Immediate or Later) before advancing.');
         return;
      }
      if (screeningForm.timeline === 'Later') {
         if (!screeningForm.followUpDate) {
            toast.error('Please select a follow-up date before advancing.');
            return;
         }
         handleStageTransition('screening', screeningForm);
      } else if (screeningForm.timeline === 'Immediate') {
         setShowReadyModal(true);
         toast.info('Please confirm surgery readiness date and details to proceed immediately.');
      }
   };

   const onFileSelect = (e, customTag = 'medical_report') => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      if (selectedFiles.length + files.length > 10) {
         return toast.error('Upload Limit: Maximum 10 documents per stage allowed.');
      }

      const taggedFiles = files.map(file => {
         // Create a unique identifier for the file to handle tagging
         file.customTag = customTag;
         return file;
      });

      setSelectedFiles(prev => [...prev, ...taggedFiles]);
      toast.success(`${files.length} documents added to staging (${customTag.replace(/_/g, ' ')})`);
      e.target.value = '';
   };

   const handleFilePreview = (file) => {
      if (file instanceof File) {
         const url = URL.createObjectURL(file);
         setPreviewFile({
            url,
            name: file.name,
            type: file.type,
            isPDF: file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
         });
      } else {
         setPreviewFile({
            ...file,
            isPDF: file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf') || file.url?.toLowerCase().endsWith('.pdf')
         });
      }
   };

   const handleDeleteDocument = async (docToDelete) => {
      if (!window.confirm("Are you sure you want to delete this document?")) return;
      const updatedDocs = lead.documents.filter(d => d._id !== docToDelete._id && d.url !== docToDelete.url);
      setUpdating(true);
      try {
         await api.patch(`/leads/${id}`, {
            documents: updatedDocs,
            replaceDocuments: true
         });
         toast.success('Document deleted successfully');
         fetchLead();
      } catch (err) {
         toast.error(err.response?.data?.message || 'Failed to delete document');
      } finally {
         setUpdating(false);
      }
   };

   const handleVaultUpload = async (e, type) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      setUpdating(true);
      try {
         const formData = new FormData();
         files.forEach(file => formData.append('documents', file));
         const uploadRes = await api.post('/upload', formData, {
            headers: {
               'Content-Type': 'multipart/form-data'
            }
         });
         const newDocs = uploadRes.data.data.files.map((f, i) => ({
            name: files[i].name,
            type: type,
            url: f.url,
            source: 'CC'
         }));

         const updatedDocs = [...(lead.documents || []), ...newDocs];
         await api.patch(`/leads/${id}`, {
            documents: updatedDocs,
            replaceDocuments: true
         });
         toast.success(`${files.length} document(s) uploaded successfully`);
         fetchLead();
      } catch (err) {
         toast.error(err.response?.data?.message || 'Failed to upload document');
      } finally {
         setUpdating(false);
      }
   };

   const handleClinicalUpload = async () => {
      // Stage 4 Validation: require Identity Proof + (Medical Doc OR Ayushman)
      const currentDocs = lead.documents || [];
      const idProofs = currentDocs.filter(d => d.type === 'id_proof');
      const medicalReports = currentDocs.filter(d => d.type === 'medical_report');
      const hasAyushman = lead.clinicalIntake?.insurance?.hasAyushmanCard === 'Yes' &&
         (currentDocs.some(d => d.type === 'ayushman_card') || selectedFiles.some(f => f.customTag === 'ayushman_card'));

      const stagedId = selectedFiles.filter(f => f.customTag === 'id_proof');
      const stagedMedical = selectedFiles.filter(f => f.customTag === 'medical_report');

      // Global Compliance Check for final verification (Step 6)
      if (intakeStep === 6) {
         if (!intakeForm.readinessDate && !lead.clinicalIntake?.readinessDate) {
            return toast.error('Compliance Block: Surgery Readiness Date is mandatory for clearance.');
         }

         const hasIdDocs = idProofs.length > 0 || stagedId.length > 0;
         const hasMedicalDocs = medicalReports.length > 0 || stagedMedical.length > 0 || hasAyushman;

         if (!hasIdDocs) {
            return toast.error('Compliance Block: At least 1 Identity Proof required.');
         }
         if (!hasMedicalDocs) {
            return toast.error('Compliance Block: At least 1 Medical Report or Ayushman Card required.');
         }
      }

      setUpdating(true);
      try {
         let newDocs = [];
         if (selectedFiles.length > 0) {
            const formData = new FormData();
            selectedFiles.forEach(file => formData.append('documents', file));
            const uploadRes = await api.post('/upload', formData, {
               headers: {
                  'Content-Type': 'multipart/form-data'
               }
            });
            newDocs = uploadRes.data.data.files.map((f, i) => ({
               name: selectedFiles[i].name,
               type: selectedFiles[i].customTag || 'medical_report',
               url: f.url,
               source: 'CC'
            }));
         }

         const payload = {
            documents: newDocs,
            clinicalIntake: {
               ...intakeForm,
               insurance: {
                  ...intakeForm.insurance,
                  hasAyushmanCard: lead.clinicalIntake?.insurance?.hasAyushmanCard || intakeForm.insurance?.hasAyushmanCard
               },
               hospitalProcessStatus: hospitalProcess === 'tests_ongoing' ? 'completed' : 'pending'
            }
         };

         await api.put(`/leads/${id}/status`, {
            status: 'doc_verification',
            payload
         });

         toast.success('Clinical Intake Verified & Pipeline Advanced');
         sessionStorage.removeItem(`intake_draft_${id}`);
         setSelectedFiles([]);
         setIntakeStep(1); // Reset step for next use if any
         fetchLead();
      } catch (err) {
         toast.error(err.response?.data?.message || 'Verification Protocol failed.');
      } finally {
         setUpdating(false);
      }
   };

   const handleSendClinicalLink = async (channel) => {
      if (channel === 'email' && !shareForm.email) return toast.error('Enter email address');
      if (channel === 'whatsapp' && !shareForm.whatsapp) return toast.error('Enter WhatsApp number');

      setUpdating(true);
      try {
         const basePath = sharingChannel === 'hospital' ? '/hospital-submit-reports' : '/submit-reports';
         // 🔐 ENCRYPTED: Encrypt lead ID before embedding in the share URL
         const encId = await getEncryptedLeadId(id);
         const shareUrl = `${window.location.origin}${basePath}/${encId}`;
         await api.post(`/leads/${id}/notify-clinical-link`, {
            channel,
            shareUrl,
            alternativeEmail: shareForm.email,
            alternativePhone: shareForm.whatsapp,
            recipientType: sharingChannel
         });
         toast.success(`Submission link shared via ${channel}`);
      } catch (err) {
         toast.error(err.response?.data?.message || 'Sharing failed.');
      } finally {
         setUpdating(false);
      }
   };

   const handleSendDoctorReviewLink = async () => {
      const docId = clinicalForm.reviewingDoctorId ||
         lead?.clinicalData?.reviewingDoctorId?._id ||
         (typeof lead?.clinicalData?.reviewingDoctorId === 'string' ? lead?.clinicalData?.reviewingDoctorId : null) ||
         lead?.assignedSurgeon?._id ||
         (typeof lead?.assignedSurgeon === 'string' ? lead?.assignedSurgeon : null);

      if (!docId) return toast.error('Please select a reviewing doctor first');

      setUpdating(true);
      try {
         await api.post(`/leads/${id}/notify-doctor-review`, {
            doctorId: docId
         });
         toast.success('Surgical review link sent to doctor via WhatsApp & Email');
         fetchLead();
      } catch (err) {
         toast.error(err.response?.data?.message || 'Failed to send review link');
      } finally {
         setUpdating(false);
      }
   };

   const handleRemoveSettlementProof = async (urlToRemove) => {
      if (!id || !urlToRemove) return;
      setUpdating(true);
      try {
         const remainingDocs = (lead?.documents || []).filter(d => d.url !== urlToRemove);
         const remainingProofs = remainingDocs.filter(d => d.type === 'past_payment_proof');
         const nextProof = remainingProofs[0];

         await api.patch(`/leads/${id}`, {
            pastPaymentProof: nextProof ? { url: nextProof.url, name: nextProof.name } : { url: '', name: '' },
            documents: remainingDocs,
            replaceDocuments: true
         });
         toast.success('Settlement proof file removed');
         fetchLead();
      } catch (err) {
         console.error('[REMOVE_SETTLEMENT_PROOF]', err);
         toast.error(err.response?.data?.message || 'Failed to remove settlement proof');
      } finally {
         setUpdating(false);
      }
   };



   const handleMarkNotInterested = async () => {
      if (!notInterestedReason.trim()) return toast.error('Please provide a reason.');
      setUpdating(true);
      try {
         await api.post(`/leads/${id}/not-interested`, { reason: notInterestedReason });
         toast.success('Lead closed');
         setShowNotInterestedModal(false);
         fetchLead();
      } catch (err) {
         toast.error('Action Failed');
      } finally {
         setUpdating(false);
      }
   };

   const handleRevertLead = async () => {
      setUpdating(true);
      try {
         await api.put(`/leads/${id}/status`, {
            status: 'screening',
            payload: {
               timeline: 'Later',
               followUpDate: new Date().toISOString(),
               eligibility: true,
               conditionSuitable: true
            }
         });
         toast.success('Lead successfully re-opened and moved to Follow-up queue');
         setShowRevertModal(false);
         fetchLead();
      } catch (err) {
         toast.error('Re-opening Fault: Check operational permissions.');
      } finally {
         setUpdating(false);
      }
   };

   const handleFollowUpAction = async (action) => {
      if (action === 'ready') {
         setShowReadyModal(true);
         return;
      }
      if (action === 'not_ready' && !rescheduleDate) return toast.error('Set follow-up date first');
      if (action === 'not_interested' && !notInterestedReason) return toast.error('Select closure reason');

      setUpdating(true);
      try {
         await api.post(`/leads/${id}/follow-up-action`, {
            action,
            newDate: rescheduleDate,
            newNotes: rescheduleNotes,
            reason: notInterestedReason
         });
         toast.success('Follow-up Status Updated');
         setFollowUpAction(null);
         setRescheduleDate('');
         setRescheduleNotes('');
         setNotInterestedReason('');
         fetchLead();
      } catch (err) {
         toast.error(err.response?.data?.message || 'Action Protocol Failed');
      } finally {
         setUpdating(false);
      }
   };

   const handleDirectMarkLost = async () => {
      if (!notInterestedReason.trim()) {
         toast.error('Please enter a reason for marking as lost');
         return;
      }
      setUpdating(true);
      try {
         await api.post(`/leads/${id}/not-interested`, { reason: notInterestedReason.trim() });
         toast.success('Lead marked as lost');
         setShowNotInterestedModal(false);
         setNotInterestedReason('');
         await fetchLead();
      } catch (err) {
         toast.error(err.response?.data?.message || 'Failed to mark lead as lost');
      } finally {
         setUpdating(false);
      }
   };

   const handleConfirmReady = async () => {
      if (!readyFormData.readinessDate) return toast.error('Readiness date is required');
      if (!readyFormData.confirmed) return toast.error('Please confirm patient readiness');

      setUpdating(true);
      try {
         let ayushmanDoc = [];
         if (readyFormData.hasAyushmanCard === 'Yes' && ayushmanFile) {
            const formData = new FormData();
            formData.append('documents', ayushmanFile);
            const uploadRes = await api.post('/upload', formData, {
               headers: { 'Content-Type': 'multipart/form-data' }
            });
            ayushmanDoc = uploadRes.data.data.files.map((f, i) => ({
               name: ayushmanFile.name,
               type: 'ayushman_card',
               url: f.url,
               source: 'CC'
            }));
         }

         await api.put(`/leads/${id}/status`, {
            status: 'patient_ready',
            payload: {
               readinessDate: readyFormData.readinessDate,
               notes: readyFormData.notes,
               documents: ayushmanDoc,
               clinicalIntake: {
                  insurance: { hasAyushmanCard: readyFormData.hasAyushmanCard }
               },
               ...(lead.status === 'new' && { screeningData: screeningForm })
            }
         });
         toast.success('Patient Ready: Moving to Clinical Intake');
         sessionStorage.removeItem(`intake_draft_${id}`);
         setShowReadyModal(false);
         setAyushmanFile(null);
         await fetchLead();
         setIntakeStep(1); // 🚀 Protocol: Immediately reset UI to CC Call stage
         // Smooth scroll to intake form
         setTimeout(() => {
            intakeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
         }, 500);
      } catch (err) {
         toast.error(err.response?.data?.message || 'Transition Failed');
      } finally {
         setUpdating(false);
      }
   };

   const handleSendPaymentLink = async () => {
      setUpdating(true);
      try {
         await api.post(`/leads/${id}/notify-payment`);
         toast.success('Payment link dispatched');
      } catch (err) {
         toast.error('Failed to send payment link');
      } finally {
         setUpdating(false);
      }
   };

   const getStatusColor = (status) => {
      const colors = {
         new: 'bg-blue-50 text-blue-600 border-blue-100',
         screening: 'bg-yellow-50 text-yellow-600 border-yellow-100',
         patient_ready: 'bg-emerald-50 text-emerald-600 border-emerald-100',
         doc_verification: 'bg-purple-50 text-purple-600 border-purple-100',
         clinical_approval: 'bg-indigo-50 text-indigo-600 border-indigo-100',
         surgery_ready: 'bg-emerald-50 text-emerald-700 border-emerald-200',
         hospital_assignment: 'bg-blue-50 text-blue-700 border-blue-200',
         scheduling: 'bg-violet-50 text-violet-600 border-violet-100',
         payment_pending: 'bg-orange-50 text-orange-600 border-orange-100',
         paid: 'bg-teal-50 text-teal-600 border-teal-100',
         surgery: 'bg-pink-50 text-pink-600 border-pink-100',
         closure: 'bg-slate-50 text-slate-600 border-slate-200',
         payout_processing: 'bg-slate-100 text-slate-800 border-slate-200',
         completed: 'bg-emerald-500 text-white border-emerald-600 shadow-lg shadow-emerald-100',
         doctor_rejected: 'bg-rose-50 text-rose-600 border-rose-100',
         not_interested: 'bg-red-50 text-red-600 border-red-100'
      };
      return colors[status] || 'bg-gray-50 text-gray-600 border-gray-100';
   };

   const pipelineStages = [
      { key: 'new', label: 'Intake', subtitle: 'Patient Intake & Registration', desc: 'Initial registration of the patient with baseline demographic and medical requirement details.' },
      { key: 'screening', label: 'Screening', subtitle: 'Eligibility & Medical Screening', desc: 'Call Center conducts preliminary assessment, eligibility verification (Ayushman / Cash / Insurance), and willingness.' },
      { key: 'patient_ready', label: 'Readiness', subtitle: 'Patient Readiness Confirmation', desc: 'Patient readiness is confirmed, gathering required medical reports and preparing clinical intake.' },
      { key: 'doc_verification', label: 'Doctor Review', subtitle: 'Doctor Review & Verification', desc: 'Case file and reports compiled and dispatched to the surgeon for clinical evaluation.' },
      { key: 'clinical_approval', label: 'Surgeon Assignment', subtitle: 'Surgeon & Anesthesiologist Selection', desc: 'Select and assign both the operating surgeon and the anesthesiologist for the surgery.' },
      { key: 'hospital_assignment', label: 'OT Schedule', subtitle: 'Operation Theater Scheduling', desc: 'Coordinating surgery date and Operation Theater slot with hospital and clinical team.' },
      { key: 'scheduling', label: 'Payment Links', subtitle: 'Deal Settlement & Payment Generation', desc: 'Reviewing case summary, configuring platform fee & GST, and generating payment links.' },
      { key: 'payment_pending', label: 'Payment Pending', subtitle: 'Awaiting Settlement Confirmation', desc: 'Payment link shared with patient/hospital; awaiting transaction confirmation.' },
      { key: 'paid', label: 'Paid / Confirmed', subtitle: 'Payment Confirmed & Slot Locked', desc: 'Payment verified and surgery slot locked. Preparing OTP verification protocols.' },
      { key: 'surgery', label: 'Live Surgery', subtitle: 'Intra-Operative Stage', desc: 'Surgery in progress in the Operation Theater. Verifying surgical team entry OTPs.' },
      { key: 'closure', label: 'Post-Op', subtitle: 'Post-Operative Monitoring', desc: 'Surgery completed. Conducting recovery monitoring logs and completion OTP validation.' },
      { key: 'payout_processing', label: 'Payout', subtitle: 'Payout Calculations & Approval', desc: 'Post-op clinical checks validated. Processing surgeon, hospital, and partner incentive payouts.' },
      { key: 'completed', label: 'Finalized', subtitle: 'Case Finalized & Closed', desc: 'Deal finalized, final invoice generated, and case archived as complete.' }
   ];

   const getStageLabelForStatus = (statusKey) => {
      if (!statusKey) return 'Stage 1: Intake';
      const index = pipelineStages.findIndex(s => s.key === statusKey);
      if (index !== -1) {
         return `Stage ${index + 1}: ${pipelineStages[index].label}`;
      }
      return `Stage 1: Intake (${statusKey.replace(/_/g, ' ')})`;
   };

   useEffect(() => {
      setViewedStatus(null);
   }, [lead?.status]);

   const effectiveStatus = lead?.status === 'surgery_ready' ? 'clinical_approval' : lead?.status;
   const actualStageIndex = pipelineStages.findIndex(s => s.key === effectiveStatus);
   const displayStatus = viewedStatus || effectiveStatus;
   const currentStageIndex = pipelineStages.findIndex(s => s.key === displayStatus);

   // Stage read-only mode:
   // When workflow reaches Post-Op (index >= 10: closure, payout_processing, completed) or any stage after the viewed stage,
   // viewing any completed prior stage is rendered in read-only mode to prevent editing completed history.
   const isAtOrPastPostOp = actualStageIndex >= 10 || ['closure', 'payout_processing', 'completed'].includes(lead?.status);
   const isPastStage = currentStageIndex !== -1 && actualStageIndex !== -1 && currentStageIndex < actualStageIndex;
   const isStageReadOnly = isPastStage;
   const isPayoutLocked = isStageReadOnly || Boolean(lead?.payoutData?.finalizedAt);

   useEffect(() => {
      const v = lead?.surgicalData?.manualDoctorVerification;
      const tzOffset = (new Date()).getTimezoneOffset() * 60000;
      const nowIso = (new Date(Date.now() - tzOffset)).toISOString().slice(0, 16);

      const formatIsoForInput = (val) => {
         if (!val) return '';
         try {
            const d = new Date(val);
            if (isNaN(d.getTime())) return '';
            return (new Date(d.getTime() - tzOffset)).toISOString().slice(0, 16);
         } catch {
            return '';
         }
      };

      if (v) {
         setManualVerifyForm({
            surgeonArrivalTime: formatIsoForInput(v.surgeonArrivalTime) || nowIso,
            anesthesiologistArrivalTime: formatIsoForInput(v.anesthesiologistArrivalTime) || nowIso,
            startTime: formatIsoForInput(v.startTime) || nowIso,
            endTime: formatIsoForInput(v.endTime) || nowIso,
            notes: v.notes || ''
         });
      } else if (displayStatus === 'surgery' || lead?.status === 'surgery') {
         setManualVerifyForm(prev => ({
            surgeonArrivalTime: prev.surgeonArrivalTime || nowIso,
            anesthesiologistArrivalTime: prev.anesthesiologistArrivalTime || nowIso,
            startTime: prev.startTime || nowIso,
            endTime: prev.endTime || nowIso,
            notes: prev.notes || ''
         }));
      }
   }, [lead?.surgicalData?.manualDoctorVerification, displayStatus, lead?.status]);

   // System roles that always have full pipeline access
   const SYSTEM_FULL_ACCESS_ROLES = ['admin', 'customer_care', 'cc_head', 'accountant'];
   const isSystemRole = SYSTEM_FULL_ACCESS_ROLES.includes(user?.role);

   // Helper: get permission name string from populated permission object
   const getPermName = (p) => (typeof p === 'object' && p !== null ? p.name : p);

   /**
    * canUpdateStage(stageKey):
    * Returns true if the current user is allowed to submit update actions for this stage.
    * - System roles: always true
    * - Custom role users: must have surgery:update_status AND the stage must be in their pipelinePermissions updateStages
    *   (checks both appointment and surgical pipeline lists)
    */
   const canUpdateStage = (stageKey) => {
      if (!stageKey) return false;
      // System roles always have full access
      if (isSystemRole) return true;

      // For custom role users — check if any update-related permission exists AND stage is allowed
      const customRolePerms = user?.roleDetails?.permissions;
      if (!customRolePerms || !Array.isArray(customRolePerms)) return false;

      const hasSurgeryUpdatePerm = customRolePerms.some(p =>
         ['surgery:update_status', 'lead:update', 'payout:approve', 'payout:process'].includes(getPermName(p))
      );

      if (!hasSurgeryUpdatePerm) return false;

      // Check pipeline-specific stage access
      const surgPP = user?.roleDetails?.pipelinePermissions?.surgical;
      const apptPP = user?.roleDetails?.pipelinePermissions?.appointment;

      const inSurgUpdate = surgPP?.updateStages && Array.isArray(surgPP.updateStages)
         ? surgPP.updateStages.includes(stageKey)
         : false;
      const inApptUpdate = apptPP?.updateStages && Array.isArray(apptPP.updateStages)
         ? apptPP.updateStages.includes(stageKey)
         : false;

      // If pipelinePermissions are configured, enforce them
      const hasPipelineConfig = (surgPP?.updateStages?.length > 0) || (apptPP?.updateStages?.length > 0);
      if (hasPipelineConfig) {
         return inSurgUpdate || inApptUpdate;
      }

      // If no pipeline config, fall back to the legacy permission check (lead:update alone grants access)
      return hasSurgeryUpdatePerm;
   };

   /**
    * canViewStage(stageKey):
    * Returns true if the current user is allowed to view the action section for this stage.
    */
   const canViewStage = (stageKey) => {
      if (!stageKey) return false;
      if (isSystemRole) return true;

      const customRolePerms = user?.roleDetails?.permissions;
      if (!customRolePerms || !Array.isArray(customRolePerms)) return false;

      const hasViewPerm = customRolePerms.some(p =>
         ['surgery:view', 'lead:read_all', 'lead:read_assigned'].includes(getPermName(p))
      );
      if (!hasViewPerm) return false;

      const surgPP = user?.roleDetails?.pipelinePermissions?.surgical;
      const apptPP = user?.roleDetails?.pipelinePermissions?.appointment;

      const inSurgView = surgPP?.viewStages && Array.isArray(surgPP.viewStages)
         ? surgPP.viewStages.includes(stageKey)
         : false;
      const inApptView = apptPP?.viewStages && Array.isArray(apptPP.viewStages)
         ? apptPP.viewStages.includes(stageKey)
         : false;

      const hasPipelineConfig = (surgPP?.viewStages?.length > 0) || (apptPP?.viewStages?.length > 0);
      if (hasPipelineConfig) {
         return inSurgView || inApptView;
      }

      return hasViewPerm;
   };


   const getStatusWhereText = (status) => {
      const descriptions = {
         new: 'The lead has been successfully registered in the system. The details are being initialized.',
         screening: 'The lead is currently undergoing preliminary screening by Customer Care.',
         patient_ready: 'The patient has confirmed their readiness for clinical evaluation.',
         doc_verification: 'The medical documents and identity cards are being uploaded and verified.',
         clinical_approval: 'The operating surgeon and anesthesiologist are being selected and assigned for the surgery.',
         surgery_ready: 'Surgical clearance has been granted by the doctor.',
         hospital_assignment: 'The surgery slot and date are being coordinated with the Operation Theater department.',
         scheduling: 'The secure deal settlement and payment links are being prepared and shared with the hospital.',
         payment_pending: 'The payment link has been dispatched; awaiting transaction confirmation.',
         paid: 'The payment has been confirmed and the surgical booking slot is secured.',
         surgery: 'The surgery is currently in progress or active in the hospital theater.',
         closure: 'The surgery was successful and the patient is currently in the post-operative monitoring stage.',
         payout_processing: 'Post-op clinical checks are done. The payout and commission calculations are being processed.',
         completed: 'The case has been successfully finalized, settled, and closed.',
         doctor_rejected: 'The reviewing surgeon rejected the case because surgery is not clinically feasible.',
         not_interested: 'The lead has exited the workflow pipeline as not interested.'
      };
      return descriptions[status] || 'The lead is currently in this stage of the workflow pipeline.';
   };

   const getStatusNextText = (status) => {
      const nextSteps = {
         new: 'Customer Care will review the lead profile and contact the patient for preliminary screening.',
         screening: 'Assessing eligibility details and scheduling preferences to confirm readiness.',
         patient_ready: 'Assigning a care coordinator to collect necessary medical reports.',
         doc_verification: 'Compiling the case file for review by the surgeon.',
         clinical_approval: 'Surgeon will provide clinical feasibility report and surgery clearance.',
         surgery_ready: 'Allocating surgical team, anesthesiologist, and hospital facility.',
         hospital_assignment: 'Confirming slot availability to schedule the surgery.',
         scheduling: 'Generating secure Razorpay payment link for deal settlement.',
         payment_pending: 'Awaiting transaction confirmation to activate the surgical OTP protocols.',
         paid: 'Verifying surgical entry OTPs to start the procedure.',
         surgery: 'Conducting post-op monitoring and validating surgical completion OTPs.',
         closure: 'Preparing payouts, platform fee disbursements, and final invoicing.',
         payout_processing: 'Transferring surgeon, hospital, and referral payouts.',
         completed: 'Lead completed successfully. No further action needed.',
         doctor_rejected: 'Customer Care will contact the patient or reassign the case to another doctor.',
         not_interested: 'Lead archived in system databases. No further action needed.'
      };
      return nextSteps[status] || 'The pipeline coordinator will update the status shortly.';
   };

   if (loading) return (
      <div className="space-y-8 animate-fadeIn">
         <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
               <div className="h-10 w-10 bg-slate-100 rounded-xl animate-pulse"></div>
               <div className="space-y-2">
                  <div className="h-6 w-48 bg-slate-100 rounded animate-pulse"></div>
                  <div className="h-3 w-32 bg-slate-100 rounded animate-pulse"></div>
               </div>
            </div>
            <div className="h-4 w-full bg-slate-50 rounded-full animate-pulse"></div>
         </div>
         <PatientDetailsSkeleton />
      </div>
   );
   if (!lead) return <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-red-100 text-red-500 font-bold">Error: Lead Not Found</div>;

   return (
      <div className="space-y-4 sm:space-y-6 lg:space-y-8 animate-fadeIn">

         {/* 🏅 PREMIUM PIPELINE NAVIGATOR */}
         <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl shadow-slate-200/50 border border-slate-100 border-t-8 border-t-[#143E78]">
            <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-8 sm:mb-10">
               <div className="flex items-center gap-4">
                  <button onClick={() => {
                     const from = searchParams.get('from');
                     const page = searchParams.get('page');
                     const menu = searchParams.get('menu');
                     if (from === 'surgeries') {
                        navigate(`/surgeries?${page ? `page=${page}&` : ''}${menu ? `menu=${menu}` : ''}`);
                     } else if (from === 'leads') {
                        navigate(`/leads?${page ? `page=${page}&` : ''}${menu ? `menu=${menu}` : ''}`);
                     } else {
                        navigate(-1);
                     }
                  }} className="p-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-all border border-slate-100 shadow-sm">
                     <ArrowLeft className="w-5 h-5 text-[#143E78]" />
                  </button>
                  <div>
                     <h1 className="text-sm sm:text-2xl font-black text-slate-900 tracking-tight leading-tight">
                        Stage {pipelineStages[currentStageIndex]?.label || 'Detail'}: {lead.patientName}
                     </h1>
                     <p className="text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest mt-0.5">Pipeline Identity: {lead.leadId || id.slice(-8).toUpperCase()}</p>
                  </div>
               </div>

               <div className="flex flex-wrap items-center gap-3">
                  {viewedStatus && viewedStatus !== lead?.status && (
                     <button onClick={() => setViewedStatus(null)} className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm">
                        Back to Current Active Stage
                     </button>
                  )}
                  {canMarkAsLost && !['closure', 'payout_processing', 'completed', 'not_interested', 'surgery'].includes(lead?.status) && (
                     <button
                        onClick={() => setShowNotInterestedModal(true)}
                        className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-sm flex items-center gap-2 active:scale-95 cursor-pointer"
                     >
                        <XCircle className="w-4 h-4 text-rose-600" />
                        <span>Mark as Lost</span>
                     </button>
                  )}
                  <div className={`hidden sm:flex px-3 sm:px-5 py-2.5 ${lead.status === 'not_interested' ? 'bg-red-50 border-red-100' : 'bg-indigo-50 border-indigo-100'} border rounded-full items-center gap-3`}>
                     <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${lead.status === 'not_interested' ? 'text-red-600' : 'text-indigo-600'}`}>
                        {lead.status === 'not_interested' ? 'DEAL CLOSED / ARCHIVED' : (pipelineStages.find(s => s.key === lead.status)?.label?.toUpperCase() || lead.status?.replace(/_/g, ' '))}
                     </span>
                     <div className={`w-2 h-2 rounded-full ${lead.status === 'not_interested' ? 'bg-red-500' : 'bg-indigo-500 animate-pulse'}`}></div>
                  </div>
               </div>
            </div>

            <div className="overflow-x-auto pb-2 pt-0 no-scrollbar">
               <div className="relative min-w-[850px] sm:min-w-full px-2 py-2">
                  <div className={`absolute top-[28px] sm:top-[34px] left-10 right-10 h-1 ${lead.status === 'not_interested' ? 'bg-red-100' : 'bg-slate-100'} rounded-full`}>
                     <div className={`absolute top-0 left-0 h-full ${lead.status === 'not_interested' ? 'bg-red-600' : 'bg-indigo-600'} rounded-full transition-all duration-1000`} style={{ width: `${lead.status === 'not_interested' ? 100 : Math.max(0, (currentStageIndex / (pipelineStages.length - 1)) * 100)}%` }}></div>
                  </div>

                  <div className="flex justify-between relative">
                     {pipelineStages.map((stage, idx) => {
                        const isPassed = lead.status === 'not_interested' ? false : idx < actualStageIndex;
                        const isCurrent = lead.status === 'not_interested' ? false : idx === currentStageIndex;
                        const isClosed = lead.status === 'not_interested';

                        return (
                           <div
                              key={stage.key}
                              onMouseEnter={(e) => {
                                 const rect = e.currentTarget.getBoundingClientRect();
                                 setHoveredStageInfo({
                                    stage,
                                    idx,
                                    x: rect.left + rect.width / 2,
                                    y: rect.bottom,
                                 });
                              }}
                              onMouseLeave={() => setHoveredStageInfo(null)}
                              onClick={() => {
                                 if (idx <= actualStageIndex) {
                                    setViewedStatus(stage.key);
                                 } else {
                                    toast.error('Stage locked! Complete previous stages first.');
                                 }
                              }}
                              className="flex flex-col items-center group relative cursor-pointer shrink-0"
                           >
                              <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center border-[3px] sm:border-4 transition-all duration-500 z-10 ${isClosed ? 'bg-red-50 border-red-200 text-red-300' : isPassed ? 'bg-[#143E78] border-[#143E78] text-white' : isCurrent ? 'bg-white border-[#143E78] text-[#143E78] scale-110 sm:scale-125 shadow-lg ring-2 sm:ring-4 ring-blue-50' : 'bg-white border-slate-100 text-slate-200'}`}>
                                 {isClosed ? <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : isPassed ? <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <span className="text-[7px] sm:text-[9px] font-black">{idx + 1}</span>}
                              </div>
                              <span className={`text-[7px] sm:text-[9px] font-black uppercase tracking-tighter mt-2 text-center w-14 sm:w-16 transition-all ${isClosed ? 'text-red-300' : isCurrent ? 'text-indigo-600 font-black' : 'text-slate-400 opacity-60'}`}>
                                 {stage.label}
                              </span>
                           </div>
                        );
                     })}
                  </div>
               </div>
            </div>
         </div>

         {/* 💬 FLOATING STAGE ABOUT POPOVER (Fixed Viewport Overlay - 0% Clipping) */}
         {hoveredStageInfo && (
            <div
               style={{
                  position: 'fixed',
                  top: `${hoveredStageInfo.y - 6}px`,
                  left: `${Math.min(Math.max(hoveredStageInfo.x, 140), window.innerWidth - 140)}px`,
                  transform: 'translateX(-50%)',
                  zIndex: 99999
               }}
               className="pointer-events-none w-64 text-left transition-opacity duration-150 animate-fadeIn"
            >
               {/* Tooltip triangle pointer on top */}
               <div className="w-2.5 h-2.5 bg-slate-900 rotate-45 -mb-1.5 border-l border-t border-slate-700/80 mx-auto"></div>
               <div className="bg-slate-900/95 text-white rounded-2xl p-3.5 shadow-2xl border border-slate-700/80 backdrop-blur-md w-full">
                  <div className="flex items-center justify-between gap-2 mb-1.5 pb-1.5 border-b border-slate-700/60">
                     <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300">
                        Stage {hoveredStageInfo.idx + 1}: {hoveredStageInfo.stage.label}
                     </span>
                     <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${lead.status === 'not_interested' ? 'bg-red-500 text-white' :
                        viewedStatus === hoveredStageInfo.stage.key ? 'bg-sky-500 text-white' :
                           hoveredStageInfo.idx === currentStageIndex ? 'bg-indigo-600 text-white' :
                              hoveredStageInfo.idx < actualStageIndex ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300'
                        }`}>
                        {
                           lead.status === 'not_interested' ? 'Closed' :
                              viewedStatus === hoveredStageInfo.stage.key ? 'Viewing' :
                                 hoveredStageInfo.idx === currentStageIndex ? 'Active Stage' :
                                    hoveredStageInfo.idx < actualStageIndex ? 'Completed' : 'Upcoming'
                        }
                     </span>
                  </div>
                  {hoveredStageInfo.stage.subtitle && (
                     <p className="text-[10px] font-bold text-slate-200 mb-1 leading-tight">{hoveredStageInfo.stage.subtitle}</p>
                  )}
                  <p className="text-[9px] font-medium text-slate-400 leading-relaxed">
                     {hoveredStageInfo.stage.desc || getStatusWhereText(hoveredStageInfo.stage.key)}
                  </p>
               </div>
            </div>
         )}

         {/* 🩺 MANUAL DOCTOR VERIFICATION MODAL (COMMENTED OUT - VERIFICATION IS NOW INLINE IN STAGE 8) */}
         {/*
         {showManualVerifyModal && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-fadeIn">
               <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-6 animate-scaleUp">
                  <div className="flex justify-between items-start border-b border-slate-100 pb-4">
                     <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-black">
                           <UserCheck className="w-5 h-5" />
                        </div>
                        <div>
                           <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Manual Doctor Verification</h3>
                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Log Timings & Advance directly to Post-Op</p>
                        </div>
                     </div>
                     <button onClick={() => setShowManualVerifyModal(false)} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-all">
                        <X className="w-5 h-5" />
                     </button>
                  </div>

                  <div className="space-y-4">
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                           Operating Surgeon Hospital Arrival Time *
                        </label>
                        <input
                           type="datetime-local"
                           value={manualVerifyForm.surgeonArrivalTime}
                           onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, surgeonArrivalTime: e.target.value })}
                           className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white transition-all"
                        />
                     </div>

                     {Boolean(lead?.assignedanesthesiologist || lead?.assignedanesthesiologistOtherName || lead?.anesthesiologistType === 'other') && (
                        <div className="space-y-1.5">
                           <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                              Anesthesiologist Hospital Arrival Time *
                           </label>
                           <input
                              type="datetime-local"
                              value={manualVerifyForm.anesthesiologistArrivalTime}
                              onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, anesthesiologistArrivalTime: e.target.value })}
                              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white transition-all"
                           />
                        </div>
                     )}

                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Surgery Start Time *</label>
                        <input
                           type="datetime-local"
                           value={manualVerifyForm.startTime}
                           onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, startTime: e.target.value })}
                           className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white transition-all"
                        />
                     </div>

                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Surgery End Time *</label>
                        <input
                           type="datetime-local"
                           value={manualVerifyForm.endTime}
                           onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, endTime: e.target.value })}
                           className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white transition-all"
                        />
                     </div>

                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Verification Remarks / Notes</label>
                        <textarea
                           rows={3}
                           placeholder="Enter notes (e.g. Both surgeon and anesthesiologist arrived at hospital and surgery started...)"
                           value={manualVerifyForm.notes}
                           onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, notes: e.target.value })}
                           className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white transition-all resize-none"
                        />
                     </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                     <button
                        type="button"
                        onClick={() => setShowManualVerifyModal(false)}
                        className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                     >
                        Cancel
                     </button>
                     <button
                        type="button"
                        onClick={handleManualDoctorVerify}
                        disabled={updating}
                        className="flex-1 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-emerald-200 disabled:opacity-50 flex items-center justify-center gap-2"
                     >
                        <CheckCircle className="w-4 h-4" /> Verify & Move to Post-Op
                     </button>
                  </div>
               </div>
            </div>
         )}
         */}

         <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* LEFT COMPONENT */}
            <div className="lg:col-span-1 space-y-6">
               <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between border-b pb-2 mb-4">
                     <h2 className="text-[16px] font-bold text-gray-800">Patient Details</h2>
                     {latestCall && (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold border ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).bg}`}>
                           <span className={`w-1.5 h-1.5 rounded-full ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).dot}`}></span>
                           {getLightDispositionBadge(getEffectiveDisposition(latestCall)).label}
                        </span>
                     )}
                  </div>
                  <div className="space-y-4">
                     <div className="flex flex-col sm:grid sm:grid-cols-2 gap-4">
                        <div className="space-y-0.5">
                           <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">Name</p>
                           <p className="font-black text-slate-800 text-sm sm:text-lg truncate">{lead.patientName}</p>
                        </div>
                        <div className="space-y-0.5">
                           <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">Phone</p>
                           <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-slate-700 text-sm">{lead.patientPhone}</p>
                              {canInitiateCalls && lead?.patientPhone && (
                                 <button
                                    id="exotel-call-patient-btn"
                                    onClick={handleExotelCall}
                                    disabled={isCallingPatient}
                                    title="Click-to-Call patient via Exotel"
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{
                                       background: isCallingPatient ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg,#7c3aed,#6d28d9)',
                                       boxShadow: '0 2px 8px rgba(124,58,237,0.3)'
                                    }}
                                 >
                                    {isCallingPatient
                                       ? <><Loader2 size={10} className="animate-spin" />&nbsp;Calling…</>
                                       : <><PhoneCall size={10} />&nbsp;Call</>}
                                 </button>
                              )}
                           </div>
                        </div>
                     </div>
                     {lead.preferredCallTime && (
                        <div className="pt-2">
                           <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mb-1">Preferred Callback</p>
                           <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.59A2 2 0 012 1h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 8a16 16 0 006 6l.36-.36a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16z" /></svg>
                              <span className="text-[11px] font-black text-orange-700 uppercase tracking-tighter">{lead.preferredCallTime}</span>
                           </div>
                        </div>
                     )}
                     <div className="pt-2">
                        <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mb-1">Treatment Required</p>
                        <p className="font-black text-indigo-600 bg-indigo-50 py-1 px-3 rounded-lg text-[11px] inline-block border border-indigo-100 uppercase tracking-tighter">
                           {lead.disease || lead.treatmentRequired}
                        </p>
                     </div>

                     {/* ── Call & Follow-up Status Section in Patient Details ── */}
                     <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-2">
                           <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest flex items-center gap-1.5">
                              <PhoneCall size={12} className="text-violet-600" />
                              Call & Follow-up Status
                           </p>
                           {latestCall && (
                              <div className="flex items-center gap-2">
                                 <button
                                    type="button"
                                    onClick={handleOpenQuickDispModal}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-600 hover:text-violet-700 bg-violet-50 hover:bg-violet-100 px-2 py-0.5 rounded border border-violet-200 transition-all cursor-pointer"
                                    title="Update Follow-up Status"
                                 >
                                    <Edit size={10} /> Update Status
                                 </button>
                                 <button
                                    type="button"
                                    onClick={() => setShowCallHistory(prev => !prev)}
                                    className="text-[10px] font-bold text-slate-500 hover:text-slate-700 hover:underline cursor-pointer"
                                 >
                                    {showCallHistory ? 'Hide' : 'History'}
                                 </button>
                              </div>
                           )}
                        </div>

                        {latestCall ? (
                           <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-3 space-y-2">
                              {/* Call Status (Exotel Telephony) */}
                              <div className="flex items-center justify-between gap-2 text-xs">
                                 <span className="text-[11px] font-semibold text-slate-500">Call Status:</span>
                                 {(() => {
                                    const badge = getLightStatusBadge(latestCall.status);
                                    return (
                                       <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${badge.bg}`}>
                                          <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                                          {badge.label}
                                       </span>
                                    );
                                 })()}
                              </div>

                              {/* Follow-up Status (CRM Disposition) */}
                              <div className="flex items-center justify-between gap-2 text-xs">
                                 <span className="text-[11px] font-semibold text-slate-500">Follow-up:</span>
                                 {(() => {
                                    const eff = getEffectiveDisposition(latestCall);
                                    const disp = getLightDispositionBadge(eff);
                                    return (
                                       <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${disp.bg}`}>
                                          <span className={`w-1.5 h-1.5 rounded-full ${disp.dot}`} />
                                          {disp.label}
                                       </span>
                                    );
                                 })()}
                              </div>

                              {/* Time & Direction */}
                              <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1.5 border-t border-slate-200/60">
                                 <span className="flex items-center gap-1">
                                    <Clock size={10} className="text-slate-400" />
                                    {new Date(latestCall.createdAt || latestCall.startTime).toLocaleString('en-IN', {
                                       day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true
                                    })}
                                 </span>
                                 <span className="capitalize font-bold text-slate-600">
                                    {latestCall.direction || 'Inbound'} {latestCall.duration > 0 ? `• ${Math.floor(latestCall.duration / 60)}m ${latestCall.duration % 60}s` : ''}
                                 </span>
                              </div>

                              {/* Remarks if any */}
                              {(latestCall.dispositionNotes || latestCall.notes) && (
                                 <div className="bg-white p-2 rounded-lg border border-slate-200 text-[11px] text-slate-600">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Note</p>
                                    <p className="italic">"{latestCall.dispositionNotes || latestCall.notes}"</p>
                                    {latestCall.dispositionUpdatedBy && (
                                       <p className="text-[9px] text-slate-400 text-right mt-1">
                                          — {latestCall.dispositionUpdatedBy.firstName} {latestCall.dispositionUpdatedBy.lastName}
                                       </p>
                                    )}
                                 </div>
                              )}
                           </div>
                        ) : (
                           <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs text-slate-500">
                              <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                 <PhoneOff size={13} className="text-slate-400" /> No call records yet
                              </span>
                              {canInitiateCalls && lead?.patientPhone && (
                                 <button
                                    onClick={handleExotelCall}
                                    disabled={isCallingPatient}
                                    className="text-[10px] font-bold text-violet-600 hover:text-violet-700 hover:underline"
                                 >
                                    Call Now
                                 </button>
                              )}
                           </div>
                        )}
                     </div>
                  </div>

                  {/* ── Exotel Call History Panel ─────────────────────────────── */}
                  {canManagePipeline && id && (
                     <div className="mt-4 pt-4 border-t border-slate-100">
                        <button
                           onClick={() => setShowCallHistory(prev => !prev)}
                           className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-100 transition-all cursor-pointer"
                        >
                           <span className="flex items-center gap-2">
                              <PhoneCall size={14} className="text-violet-500" />
                              Full Call History
                           </span>
                           <span className="text-[10px] text-slate-400 font-normal">
                              {showCallHistory ? 'Hide' : 'Show All'}
                           </span>
                        </button>
                        {showCallHistory && (
                           <div className="mt-2">
                              <CallHistoryPanel
                                 leadId={id}
                                 refreshTrigger={callHistoryRefresh}
                                 onLatestCallChange={setLatestCall}
                              />
                           </div>
                        )}
                     </div>
                  )}

                  {(user?.role === 'admin' || user?.role === 'cc_head') && (
                     <div className="mt-6 pt-6 border-t border-slate-100">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Case Management</h3>
                        <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                           <div>
                              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Assigned Agent</p>
                              <p className="text-sm font-bold text-slate-800">
                                 {lead.assignedCustomerCare ? `${lead.assignedCustomerCare.firstName} ${lead.assignedCustomerCare.lastName}` : 'Unassigned'}
                              </p>
                           </div>
                           <button
                              onClick={() => setShowCCReassignModal(true)}
                              className="px-3 py-1.5 bg-indigo-600 text-white text-[10px] font-black uppercase rounded-lg shadow-sm active:scale-95 transition-all"
                           >
                              Reassign
                           </button>
                        </div>
                     </div>
                  )}

                  {lead.clinicalData?.doctorReviewDone && (lead.clinicalData?.reviewDecision || lead.clinicalData?.riskLevel || lead.clinicalData?.reviewRemarks) && (
                     <div className="mt-6 pt-6 border-t border-slate-100">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                           <Activity className="w-3.5 h-3.5 text-blue-600" /> Medical Assessment
                        </h3>
                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4 text-left">
                           {lead.clinicalData.reviewDecision && (
                              <div>
                                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Doctor Approval Status</p>
                                 <div className="flex items-center gap-2">
                                    <div className={`w-2.5 h-2.5 rounded-full ${lead.clinicalData.reviewDecision === 'Surgery Possible' ? 'bg-emerald-500' : lead.clinicalData.reviewDecision === 'Additional Investigation Required' ? 'bg-amber-500' : 'bg-rose-500'}`}></div>
                                    <span className="font-bold text-sm text-slate-800">{lead.clinicalData.reviewDecision}</span>
                                 </div>
                              </div>
                           )}
                           {lead.clinicalData.riskLevel && (
                              <div>
                                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Risk Level</p>
                                 <span className={`inline-block px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide border ${lead.clinicalData.riskLevel.includes('Mild') || lead.clinicalData.riskLevel === 'Low' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : lead.clinicalData.riskLevel.includes('Moderate') || lead.clinicalData.riskLevel === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                                    {lead.clinicalData.riskLevel}
                                 </span>
                              </div>
                           )}
                           {lead.clinicalData.reviewRemarks && (
                              <div>
                                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Doctor Remarks</p>
                                 <p className="text-xs text-slate-600 bg-white p-3 rounded-xl border border-slate-200 italic shadow-sm">
                                    "{lead.clinicalData.reviewRemarks}"
                                 </p>
                              </div>
                           )}
                        </div>
                     </div>
                  )}

                  <div className="mt-8 pt-6 border-t border-slate-100">
                     <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Patient Documents</h3>
                        {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head' || user?.role === 'accountant') && (
                           <button
                              onClick={() => setIsEditingVault(!isEditingVault)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${isEditingVault ? 'bg-amber-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                           >
                              {isEditingVault ? (
                                 <><X className="w-3 h-3" /> Done</>
                              ) : (
                                 <><Edit className="w-3 h-3" /> Edit Vault</>
                              )}
                           </button>
                        )}
                     </div>

                     {(lead.documents && lead.documents.length > 0) || isEditingVault ? (
                        <div className="space-y-6">
                           {/* 🛡️ GROUP DOCUMENTS BY TYPE */}
                           {[
                              { type: 'ayushman_card', label: 'Ayushman Card', color: 'green', icon: <Activity className="w-3.5 h-3.5" /> },
                              { type: 'id_proof', label: 'Identity Proof', color: 'blue', icon: <UserPlus className="w-3.5 h-3.5" /> },
                              { type: 'medical_report', label: 'Medical Documents', color: 'purple', icon: <FileText className="w-3.5 h-3.5" /> }
                           ].map(category => {
                              const viewerIsCC = user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head' || user?.role === 'accountant';
                              const allowedDocs = viewerIsCC
                                 ? (lead.documents || [])
                                 : (lead.documents || []).filter(doc => {
                                    const docTime = new Date(doc.uploadedAt || lead.createdAt).getTime();
                                    const leadTime = new Date(lead.createdAt).getTime();
                                    return (docTime - leadTime) < 60000; // Only docs from creation time
                                 });

                              const categoryDocs = allowedDocs.filter(d => d.type === category.type);
                              if (categoryDocs.length === 0 && !isEditingVault) return null;

                              return (
                                 <div key={category.type} className="space-y-2.5">
                                    <div className={`flex items-center gap-2 text-${category.color}-600`}>
                                       {category.icon}
                                       <span className="text-[10px] font-black uppercase tracking-widest">{category.label}</span>
                                    </div>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                       {categoryDocs.map((doc, idx) => (
                                          <div key={idx} className="relative aspect-square">
                                             <button
                                                onClick={() => {
                                                   if (isEditingVault) return;
                                                   handleFilePreview(doc);
                                                }}
                                                className={`group w-full h-full bg-slate-50 border border-slate-100 rounded-lg flex flex-col items-center justify-center p-1.5 hover:border-${category.color}-500 hover:shadow-md transition-all overflow-hidden shadow-sm`}
                                             >
                                                <div className={`absolute top-2 right-2 w-4 h-4 bg-${category.color}-50 text-${category.color}-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
                                                   <ExternalLink className="w-2.5 h-2.5" />
                                                </div>
                                                {doc.url?.toLowerCase().endsWith('.pdf') ? (
                                                   <FileText className="w-5 h-5 text-red-500 mb-1" />
                                                ) : (
                                                   <div className="absolute inset-0 w-full h-full">
                                                      <img
                                                         src={doc.url}
                                                         alt={doc.name}
                                                         className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                                                      />
                                                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                                   </div>
                                                )}
                                                {!doc.url?.toLowerCase().endsWith('.pdf') && (
                                                   <Image className={`w-5 h-5 text-${category.color}-500 mb-1 relative z-10 group-hover:opacity-0 transition-opacity duration-300`} />
                                                )}
                                                <span className="text-[6px] sm:text-[7px] font-black text-slate-600 truncate w-full text-center px-1.5 relative z-10 bg-white/90 py-0.5 rounded-md mt-auto group-hover:bg-[#143E78] group-hover:text-white transition-all shadow-sm">
                                                   {doc.name || `View`}
                                                </span>
                                             </button>
                                             {isEditingVault && (
                                                <button
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteDocument(doc);
                                                   }}
                                                   className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-600 hover:bg-rose-700 text-white rounded-full flex items-center justify-center shadow-md z-30 transition-all cursor-pointer border border-white"
                                                   title="Delete Document"
                                                >
                                                   <Trash2 className="w-2.5 h-2.5" />
                                                </button>
                                             )}
                                          </div>
                                       ))}
                                       {isEditingVault && (
                                          <label className={`aspect-square border-2 border-dashed border-${category.color}-300 hover:border-${category.color}-500 bg-${category.color}-50/30 hover:bg-${category.color}-50 rounded-lg flex flex-col items-center justify-center p-1.5 cursor-pointer transition-all gap-1`}>
                                             <input
                                                type="file"
                                                multiple
                                                hidden
                                                onChange={(e) => handleVaultUpload(e, category.type)}
                                             />
                                             <Plus className={`w-5 h-5 text-${category.color}-500`} />
                                             <span className={`text-[8px] font-black uppercase text-${category.color}-600 tracking-wider text-center`}>Upload</span>
                                          </label>
                                       )}
                                    </div>
                                 </div>
                              );
                           })}

                           {/* 📁 OTHER DOCUMENTS (FALLBACK) */}
                           {(() => {
                              const viewerIsCC = user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head';
                              const allowedDocs = viewerIsCC
                                 ? (lead.documents || [])
                                 : (lead.documents || []).filter(doc => {
                                    const docTime = new Date(doc.uploadedAt || lead.createdAt).getTime();
                                    const leadTime = new Date(lead.createdAt).getTime();
                                    return (docTime - leadTime) < 60000;
                                 });
                              const otherDocs = allowedDocs.filter(d => !['ayushman_card', 'id_proof', 'medical_report'].includes(d.type));

                              if (otherDocs.length === 0 && !isEditingVault) return null;

                              return (
                                 <div className="space-y-2.5">
                                    <div className="flex items-center gap-2 text-slate-400">
                                       <LayoutGrid className="w-3.5 h-3.5" />
                                       <span className="text-[10px] font-black uppercase tracking-widest">Additional Vault</span>
                                    </div>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                       {otherDocs.map((doc, idx) => (
                                          <div key={idx} className="relative aspect-square">
                                             <button
                                                onClick={() => {
                                                   if (isEditingVault) return;
                                                   handleFilePreview(doc);
                                                }}
                                                className="group w-full h-full bg-slate-50 border border-slate-100 rounded-lg flex flex-col items-center justify-center p-1.5 hover:border-slate-400 hover:shadow-md transition-all overflow-hidden shadow-sm"
                                             >
                                                {doc.url?.toLowerCase().endsWith('.pdf') ? (
                                                   <FileText className="w-5 h-5 text-red-500" />
                                                ) : (
                                                   <>
                                                      <div className="absolute inset-0 w-full h-full">
                                                         <img src={doc.url} alt="Other" className="w-full h-full object-cover opacity-20 group-hover:opacity-100 transition-all" />
                                                      </div>
                                                      <Image className="text-blue-500 w-5 h-5 relative z-10 group-hover:opacity-0 transition-opacity" />
                                                   </>
                                                )}
                                                <span className="text-[6px] sm:text-[7px] font-black text-slate-400 truncate w-full text-center mt-auto px-1.5 relative z-10 bg-white/90 py-0.5 rounded-md group-hover:bg-[#143E78] group-hover:text-white transition-all shadow-sm">{doc.name || 'File'}</span>
                                             </button>
                                             {isEditingVault && (
                                                <button
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteDocument(doc);
                                                   }}
                                                   className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-600 hover:bg-rose-700 text-white rounded-full flex items-center justify-center shadow-md z-30 transition-all cursor-pointer border border-white"
                                                   title="Delete Document"
                                                >
                                                   <Trash2 className="w-2.5 h-2.5" />
                                                </button>
                                             )}
                                          </div>
                                       ))}
                                       {isEditingVault && (
                                          <label className="aspect-square border-2 border-dashed border-slate-300 hover:border-slate-400 bg-slate-50/50 hover:bg-slate-50 rounded-lg flex flex-col items-center justify-center p-1.5 cursor-pointer transition-all gap-1">
                                             <input
                                                type="file"
                                                multiple
                                                hidden
                                                onChange={(e) => handleVaultUpload(e, 'other')}
                                             />
                                             <Plus className="w-5 h-5 text-slate-400" />
                                             <span className="text-[8px] font-black uppercase text-slate-500 tracking-wider text-center">Upload</span>
                                          </label>
                                       )}
                                    </div>
                                 </div>
                              );
                           })()}
                        </div>
                     ) : (
                        <div className="flex flex-col items-center justify-center py-10 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                           <LayoutGrid className="w-8 h-8 text-slate-200 mb-2" />
                           <p className="text-[10px] uppercase font-black text-slate-300 tracking-[0.2em]">No Documents Found</p>
                        </div>
                     )}
                  </div>
               </div>

               {/* 🚀 NEW: Workflow Journey Insights (Added after Vault) */}
               <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <h2 className="text-xs sm:text-sm font-black text-[#143E78] mb-4 border-b border-slate-50 pb-2 flex items-center gap-2 uppercase tracking-tight">
                     <Activity className="w-3.5 h-3.5 text-blue-600" /> Workflow Journey & Insights
                  </h2>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                     {pipelineJourney.map((stage, idx) => (
                        <div key={stage.key} className="relative pl-4 border-l-2 border-slate-100 py-2 hover:bg-slate-50/50 transition-colors rounded-r-xl">
                           <div className={`absolute -left-[5px] top-3 w-2 h-2 rounded-full bg-indigo-500`} />
                           <div className="flex justify-between items-start">
                              <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{stage.label}</p>
                              {stage.timestamp && (
                                 <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                    {new Date(stage.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                 </span>
                              )}
                           </div>
                           <div className="mt-2 space-y-1.5">
                              {stage.details.map((d, di) => (
                                 <div key={di} className="flex items-start gap-2">
                                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest shrink-0 w-24 pt-0.5">{d.label}</span>
                                    <span className="text-[10px] font-bold text-slate-700 leading-tight">{d.value}</span>
                                 </div>
                              ))}
                           </div>
                        </div>
                     ))}
                     {pipelineJourney.length === 0 && (
                        <p className="text-[10px] text-slate-400 text-center py-4 font-bold uppercase tracking-widest">No stage data recorded yet</p>
                     )}
                  </div>
               </div>

               <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <h2 className="text-xs sm:text-sm font-black text-[#143E78] mb-3 border-b border-slate-50 pb-2 uppercase tracking-tight">Audit Trail</h2>
                  <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                     {unifiedTimeline.map((item, idx) => (
                        <div key={idx} className="relative pl-4 border-l-2 border-slate-100 py-1">
                           <div className={`absolute -left-[5px] top-2 w-2 h-2 rounded-full ${item.type === 'status' ? 'bg-[#143E78]' : 'bg-emerald-500'}`} />
                           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.type === 'status' ? 'Status Move' : 'Observation Log'}</p>
                           <p className="text-xs font-black text-[#143E78] uppercase truncate mt-0.5">{item.title}</p>
                           <p className="text-[10px] font-medium text-slate-500 mt-1">{new Date(item.date).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                           {item.notes && <p className="text-[9px] font-bold text-slate-400 mt-1 italic">"{item.notes}"</p>}
                        </div>
                     ))}
                  </div>
               </div>

               <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <h2 className="text-xs sm:text-sm font-black text-[#143E78] mb-4 border-b border-slate-50 pb-2 flex items-center gap-2 uppercase tracking-tight">
                     <MessageSquare className="w-3.5 h-3.5 text-blue-500" /> Internal Notes & Discussion
                  </h2>
                  <div className="space-y-4 mb-6 max-h-[300px] overflow-y-auto pr-2">
                     {lead.notes?.map((note, idx) => (
                        <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-1">
                           <div className="flex justify-between text-[10px] font-bold text-slate-400">
                              <span>{note.addedBy?.role?.toUpperCase()}</span>
                              <span>{new Date(note.addedAt).toLocaleTimeString()}</span>
                           </div>
                           <p className="text-sm font-medium text-gray-700">{note.content}</p>
                        </div>
                     ))}
                  </div>
                  <div className="space-y-2">
                     <textarea className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white transition-all" rows={3} placeholder="Add a note..." value={stakeholderNote} onChange={(e) => setStakeholderNote(e.target.value)} />
                     <button onClick={handleAddNote} disabled={addingNote || !stakeholderNote.trim()} className="w-full py-2 bg-[#143E78] text-white font-black rounded-lg text-xs uppercase disabled:opacity-50">Post Note</button>
                  </div>
               </div>
            </div>

            {/* RIGHT COMPONENT */}
            <div className="lg:col-span-2" ref={workflowRef}>
               <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 min-h-[500px]">
                  <h2 className="text-lg font-bold text-[#143E78] mb-6 border-b pb-3 flex items-center gap-2">
                     <CheckCircle className="text-emerald-600 w-5 h-5" /> Workflow Action Center
                  </h2>

                  {isStageReadOnly && canUpdateStage(displayStatus) && (
                     <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-slate-900 via-slate-800 to-[#143E78] text-white rounded-2xl sm:rounded-3xl border border-slate-700 shadow-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeIn">
                        <div className="flex items-center gap-3.5">
                           <div className="w-11 h-11 rounded-2xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center text-amber-400 shrink-0 shadow-inner">
                              <Lock className="w-5 h-5" />
                           </div>
                           <div>
                              <div className="flex flex-wrap items-center gap-2">
                                 <span className="text-xs sm:text-sm font-black uppercase tracking-wider text-white">
                                    Stage {currentStageIndex + 1}: {pipelineStages[currentStageIndex]?.label} — Read-Only Mode
                                 </span>
                                 <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-black uppercase tracking-widest">
                                    Locked
                                 </span>
                              </div>
                              <p className="text-[10px] sm:text-[11px] font-bold text-slate-300 mt-0.5">
                                 {isAtOrPastPostOp
                                    ? 'This prior stage is locked in read-only mode to prevent edits.'
                                    : 'This stage has already been completed. All fields are locked for audit & review.'}
                              </p>
                           </div>
                        </div>
                        {viewedStatus && viewedStatus !== lead?.status && (
                           <button
                              onClick={() => setViewedStatus(null)}
                              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all self-start sm:self-auto shrink-0 flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
                           >
                              <span>Return to Active Stage</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                           </button>
                        )}
                     </div>
                  )}

                  {!canUpdateStage(displayStatus) && (
                     <div className="mb-8 p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-start gap-4">
                        <AlertCircle className="w-6 h-6 text-[#143E78]" />
                        <div>
                           <h4 className="text-sm font-black text-[#143E78] uppercase tracking-widest">Pipeline Control Restriction</h4>
                           <p className="text-[11px] font-bold text-slate-500">Only authorized staff with update permission for this stage can advance the pipeline. You have view-only access for this stage.</p>
                        </div>
                     </div>
                  )}

                  {!canUpdateStage(displayStatus) ? (
                     <div className="space-y-6 animate-fadeIn">
                        {/* 🏅 PREMIUM VIEWER STATUS INSIGHTS */}
                        <div className="bg-gradient-to-br from-[#143E78] to-[#1e5aa9] text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden group">
                           <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all"></div>
                           <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-blue-300/5 rounded-full blur-2xl"></div>

                           <div className="relative z-10 space-y-6">
                              <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-blue-200" />
                                 </div>
                                 <div>
                                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-200">Current Lead Status</h3>
                                    <p className="text-sm sm:text-lg font-black uppercase tracking-tight">
                                       Stage {currentStageIndex !== -1 ? currentStageIndex + 1 : 'Detail'}: {pipelineStages[currentStageIndex]?.label || lead.status?.replace(/_/g, ' ')}
                                    </p>
                                 </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-white/10">
                                 <div className="space-y-1.5">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-blue-200">Where the Lead is currently</h4>
                                    <p className="text-xs sm:text-sm font-medium leading-relaxed text-blue-50">
                                       {getStatusWhereText(lead.status)}
                                    </p>
                                 </div>
                                 <div className="space-y-1.5">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-blue-200">What happens next</h4>
                                    <p className="text-xs sm:text-sm font-medium leading-relaxed text-blue-50">
                                       {getStatusNextText(lead.status)}
                                    </p>
                                 </div>
                              </div>
                           </div>
                        </div>

                        {/* Audit log preview for viewer visibility */}
                        {unifiedTimeline && unifiedTimeline.length > 0 && (
                           <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 shadow-sm">
                              <h4 className="text-xs font-black text-[#143E78] uppercase tracking-widest mb-4 flex items-center gap-2">
                                 <Activity className="w-4 h-4 text-blue-500" /> Recent Journey Milestones
                              </h4>
                              <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                 {unifiedTimeline.map((item, idx) => (
                                    <div key={idx} className="flex gap-3">
                                       <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                       <div>
                                          <p className="text-xs font-bold text-slate-700">{item.title}</p>
                                          <p className="text-[10px] font-medium text-slate-400">
                                             {new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                          </p>
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           </div>
                        )}
                     </div>
                  ) : (
                     <div className="space-y-6">
                        {/* 🔴 DOCTOR REJECTED / CLINICAL REJECTION MASK */}
                        {lead.status === 'doctor_rejected' && (
                           <div className="p-10 bg-rose-50 border-2 border-rose-100 rounded-[3rem] text-center space-y-6 animate-fadeIn shadow-inner">
                              <div className="text-center space-y-6">
                                 <AlertTriangle className="w-16 h-16 text-rose-500 mx-auto opacity-80" />
                                 <div className="space-y-2">
                                    <h2 className="text-3xl font-black text-rose-700 uppercase tracking-tighter">Clinical Rejection</h2>
                                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-rose-600 text-white rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-rose-200 whitespace-nowrap">
                                       <AlertCircle className="w-3.5 h-3.5" /> High Priority Action Required
                                    </div>
                                 </div>
                                 <div className="bg-white p-6 rounded-3xl border border-rose-100 inline-block max-w-lg shadow-sm">
                                    <p className="text-[10px] font-black text-rose-800 uppercase tracking-widest mb-2 border-b border-rose-50 pb-2 flex items-center justify-center gap-2">
                                       <Stethoscope className="w-3 h-3" /> Doctor's Assessment Remarks
                                    </p>
                                    <p className="text-md font-bold text-slate-700 italic leading-relaxed">
                                       "{lead.clinicalData?.reviewRemarks || 'No detailed reason provided by doctor.'}"
                                    </p>
                                 </div>
                                 <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                                    {canMarkAsLost && (
                                       <button
                                          onClick={() => {
                                             setNotInterestedReason(`Doctor Rejected: ${lead.clinicalData?.reviewRemarks}`);
                                             setShowNotInterestedModal(true);
                                          }}
                                          className="px-10 py-5 bg-rose-600 text-white font-black rounded-2xl uppercase text-[10px] tracking-[0.2em] shadow-xl shadow-rose-200 hover:bg-rose-700 transition-all flex items-center justify-center gap-2"
                                       >
                                          <XCircle className="w-4 h-4" /> Close Lead Permanently
                                       </button>
                                    )}
                                    <button
                                       onClick={async () => {
                                          setUpdating(true);
                                          try {
                                             await api.put(`/leads/${id}/status`, {
                                                status: 'doc_verification',
                                                payload: { doctorReviewDone: false, reviewRemarks: null, reviewingDoctorId: null }
                                             });
                                             toast.success('Lead reverted for Re-assignment');
                                             fetchLead();
                                          } catch (e) { toast.error('Reset Failed'); }
                                          finally { setUpdating(false); }
                                       }}
                                       className="px-10 py-5 bg-white border-2 border-slate-200 text-slate-600 font-black rounded-2xl uppercase text-[10px] tracking-[0.2em] hover:border-[#143E78] hover:text-[#143E78] transition-all flex items-center justify-center gap-2"
                                    >
                                       <RefreshCcw className="w-4 h-4" /> Reassign Another Doctor
                                    </button>
                                 </div>
                              </div>
                           </div>
                        )}

                        {/* 🚫 LEAD CLOSED / NOT INTERESTED MASK */}
                        {lead.status === 'not_interested' && (
                           <div className="p-10 bg-red-50 border-2 border-red-100 rounded-[3rem] text-center space-y-6 animate-fadeIn shadow-inner">
                              <div className="text-center space-y-6">
                                 <Activity className="w-16 h-16 text-red-500 mx-auto opacity-80" />
                                 <div className="space-y-2">
                                    <h2 className="text-3xl font-black text-red-700 uppercase tracking-tighter">Deal Closed: Not Interested</h2>
                                    <p className="text-xs font-bold text-red-500/60 uppercase tracking-[0.2em]">Lead has been moved to terminal archival state</p>
                                 </div>
                                 <div className="bg-red-50 p-6 rounded-3xl border border-red-100 inline-block">
                                    <p className="text-[10px] font-black text-red-800 uppercase tracking-widest mb-1">Reason for Withdrawal</p>
                                    <p className="text-lg font-black text-red-900 uppercase italic">"{lead.closedReason || 'No specific reason provided'}"</p>
                                 </div>
                                 <div className="pt-4">
                                    <button onClick={() => setShowRevertModal(true)} className="px-10 py-4 bg-white border-2 border-red-200 text-red-600 font-black rounded-2xl uppercase text-[10px] tracking-[0.2em] shadow-sm hover:bg-red-600 hover:text-white hover:border-red-600 transition-all">RE-OPEN DEAL & REVERT TO FOLLOW-UP</button>
                                 </div>
                              </div>
                           </div>
                        )}

                        {lead.status !== 'not_interested' && (
                           <>
                              {/* 🏃 ACTIVE FOLLOW-UP MANAGEMENT (Global Hook) */}
                              {lead.nextFollowUpDate && lead.status !== 'patient_ready' && !isStageReadOnly && (
                                 <div className="bg-indigo-900 text-white p-6 rounded-3xl border border-indigo-800 shadow-2xl relative overflow-hidden group mb-6">
                                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-all"></div>
                                    <div className="relative z-10 space-y-5">
                                       <div className="flex justify-between items-start">
                                          <div className="space-y-1">
                                             <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-indigo-300">Scheduled Follow-up</h3>
                                             <p className="text-xl font-black flex items-center gap-2">
                                                <Calendar className="w-5 h-5 text-indigo-400" />
                                                {new Date(lead.nextFollowUpDate).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                                             </p>
                                          </div>
                                          <div className="px-3 py-1 bg-indigo-500/20 border border-indigo-400/20 rounded-full text-[10px] font-black uppercase tracking-widest text-indigo-200">
                                             Due {new Date(lead.nextFollowUpDate) < new Date() ? 'Now' : 'Soon'}
                                          </div>
                                       </div>

                                       {!followUpAction ? (
                                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                             <button onClick={() => handleFollowUpAction('ready')} className="py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-xl text-[10px] uppercase tracking-widest transition-all shadow-lg shadow-emerald-900/40">Patient Ready (YES)</button>
                                             <button onClick={() => setFollowUpAction('not_ready')} className="py-3 bg-indigo-700 hover:bg-indigo-600 text-white font-black rounded-xl text-[10px] uppercase tracking-widest transition-all">Reschedule</button>
                                             <button onClick={() => setFollowUpAction('not_interested')} className="py-3 bg-red-900/40 hover:bg-red-900/60 text-white font-black border border-red-500/30 rounded-xl text-[10px] uppercase tracking-widest transition-all">Not Interested (NO)</button>
                                          </div>
                                       ) : (
                                          <div className="bg-indigo-950/50 p-5 rounded-2xl border border-indigo-500/20 space-y-4 animate-fadeIn">
                                             <div className="flex justify-between items-center">
                                                <h4 className="font-black text-[10px] uppercase tracking-widest text-indigo-300">
                                                   {followUpAction === 'not_ready' ? 'Set New Follow-up' : 'Confirm Cancellation'}
                                                </h4>
                                                <button onClick={() => setFollowUpAction(null)} className="text-[10px] font-black uppercase text-indigo-400 hover:text-white transition-all">Cancel</button>
                                             </div>

                                             {followUpAction === 'not_ready' ? (
                                                <div className="space-y-4">
                                                   <input type="date" min={isPastSurgeryCase ? undefined : todayDateStr} className="w-full p-4 bg-indigo-900 border border-indigo-700 rounded-xl text-white font-bold outline-none uppercase" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} />
                                                   <textarea className="w-full p-4 bg-indigo-900 border border-indigo-700 rounded-xl text-white text-sm outline-none placeholder:text-indigo-400/50" placeholder="Why were they not ready? Add context..." value={rescheduleNotes} onChange={(e) => setRescheduleNotes(e.target.value)} rows={2} />
                                                   <button onClick={() => handleFollowUpAction('not_ready')} disabled={updating || !rescheduleDate} className="w-full py-4 bg-indigo-500 text-white font-black rounded-xl text-xs uppercase tracking-widest disabled:opacity-50">Schedule Next Call</button>
                                                </div>
                                             ) : (
                                                <div className="space-y-4 text-center">
                                                   <select className="w-full p-4 bg-red-900/20 border border-red-900/50 rounded-xl text-white font-bold outline-none" value={notInterestedReason} onChange={(e) => setNotInterestedReason(e.target.value)}>
                                                      <option value="">Select Reason for Closure</option>
                                                      <option value="Not Interested">Not Interested</option>
                                                      <option value="Delayed">Delayed Indefinitely</option>
                                                      <option value="Financial">Financial Constraints</option>
                                                      <option value="Other">Other</option>
                                                   </select>
                                                   <button onClick={() => handleFollowUpAction('not_interested')} disabled={updating || !notInterestedReason} className="w-full py-4 bg-red-600 text-white font-black rounded-xl text-xs uppercase tracking-widest shadow-lg shadow-red-900/50 disabled:opacity-50">Confirm Close Deal</button>
                                                </div>
                                             )}
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              )}

                              {/* 🛡️ PATIENT READY STATUS CARD */}
                              {displayStatus === 'patient_ready' && (
                                 <div className="bg-emerald-900 text-white p-6 rounded-3xl border border-emerald-800 shadow-2xl relative overflow-hidden group mb-6">
                                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
                                    <div className="relative z-10 space-y-5">
                                       <div className="flex justify-between items-center">
                                          <div className="space-y-1">
                                             <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-emerald-300">Readiness Confirmed</h3>
                                             <p className="text-xl font-black flex items-center gap-2">
                                                <CheckCircle className="w-5 h-5 text-emerald-400" />
                                                Ready by {new Date(lead.clinicalIntake?.readinessDate).toLocaleDateString()}
                                             </p>
                                          </div>
                                          {!isStageReadOnly && (
                                             <button onClick={() => {
                                                setReadyFormData({
                                                   readinessDate: lead.clinicalIntake?.readinessDate?.split('T')[0] || '',
                                                   notes: lead.clinicalIntake?.readinessNotes || '',
                                                   confirmed: true
                                                });
                                                setShowReadyModal(true);
                                             }} className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/40 border border-emerald-400/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">Edit Date</button>
                                          )}
                                       </div>

                                       <div className="p-4 bg-emerald-950/40 rounded-2xl border border-emerald-500/20">
                                          <p className="text-[11px] font-medium text-emerald-100/80 italic">"{lead.clinicalIntake?.readinessNotes || 'No notes provided.'}"</p>
                                       </div>

                                       {!isStageReadOnly && (
                                          <button onClick={() => setShowRevertModal(true)} className="w-full py-3 text-[10px] font-black uppercase text-emerald-400 hover:text-white transition-all border-t border-emerald-800/50 mt-2">Revert to Follow-up Stage</button>
                                       )}
                                    </div>
                                 </div>
                              )}
                              {showReadyModal && createPortal(
                                 <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-900/80 backdrop-blur-md">
                                    <div className="bg-white rounded-[2rem] w-full max-w-md mx-6 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] flex flex-col max-h-[85vh]">

                                       {/* HEADER */}
                                       <div className="bg-indigo-900 rounded-t-[2rem] px-6 py-5 text-white shrink-0 relative overflow-hidden flex justify-between items-center">
                                          <div className="absolute top-0 right-0 p-6 opacity-20">
                                             <ShieldCheck className="w-16 h-16 rotate-12" />
                                          </div>
                                          <div className="relative z-10">
                                             <h3 className="text-xl font-black uppercase tracking-tighter">Confirm Readiness</h3>
                                             <p className="text-indigo-200 text-[10px] font-bold mt-1 uppercase tracking-widest leading-relaxed">
                                                Final check before scheduling patient intake
                                             </p>
                                          </div>
                                          <button onClick={() => setShowReadyModal(false)} className="relative z-20 p-2 hover:bg-white/10 rounded-full transition-all">
                                             <X className="w-6 h-6" />
                                          </button>
                                       </div>

                                       {/* BODY */}
                                       <div className="p-6 space-y-4 overflow-y-auto">
                                          <div className="space-y-3">
                                             <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                                   Earliest Surgery Date
                                                </label>
                                                <input
                                                   type="date" min={isPastSurgeryCase ? undefined : todayDateStr}
                                                   className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl font-black text-slate-800 outline-none focus:border-indigo-600 focus:bg-white transition-all"
                                                   value={readyFormData.readinessDate}
                                                   onChange={(e) => setReadyFormData({ ...readyFormData, readinessDate: e.target.value })}
                                                />
                                             </div>

                                             <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                                   Consultation / Triage Notes
                                                </label>
                                                <textarea
                                                   className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-bold text-slate-600 outline-none focus:border-indigo-600 focus:bg-white transition-all"
                                                   rows={2}
                                                   placeholder="Enter clinical observations..."
                                                   value={readyFormData.notes}
                                                   onChange={(e) => setReadyFormData({ ...readyFormData, notes: e.target.value })}
                                                />
                                             </div>

                                             <label className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-2xl cursor-pointer transition-all">
                                                <input
                                                   type="checkbox"
                                                   className="w-5 h-5 rounded-lg border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                                                   checked={readyFormData.confirmed}
                                                   onChange={(e) => setReadyFormData({ ...readyFormData, confirmed: e.target.checked })}
                                                />
                                                <span className="text-xs font-black text-emerald-800 uppercase tracking-widest">
                                                   Patient confirmed for surgery
                                                </span>
                                             </label>
                                          </div>

                                          <div className="flex flex-col gap-2">
                                             {updating ? (
                                                <div className="w-full py-4 bg-emerald-600 rounded-2xl relative overflow-hidden flex items-center justify-center gap-3">
                                                   {/* Shimmer sweep */}
                                                   <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                                                   {/* Skeleton blobs */}
                                                   <div className="w-4 h-4 rounded-full bg-emerald-400/60 animate-pulse" />
                                                   <div className="h-3 w-32 rounded-full bg-emerald-400/60 animate-pulse" />
                                                </div>
                                             ) : (
                                                <button
                                                   onClick={handleConfirmReady}
                                                   disabled={!readyFormData.readinessDate || !readyFormData.confirmed}
                                                   className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-2xl shadow-lg shadow-emerald-200 uppercase text-xs tracking-[0.2em] transition-all disabled:opacity-50 active:scale-95"
                                                >
                                                   Confirm & Proceed
                                                </button>
                                             )}
                                          </div>
                                       </div>
                                    </div>
                                 </div>,
                                 document.body  // 👈 renders outside all parent stacking contexts
                              )}

                              {displayStatus === 'new' && (
                                 <div className="bg-amber-50 p-6 rounded-2xl border border-amber-100 space-y-5">
                                    <h3 className="font-black text-amber-800 uppercase text-xs tracking-widest">Stage 1: Initial Medical Check</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                       <label className="flex items-center gap-3 p-4 bg-white rounded-xl border border-amber-100 text-sm font-bold">
                                          <input type="checkbox" disabled={isStageReadOnly} checked={screeningForm.eligibility} onChange={(e) => setScreeningForm({ ...screeningForm, eligibility: e.target.checked })} /> Patient Eligible
                                       </label>
                                       <label className="flex items-center gap-3 p-4 bg-white rounded-xl border border-amber-100 text-sm font-bold">
                                          <input type="checkbox" disabled={isStageReadOnly} checked={screeningForm.conditionSuitable} onChange={(e) => setScreeningForm({ ...screeningForm, conditionSuitable: e.target.checked })} /> Medically Approved
                                       </label>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                       <button type="button" disabled={isStageReadOnly} onClick={() => {
                                          setScreeningForm({ ...screeningForm, timeline: 'Immediate' });
                                          setShowReadyModal(true);
                                       }} className={`py-4 rounded-xl font-black text-xs uppercase transition-all ${screeningForm.timeline === 'Immediate' ? 'bg-amber-600 text-white shadow-lg shadow-amber-200/50' : 'bg-white text-amber-700 border border-amber-200'} ${isStageReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}>⚡ Ready Now</button>
                                       <button type="button" disabled={isStageReadOnly} onClick={() => setScreeningForm({ ...screeningForm, timeline: 'Later' })} className={`py-4 rounded-xl font-black text-xs uppercase transition-all ${screeningForm.timeline === 'Later' ? 'bg-blue-600 text-white shadow-lg shadow-blue-200/50' : 'bg-white text-blue-700 border border-blue-200'} ${isStageReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}>📅 Follow Up Later</button>
                                    </div>

                                    {screeningForm.timeline === 'Later' && (
                                       <div className="space-y-4 animate-fadeIn">
                                          <div className="bg-white p-4 rounded-xl border border-amber-100 flex flex-col gap-2">
                                             <label className="text-[10px] font-black text-amber-800 uppercase tracking-widest">Follow-up Date</label>
                                             <input type="date" disabled={isStageReadOnly} min={isPastSurgeryCase ? undefined : todayDateStr} className="w-full p-2 bg-slate-50 border border-slate-100 rounded-lg font-bold" value={screeningForm.followUpDate} onChange={(e) => setScreeningForm({ ...screeningForm, followUpDate: e.target.value })} />
                                             <textarea disabled={isStageReadOnly} className="w-full p-3 bg-slate-50 border border-slate-100 rounded-lg text-xs font-medium" placeholder="Triage notes for follow-up queue..." value={screeningForm.followUpNotes} onChange={(e) => setScreeningForm({ ...screeningForm, followUpNotes: e.target.value })} rows={2} />
                                          </div>
                                       </div>
                                    )}

                                    {displayStatus === 'new' && (
                                       !isStageReadOnly ? (
                                          <button onClick={handleAdvanceStageOne} disabled={!canUpdateStage(displayStatus) || updating} className="w-full bg-[#143E78] text-white font-black py-4 rounded-xl shadow-lg uppercase text-xs tracking-widest disabled:opacity-50">Advance Stage</button>
                                       ) : (
                                          <div className="w-full bg-emerald-50 text-emerald-700 font-black py-3 rounded-xl border border-emerald-200 uppercase text-xs tracking-widest text-center flex items-center justify-center gap-2">
                                             <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Stage 1 Completed & Verified
                                          </div>
                                       )
                                    )}
                                 </div>
                              )}

                              {displayStatus === 'screening' && (
                                 <div className="bg-slate-50 p-8 rounded-3xl border-2 border-dashed border-slate-200 text-center space-y-4">
                                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto shadow-sm border border-slate-100">
                                       <CheckCircle className="text-emerald-500 w-8 h-8" />
                                    </div>
                                    <div>
                                       <h3 className="text-sm font-black text-[#143E78] uppercase tracking-widest">Pre-Screening Complete</h3>
                                       <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Lead has been screened and moved forward</p>
                                    </div>
                                    <div className="pt-4 border-t border-slate-100">
                                       <p className="text-[11px] font-medium text-slate-500 italic mb-4">"The patient screening details have been triaged and verified."</p>
                                       {!isStageReadOnly && (
                                          <button
                                             onClick={() => setShowReadyModal(true)}
                                             className="px-6 py-3 bg-[#143E78] text-white text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
                                          >
                                             Jump to Immediate Readiness
                                          </button>
                                       )}
                                    </div>
                                 </div>
                              )}

                              {/* Stage 2: REAL CC CALL WORKFLOW */}
                              {displayStatus === 'patient_ready' && (
                                 <div className="space-y-6" ref={intakeRef}>
                                    {/* 📊 INTAKE PROGRESS TRACKER */}
                                    <div className="flex justify-between items-center px-4 sm:px-6 py-4 bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm mb-6 overflow-x-auto no-scrollbar">
                                       {[
                                          { s: 1, label: 'CC Call' },
                                          { s: 2, label: 'Reports' },
                                          { s: 3, label: 'Vitals' },
                                          { s: 4, label: 'Hospital' },
                                          { s: 5, label: 'Docs' },
                                          { s: 6, label: 'Verify' }
                                       ].map((step) => (
                                          <div key={step.s} onClick={() => setIntakeStep(step.s)} className="flex items-center gap-2 cursor-pointer group">
                                             <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black transition-all ${intakeStep === step.s ? 'bg-[#143E78] text-white shadow-lg ring-2 ring-blue-200 scale-105' : intakeStep > step.s ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'}`}>
                                                {intakeStep > step.s ? <CheckCircle2 className="w-4 h-4" /> : step.s}
                                             </div>
                                             <span className={`text-[9px] font-black uppercase tracking-tight hidden sm:block ${intakeStep === step.s ? 'text-[#143E78]' : 'text-slate-400'}`}>{step.label}</span>
                                             {step.s < 6 && <div className={`h-1 w-3 sm:w-6 rounded-full ${intakeStep > step.s ? 'bg-emerald-600' : 'bg-slate-100'}`}></div>}
                                          </div>
                                       ))}
                                    </div>

                                    {/* STEP 1: CC CALL & INITIAL TRIAGE */}
                                    {intakeStep === 1 && (
                                       <div className="space-y-6 animate-fadeIn">
                                          <div className="bg-indigo-50 p-8 rounded-[2.5rem] border border-indigo-100 text-center space-y-6">
                                             <div className="w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center mx-auto">
                                                <PhoneCall className="w-8 h-8 text-[#143E78]" />
                                             </div>
                                             <div className="space-y-2">
                                                <h3 className="text-xl font-black text-[#143E78] uppercase tracking-tighter">Check for Medical Reports</h3>
                                                <p className="text-sm font-bold text-slate-500 italic">"Does {lead.patientName} have their latest medical reports or prescriptions ready?"</p>
                                             </div>

                                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-sm mx-auto">
                                                <button disabled={isStageReadOnly} onClick={() => {
                                                   setCallStatus('documents_yes');
                                                   setIntakeStep(2);
                                                }} className={`p-6 bg-white border-2 border-emerald-100 hover:border-emerald-500 hover:bg-emerald-50 rounded-3xl flex flex-col items-center gap-3 transition-all group shadow-sm text-center text-[10px] sm:text-xs ${isStageReadOnly ? 'opacity-80 cursor-default' : ''}`}>
                                                   <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto" />
                                                   <span className="font-black text-xs uppercase tracking-widest text-emerald-700">Yes, Has Reports</span>
                                                </button>
                                                <button disabled={isStageReadOnly} onClick={() => {
                                                   setCallStatus('documents_no');
                                                   setIntakeForm({ ...intakeForm, reportType: 'None' });
                                                   setIntakeStep(3);
                                                }} className={`p-6 bg-white border-2 border-amber-100 hover:border-amber-500 hover:bg-amber-50 rounded-3xl flex flex-col items-center gap-3 transition-all group shadow-sm text-center text-[10px] sm:text-xs ${isStageReadOnly ? 'opacity-80 cursor-default' : ''}`}>
                                                   <HelpCircle className="w-6 h-6 text-amber-500 mx-auto" />
                                                   <span className="font-black text-xs uppercase tracking-widest text-amber-700">No Reports Available</span>
                                                </button>
                                             </div>
                                          </div>
                                       </div>
                                    )}

                                    {/* STEP 2: REPORT OPTIONS */}
                                    {intakeStep === 2 && (
                                       <div className="space-y-6 animate-fadeIn">
                                          <div className="bg-white p-8 rounded-[2.5rem] border-2 border-[#143E78]/10 shadow-xl space-y-8">
                                             <div className="flex items-center gap-4">
                                                <FileText className="w-10 h-10 text-blue-600 bg-blue-50 p-2 rounded-2xl" />
                                                <div>
                                                   <h3 className="text-lg font-black text-[#143E78] uppercase tracking-widest leading-none">Report Selection</h3>
                                                   <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">What type of reports are available?</p>
                                                </div>
                                             </div>

                                             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                {['Complete', 'Partial', 'Old Reports'].map((type) => (
                                                   <button
                                                      key={type}
                                                      disabled={isStageReadOnly}
                                                      onClick={() => setIntakeForm({ ...intakeForm, reportType: type })}
                                                      className={`p-6 rounded-3xl border-2 flex flex-col items-center gap-3 transition-all ${intakeForm.reportType === type ? 'bg-[#143E78] border-[#143E78] text-white' : 'bg-white border-slate-100 text-slate-600 hover:border-blue-200'} ${isStageReadOnly ? 'cursor-default' : ''}`}
                                                   >
                                                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${intakeForm.reportType === type ? 'bg-white/20' : 'bg-blue-50'}`}>
                                                         <FileText className={`w-5 h-5 ${intakeForm.reportType === type ? 'text-white' : 'text-blue-600'}`} />
                                                      </div>
                                                      <span className="font-black text-xs uppercase tracking-widest">{type}</span>
                                                   </button>
                                                ))}
                                             </div>

                                             <div className="flex gap-4">
                                                <button onClick={() => setIntakeStep(1)} className="flex-1 py-4 bg-slate-100 text-slate-500 font-black rounded-2xl uppercase tracking-widest text-center text-[9px] sm:text-xs">Back</button>
                                                <button
                                                   onClick={() => setIntakeStep(3)}
                                                   disabled={!intakeForm.reportType}
                                                   className="flex-[2] py-4 bg-[#143E78] text-white font-black rounded-2xl shadow-xl uppercase tracking-widest transition-all hover:scale-[1.02] text-center disabled:opacity-50"
                                                >
                                                   Next: Vitals & History
                                                </button>
                                             </div>
                                          </div>
                                       </div>
                                    )}

                                    {/* STEP 3: VITALS & HISTORY */}
                                    {intakeStep === 3 && (
                                       <div className="space-y-6 animate-fadeIn">
                                          <div className="bg-white p-8 rounded-[2.5rem] border-2 border-[#143E78]/10 shadow-xl space-y-8">
                                             <div className="flex items-center gap-4">
                                                <Activity className="w-10 h-10 text-purple-600 bg-purple-50 p-2 rounded-2xl" />
                                                <div>
                                                   <h3 className="text-lg font-black text-[#143E78] uppercase tracking-widest leading-none">Medical Details & History</h3>
                                                   <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">Record basic health information</p>
                                                </div>

                                             </div>
                                             <div className="p-6 bg-indigo-50/50 rounded-3xl border border-indigo-100 flex items-center justify-between group hover:border-[#143E78]/30 transition-all">
                                                <div className="flex items-center gap-4">
                                                   <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                                                      <Calendar className="w-6 h-6 text-[#143E78]" />
                                                   </div>
                                                   <div>
                                                      <p className="text-[10px] font-black text-[#143E78] uppercase tracking-[0.2em]">Required Information</p>
                                                      {/* <h4 className="text-sm font-black text-[#143E78]">Expected Surgery Date</h4> */}
                                                   </div>
                                                </div>

                                             </div>

                                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-4">
                                                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">A. Medical History</p>
                                                   <textarea disabled={isStageReadOnly} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold disabled:bg-slate-100 disabled:text-slate-700" rows={3} placeholder="Current Illness" value={intakeForm.medicalDetails.history.currentIllness} onChange={(e) => setIntakeForm({ ...intakeForm, medicalDetails: { ...intakeForm.medicalDetails, history: { ...intakeForm.medicalDetails.history, currentIllness: e.target.value } } })} />
                                                   <textarea disabled={isStageReadOnly} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold disabled:bg-slate-100 disabled:text-slate-700" rows={3} placeholder="Past Illness" value={intakeForm.medicalDetails.history.pastDiseases} onChange={(e) => setIntakeForm({ ...intakeForm, medicalDetails: { ...intakeForm.medicalDetails, history: { ...intakeForm.medicalDetails.history, pastDiseases: e.target.value } } })} />
                                                </div>
                                                <div className="space-y-4">
                                                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">B. Surgical History</p>
                                                   <textarea disabled={isStageReadOnly} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold disabled:bg-slate-100 disabled:text-slate-700" rows={3} placeholder="Previous Surgical History" value={intakeForm.medicalDetails.history.previousSurgeries} onChange={(e) => setIntakeForm({ ...intakeForm, medicalDetails: { ...intakeForm.medicalDetails, history: { ...intakeForm.medicalDetails.history, previousSurgeries: e.target.value } } })} />
                                                </div>
                                             </div>

                                             {!isStageReadOnly && (
                                                <div className="flex gap-4">
                                                   <button onClick={() => setIntakeStep(callStatus === 'documents_yes' ? 2 : 1)} className="flex-1 py-4 bg-slate-100 text-slate-500 font-black rounded-2xl uppercase tracking-widest text-center text-[9px] sm:text-xs">Back</button>
                                                   <button onClick={() => setIntakeStep(4)} className="flex-[2] py-4 bg-[#143E78] text-white font-black rounded-2xl shadow-xl uppercase tracking-widest transition-all hover:scale-[1.02] text-center text-[9px] sm:text-xs">Next: Hospital Assignment</button>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    )}

                                    {/* STEP 4: HOSPITAL ASSIGNMENT */}
                                    {intakeStep === 4 && (
                                       <div className="space-y-6 animate-fadeIn">
                                          <div className="bg-white p-8 rounded-[2.5rem] border-2 border-amber-600/10 shadow-xl space-y-8">
                                             <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                <div className="flex items-center gap-4">
                                                   <Building2 className="w-10 h-10 text-amber-600 bg-amber-50 p-2 rounded-2xl shrink-0" />
                                                   <div>
                                                      <h3 className="text-base sm:text-lg font-black text-[#143E78] uppercase tracking-widest leading-tight">Hospital Assignment</h3>
                                                      <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">Choose hospital for treatment</p>
                                                   </div>
                                                </div>
                                                {(lead.assignedHospital || lead.leadProviderHospitalId) && (
                                                   <div className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-[10px] font-black uppercase ring-1 ring-emerald-200 self-start sm:self-center whitespace-nowrap shrink-0">System Detected</div>
                                                )}
                                             </div>

                                             <div className="p-6 bg-slate-50 rounded-3xl border border-slate-200 space-y-6">
                                                {resolvedHospital ? (
                                                   <div className="space-y-6">
                                                      <div className="flex items-center gap-4">
                                                         <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-md">
                                                            <MapPin className="text-amber-600 w-6 h-6" />
                                                         </div>
                                                         <div>
                                                            <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Target Hospital</p>
                                                            <p className="text-lg font-black text-[#143E78]">{resolvedHospital.hospitalName}</p>
                                                         </div>
                                                      </div>

                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                         <button disabled={isStageReadOnly} onClick={() => {
                                                            setHospitalProcess('bed_check');
                                                            toast.success('Bed Availability Confirmed');
                                                         }} className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all text-center ${hospitalProcess !== 'assignment' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-white border-slate-100 text-slate-400'}`}>
                                                            <Bed className="w-6 h-6" />
                                                            <span className="text-[9px] font-black uppercase">Check Bed</span>
                                                         </button>
                                                         <button disabled={isStageReadOnly} onClick={() => setShowHospitalChange(true)} className={`p-4 bg-white border-2 border-slate-100 rounded-2xl flex flex-col items-center gap-2 transition-all text-slate-400 text-center text-[9px] sm:text-xs ${isStageReadOnly ? 'cursor-default' : 'hover:border-amber-500 hover:text-amber-600'}`}>
                                                            <UserPlus className="w-6 h-6" />
                                                            <span className="text-[9px] font-black uppercase">Change Center</span>
                                                         </button>
                                                      </div>
                                                   </div>
                                                ) : (
                                                   <div className="text-center py-6 space-y-4">
                                                      <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
                                                      {lead.leadGeneratedByName && (
                                                         <div className="bg-slate-100 px-4 py-2 rounded-xl inline-flex items-center gap-2">
                                                            <Stethoscope className="w-3.5 h-3.5 text-slate-500" />
                                                            <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">
                                                               {lead.leadGeneratedByType === 'doctor' ? 'Dr. ' : ''}{lead.leadGeneratedByName}
                                                            </span>
                                                         </div>
                                                      )}
                                                      <p className="text-xs font-bold text-slate-600 px-8">No hospital is linked to this lead yet.</p>
                                                      {!isStageReadOnly && (
                                                         <button
                                                            onClick={() => setShowHospitalChange(true)}
                                                            className="px-6 py-3 bg-[#143E78] text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 mx-auto shadow-lg"
                                                         >
                                                            <MapPin className="w-4 h-4" /> Assign Hospital
                                                         </button>
                                                      )}
                                                   </div>
                                                )}
                                             </div>

                                             {hospitalProcess === 'bed_check' && !isStageReadOnly && (
                                                <div className="bg-indigo-900 p-8 rounded-3xl text-white space-y-4 animate-fadeIn">
                                                   <h4 className="text-xs font-black uppercase tracking-widest text-indigo-300">Next Step</h4>
                                                   <p className="text-sm font-medium opacity-80 leading-relaxed text-center text-[9px] sm:text-xs">"Bed availability is confirmed. Please guide the patient to begin their tests."</p>
                                                   <button onClick={() => {
                                                      setHospitalProcess('tests_ongoing');
                                                      toast.success('Patient dispatched for tests at center');
                                                      setIntakeStep(5);
                                                   }} className="w-full py-4 bg-white text-[#143E78] font-black rounded-2xl uppercase tracking-widest shadow-xl text-center text-[9px] sm:text-xs">Start Tests</button>
                                                </div>
                                             )}

                                             {!isStageReadOnly && (
                                                <div className="flex gap-4">
                                                   <button onClick={() => setIntakeStep(3)} className="flex-1 py-4 bg-slate-100 text-slate-500 font-black rounded-2xl uppercase tracking-widest text-center text-[9px] sm:text-xs">Back</button>
                                                   <button onClick={() => setIntakeStep(5)} className="flex-[2] py-4 bg-[#143E78] text-white font-black rounded-2xl shadow-xl uppercase tracking-widest transition-all hover:scale-[1.02] text-center text-[9px] sm:text-xs">Next: Medical Reports</button>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    )}

                                    {/* STEP 5: DOCUMENT COLLECTION (PATIENT OR HOSPITAL) */}
                                    {intakeStep === 5 && (
                                       <div className="space-y-6 animate-fadeIn">
                                          <div className="bg-white p-8 rounded-[2.5rem] border-2 border-[#143E78]/10 shadow-xl space-y-8">
                                             <div className="flex justify-between items-center sm:flex-row flex-col gap-4">
                                                <div className="flex items-center gap-4">
                                                   <FileText className="w-10 h-10 text-[#143E78] bg-indigo-50 p-2 rounded-2xl" />
                                                   <div>
                                                      <h3 className="text-lg font-black text-[#143E78] uppercase tracking-widest leading-none">Upload Medical Records</h3>
                                                      <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">Gathering reports from {callStatus === 'documents_no' ? 'the Hospital' : 'the Patient'}</p>
                                                   </div>
                                                </div>
                                                {!isStageReadOnly && (
                                                   <div className="flex gap-2">
                                                      <button onClick={() => setSharingChannel('patient')} className={`px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${sharingChannel === 'patient' ? 'bg-[#143E78] text-white' : 'bg-slate-100 text-slate-500'}`}>Contact Patient</button>
                                                      <button onClick={() => setSharingChannel('hospital')} className={`px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${sharingChannel === 'hospital' ? 'bg-amber-600 text-white shadow-lg shadow-amber-200' : 'bg-slate-100 text-slate-500'}`}>Contact Hospital</button>
                                                   </div>
                                                )}
                                             </div>

                                             {/* ── PATIENT CONTACT PANEL ── */}
                                             {!isStageReadOnly && sharingChannel === 'patient' && (
                                                <div className="bg-slate-50 p-6 rounded-[2rem] border-2 border-dashed border-indigo-200 space-y-5 animate-fadeIn">
                                                   {/* Patient Info Card */}
                                                   <div className="bg-white p-5 rounded-2xl border border-indigo-100 shadow-sm">
                                                      <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-3">Patient Contact Details</p>
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                         <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-xl">
                                                            <PhoneCall className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                                                            <div>
                                                               <p className="text-[8px] font-black text-slate-400 uppercase">Phone / WhatsApp</p>
                                                               <p className="text-sm font-black text-slate-800">{lead.patientPhone || '—'}</p>
                                                            </div>
                                                         </div>
                                                         <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-xl">
                                                            <Mail className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                                                            <div>
                                                               <p className="text-[8px] font-black text-slate-400 uppercase">Email</p>
                                                               <p className="text-xs font-bold text-slate-800 truncate max-w-[140px]">{lead.patientEmail || '—'}</p>
                                                            </div>
                                                         </div>
                                                      </div>
                                                   </div>

                                                   {/* Sharing Form */}
                                                   <div className="space-y-3">
                                                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Send Report Upload Link To Patient</label>
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                         <div className="relative">
                                                            <input className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs font-bold pr-16" placeholder="WhatsApp No." value={shareForm.whatsapp} onChange={(e) => setShareForm({ ...shareForm, whatsapp: e.target.value })} />
                                                            {lead.patientPhone && (
                                                               <button onClick={() => setShareForm({ ...shareForm, whatsapp: lead.patientPhone })} className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-black text-indigo-500 bg-indigo-50 px-2 py-1 rounded-lg hover:bg-indigo-100 transition-all uppercase">Auto</button>
                                                            )}
                                                         </div>
                                                         <div className="relative">
                                                            <input className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs font-bold pr-16" placeholder="Email Address" value={shareForm.email} onChange={(e) => setShareForm({ ...shareForm, email: e.target.value })} />
                                                            {lead.patientEmail && (
                                                               <button onClick={() => setShareForm({ ...shareForm, email: lead.patientEmail })} className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-black text-indigo-500 bg-indigo-50 px-2 py-1 rounded-lg hover:bg-indigo-100 transition-all uppercase">Auto</button>
                                                            )}
                                                         </div>
                                                      </div>
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                         <a
                                                            href={shareForm.whatsapp ? `https://wa.me/${shareForm.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hello ${lead.patientName}, please use this secure link to upload your medical documents: ${window.location.origin}/submit-reports/${id}\n\nRegards,\nDOXEZ Clinical Team`)}` : '#'}
                                                            target="_blank" rel="noopener noreferrer"
                                                            onClick={(e) => { if (!shareForm.whatsapp) { e.preventDefault(); toast.error('Enter WhatsApp number first'); } }}
                                                            className="p-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl flex items-center justify-center gap-2 shadow-sm transition-all text-center text-[9px] sm:text-xs"
                                                         >
                                                            <MessageSquare className="w-4 h-4" />
                                                            <span className="text-[9px] font-black uppercase tracking-widest">WhatsApp</span>
                                                         </a>
                                                         <button onClick={() => handleSendClinicalLink('email')} disabled={updating} className="p-4 bg-[#143E78] hover:bg-blue-900 text-white rounded-2xl flex items-center justify-center gap-2 shadow-sm transition-all text-center disabled:opacity-50">
                                                            <Mail className="w-4 h-4" />
                                                            <span className="text-[9px] font-black uppercase tracking-widest">Email Link</span>
                                                         </button>
                                                      </div>
                                                   </div>
                                                   <p className="text-[9px] font-bold text-slate-400 italic text-center uppercase tracking-widest">Patient uploads via secure portal — docs auto-sync to this lead</p>
                                                </div>
                                             )}

                                             {/* ── HOSPITAL CONTACT PANEL ── */}
                                             {!isStageReadOnly && sharingChannel === 'hospital' && (
                                                <div className="bg-amber-50 p-6 rounded-[2rem] border-2 border-dashed border-amber-200 space-y-5 animate-fadeIn">
                                                   {/* Hospital Info Card */}
                                                   <div className="bg-white p-5 rounded-2xl border border-amber-100 shadow-sm">
                                                      <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-3">Hospital Contact Details</p>
                                                      {resolvedHospital ? (
                                                         <div className="space-y-3">
                                                            <div className="flex items-center gap-3 mb-3">
                                                               <Building2 className="w-5 h-5 text-amber-600" />
                                                               <p className="text-sm font-black text-[#143E78]">{resolvedHospital.hospitalName}</p>
                                                            </div>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                               <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                                                                  <PhoneCall className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                                  <div>
                                                                     <p className="text-[8px] font-black text-slate-400 uppercase">Contact Phone</p>
                                                                     <p className="text-sm font-black text-slate-800">{resolvedHospital.contactPerson?.phone || '—'}</p>
                                                                  </div>
                                                               </div>
                                                               <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                                                                  <Mail className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                                  <div>
                                                                     <p className="text-[8px] font-black text-slate-400 uppercase">Contact Email</p>
                                                                     <p className="text-xs font-bold text-slate-800 truncate max-w-[130px]">{resolvedHospital.contactPerson?.email || resolvedHospital.mailId || '—'}</p>
                                                                  </div>
                                                               </div>
                                                            </div>
                                                            {resolvedHospital.contactPerson?.name && (
                                                               <p className="text-[10px] font-bold text-slate-500 pl-1">Contact Person: <span className="text-slate-700 font-black">{resolvedHospital.contactPerson.name}</span>{resolvedHospital.contactPerson.designation ? ` · ${resolvedHospital.contactPerson.designation}` : ''}</p>
                                                            )}
                                                         </div>
                                                      ) : (
                                                         <div className="text-center py-4">
                                                            <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                                                            <p className="text-xs font-bold text-slate-500">No hospital assigned yet. Assign a hospital first.</p>
                                                         </div>
                                                      )}
                                                   </div>

                                                   {/* Hospital Upload Link */}
                                                   <div className="space-y-3">
                                                      <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Send Report Upload Link To Hospital</label>
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                         <div className="relative">
                                                            <input className="w-full p-3 bg-white border border-amber-200 rounded-xl text-xs font-bold pr-16" placeholder="Hospital WhatsApp" value={shareForm.whatsapp} onChange={(e) => setShareForm({ ...shareForm, whatsapp: e.target.value })} />
                                                            {resolvedHospital?.contactPerson?.phone && (
                                                               <button onClick={() => setShareForm({ ...shareForm, whatsapp: resolvedHospital.contactPerson.phone })} className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-black text-amber-600 bg-amber-50 px-2 py-1 rounded-lg hover:bg-amber-100 transition-all uppercase">Auto</button>
                                                            )}
                                                         </div>
                                                         <div className="relative">
                                                            <input className="w-full p-3 bg-white border border-amber-200 rounded-xl text-xs font-bold pr-16" placeholder="Hospital Email" value={shareForm.email} onChange={(e) => setShareForm({ ...shareForm, email: e.target.value })} />
                                                            {(resolvedHospital?.contactPerson?.email || resolvedHospital?.mailId) && (
                                                               <button onClick={() => setShareForm({ ...shareForm, email: resolvedHospital.contactPerson?.email || resolvedHospital.mailId })} className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-black text-amber-600 bg-amber-50 px-2 py-1 rounded-lg hover:bg-amber-100 transition-all uppercase">Auto</button>
                                                            )}
                                                         </div>
                                                      </div>
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                         <a
                                                            href={shareForm.whatsapp ? `https://wa.me/${shareForm.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Dear ${resolvedHospital?.contactPerson?.name || 'Hospital Team'},\n\nPlease upload the medical reports for patient ${lead.patientName} via this secure link:\n${window.location.origin}/hospital-submit-reports/${id}\n\nRegards,\nDOXEZ Clinical Team`)}` : '#'}
                                                            target="_blank" rel="noopener noreferrer"
                                                            onClick={(e) => { if (!shareForm.whatsapp) { e.preventDefault(); toast.error('Enter hospital WhatsApp number first'); } }}
                                                            className="p-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl flex items-center justify-center gap-2 shadow-sm transition-all text-center text-[9px] sm:text-xs"
                                                         >
                                                            <MessageSquare className="w-4 h-4" />
                                                            <span className="text-[9px] font-black uppercase tracking-widest">WhatsApp</span>
                                                         </a>
                                                         <button onClick={() => handleSendClinicalLink('email')} disabled={updating} className="p-4 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl flex items-center justify-center gap-2 shadow-sm transition-all text-center disabled:opacity-50">
                                                            <Mail className="w-4 h-4" />
                                                            <span className="text-[9px] font-black uppercase tracking-widest">Email Link</span>
                                                         </button>
                                                      </div>
                                                   </div>
                                                   <p className="text-[9px] font-bold text-amber-600 italic text-center uppercase tracking-widest">Hospital uploads via secure portal — docs auto-sync to this lead</p>
                                                </div>
                                             )}

                                             <div className="space-y-4">
                                                <div className="flex items-center gap-2">
                                                   <Upload className="w-4 h-4 text-[#143E78]" />
                                                   <p className="text-[10px] font-black text-[#143E78] uppercase tracking-widest">CC Manual Upload</p>
                                                   <span className="text-[8px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Upload on behalf of patient/hospital</span>
                                                </div>
                                                <div className={`grid grid-cols-1 ${lead.hasAyushmanCard ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
                                                   {/* MEDICAL REPORTS BOX */}
                                                   <div className={`p-5 rounded-2xl border-2 border-dashed transition-all space-y-3 ${lead.documents?.filter(d => d.type === 'medical_report').length > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                                                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] flex justify-between">
                                                         Medical Reports
                                                         {lead.documents?.filter(d => d.type === 'medical_report').length > 0 && <span className="text-emerald-600">Sync Active</span>}
                                                      </p>

                                                      {lead.documents?.filter(d => d.type === 'medical_report').length > 0 ? (
                                                         <div className="space-y-2">
                                                            <div className="flex -space-x-2 overflow-hidden">
                                                               {lead.documents.filter(d => d.type === 'medical_report').slice(0, 4).map((doc, i) => (
                                                                  <button key={i} onClick={() => handleFilePreview(doc)} className="inline-block h-8 w-8 rounded-full ring-2 ring-white bg-white overflow-hidden border border-emerald-100">
                                                                     {doc.url?.toLowerCase().endsWith('.pdf') ? <FileText className="w-full h-full p-1.5 text-red-500" /> : <img src={doc.url} className="h-full w-full object-cover" />}
                                                                  </button>
                                                               ))}
                                                               {lead.documents.filter(d => d.type === 'medical_report').length > 4 && (
                                                                  <div className="flex items-center justify-center h-8 w-8 rounded-full ring-2 ring-white bg-emerald-100 text-[10px] font-black text-emerald-600">
                                                                     +{lead.documents.filter(d => d.type === 'medical_report').length - 4}
                                                                  </div>
                                                               )}
                                                            </div>
                                                            {!isStageReadOnly && (
                                                               <button onClick={() => {
                                                                  const input = document.createElement('input');
                                                                  input.type = 'file'; input.multiple = true;
                                                                  input.onchange = (e) => onFileSelect(e, 'medical_report');
                                                                  input.click();
                                                               }} className="w-full py-2 bg-white border border-emerald-200 rounded-lg text-[9px] font-black text-emerald-600 hover:bg-emerald-50 transition-all uppercase flex items-center justify-center gap-2">
                                                                  <Plus className="w-3 h-3" /> Add More
                                                               </button>
                                                            )}
                                                         </div>
                                                      ) : (
                                                         !isStageReadOnly ? (
                                                            <button onClick={() => {
                                                               const input = document.createElement('input');
                                                               input.type = 'file'; input.multiple = true;
                                                               input.onchange = (e) => onFileSelect(e, 'medical_report');
                                                               input.click();
                                                            }} className="w-full py-3 bg-white border border-slate-200 rounded-xl text-[10px] font-black text-slate-400 hover:border-[#143E78] hover:text-[#143E78] transition-all uppercase flex items-center justify-center gap-2">
                                                               <Upload className="w-3.5 h-3.5" /> Reports
                                                            </button>
                                                         ) : (
                                                            <p className="text-[10px] font-bold text-slate-400 italic">No medical reports uploaded</p>
                                                         )
                                                      )}
                                                   </div>

                                                   {/* IDENTITY PROOF BOX */}
                                                   <div className={`p-5 rounded-2xl border-2 border-dashed transition-all space-y-3 ${lead.documents?.filter(d => d.type === 'id_proof').length > 0 ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                                                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] flex justify-between">
                                                         Identity Proof
                                                         {lead.documents?.filter(d => d.type === 'id_proof').length > 0 && <span className="text-blue-600">Secured</span>}
                                                      </p>

                                                      {lead.documents?.filter(d => d.type === 'id_proof').length > 0 ? (
                                                         <div className="space-y-2">
                                                            <div className="flex items-center gap-2 p-2 bg-white rounded-xl border border-blue-100">
                                                               {lead.documents.find(d => d.type === 'id_proof').url?.toLowerCase().endsWith('.pdf') ? (
                                                                  <FileText className="w-8 h-8 text-red-500" />
                                                               ) : (
                                                                  <img src={lead.documents.find(d => d.type === 'id_proof').url} className="w-10 h-8 object-cover rounded-md" />
                                                               )}
                                                               <span className="text-[9px] font-bold text-slate-500 truncate flex-1">{lead.documents.find(d => d.type === 'id_proof').name || 'ID Preview'}</span>
                                                               <button onClick={() => handleFilePreview(lead.documents.find(d => d.type === 'id_proof'))} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600">
                                                                  <ExternalLink className="w-3 h-3" />
                                                               </button>
                                                            </div>
                                                            {!isStageReadOnly && (
                                                               <button onClick={() => {
                                                                  const input = document.createElement('input');
                                                                  input.type = 'file'; input.multiple = true;
                                                                  input.onchange = (e) => onFileSelect(e, 'id_proof');
                                                                  input.click();
                                                               }} className="w-full py-2 bg-white border border-blue-200 rounded-lg text-[9px] font-black text-blue-600 hover:bg-blue-50 transition-all uppercase flex items-center justify-center gap-2">
                                                                  <Plus className="w-3 h-3" /> Update / Add
                                                               </button>
                                                            )}
                                                         </div>
                                                      ) : (
                                                         !isStageReadOnly ? (
                                                            <button onClick={() => {
                                                               const input = document.createElement('input');
                                                               input.type = 'file'; input.multiple = true;
                                                               input.onchange = (e) => onFileSelect(e, 'id_proof');
                                                               input.click();
                                                            }} className="w-full py-3 bg-white border border-blue-200 rounded-xl text-[10px] font-black text-blue-400 hover:border-blue-600 hover:text-blue-600 transition-all uppercase flex items-center justify-center gap-2">
                                                               <UserPlus className="w-3.5 h-3.5" /> ID Proof
                                                            </button>
                                                         ) : (
                                                            <p className="text-[10px] font-bold text-slate-400 italic">No ID proof attached</p>
                                                         )
                                                      )}
                                                   </div>

                                                   {/* AYUSHMAN CARD BOX */}
                                                   {lead.hasAyushmanCard && (
                                                      <div className={`p-5 rounded-2xl border-2 border-dashed transition-all space-y-3 ${lead.documents?.filter(d => d.type === 'ayushman_card').length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                                                         <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] flex justify-between">
                                                            Ayushman Card
                                                            {lead.documents?.filter(d => d.type === 'ayushman_card').length > 0 && <span className="text-amber-600">Available</span>}
                                                         </p>

                                                         {lead.documents?.filter(d => d.type === 'ayushman_card').length > 0 ? (
                                                            <div className="space-y-2">
                                                               <div className="flex items-center gap-2 p-2 bg-white rounded-xl border border-amber-100">
                                                                  {lead.documents.find(d => d.type === 'ayushman_card').url?.toLowerCase().endsWith('.pdf') ? (
                                                                     <FileText className="w-8 h-8 text-red-500" />
                                                                  ) : (
                                                                     <img src={lead.documents.find(d => d.type === 'ayushman_card').url} className="w-10 h-8 object-cover rounded-md" />
                                                                  )}
                                                                  <span className="text-[9px] font-bold text-slate-500 truncate flex-1">Ayushman Card</span>
                                                                  <button onClick={() => handleFilePreview(lead.documents.find(d => d.type === 'ayushman_card'))} className="p-1.5 hover:bg-amber-50 rounded-lg text-amber-600">
                                                                     <ExternalLink className="w-3 h-3" />
                                                                  </button>
                                                               </div>
                                                               {!isStageReadOnly && (
                                                                  <button onClick={() => {
                                                                     const input = document.createElement('input');
                                                                     input.type = 'file'; input.multiple = true;
                                                                     input.onchange = (e) => onFileSelect(e, 'ayushman_card');
                                                                     input.click();
                                                                  }} className="w-full py-2 bg-white border border-amber-200 rounded-lg text-[9px] font-black text-amber-600 hover:bg-amber-50 transition-all uppercase flex items-center justify-center gap-2">
                                                                     <Plus className="w-3 h-3" /> Replace
                                                                  </button>
                                                               )}
                                                            </div>
                                                         ) : (
                                                            !isStageReadOnly ? (
                                                               <button onClick={() => {
                                                                  const input = document.createElement('input');
                                                                  input.type = 'file'; input.multiple = true;
                                                                  input.onchange = (e) => onFileSelect(e, 'ayushman_card');
                                                                  input.click();
                                                               }} className="w-full py-3 bg-white border border-amber-200 rounded-xl text-[10px] font-black text-amber-500 hover:border-amber-600 hover:text-amber-600 transition-all uppercase flex items-center justify-center gap-2">
                                                                  <CreditCard className="w-3.5 h-3.5" /> Ayushman
                                                               </button>
                                                            ) : (
                                                               <p className="text-[10px] font-bold text-slate-400 italic">No Ayushman card attached</p>
                                                            )
                                                         )}
                                                      </div>
                                                   )}
                                                </div>
                                             </div>


                                             {/* 🏁 STAGED DOCUMENTS PREVIEW */}
                                             {selectedFiles.length > 0 && (
                                                <div className="bg-emerald-50 p-6 rounded-3xl border border-emerald-100 animate-fadeIn">
                                                   <div className="flex justify-between items-center mb-4">
                                                      <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-800">Files Staged for Verification ({selectedFiles.length}/10)</h4>
                                                      <button onClick={() => setSelectedFiles([])} className="text-emerald-800 font-bold text-[9px] uppercase">Clear All</button>
                                                   </div>
                                                   <div className="space-y-2">
                                                      {selectedFiles.map((f, i) => (
                                                         <div key={i} className="bg-white p-3 rounded-xl border border-emerald-200 flex justify-between items-center">
                                                            <div className="flex items-center gap-2">
                                                               <FileText className="w-4 h-4 text-emerald-600" />
                                                               <div>
                                                                  <span className="text-[11px] font-bold text-slate-700 truncate max-w-[150px] block">{f.name}</span>
                                                                  <span className="text-[8px] font-bold text-slate-400 uppercase">{f.customTag?.replace(/_/g, ' ')}</span>
                                                               </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                               <button onClick={() => handleFilePreview(f)} className="p-1.5 hover:bg-emerald-100 rounded-lg text-emerald-600 transition-all">
                                                                  <Image className="w-3.5 h-3.5" />
                                                               </button>
                                                               <button onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))} className="p-1.5 hover:bg-red-100 rounded-lg text-red-400 transition-all">
                                                                  <X className="w-3.5 h-3.5" />
                                                               </button>
                                                               <span className="text-[8px] font-black bg-emerald-100 px-2 py-0.5 rounded uppercase">CC Sync</span>
                                                            </div>
                                                         </div>
                                                      ))}
                                                   </div>
                                                </div>
                                             )}

                                             {!isStageReadOnly && (
                                                <div className="flex gap-4">
                                                   <button onClick={() => setIntakeStep(4)} className="flex-1 py-4 bg-slate-100 text-slate-500 font-black rounded-2xl uppercase tracking-widest text-center text-[9px] sm:text-xs">Back</button>
                                                   <button onClick={() => setIntakeStep(6)} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-xl uppercase tracking-widest transition-all hover:scale-[1.02] text-center text-[9px] sm:text-xs">Proceed to Verification</button>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    )}

                                    {/* STEP 6: FINAL COMPLIANCE VERIFICATION */}
                                    {intakeStep === 6 && (
                                       <div className="space-y-6 animate-fadeIn">
                                          <div className="bg-white p-8 rounded-[2.5rem] border-2 border-emerald-600/10 shadow-xl space-y-8">
                                             <div className="text-center space-y-3">
                                                <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto">
                                                   <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                                </div>
                                                <h3 className="text-lg font-black text-[#143E78] uppercase tracking-widest">Compliance Verification</h3>
                                                <p className="text-[11px] font-bold text-slate-400 uppercase">Review all captured data before advancing the deal</p>
                                             </div>

                                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className={`p-5 rounded-2xl border flex items-center justify-between ${(lead.documents?.filter(d => d.type === 'medical_report').length > 0 || selectedFiles.some(f => f.customTag === 'medical_report') || lead.clinicalIntake?.insurance?.hasAyushmanCard === 'Yes') ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                                                   <div className="space-y-1">
                                                      <p className="text-[9px] font-black uppercase text-slate-400">Clinical Checks</p>
                                                      <p className="text-xs font-black text-slate-700">
                                                         {(lead.documents?.filter(d => d.type === 'medical_report').length || 0) + selectedFiles.filter(f => f.customTag === 'medical_report').length} Reports Collected
                                                         {lead.clinicalIntake?.insurance?.hasAyushmanCard === 'Yes' && " (+ Ayushman)"}
                                                      </p>
                                                   </div>
                                                   {(lead.documents?.filter(d => d.type === 'medical_report').length > 0 || selectedFiles.some(f => f.customTag === 'medical_report') || lead.clinicalIntake?.insurance?.hasAyushmanCard === 'Yes') ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <AlertTriangle className="w-5 h-5 text-red-500" />}
                                                </div>

                                                <div className={`p-5 rounded-2xl border flex items-center justify-between ${(lead.documents?.filter(d => d.type === 'id_proof').length > 0 || selectedFiles.some(f => f.customTag === 'id_proof')) ? 'bg-blue-50 border-blue-100' : 'bg-amber-50 border-amber-100'}`}>
                                                   <div className="space-y-1">
                                                      <p className="text-[9px] font-black uppercase text-slate-400">Identity Check</p>
                                                      <p className="text-xs font-black text-slate-700">
                                                         {(lead.documents?.filter(d => d.type === 'id_proof').length || 0) + selectedFiles.filter(f => f.customTag === 'id_proof').length} ID Proofs Found
                                                      </p>
                                                   </div>
                                                   {(lead.documents?.filter(d => d.type === 'id_proof').length > 0 || selectedFiles.some(f => f.customTag === 'id_proof')) ? <CheckCircle2 className="w-5 h-5 text-blue-500" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                                                </div>
                                             </div>


                                             <div className="p-6 bg-slate-50 border border-slate-100 rounded-3xl space-y-4">
                                                <div className="flex justify-between items-center px-1">
                                                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Review All Documents</p>
                                                   {!isStageReadOnly && (
                                                      <button onClick={() => setIntakeStep(5)} className="text-[10px] font-black text-emerald-600 uppercase hover:underline">Add More</button>
                                                   )}
                                                </div>
                                                <div className="grid grid-cols-1 gap-2 max-h-[160px] overflow-y-auto pr-1">
                                                   {/* Existing Docs */}
                                                   {lead.documents?.map((doc, idx) => (
                                                      <div key={`old-${idx}`} className="bg-white p-3 rounded-xl border border-slate-100 flex justify-between items-center bg-emerald-50/30 p-4 rounded-2xl border border-emerald-100/50 hover:border-[#143E78]/30 transition-all">
                                                         <div className="flex items-center gap-3">
                                                            <div className={`p-2 rounded-xl ${doc.type === 'id_proof' ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                                                               {doc.type === 'id_proof' ? <UserPlus className="w-4 h-4 text-blue-500" /> : <FileText className="w-4 h-4 text-emerald-500" />}
                                                            </div>
                                                            <div>
                                                               <span className="text-[11px] font-black text-slate-700 truncate max-w-[140px] block">{doc.name || `Document ${idx + 1}`}</span>
                                                               <div className="flex gap-2 mt-0.5">
                                                                  <span className="text-[8px] font-black text-slate-400 border border-slate-100 px-1.5 py-0.5 rounded uppercase">{doc.type?.replace(/_/g, ' ')}</span>
                                                                  <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase ${doc.source === 'Patient' ? 'bg-indigo-50 text-indigo-500' : 'bg-amber-50 text-amber-600'}`}>
                                                                     Source: {doc.source || 'Legacy'}
                                                                  </span>
                                                               </div>
                                                            </div>
                                                         </div>
                                                         <button onClick={() => handleFilePreview(doc)} className="p-2 hover:bg-[#143E78] hover:text-white text-[#143E78] rounded-xl transition-all shadow-sm">
                                                            <Image className="w-4 h-4" />
                                                         </button>
                                                      </div>
                                                   ))}
                                                   {/* Staged Docs */}
                                                   {selectedFiles.map((file, idx) => (
                                                      <div key={`new-${idx}`} className="bg-emerald-50/30 p-3 rounded-xl border border-dashed border-emerald-200 flex justify-between items-center">
                                                         <div className="flex items-center gap-3">
                                                            <div className="p-2 bg-white rounded-xl shadow-sm border border-emerald-100">
                                                               {file.customTag === 'id_proof' ? <UserPlus className="w-4 h-4 text-blue-500" /> : <FileText className="w-4 h-4 text-emerald-500" />}
                                                            </div>
                                                            <div>
                                                               <span className="text-[11px] font-black text-slate-800 truncate max-w-[140px] block">{file.name}</span>
                                                               <div className="flex gap-2 mt-0.5">
                                                                  <span className="text-[8px] font-black text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded uppercase">Staged (Unsaved)</span>
                                                                  <span className="text-[8px] font-black text-slate-400 bg-white px-1.5 py-0.5 rounded border border-slate-100 uppercase">{file.customTag?.replace(/_/g, ' ')}</span>
                                                               </div>
                                                            </div>
                                                         </div>
                                                         <div className="flex items-center gap-2">
                                                            <button onClick={() => handleFilePreview(file)} className="p-2 hover:bg-emerald-600 hover:text-white text-emerald-600 rounded-xl transition-all bg-white border border-emerald-100 shadow-sm">
                                                               <Image className="w-4 h-4" />
                                                            </button>
                                                         </div>
                                                      </div>
                                                   ))}
                                                   {lead.documents?.length === 0 && selectedFiles.length === 0 && (
                                                      <div className="py-4 text-center text-[9px] sm:text-xs">
                                                         <AlertCircle className="w-8 h-8 text-slate-200 mx-auto" />
                                                         <p className="text-[10px] font-bold text-slate-400 uppercase mt-2">No documents found for review</p>
                                                      </div>
                                                   )}
                                                </div>
                                             </div>

                                             <div className="p-6 bg-slate-50 border border-slate-100 rounded-3xl space-y-4">
                                                <div className="flex justify-between items-start">
                                                   <div>
                                                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Eligibility Mode</p>
                                                      <div className="flex flex-wrap items-center gap-2 mt-2">
                                                         <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase ${lead.hasAyushmanCard ? 'bg-indigo-100 text-indigo-600' : lead.paymentType?.includes('Insurance') ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                                            {lead.hasAyushmanCard ? 'Ayushman Card' : lead.paymentType?.includes('Insurance') ? 'Insurance' : 'Cash Payment'}
                                                         </span>
                                                         {lead.hasAyushmanCard && (
                                                            <button
                                                               onClick={() => {
                                                                  const cardDoc = lead.documents?.find(d => d.type === 'ayushman_card') || selectedFiles.find(f => f.customTag === 'ayushman_card');
                                                                  if (cardDoc) handleFilePreview(cardDoc);
                                                                  else toast.error('Ayushman card document not found in files');
                                                               }}
                                                               className="px-3 py-1.5 bg-[#143E78] hover:bg-blue-900 text-white rounded-lg text-[9px] font-black uppercase tracking-widest transition-all"
                                                            >
                                                               Review Card
                                                            </button>
                                                         )}
                                                      </div>
                                                   </div>
                                                   {!isStageReadOnly && (
                                                      <button onClick={() => setIntakeStep(1)} className="text-xs font-black text-[#143E78] underline hover:text-blue-800 shrink-0">Edit</button>
                                                   )}
                                                </div>
                                             </div>

                                             <div className="p-6 bg-slate-50 border border-slate-100 rounded-3xl space-y-4">
                                                <div className="flex justify-between items-start">
                                                   <div className="flex-1 pr-4">
                                                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Patient History Summary</p>
                                                      <div className="mt-2 space-y-3">
                                                         {intakeForm.medicalDetails.history.currentIllness && (
                                                            <div>
                                                               <p className="text-[9px] font-black text-slate-400 uppercase">Current Illness</p>
                                                               <p className="text-xs font-bold text-slate-700">{intakeForm.medicalDetails.history.currentIllness}</p>
                                                            </div>
                                                         )}
                                                         {intakeForm.medicalDetails.history.pastDiseases && (
                                                            <div>
                                                               <p className="text-[9px] font-black text-slate-400 uppercase">Past Illness</p>
                                                               <p className="text-xs font-bold text-slate-700">{intakeForm.medicalDetails.history.pastDiseases}</p>
                                                            </div>
                                                         )}
                                                         {intakeForm.medicalDetails.history.previousSurgeries && (
                                                            <div>
                                                               <p className="text-[9px] font-black text-slate-400 uppercase">Surgical History</p>
                                                               <p className="text-xs font-bold text-slate-700">{intakeForm.medicalDetails.history.previousSurgeries}</p>
                                                            </div>
                                                         )}
                                                         {!intakeForm.medicalDetails.history.currentIllness && !intakeForm.medicalDetails.history.pastDiseases && !intakeForm.medicalDetails.history.previousSurgeries && (
                                                            <p className="text-xs font-bold text-slate-400 italic">No history captured</p>
                                                         )}
                                                      </div>
                                                   </div>
                                                   {!isStageReadOnly && (
                                                      <button onClick={() => setIntakeStep(3)} className="text-xs font-black text-[#143E78] underline hover:text-blue-800 shrink-0">Edit</button>
                                                   )}
                                                </div>
                                             </div>

                                             <div className="flex items-center gap-4 mt-6">
                                                {!isStageReadOnly && (
                                                   <button
                                                      onClick={() => setIntakeStep(5)}
                                                      className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-500 font-black rounded-2xl uppercase tracking-widest text-center text-[9px] sm:text-xs transition-all"
                                                   >
                                                      Back
                                                   </button>
                                                )}

                                                {!isStageReadOnly ? (
                                                   updating ? (
                                                      <div className="flex-[3] py-4 bg-[#143E78] rounded-2xl relative overflow-hidden flex items-center justify-center gap-3">
                                                         {/* Shimmer sweep */}
                                                         <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                                                         {/* Skeleton blobs */}
                                                         <div className="w-4 h-4 rounded-full bg-blue-500/60 animate-pulse" />
                                                         <div className="h-3 w-48 rounded-full bg-blue-500/60 animate-pulse" />
                                                      </div>
                                                   ) : (
                                                      <button
                                                         onClick={handleClinicalUpload}
                                                         disabled={updating}
                                                         className="flex-[3] py-4 bg-[#143E78] hover:bg-blue-900 text-white font-black rounded-2xl shadow-[0_20px_50px_rgba(20,62,120,0.3)] uppercase text-[10px] tracking-[0.15em] transition-all hover:scale-[1.02] text-center"
                                                      >
                                                         Verify & Proceed to Team Assignment
                                                      </button>
                                                   )
                                                ) : (
                                                   <div className="flex-[3] py-3.5 bg-emerald-50 text-emerald-700 font-black rounded-2xl border border-emerald-200 uppercase text-xs tracking-widest text-center flex items-center justify-center gap-2">
                                                      <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Intake Data Verified & Clear
                                                   </div>
                                                )}
                                             </div>
                                          </div>
                                       </div>
                                    )}
                                    <input type="file" hidden ref={fileInputRef} onChange={onFileSelect} multiple />
                                 </div>
                              )}

                              {/* Stage 3 -> Stage 4.5 Transition */}
                              {displayStatus === 'doc_verification' && (
                                 <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100 space-y-4">
                                    <div className="flex justify-between items-center bg-white/40 p-4 rounded-xl border border-white">
                                       <div>
                                          <p className="text-[10px] font-black text-indigo-800 uppercase tracking-widest">Stage 4: Doctor Review & Approval</p>
                                          <p className="text-[11px] font-bold text-slate-500 mt-0.5">Specialist doctor review & clearance status</p>
                                       </div>
                                       <div className="p-2 bg-indigo-100 rounded-lg">
                                          <Stethoscope className="w-5 h-5 text-indigo-600" />
                                       </div>
                                    </div>

                                    <div className="p-4 bg-white rounded-xl border space-y-4 shadow-sm">
                                       <SearchableSelect
                                          label="Reports Review by Doctor"
                                          options={inHouseDoctors}
                                          value={clinicalForm.reviewingDoctorId || lead.clinicalData?.reviewingDoctorId || ''}
                                          onChange={(v) => setClinicalForm({ ...clinicalForm, reviewingDoctorId: v })}
                                          placeholder="Select Doctor for Review"
                                          disabled={isStageReadOnly}
                                       />

                                       {lead.clinicalData?.reviewToken ? (
                                          <div className="p-4 bg-slate-50 rounded-xl border space-y-3">
                                             <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                                <span className="text-[10px] font-black uppercase text-slate-400">Review Status</span>
                                                {lead.clinicalData?.doctorReviewDone ? (
                                                   <div className="flex items-center gap-2">
                                                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${lead.clinicalData?.reviewDecision === 'Surgery Possible' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                         {lead.clinicalData?.reviewDecision === 'Surgery Possible' ? 'Approved by Doctor' : 'Not Possible'}
                                                      </span>
                                                      {lead.clinicalData?.reviewDecision === 'Surgery Possible' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                                                   </div>
                                                ) : (
                                                   <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-black uppercase anime-pulse">
                                                      Awaiting Clinical Response
                                                   </span>
                                                )}
                                             </div>
                                             <div className="space-y-1.5">
                                                <p className="text-[10px] font-bold text-slate-500">Link Generated: {new Date(lead.clinicalData.reviewLinkSentAt).toLocaleString()}</p>
                                                {lead.clinicalData.reviewOpenedAt && <p className="text-[10px] font-black text-indigo-600 italic">Doctor viewed link: {new Date(lead.clinicalData.reviewOpenedAt).toLocaleString()}</p>}
                                                {lead.clinicalData.reviewRemarks && (
                                                   <div className="mt-2 text-[11px] bg-white p-3 rounded-xl border-l-4 border-indigo-500 shadow-sm text-slate-700 font-medium">
                                                      <span className="text-[9px] font-black text-indigo-400 uppercase block mb-1">Doctor's Clinical Note</span>
                                                      "{lead.clinicalData.reviewRemarks}"
                                                   </div>
                                                )}
                                             </div>
                                             {!lead.clinicalData?.doctorReviewDone && !isStageReadOnly && (
                                                <div className="flex flex-col sm:flex-row gap-2">
                                                   <button
                                                      onClick={handleSendDoctorReviewLink}
                                                      disabled={updating}
                                                      className="flex-1 py-2 bg-white border border-indigo-200 text-indigo-600 rounded-lg text-[9px] font-black uppercase hover:bg-indigo-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
                                                   >
                                                      <RefreshCcw className="w-3 h-3" /> Resend Review Link
                                                   </button>
                                                   {isPastSurgeryCase && (
                                                      <button
                                                         type="button"
                                                         onClick={handleSendDoctorReviewLink}
                                                         disabled={updating}
                                                         className="flex-1 py-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg text-[9px] font-black uppercase transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                                                      >
                                                         <Send className="w-3 h-3 text-amber-400" /> Send manual email
                                                      </button>
                                                   )}
                                                </div>
                                             )}
                                          </div>
                                       ) : (
                                          !isStageReadOnly && (
                                             <div className="flex flex-col sm:flex-row gap-3">
                                                <button
                                                   onClick={handleSendDoctorReviewLink}
                                                   disabled={!clinicalForm.reviewingDoctorId || updating}
                                                   className="flex-1 py-4 bg-indigo-600 text-white font-black rounded-xl text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 disabled:opacity-30 transition-all flex items-center justify-center gap-2 cursor-pointer"
                                                >
                                                   <Send className="w-4 h-4" /> Dispatch Review Link to Doctor
                                                </button>
                                                {isPastSurgeryCase && (
                                                   <button
                                                      type="button"
                                                      onClick={handleSendDoctorReviewLink}
                                                      disabled={!clinicalForm.reviewingDoctorId || updating}
                                                      className="px-6 py-4 bg-indigo-800 hover:bg-indigo-900 text-white font-black rounded-xl text-[10px] uppercase tracking-widest shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-30"
                                                   >
                                                      <Send className="w-4 h-4 text-amber-400" /> Send manual email
                                                   </button>
                                                )}
                                             </div>
                                          )
                                       )}

                                       <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border text-[11px] font-black uppercase text-slate-500 cursor-pointer hover:bg-slate-100 transition-all">
                                          <input
                                             type="checkbox"
                                             disabled={isStageReadOnly}
                                             checked={clinicalForm.doctorReviewDone || lead.clinicalData?.doctorReviewDone}
                                             onChange={(e) => setClinicalForm({ ...clinicalForm, doctorReviewDone: e.target.checked })}
                                             className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                          />
                                          Manual Clearance Override
                                       </label>
                                    </div>

                                    {!isStageReadOnly ? (
                                       <button
                                          onClick={() => {
                                             const isReady = clinicalForm.doctorReviewDone || (lead.clinicalData?.doctorReviewDone && lead.clinicalData?.reviewDecision === 'Surgery Possible');
                                             if (!isReady) return toast.error('Surgical Clearance Blocked: Awaiting positive clinical review.');

                                             const p = {
                                                ...clinicalForm,
                                                doctorReviewDone: true,
                                                isFit: true
                                             };
                                             handleStageTransition("surgery_ready", p);
                                          }}
                                          disabled={!canUpdateStage(displayStatus) || updating || (!clinicalForm.doctorReviewDone && (!lead.clinicalData?.doctorReviewDone || lead.clinicalData?.reviewDecision !== 'Surgery Possible'))}
                                          className="w-full bg-emerald-600 text-white font-black py-5 rounded-2xl shadow-xl shadow-emerald-100 uppercase text-xs tracking-[0.2em] disabled:opacity-50 hover:bg-emerald-700 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3"
                                       >
                                          <CheckCircle2 className="w-5 h-5" /> Grant Surgical Clearance
                                       </button>
                                    ) : (
                                       <div className="w-full bg-emerald-50 text-emerald-700 font-black py-4 rounded-2xl border border-emerald-200 uppercase text-xs tracking-widest text-center flex items-center justify-center gap-2">
                                          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Surgical Clearance Granted & Verified
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* Stage 4.5 & 5: Team Assignment & Surgery Preparation */}
                              {(displayStatus === 'clinical_approval' || displayStatus === 'surgery_ready') && (
                                 <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-white/40 p-4 rounded-xl border border-white gap-4">
                                       <div>
                                          <p className="text-[10px] font-black text-blue-800 uppercase tracking-widest">Stage 5: Team Assignment</p>
                                          <p className="text-[11px] font-bold text-slate-500 mt-0.5">Match surgeon and anesthesiologist to facility</p>
                                       </div>
                                       {!isStageReadOnly && (
                                          <div className="flex flex-wrap items-center gap-2">
                                             <button type="button" onClick={() => setShowDocListModal(true)} className="flex-1 sm:flex-none px-4 py-2.5 bg-white text-blue-600 border border-blue-200 text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-50 transition-all flex items-center justify-center gap-2 shadow-sm">
                                                <UserPlus className="w-4 h-4" /> Doctor Directory
                                             </button>
                                             <button type="button" onClick={() => setShowAvailModal(true)} className="flex-1 sm:flex-none px-4 py-2.5 bg-white text-emerald-600 border border-emerald-200 text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-emerald-50 transition-all flex items-center justify-center gap-2 shadow-sm">
                                                <Calendar className="w-4 h-4" /> Availability
                                             </button>
                                             <button type="button" onClick={() => setShowHospListModal(true)} className="flex-1 sm:flex-none px-4 py-2.5 bg-white text-amber-600 border border-amber-200 text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-amber-50 transition-all flex items-center justify-center gap-2 shadow-sm">
                                                <Building2 className="w-4 h-4" /> Hospital Directory
                                             </button>
                                             {isPastSurgeryCase && (
                                                <button type="button" onClick={handleSendManualNotification} disabled={sendingManualNotif} className="flex-1 sm:flex-none px-4 py-2.5 bg-indigo-700 text-white font-black uppercase text-[9px] tracking-widest rounded-xl hover:bg-indigo-800 transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 cursor-pointer">
                                                   {sendingManualNotif ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white" /> : <Send className="w-3.5 h-3.5 text-amber-400" />}
                                                   Notify Doctors & Hospital
                                                </button>
                                             )}
                                          </div>
                                       )}
                                    </div>

                                    <div className="p-4 bg-white rounded-xl border space-y-6 shadow-sm">
                                       {/* MAIN SURGEON */}
                                       <div>
                                          <div className="flex items-center justify-between mb-2">
                                             <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Main Surgeon</label>
                                             {!isStageReadOnly && (
                                                <div className="inline-flex p-1 bg-slate-100 rounded-xl">
                                                   <button
                                                      type="button"
                                                      onClick={() => setAssignmentForm(prev => ({ ...prev, surgeonType: 'partner' }))}
                                                      className={`px-3 py-1 text-[10px] font-black uppercase rounded-lg transition-all ${(assignmentForm.surgeonType || 'partner') === 'partner' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                                         }`}
                                                   >
                                                      Partner Doctor
                                                   </button>
                                                   <button
                                                      type="button"
                                                      onClick={() => setAssignmentForm(prev => ({ ...prev, surgeonType: 'other' }))}
                                                      className={`px-3 py-1 text-[10px] font-black uppercase rounded-lg transition-all ${assignmentForm.surgeonType === 'other' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                                         }`}
                                                   >
                                                      Other Doctor
                                                   </button>
                                                </div>
                                             )}
                                          </div>

                                          {(assignmentForm.surgeonType || 'partner') === 'partner' ? (
                                             <SearchableSelect
                                                options={surgeonDoctors}
                                                value={assignmentForm.assignedSurgeon}
                                                onChange={(v) => setAssignmentForm({ ...assignmentForm, assignedSurgeon: v })}
                                                placeholder="Select Partner Surgeon"
                                                disabled={isStageReadOnly}
                                             />
                                          ) : (
                                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200 animate-fadeIn">
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Surgeon Doctor Name *</label>
                                                   <input
                                                      type="text"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. Dr. Rajesh Kumar"
                                                      value={assignmentForm.assignedSurgeonOtherName || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherName: e.target.value })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                   />
                                                </div>
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">OCMR / Reg. Number</label>
                                                   <input
                                                      type="text"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. OCMR/12345/2020"
                                                      value={assignmentForm.assignedSurgeonOtherOCMR || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherOCMR: e.target.value.toUpperCase() })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none uppercase disabled:bg-slate-100"
                                                   />
                                                </div>
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Phone Number (Optional)</label>
                                                   <input
                                                      type="text"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. 9876543210"
                                                      value={assignmentForm.assignedSurgeonOtherPhone || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherPhone: e.target.value })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                   />
                                                </div>
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Email Address (Optional)</label>
                                                   <input
                                                      type="email"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. doctor@gmail.com"
                                                      value={assignmentForm.assignedSurgeonOtherEmail || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherEmail: e.target.value })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                   />
                                                </div>

                                                {/* Collapsible KYC & Bank Details */}
                                                <div className="col-span-1 sm:col-span-2 pt-1 border-t border-slate-200/60 mt-1">
                                                   <button
                                                      type="button"
                                                      onClick={() => setShowSurgeonKyc(prev => !prev)}
                                                      className="flex items-center gap-1.5 text-[10px] font-black text-indigo-600 hover:text-indigo-700 uppercase tracking-wider transition-colors cursor-pointer"
                                                   >
                                                      {showSurgeonKyc ? '− Hide KYC & Bank Details' : '+ Add KYC & Bank Details (Aadhaar, PAN, Bank Account)'}
                                                   </button>

                                                   {showSurgeonKyc && (
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2.5 pt-3 border-t border-indigo-100/80 bg-indigo-50/40 p-3 rounded-xl border border-indigo-100 animate-fadeIn">
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Aadhaar Number</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. 1234 5678 9012"
                                                               value={assignmentForm.assignedSurgeonOtherAadhar || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherAadhar: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">PAN Number</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. ABCDE1234F"
                                                               value={assignmentForm.assignedSurgeonOtherPan || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherPan: e.target.value.toUpperCase() })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none uppercase disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Account Holder Name</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. Dr. Rajesh Kumar"
                                                               value={assignmentForm.assignedSurgeonOtherAccountHolder || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherAccountHolder: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Bank Name</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. HDFC Bank"
                                                               value={assignmentForm.assignedSurgeonOtherBankName || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherBankName: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Account Number</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. 5010023456789"
                                                               value={assignmentForm.assignedSurgeonOtherAccountNo || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherAccountNo: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">IFSC Code</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. HDFC0001234"
                                                               value={assignmentForm.assignedSurgeonOtherIfsc || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedSurgeonOtherIfsc: e.target.value.toUpperCase() })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none uppercase disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                      </div>
                                                   )}
                                                </div>
                                             </div>
                                          )}
                                       </div>

                                       {/* ANESTHESIOLOGIST */}
                                       <div>
                                          <div className="flex items-center justify-between mb-2">
                                             <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Anesthesiologist</label>
                                             {!isStageReadOnly && (
                                                <div className="inline-flex p-1 bg-slate-100 rounded-xl">
                                                   <button
                                                      type="button"
                                                      onClick={() => setAssignmentForm(prev => ({ ...prev, anesthesiologistType: 'partner' }))}
                                                      className={`px-3 py-1 text-[10px] font-black uppercase rounded-lg transition-all ${(assignmentForm.anesthesiologistType || 'partner') === 'partner' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                                         }`}
                                                   >
                                                      Partner Doctor
                                                   </button>
                                                   <button
                                                      type="button"
                                                      onClick={() => setAssignmentForm(prev => ({ ...prev, anesthesiologistType: 'other' }))}
                                                      className={`px-3 py-1 text-[10px] font-black uppercase rounded-lg transition-all ${assignmentForm.anesthesiologistType === 'other' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                                         }`}
                                                   >
                                                      Other Doctor
                                                   </button>
                                                </div>
                                             )}
                                          </div>

                                          {(assignmentForm.anesthesiologistType || 'partner') === 'partner' ? (
                                             <SearchableSelect
                                                options={anesthesiologistDoctors}
                                                value={assignmentForm.assignedanesthesiologist}
                                                onChange={(v) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologist: v })}
                                                placeholder="Select Partner Anesthesiologist"
                                                disabled={isStageReadOnly}
                                             />
                                          ) : (
                                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200 animate-fadeIn">
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Anesthesiologist Name</label>
                                                   <input
                                                      type="text"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. Dr. Anita Sharma"
                                                      value={assignmentForm.assignedanesthesiologistOtherName || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherName: e.target.value })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                   />
                                                </div>
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">OCMR / Reg. Number</label>
                                                   <input
                                                      type="text"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. OCMR/98765/2021"
                                                      value={assignmentForm.assignedanesthesiologistOtherOCMR || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherOCMR: e.target.value.toUpperCase() })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                   />
                                                </div>
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Phone Number (Optional)</label>
                                                   <input
                                                      type="text"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. 9876543210"
                                                      value={assignmentForm.assignedanesthesiologistOtherPhone || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherPhone: e.target.value })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                   />
                                                </div>
                                                <div>
                                                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Email Address (Optional)</label>
                                                   <input
                                                      type="email"
                                                      disabled={isStageReadOnly}
                                                      placeholder="e.g. doctor@gmail.com"
                                                      value={assignmentForm.assignedanesthesiologistOtherEmail || ''}
                                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherEmail: e.target.value })}
                                                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                   />
                                                </div>

                                                {/* Collapsible KYC & Bank Details */}
                                                <div className="col-span-1 sm:col-span-2 pt-1 border-t border-slate-200/60 mt-1">
                                                   <button
                                                      type="button"
                                                      onClick={() => setShowAnesKyc(prev => !prev)}
                                                      className="flex items-center gap-1.5 text-[10px] font-black text-indigo-600 hover:text-indigo-700 uppercase tracking-wider transition-colors cursor-pointer"
                                                   >
                                                      {showAnesKyc ? '− Hide KYC & Bank Details' : '+ Add KYC & Bank Details (Aadhaar, PAN, Bank Account)'}
                                                   </button>

                                                   {showAnesKyc && (
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2.5 pt-3 border-t border-indigo-100/80 bg-indigo-50/40 p-3 rounded-xl border border-indigo-100 animate-fadeIn">
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Aadhaar Number</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. 1234 5678 9012"
                                                               value={assignmentForm.assignedanesthesiologistOtherAadhar || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherAadhar: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">PAN Number</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. ABCDE1234F"
                                                               value={assignmentForm.assignedanesthesiologistOtherPan || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherPan: e.target.value.toUpperCase() })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none uppercase disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Account Holder Name</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. Dr. Anita Sharma"
                                                               value={assignmentForm.assignedanesthesiologistOtherAccountHolder || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherAccountHolder: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Bank Name</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. ICICI Bank"
                                                               value={assignmentForm.assignedanesthesiologistOtherBankName || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherBankName: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">Account Number</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. 000401234567"
                                                               value={assignmentForm.assignedanesthesiologistOtherAccountNo || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherAccountNo: e.target.value })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                         <div>
                                                            <label className="text-[9px] font-black text-indigo-900 uppercase tracking-widest block mb-1">IFSC Code</label>
                                                            <input
                                                               type="text"
                                                               disabled={isStageReadOnly}
                                                               placeholder="e.g. ICIC0000004"
                                                               value={assignmentForm.assignedanesthesiologistOtherIfsc || ''}
                                                               onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedanesthesiologistOtherIfsc: e.target.value.toUpperCase() })}
                                                               className="w-full px-3.5 py-2 bg-white border border-indigo-200/70 rounded-xl text-xs font-bold focus:border-indigo-600 outline-none uppercase disabled:bg-slate-100"
                                                            />
                                                         </div>
                                                      </div>
                                                   )}
                                                </div>
                                             </div>
                                          )}
                                       </div>

                                       {/* HOSPITAL */}
                                       <SearchableSelect
                                          label="Surgical Hospital"
                                          type="hospital"
                                          options={hospitals}
                                          value={assignmentForm.assignedHospital}
                                          onChange={(v) => setAssignmentForm({ ...assignmentForm, assignedHospital: v })}
                                          placeholder="Select Facility"
                                          disabled={isStageReadOnly}
                                       />
                                    </div>

                                    {!isStageReadOnly ? (
                                       <button
                                          onClick={async () => {
                                             if (assignmentForm.surgeonType === 'other') {
                                                if (!assignmentForm.assignedSurgeonOtherName || !assignmentForm.assignedSurgeonOtherName.trim()) {
                                                   return toast.error('Incomplete Team: Other Surgeon Name is required.');
                                                }
                                             } else {
                                                if (!assignmentForm.assignedSurgeon) {
                                                   return toast.error('Incomplete Team: Main Surgeon is mandatory.');
                                                }
                                             }
                                             setIsLockingTeam(true);

                                             const oldHospitalId = lead.assignedHospital?._id || lead.assignedHospital || lead.leadProviderHospitalId?._id || lead.leadProviderHospitalId;
                                             const isHospitalChanged = assignmentForm.assignedHospital !== oldHospitalId;

                                             try {
                                                await api.patch(`/leads/${id}`, assignmentForm);
                                             } catch (pErr) {
                                                console.error("Failed to patch lead team fields:", pErr);
                                             }
                                             await handleStageTransition("hospital_assignment", assignmentForm);

                                             if (isHospitalChanged) {
                                                const newHospital = hospitals.find(h => h._id === assignmentForm.assignedHospital);
                                                if (newHospital) {
                                                   try {
                                                      await api.post(`/leads/${id}/notify-hospital-change`, {
                                                         hospitalName: newHospital.hospitalName,
                                                         location: newHospital.address?.city || 'N/A'
                                                      });
                                                   } catch (e) { console.error("Notification failed", e); }
                                                }
                                             }
                                             setIsLockingTeam(false);
                                          }}
                                          disabled={!canUpdateStage(displayStatus) || updating || isLockingTeam}
                                          className="w-full bg-[#143E78] text-white font-black py-4 rounded-2xl shadow-xl uppercase text-xs tracking-[0.2em] disabled:opacity-50 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
                                       >
                                          {isLockingTeam ? (
                                             <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Locking Assignment...
                                             </>
                                          ) : (
                                             'Lock Assignment & Finalize Team'
                                          )}
                                       </button>
                                    ) : (
                                       <div className="w-full bg-emerald-50 text-emerald-700 font-black py-4 rounded-2xl border border-emerald-200 uppercase text-xs tracking-widest text-center flex items-center justify-center gap-2">
                                          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Surgical Team Locked & Finalized
                                       </div>
                                    )}

                                    {isPastSurgeryCase && !isStageReadOnly && (
                                       <button
                                          type="button"
                                          onClick={handleSendManualNotification}
                                          disabled={sendingManualNotif}
                                          className="w-full py-3.5 bg-amber-500/10 border-2 border-amber-300 text-amber-900 font-black rounded-2xl text-[10px] sm:text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all hover:bg-amber-500/20 cursor-pointer disabled:opacity-50"
                                       >
                                          {sendingManualNotif ? <Loader2 className="w-4 h-4 animate-spin text-amber-800" /> : <Send className="w-4 h-4 text-amber-700" />}
                                          Send Notification To Assigned Doctors & Facility
                                       </button>
                                    )}
                                 </div>
                              )}

                              {/* Stage 5 */}
                              {displayStatus === 'hospital_assignment' && (
                                 <div className="bg-violet-50 p-6 rounded-2xl border border-violet-100 space-y-4">
                                    <h3 className="font-black text-violet-800 uppercase text-xs tracking-widest">Stage 6: OT Scheduling</h3>

                                    {/* 📜 Previously Scheduled Slot Information Reference Card */}
                                    {lead.schedulingData?.surgeryDate && (
                                       <div className="bg-white p-4 rounded-xl border border-violet-200 shadow-sm text-left space-y-2">
                                          <p className="text-[10px] font-black text-violet-600 uppercase tracking-widest flex items-center gap-1.5">
                                             <Clock className="w-3.5 h-3.5 text-violet-500" />
                                             Previously Scheduled Slot Information
                                          </p>
                                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-bold text-slate-700">
                                             <div>
                                                <span className="text-[9px] font-black text-slate-400 uppercase block">Previous Date</span>
                                                {new Date(lead.schedulingData.surgeryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                             </div>
                                             <div>
                                                <span className="text-[9px] font-black text-slate-400 uppercase block">Previous Slot</span>
                                                {lead.schedulingData.slotType || lead.schedulingData.otSlot?.split('@')?.[0]?.trim() || 'N/A'}
                                             </div>
                                             <div>
                                                <span className="text-[9px] font-black text-slate-400 uppercase block">Previous Time</span>
                                                {formatTime12(lead.schedulingData.specificTime || lead.schedulingData.otSlot?.split('@')?.[1]?.trim(), 'N/A')}
                                             </div>
                                          </div>
                                       </div>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                       <div className="space-y-2 text-left">
                                          <label className="text-[10px] font-black text-violet-400 uppercase tracking-widest ml-1">New Surgery Date</label>
                                          <input type="date" disabled={isStageReadOnly} min={isPastSurgeryCase ? undefined : todayDateStr} className="w-full p-4 rounded-xl border-2 border-slate-100 font-bold focus:border-violet-500 outline-none transition-all disabled:bg-slate-100" value={schedulingForm.surgeryDate} onChange={(e) => setSchedulingForm({ ...schedulingForm, surgeryDate: e.target.value })} />
                                       </div>
                                       <div className="space-y-2 text-left">
                                          <label className="text-[10px] font-black text-violet-400 uppercase tracking-widest ml-1">OT Slot</label>
                                          <select disabled={isStageReadOnly} className="w-full p-4 rounded-xl border-2 border-slate-100 font-bold focus:border-violet-500 outline-none transition-all appearance-none bg-white disabled:bg-slate-100" value={schedulingForm.slotType || ''} onChange={(e) => setSchedulingForm({ ...schedulingForm, slotType: e.target.value })}>
                                             <option value="">Select Slot</option>
                                             {(!schedulingForm.surgeryDate || schedulingForm.surgeryDate !== todayDateStr || new Date().getHours() < 12) && (
                                                <option value="Morning">Morning Slot</option>
                                             )}
                                             {(!schedulingForm.surgeryDate || schedulingForm.surgeryDate !== todayDateStr || new Date().getHours() < 17) && (
                                                <option value="Afternoon">Afternoon Slot</option>
                                             )}
                                             <option value="Evening">Evening Slot</option>
                                             <option value="Emergency">Emergency OT</option>
                                          </select>
                                       </div>
                                       <div className="space-y-2 text-left">
                                          <label className="text-[10px] font-black text-violet-400 uppercase tracking-widest ml-1">Specific Time</label>
                                          <TimePicker12 disabled={isStageReadOnly} value={schedulingForm.specificTime || ''} onChange={(val) => setSchedulingForm({ ...schedulingForm, specificTime: val })} />
                                       </div>
                                    </div>
                                    {!isStageReadOnly ? (
                                       <button
                                          onClick={async () => {
                                             const combinedPayload = {
                                                ...schedulingForm,
                                                otSlot: `${schedulingForm.slotType} @ ${schedulingForm.specificTime}`
                                             };
                                             await handleStageTransition("scheduling", combinedPayload);
                                             window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                                          }}
                                          disabled={!canUpdateStage(displayStatus) || updating || !schedulingForm.surgeryDate || !schedulingForm.slotType || !schedulingForm.specificTime}
                                          className="w-full bg-violet-700 hover:bg-violet-800 text-white font-black py-4 rounded-xl shadow-lg shadow-violet-200 uppercase text-xs tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2 relative overflow-hidden"
                                       >
                                          {updating ? (
                                             <>
                                                {/* Shimmer overlay */}
                                                <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                                                {/* Skeleton content */}
                                                <div className="flex items-center gap-3 w-full justify-center">
                                                   <div className="w-4 h-4 rounded-full bg-violet-500/60 animate-pulse" />
                                                   <div className="h-3 w-40 rounded-full bg-violet-500/60 animate-pulse" />
                                                </div>
                                             </>
                                          ) : (
                                             <>
                                                <CheckCircle className="w-4 h-4" />
                                                Confirm Schedule & Proceed to Billing
                                             </>
                                          )}
                                       </button>
                                    ) : (
                                       <div className="w-full bg-emerald-50 text-emerald-700 font-black py-4 rounded-xl border border-emerald-200 uppercase text-xs tracking-widest text-center flex items-center justify-center gap-2">
                                          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> OT Schedule Confirmed & Locked
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* Stage 7 */}
                              {['scheduling', 'payment_pending'].includes(lead.status) && (
                                 <div className="space-y-6 animate-fadeIn pb-10">
                                    {/* 1. Case Final Summary Card */}
                                    <div className="bg-emerald-50/50 rounded-3xl p-6 border-2 border-emerald-100/50 shadow-sm">
                                       <div className="flex items-center gap-3 mb-4">
                                          <div className="p-2 bg-emerald-100 rounded-xl">
                                             <Activity className="w-5 h-5 text-emerald-600" />
                                          </div>
                                          <div>
                                             <h3 className="text-sm font-black text-emerald-900 uppercase tracking-widest">Case Final Summary</h3>
                                             <p className="text-[10px] font-bold text-emerald-600 uppercase">Review all details before billing</p>
                                          </div>
                                       </div>

                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                          <div className="p-4 bg-white/60 rounded-2xl border border-emerald-100">
                                             <div className="flex justify-between items-start mb-2">
                                                <p className="text-[9px] font-black text-slate-400 uppercase">Patient Details</p>
                                                <span className="text-[8px] font-black bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase">Verified</span>
                                             </div>
                                             <p className="text-sm font-black text-slate-800">{lead.patientName}</p>
                                             <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 mt-1">
                                                <Phone className="w-3 h-3" /> {lead.patientPhone}
                                             </div>
                                             <p className="text-[10px] text-emerald-600 font-bold mt-2 bg-emerald-50 px-2 py-0.5 rounded-full inline-block border border-emerald-100">{lead.disease || lead.treatmentRequired}</p>
                                          </div>
                                          <div className="p-4 bg-white/60 rounded-2xl border border-emerald-100">
                                             <div className="flex justify-between items-start mb-2">
                                                <p className="text-[9px] font-black text-slate-400 uppercase">Hospital & Schedule</p>
                                                <div className="flex items-center gap-1">
                                                   <Building2 className="w-2.5 h-2.5 text-emerald-600" />
                                                   <p className="text-[9px] font-black text-emerald-700 uppercase">{lead.assignedHospital?.hospitalName || "Not Assigned"}</p>
                                                </div>
                                             </div>
                                             <div className="space-y-1.5">
                                                <div className="flex items-center gap-2">
                                                   <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                                                   <p className="text-[11px] font-bold text-slate-700">
                                                      {lead.schedulingData?.surgeryDate ? new Date(lead.schedulingData.surgeryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "TBD"}
                                                   </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                   <Clock className="w-3.5 h-3.5 text-emerald-600" />
                                                   <p className="text-[11px] font-bold text-slate-700">
                                                      {lead.schedulingData?.otSlot || "Time TBD"}
                                                   </p>
                                                </div>
                                             </div>
                                          </div>
                                          <div className="p-4 bg-white/60 rounded-2xl border border-emerald-100 sm:col-span-2">
                                             <p className="text-[9px] font-black text-slate-400 uppercase mb-3">Surgical Clinical Team</p>
                                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="flex items-center gap-3 p-2 bg-indigo-50/50 rounded-xl border border-indigo-100">
                                                   <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center text-white shadow-lg shadow-indigo-100">
                                                      <Stethoscope className="w-5 h-5" />
                                                   </div>
                                                   <div>
                                                      <p className="text-[9px] font-black text-indigo-400 uppercase leading-tight">Surgeon</p>
                                                      <p className="text-xs font-black text-indigo-900 truncate">
                                                         {lead.surgeonType === 'other' && lead.assignedSurgeonOtherName
                                                            ? `Dr. ${lead.assignedSurgeonOtherName}`
                                                            : `Dr. ${lead.assignedSurgeon?.user?.firstName || 'Assigned'} ${lead.assignedSurgeon?.user?.lastName || 'Surgeon'}`}
                                                      </p>
                                                   </div>
                                                </div>
                                                <div className="flex items-center gap-3 p-2 bg-purple-50/50 rounded-xl border border-purple-100">
                                                   <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center text-white shadow-lg shadow-purple-100">
                                                      <Activity className="w-5 h-5" />
                                                   </div>
                                                   <div>
                                                      <p className="text-[9px] font-black text-purple-400 uppercase leading-tight">anesthesiologist</p>
                                                      <p className="text-xs font-black text-purple-900 truncate">
                                                         {lead.anesthesiologistType === 'other' && lead.assignedanesthesiologistOtherName
                                                            ? `Dr. ${lead.assignedanesthesiologistOtherName}`
                                                            : `Dr. ${lead.assignedanesthesiologist?.user?.firstName || 'Assigned'} ${lead.assignedanesthesiologist?.user?.lastName || 'anesthesiologist'}`}
                                                      </p>
                                                   </div>
                                                </div>
                                             </div>
                                          </div>
                                       </div>
                                    </div>

                                    {/* 2. Hospital Contact Section */}
                                    <div className="bg-white rounded-3xl p-6 border border-slate-200 space-y-4 shadow-sm">
                                       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-slate-50 p-4 rounded-2xl border border-slate-100 mb-2 gap-3 sm:gap-0">
                                          <div>
                                             <h3 className="text-[10px] sm:text-xs font-black text-slate-800 uppercase tracking-[0.2em]">Hospital Contact Control</h3>
                                             <p className="text-[8px] sm:text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-widest leading-none">Primary & Alternate Coordination</p>
                                          </div>
                                          <button
                                             onClick={() => setPaymentForm({ ...paymentForm, useAlternate: !paymentForm.useAlternate })}
                                             className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[8px] sm:text-[9px] font-black uppercase transition-all whitespace-nowrap ${paymentForm.useAlternate ? "bg-orange-600 text-white shadow-lg shadow-orange-100" : "bg-slate-200 text-slate-600"}`}
                                          >
                                             {paymentForm.useAlternate ? <CheckCircle className="w-3 h-3" /> : <div className="w-3 h-3 border-2 border-slate-400 rounded-full" />}
                                             Use Alternate
                                          </button>
                                       </div>

                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                          <div className="space-y-2">
                                             <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1">Primary Email (Hospital)</label>
                                             <div className="flex items-center gap-3 p-3 sm:p-3.5 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl overflow-hidden">
                                                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                <span className="text-[10px] sm:text-xs font-black text-slate-600 truncate">{lead.assignedHospital?.email || lead.assignedHospital?.mailId || "No email registered"}</span>
                                             </div>
                                          </div>
                                          <div className="space-y-2">
                                             <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1">Primary WhatsApp (Hospital)</label>
                                             <div className="flex items-center gap-3 p-3.5 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl">
                                                <MessageSquare className="w-4 h-4 text-emerald-500" />
                                                <span className="text-xs font-black text-slate-600">{lead.assignedHospital?.phone || lead.assignedHospital?.contactPerson?.phone || "No contact registered"}</span>
                                             </div>
                                          </div>

                                          {paymentForm.useAlternate && (
                                             <>
                                                <div className="space-y-2 animate-fadeIn">
                                                   <label className="text-[9px] font-black text-orange-600 uppercase tracking-widest px-1">Alternate Contact Email</label>
                                                   <input
                                                      type="email"
                                                      value={paymentForm.alternateEmail}
                                                      onChange={(e) => setPaymentForm({ ...paymentForm, alternateEmail: e.target.value })}
                                                      className="w-full p-3.5 bg-orange-50 border border-orange-100 rounded-xl text-orange-900 text-xs font-bold outline-none focus:ring-2 focus:ring-orange-200"
                                                      placeholder="Enter billing email..."
                                                   />
                                                </div>
                                                <div className="space-y-2 animate-fadeIn">
                                                   <label className="text-[9px] font-black text-orange-600 uppercase tracking-widest px-1">Alternate WhatsApp</label>
                                                   <input
                                                      type="text"
                                                      value={paymentForm.alternateWhatsapp}
                                                      onChange={(e) => setPaymentForm({ ...paymentForm, alternateWhatsapp: e.target.value })}
                                                      className="w-full p-3.5 bg-orange-50 border border-orange-100 rounded-xl text-orange-900 text-xs font-bold outline-none focus:ring-2 focus:ring-orange-200"
                                                      placeholder="Enter billing WhatsApp..."
                                                   />
                                                </div>
                                             </>
                                          )}
                                       </div>
                                    </div>

                                    {/* 3. Dynamic Billing Section */}
                                    <div className="bg-[#143E78] rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden group">
                                       <div className="absolute top-0 right-0 p-8 opacity-10">
                                          <LayoutGrid className="w-24 h-24 rotate-12" />
                                       </div>
                                       <div className="relative z-10 space-y-8">
                                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/10 pb-6 mb-6">
                                             <div>
                                                <h3 className="text-xl font-black uppercase tracking-widest">Cost Estimate</h3>
                                                <p className="text-blue-200 text-[10px] font-bold mt-1 uppercase tracking-widest">Detailed Fee Breakdown & Settlement Mode</p>
                                             </div>
                                             <div className="flex flex-wrap items-center gap-3">
                                                {isPastSurgeryCase ? (
                                                   <div className="bg-amber-500/20 border border-amber-400/40 text-amber-300 px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-inner">
                                                      <FileText className="w-4 h-4 text-amber-400" />
                                                      ⚡ Past Surgery Settlement
                                                   </div>
                                                ) : (
                                                   <div className="bg-blue-600/30 border border-blue-400/40 text-blue-200 px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-inner">
                                                      <CreditCard className="w-3.5 h-3.5 text-blue-400" />
                                                      Live Razorpay Link
                                                   </div>
                                                )}
                                             </div>
                                             <div className="flex flex-wrap items-center gap-3">
                                                <button
                                                   type="button"
                                                   onClick={() => handleSyncPaymentStatus(false)}
                                                   disabled={updating}
                                                   title="Sync/refresh payment status from Razorpay"
                                                   className="bg-blue-600/80 hover:bg-blue-600 border border-blue-400/30 text-white px-3.5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1.5 transition-all hover:scale-[1.02] cursor-pointer disabled:opacity-50"
                                                >
                                                   <RefreshCcw className={`w-3.5 h-3.5 ${updating ? 'animate-spin' : ''}`} />
                                                   Sync Payment
                                                </button>
                                                <button
                                                   onClick={() => setShowCalculatorModal(true)}
                                                   className="bg-blue-600/80 hover:bg-blue-600 border border-blue-400/30 text-white px-3.5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1.5 transition-all hover:scale-[1.02] cursor-pointer"
                                                >
                                                   <Calculator className="w-3.5 h-3.5" />
                                                   Calculator
                                                </button>
                                                <button
                                                   onClick={() => {
                                                      setManualCalcItems([]);
                                                      setShowManualCalculatorModal(true);
                                                   }}
                                                   className="bg-amber-500 hover:bg-amber-600 border border-amber-400/40 text-slate-950 px-3.5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1.5 transition-all hover:scale-[1.02] cursor-pointer"
                                                >
                                                   <Calculator className="w-3.5 h-3.5" />
                                                   Manual Calculator
                                                </button>
                                             </div>
                                          </div>

                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                             <div className="space-y-6">


                                                <div className="p-5 bg-white/10 rounded-2xl border border-white/10 backdrop-blur-sm">
                                                   <label className="text-[10px] font-black text-blue-200 uppercase tracking-widest block mb-3">Surgical Procedure Amount (₹)</label>
                                                   <input
                                                      type="text"
                                                      inputMode="numeric"
                                                      value={paymentForm.procedureAmount}
                                                      onChange={(e) => {
                                                         const raw = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
                                                         updateBillingState({ procedureAmount: raw }, false);
                                                      }}
                                                      onBlur={() => {
                                                         updateBillingState({ procedureAmount: paymentForm.procedureAmount }, true);
                                                      }}
                                                      className="w-full bg-transparent border-b-2 border-blue-400/50 text-white text-3xl font-black outline-none focus:border-white transition-all py-2 placeholder:text-blue-200"
                                                      placeholder="0.00"
                                                   />
                                                </div>


                                                <div className="flex items-center justify-between text-blue-100 font-bold px-1">
                                                   <div className="text-[11px] uppercase tracking-widest flex items-center gap-2">
                                                      <button
                                                         type="button"
                                                         onPointerDown={(e) => e.preventDefault()}
                                                         onClick={() => {
                                                            const nextState = !platformFeeEnabled;
                                                            setPlatformFeeEnabled(nextState);
                                                            updateBillingState({ platformFeeEnabled: nextState }, true);
                                                         }}
                                                         className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${platformFeeEnabled ? 'bg-emerald-400' : 'bg-slate-600'
                                                            }`}
                                                         title={platformFeeEnabled ? "Disable Platform Fee" : "Enable Platform Fee"}
                                                      >
                                                         <span
                                                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${platformFeeEnabled ? 'translate-x-4' : 'translate-x-0'
                                                               }`}
                                                         />
                                                      </button>
                                                      <span>Doxez Platform Fee</span>
                                                      {!platformFeeEnabled && (
                                                         <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 border border-rose-500/40 rounded text-[9px] font-black uppercase tracking-wider">
                                                            Disabled / Waived
                                                         </span>
                                                      )}
                                                      {platformFeeEnabled && (
                                                         <span className="inline-flex rounded-full p-0.5 bg-blue-900 border border-blue-700/50 shrink-0">
                                                            <button
                                                               type="button"
                                                               onClick={() => {
                                                                  setPlatformFeeType('percent');
                                                                  updateBillingState({ platformFeeType: 'percent' }, true);
                                                               }}
                                                               className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase transition-all ${platformFeeType === 'percent'
                                                                  ? 'bg-blue-500 text-white shadow-md'
                                                                  : 'text-blue-300 hover:text-white'
                                                                  }`}
                                                            >
                                                               %
                                                            </button>
                                                            <button
                                                               type="button"
                                                               onClick={() => {
                                                                  setPlatformFeeType('amount');
                                                                  updateBillingState({ platformFeeType: 'amount' }, true);
                                                               }}
                                                               className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase transition-all ${platformFeeType === 'amount'
                                                                  ? 'bg-blue-500 text-white shadow-md'
                                                                  : 'text-blue-300 hover:text-white'
                                                                  }`}
                                                            >
                                                               ₹
                                                            </button>
                                                         </span>
                                                      )}
                                                   </div>
                                                   <div className="flex items-center gap-1.5">
                                                      {!platformFeeEnabled ? (
                                                         <span className="text-slate-400 text-xs font-bold italic px-2 py-1 bg-slate-800/50 rounded-lg">
                                                            ₹0
                                                         </span>
                                                      ) : platformFeeType === 'percent' ? (
                                                         <>
                                                            <input
                                                               type="text"
                                                               inputMode="numeric"
                                                               value={paymentForm.platformFee}
                                                               onChange={(e) => {
                                                                  const raw = e.target.value.replace(/[^0-9]/g, '');
                                                                  updateBillingState({ platformFee: raw }, false);
                                                               }}
                                                               onBlur={() => {
                                                                  updateBillingState({ platformFee: paymentForm.platformFee }, true);
                                                               }}
                                                               className="w-16 bg-blue-800 text-white text-center py-1 rounded-lg border border-blue-700 outline-none focus:border-white font-bold text-sm"
                                                               placeholder="0"
                                                            />
                                                            <span className="text-white text-sm font-bold">%</span>
                                                         </>
                                                      ) : (
                                                         <>
                                                            <span className="text-white text-sm font-bold">₹</span>
                                                            <input
                                                               type="text"
                                                               inputMode="numeric"
                                                               value={paymentForm.platformFeeAmount === 0 ? '' : paymentForm.platformFeeAmount.toString()}
                                                               onChange={(e) => {
                                                                  const raw = e.target.value.replace(/[^0-9]/g, '');
                                                                  const pfAmount = raw === '' ? 0 : parseInt(raw);
                                                                  updateBillingState({ platformFeeAmount: pfAmount }, false);
                                                               }}
                                                               onBlur={() => {
                                                                  updateBillingState({ platformFeeAmount: paymentForm.platformFeeAmount }, true);
                                                               }}
                                                               className="w-24 bg-blue-800 text-white text-right px-2 py-1 rounded-lg border border-blue-700 outline-none focus:border-white font-bold text-sm"
                                                               placeholder="0"
                                                            />
                                                         </>
                                                      )}
                                                   </div>
                                                </div>
                                                <div className="flex items-center justify-between text-blue-100 font-bold px-1">
                                                   <div className="text-[11px] uppercase tracking-widest flex items-center gap-2">
                                                      <button
                                                         type="button"
                                                         onPointerDown={(e) => e.preventDefault()}
                                                         onClick={() => {
                                                            const nextGstEnabled = !gstEnabled;
                                                            setGstEnabled(nextGstEnabled);
                                                            updateBillingState({ gstEnabled: nextGstEnabled }, true);
                                                         }}
                                                         className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${gstEnabled ? 'bg-emerald-400' : 'bg-slate-600'}`}
                                                         title={gstEnabled ? 'Disable GST' : 'Enable GST'}
                                                      >
                                                         <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${gstEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                                      </button>
                                                      <span>{platformFeeEnabled ? 'GST on Platform Fee' : 'GST Percentage'}</span>
                                                      {!gstEnabled && (
                                                         <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 border border-rose-500/40 rounded text-[9px] font-black uppercase tracking-wider">
                                                            Disabled / Waived
                                                         </span>
                                                      )}
                                                   </div>
                                                   <div className="flex items-center gap-1.5">
                                                      {!gstEnabled ? (
                                                         <span className="text-slate-400 text-xs font-bold italic px-2 py-1 bg-slate-800/50 rounded-lg">₹0</span>
                                                      ) : (
                                                         <>
                                                            <input
                                                               type="text"
                                                               inputMode="numeric"
                                                               value={paymentForm.gstPercent}
                                                               onChange={(e) => {
                                                                  const raw = e.target.value.replace(/[^0-9]/g, '');
                                                                  updateBillingState({ gstPercent: raw }, false);
                                                               }}
                                                               onBlur={() => {
                                                                  updateBillingState({ gstPercent: paymentForm.gstPercent }, true);
                                                               }}
                                                               className="w-16 bg-blue-800 text-white text-center py-1 rounded-lg border border-blue-700 outline-none focus:border-white font-bold text-sm"
                                                               placeholder="0"
                                                            />
                                                            <span className="text-white text-sm font-bold">%</span>
                                                         </>
                                                      )}
                                                   </div>
                                                </div>
                                             </div>

                                             <div className="bg-white/5 rounded-3xl p-6 flex flex-col justify-center gap-4">
                                                {(() => {
                                                   const mItems = paymentForm.manualCalcItems || lead?.billingDetails?.manualCalcItems || [];
                                                   const hasManual = mItems.length > 0;

                                                   if (hasManual) {
                                                      const netVal = paymentForm.procedureAmount ? parseInt(paymentForm.procedureAmount) : 0;
                                                      return (
                                                         <>
                                                            <div className="text-[10px] font-black uppercase text-amber-400 border-b border-white/10 pb-1 flex items-center justify-between">
                                                               <span>Manual Calculation Breakdown</span>
                                                               <span className="text-[9px] bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded font-bold">Manual</span>
                                                            </div>
                                                            {mItems.map((item, idx) => (
                                                               <div key={`manual-item-${idx}`} className={`flex justify-between items-center text-[10px] font-black uppercase ${item.type === 'deduct' ? 'text-rose-300' : 'text-emerald-300'}`}>
                                                                  <span>{item.type === 'deduct' ? 'Less: ' : ''}{item.name || 'Charge Item'}</span>
                                                                  <span>{item.type === 'deduct' ? '-' : '+'}₹{Number(item.amount || 0).toLocaleString()}</span>
                                                               </div>
                                                            ))}
                                                            <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase border-b border-white/5 pb-2 pt-1">
                                                               <span>Total Surgical Amount</span>
                                                               <span className="text-white text-xs font-black">₹{netVal.toLocaleString()}</span>
                                                            </div>
                                                         </>
                                                      );
                                                   }

                                                   const baseTotal = calcBaseItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
                                                   const activeDeductions = calcDeductedItems.filter(item => (Number(item.amount) || 0) > 0);
                                                   const deductionsSum = activeDeductions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
                                                   const procedureAmountVal = paymentForm.procedureAmount ? parseInt(paymentForm.procedureAmount) : 0;
                                                   const netPackageVal = baseTotal > 0 ? baseTotal : procedureAmountVal;
                                                   const totalSurgicalPackageVal = netPackageVal + deductionsSum;

                                                   return (
                                                      <>
                                                         <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase">
                                                            <span>Total Surgical Package</span>
                                                            <span className="text-white text-xs">₹{totalSurgicalPackageVal.toLocaleString()}</span>
                                                         </div>
                                                         {activeDeductions.map((item, idx) => (
                                                            <div key={`deduct-row-${idx}`} className="flex justify-between items-center text-rose-300 text-[10px] font-black uppercase">
                                                               <span>Less: {item.name}</span>
                                                               <span className="text-rose-400 text-xs">₹{(Number(item.amount) || 0).toLocaleString()}</span>
                                                            </div>
                                                         ))}
                                                         {activeDeductions.length > 0 && (
                                                            <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase border-b border-white/5 pb-2">
                                                               <span>Net Eligible Package Amount</span>
                                                               <span className="text-white text-xs">₹{netPackageVal.toLocaleString()}</span>
                                                            </div>
                                                         )}
                                                      </>
                                                   );
                                                })()}
                                                <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase border-b border-white/5 pb-2">
                                                   <span>Platform Booking Fee</span>
                                                   <span className="text-white text-xs">
                                                      {!platformFeeEnabled ? (
                                                         <span className="text-rose-300 font-bold">₹0</span>
                                                      ) : (
                                                         `₹${(paymentForm.platformFeeAmount || 0).toLocaleString()}`
                                                      )}
                                                   </span>
                                                </div>
                                                <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase">
                                                   <span>{platformFeeEnabled ? 'GST on platform Fee' : 'GST Amount'}</span>
                                                   <span className="text-white text-xs">
                                                      ₹{(paymentForm.gstAmount || 0).toLocaleString()}
                                                   </span>
                                                </div>

                                                {/* 📝 DESCRIPTION / SETTLEMENT REMARKS INPUT */}
                                                {platformFeeType === 'amount' && (
                                                   <div className="bg-[#0f2c59]/80 border border-white/10 rounded-2xl p-4 space-y-2 animate-fadeIn">
                                                      <label className="text-[10px] font-black text-blue-300 uppercase tracking-widest flex items-center gap-2">
                                                         <FileText className="w-3.5 h-3.5 text-blue-400" />
                                                         Description / Billing Notes for Hospital (Included in Email & PDF)
                                                      </label>
                                                      <textarea
                                                         rows={2}
                                                         value={paymentForm.description || ''}
                                                         onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                                                         onBlur={() => {
                                                            api.patch(`/leads/${id}`, {
                                                               billingDetails: {
                                                                  ...(lead?.billingDetails || {}),
                                                                  description: paymentForm.description || '',
                                                                  remarks: paymentForm.description || ''
                                                               }
                                                            }).catch(() => { });
                                                         }}
                                                         placeholder="Enter remarks, itemized notes, or specific settlement instructions for hospital..."
                                                         className="w-full bg-[#143E78]/60 border border-blue-400/30 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-blue-300/40 focus:outline-none focus:border-emerald-400 transition-all resize-none shadow-inner"
                                                      />
                                                   </div>
                                                )}
                                                <div className="mt-4 pt-6 border-t border-white/10 text-center">
                                                   <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Total Payable by Hospital</p>
                                                   <p className="text-4xl font-black text-emerald-400 drop-shadow-md">₹{paymentForm.totalPayableAmount.toLocaleString()}</p>
                                                </div>
                                             </div>
                                          </div>

                                          {isPastSurgeryCase ? (
                                             <div className="bg-amber-500/10 border border-amber-400/40 rounded-3xl p-6 text-white space-y-5">
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-amber-400/20 pb-4">
                                                   <div className="flex items-center gap-3">
                                                      <div className="w-10 h-10 bg-amber-500/30 rounded-xl flex items-center justify-center text-amber-300 font-bold shrink-0">
                                                         <FileText className="w-5 h-5" />
                                                      </div>
                                                      <div>
                                                         <h4 className="text-xs font-black uppercase tracking-wider text-amber-200">Historical Case Settlement (Past Surgery)</h4>
                                                         <p className="text-[10px] text-blue-200 font-bold mt-0.5">Surgery completed in past. No Razorpay payment link or automated emails are sent to hospital.</p>
                                                      </div>
                                                   </div>
                                                </div>
                                                {/* 📁 UPLOAD SETTLEMENT / PAYMENT PROOF FILE BOX */}
                                                <div className="space-y-2.5">
                                                   <label className="text-[10px] font-black text-amber-200 uppercase tracking-widest flex items-center gap-2">
                                                      <Upload className="w-3.5 h-3.5 text-amber-300" />
                                                      Upload Hospital Settlement / Payment Proof (Receipt, Invoice, Transfer Proof)
                                                   </label>
                                                   <label
                                                      htmlFor="settlement-proof-file-input"
                                                      className="block w-full cursor-pointer border-2 border-dashed border-amber-400/40 hover:border-amber-300 bg-amber-900/20 rounded-2xl p-4 text-center transition-all group"
                                                   >
                                                      <div className="flex items-center justify-center gap-2">
                                                         <Upload className="w-4 h-4 text-amber-300 group-hover:scale-110 transition-transform" />
                                                         <span className="text-xs font-bold uppercase tracking-wider text-amber-100">
                                                            {(lead?.documents?.filter(d => d.type === 'past_payment_proof')?.length || (lead?.pastPaymentProof?.url ? 1 : 0)) > 0
                                                               ? `Attached: ${lead?.documents?.filter(d => d.type === 'past_payment_proof')?.length || 1} Proof File(s)`
                                                               : 'Click to Upload Settlement / Payment Receipt File(s)'}
                                                         </span>
                                                      </div>
                                                      <p className="text-[9px] text-amber-300/70 font-semibold mt-1">PDF, JPG, PNG up to 10MB (Select one or multiple files)</p>
                                                   </label>
                                                   <input
                                                      id="settlement-proof-file-input"
                                                      type="file"
                                                      multiple
                                                      accept="image/*,application/pdf"
                                                      className="hidden"
                                                      onClick={(e) => e.stopPropagation()}
                                                      onChange={async (e) => {
                                                         if (e.target.files && e.target.files.length > 0) {
                                                            const selectedFilesList = Array.from(e.target.files);
                                                            e.target.value = '';
                                                            const fileFormData = new FormData();
                                                            selectedFilesList.forEach(file => {
                                                               fileFormData.append('documents', file);
                                                            });

                                                            try {
                                                               setUpdating(true);
                                                               const uploadRes = await api.post('/upload', fileFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
                                                               const uploadedFiles = uploadRes.data?.data?.files || [];
                                                               if (uploadedFiles.length === 0) throw new Error('Upload failed');

                                                               const newDocEntries = uploadedFiles.map((uf, i) => ({
                                                                  name: uf.filename || selectedFilesList[i]?.name || `Settlement Proof ${i + 1}`,
                                                                  type: 'past_payment_proof',
                                                                  url: uf.url
                                                               }));

                                                               const firstUploaded = uploadedFiles[0];

                                                               await api.patch(`/leads/${id}`, {
                                                                  pastPaymentProof: { url: firstUploaded.url, name: firstUploaded.filename || selectedFilesList[0]?.name },
                                                                  documents: [
                                                                     ...(lead?.documents || []).filter(d => d.type !== 'past_payment_proof'),
                                                                     ...newDocEntries
                                                                  ]
                                                               });
                                                               toast.success(`${uploadedFiles.length} settlement proof file(s) attached successfully!`);
                                                               fetchLead();
                                                            } catch (err) {
                                                               console.error('[SETTLEMENT_UPLOAD]', err);
                                                               toast.error(err.response?.data?.message || err.message || 'Failed to upload settlement proof');
                                                            } finally {
                                                               setUpdating(false);
                                                            }
                                                         }
                                                      }}
                                                   />

                                                   {/* Display list of attached settlement proof files */}
                                                   {(() => {
                                                      const proofs = (lead?.documents || []).filter(d => d.type === 'past_payment_proof');
                                                      if (proofs.length === 0 && lead?.pastPaymentProof?.url) {
                                                         proofs.push({ name: lead.pastPaymentProof.name || 'Payment Settlement Proof', url: lead.pastPaymentProof.url });
                                                      }
                                                      if (proofs.length === 0) return null;

                                                      return (
                                                         <div className="space-y-2 mt-3">
                                                            <p className="text-[10px] font-black text-amber-300/80 uppercase tracking-widest">
                                                               Attached Settlement Proofs ({proofs.length}):
                                                            </p>
                                                            {proofs.map((doc, idx) => (
                                                               <div key={idx} className="flex items-center justify-between p-3 bg-amber-900/40 rounded-xl border border-amber-400/30 gap-2">
                                                                  <div className="flex items-center gap-2 text-xs font-bold text-amber-100 min-w-0 pr-2">
                                                                     <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                                                                     <span className="truncate max-w-[180px] sm:max-w-[260px]">{doc.name || `Payment Settlement Proof ${idx + 1}`}</span>
                                                                  </div>
                                                                  <div className="flex items-center gap-2 shrink-0">
                                                                     <a
                                                                        href={doc.url}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-[10px] uppercase tracking-wider rounded-lg transition-all flex items-center gap-1 shadow-md cursor-pointer"
                                                                     >
                                                                        <ExternalLink className="w-3 h-3" /> View Proof
                                                                     </a>
                                                                     <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveSettlementProof(doc.url)}
                                                                        disabled={updating}
                                                                        className="px-2.5 py-1.5 bg-rose-500/20 hover:bg-rose-500/40 text-rose-300 border border-rose-400/30 font-black text-[10px] uppercase rounded-lg transition-all flex items-center gap-1 cursor-pointer disabled:opacity-50"
                                                                        title="Remove Proof File"
                                                                     >
                                                                        <Trash2 className="w-3 h-3" /> Remove
                                                                     </button>
                                                                  </div>
                                                               </div>
                                                            ))}
                                                         </div>
                                                      );
                                                   })()}

                                                   {/* Save & Complete Settlement Action Button */}
                                                   <div className="pt-2">
                                                      <button
                                                         type="button"
                                                         disabled={updating}
                                                         onClick={async () => {
                                                            const procAmt = paymentForm.procedureAmount ? Number(paymentForm.procedureAmount) : 0;
                                                            const hasProof = Boolean(lead?.pastPaymentProof?.url || lead?.documents?.find(d => d.type === 'past_payment_proof')?.url);

                                                            if (!procAmt || procAmt <= 0) {
                                                               toast.error('Please enter Surgical Procedure Amount');
                                                               return;
                                                            }

                                                            if (!hasProof) {
                                                               toast.error('Please upload Hospital Settlement / Payment Receipt Proof file before completing settlement');
                                                               return;
                                                            }

                                                            try {
                                                               setUpdating(true);
                                                               await api.patch(`/leads/${id}`, {
                                                                  billingDetails: {
                                                                     ...(lead?.billingDetails || {}),
                                                                     procedureAmount: procAmt,
                                                                     platformFeeType: platformFeeType || 'percent',
                                                                     platformFeeAmount: paymentForm.platformFeeAmount || 0,
                                                                     gstAmount: paymentForm.gstAmount || 0,
                                                                     totalPayableAmount: paymentForm.totalPayableAmount || 0,
                                                                     description: paymentForm.description || '',
                                                                     remarks: paymentForm.description || ''
                                                                  }
                                                               });
                                                               await api.patch(`/leads/${id}/status`, {
                                                                  status: 'paid',
                                                                  notes: 'Historical case settlement saved and completed'
                                                               });
                                                               toast.success('Historical settlement saved and completed!');
                                                               fetchLead();
                                                            } catch (err) {
                                                               console.error('[SETTLEMENT_SAVE_ERR]', err);
                                                               toast.error(err.response?.data?.message || 'Failed to save settlement');
                                                            } finally {
                                                               setUpdating(false);
                                                            }
                                                         }}
                                                         className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-2xl uppercase text-[11px] tracking-[0.2em] shadow-xl shadow-emerald-950 transition-all flex items-center justify-center gap-2 cursor-pointer"
                                                      >
                                                         <CheckCircle className="w-4 h-4" />
                                                         Save & Complete Settlement
                                                      </button>

                                                      <button
                                                         type="button"
                                                         disabled={sendingManualNotif}
                                                         onClick={async () => {
                                                            if (!id) return;
                                                            setSendingManualNotif(true);
                                                            try {
                                                               const res = await api.post(`/leads/${id}/manual-notify`, {
                                                                  event: 'payment_link_sent',
                                                                  notes: 'Manual payment & hospital settlement notification triggered by CC/Admin'
                                                               });
                                                               toast.success(res.data?.message || 'Hospital settlement notification dispatched successfully!');
                                                            } catch (error) {
                                                               toast.error(error.response?.data?.message || 'Failed to send hospital settlement notification');
                                                            } finally {
                                                               setSendingManualNotif(false);
                                                            }
                                                         }}
                                                         className="w-full mt-3 py-3.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black rounded-2xl text-[10px] sm:text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg cursor-pointer disabled:opacity-50"
                                                      >
                                                         {sendingManualNotif ? <Loader2 className="w-4 h-4 animate-spin text-slate-950" /> : <Send className="w-4 h-4 text-slate-950" />}
                                                         Send Hospital Settlement Notification & Link
                                                      </button>
                                                   </div>
                                                </div>
                                             </div>
                                          ) : (
                                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {updating ? (
                                                   <div className="group flex items-center justify-center gap-3 py-5 bg-emerald-500 rounded-2xl relative overflow-hidden">
                                                      {/* Shimmer sweep */}
                                                      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                                                      {/* Skeleton blobs */}
                                                      <div className="w-4 h-4 rounded-full bg-emerald-400/60 animate-pulse" />
                                                      <div className="h-3 w-36 rounded-full bg-emerald-400/60 animate-pulse" />
                                                   </div>
                                                ) : (
                                                   <button
                                                      onClick={() => {
                                                         if (paymentForm.totalPayableAmount <= 0) {
                                                            return toast.error('Financial Validation Failed: Total Amount must be greater than 0');
                                                         }
                                                         handleGeneratePaymentLink();
                                                      }}
                                                      disabled={updating || paymentForm.totalPayableAmount <= 0}
                                                      className="group flex items-center justify-center gap-3 py-5 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-2xl uppercase text-[11px] tracking-[0.2em] shadow-2xl shadow-emerald-950 transition-all disabled:opacity-50"
                                                   >
                                                      <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                                      {lead.status === "payment_pending" ? "Regenerate Link" : "Generate Razorpay Link"}
                                                   </button>
                                                )}

                                                {displayStatus === 'payment_pending' && (
                                                   <button
                                                      onClick={() => handleSyncPaymentStatus(false)}
                                                      disabled={updating}
                                                      className="group flex items-center justify-center gap-3 py-5 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl uppercase text-[11px] tracking-[0.2em] shadow-2xl shadow-blue-950 transition-all disabled:opacity-50"
                                                   >
                                                      <RefreshCcw className={`w-4 h-4 ${updating ? 'animate-spin' : ''}`} />
                                                      Sync Payment Status
                                                   </button>
                                                )}
                                                <div className="flex items-center gap-2 px-6 py-4 bg-white/10 rounded-2xl border border-white/5">
                                                   <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
                                                   <p className="text-[9px] font-bold text-blue-200 leading-tight">Razorpay SECURE will dispatch a unique payment link to the hospital via multi-channel notification.</p>
                                                </div>

                                                {['scheduling', 'payment_pending'].includes(displayStatus) && (
                                                   <div className="sm:col-span-2 mt-4 pt-4 border-t border-white/10">
                                                      {!showManualPaymentUpload ? (
                                                         <button
                                                            type="button"
                                                            onClick={() => {
                                                               setManualPaymentAmount(lead?.billingDetails?.totalPayableAmount || '');
                                                               setShowManualPaymentUpload(true);
                                                            }}
                                                            className="w-full py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black rounded-2xl uppercase text-[10px] sm:text-xs tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2"
                                                         >
                                                            <Upload size={16} /> Upload Receipt Only
                                                         </button>
                                                      ) : (
                                                         <div className="bg-white p-5 rounded-3xl border border-slate-200 text-left space-y-4 animate-in slide-in-from-top-4 duration-300">
                                                            <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider">Upload Receipt Only</h4>

                                                            <div className="space-y-1">
                                                               <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Amount Received (₹)</label>
                                                               <input
                                                                  type="number"
                                                                  value={manualPaymentAmount}
                                                                  onChange={(e) => setManualPaymentAmount(e.target.value)}
                                                                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 text-sm font-bold text-slate-700"
                                                                  placeholder="Enter amount"
                                                               />
                                                            </div>

                                                            <div className="space-y-1">
                                                               <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Payment Receipt File</label>
                                                               <div className="relative">
                                                                  <input
                                                                     type="file"
                                                                     onChange={(e) => setManualPaymentReceipt(e.target.files[0])}
                                                                     className="hidden"
                                                                     id="detail-manual-payment-receipt"
                                                                     accept="image/*,.pdf"
                                                                  />
                                                                  <label
                                                                     htmlFor="detail-manual-payment-receipt"
                                                                     className={`flex items-center gap-2 w-full px-4 py-3 bg-slate-50 border border-dashed rounded-xl cursor-pointer hover:bg-slate-100 transition-all ${manualPaymentReceipt ? 'border-emerald-500 text-emerald-700' : 'border-slate-200 text-slate-500'}`}
                                                                  >
                                                                     <Upload size={16} />
                                                                     <span className="text-xs font-bold truncate">
                                                                        {manualPaymentReceipt ? manualPaymentReceipt.name : 'Upload Receipt'}
                                                                     </span>
                                                                  </label>
                                                               </div>
                                                            </div>

                                                            <div className="flex gap-2 pt-2">
                                                               <button
                                                                  type="button"
                                                                  onClick={() => setShowManualPaymentUpload(false)}
                                                                  className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-black uppercase tracking-widest rounded-xl transition-all"
                                                               >
                                                                  Cancel
                                                               </button>
                                                               <button
                                                                  type="button"
                                                                  onClick={handleManualPaymentSubmit}
                                                                  disabled={uploadingReceipt || updating}
                                                                  className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                                                               >
                                                                  {uploadingReceipt ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm & Paid'}
                                                               </button>
                                                            </div>
                                                         </div>
                                                      )}
                                                   </div>
                                                )}
                                             </div>
                                          )}
                                       </div>
                                    </div>
                                 </div>
                              )}

                              {/* Read-Only Historical view for OT Scheduling Tab */}
                              {displayStatus === 'scheduling' && !['hospital_assignment', 'scheduling'].includes(lead.status) && (
                                 <div className="bg-violet-50 p-6 rounded-2xl border border-violet-100 space-y-6 animate-fadeIn pb-10">
                                    <div className="flex items-center gap-3">
                                       <div className="w-12 h-12 bg-violet-100 text-violet-700 rounded-2xl flex items-center justify-center">
                                          <Calendar className="w-6 h-6 animate-pulse" />
                                       </div>
                                       <div>
                                          <h3 className="text-sm font-black text-violet-900 uppercase tracking-widest">Surgery Schedule Locked</h3>
                                          <p className="text-[10px] font-bold text-violet-600 uppercase">OT Scheduling has been finalized</p>
                                       </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white p-5 rounded-2xl border border-violet-100 shadow-sm text-left">
                                       <div>
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Surgery Date</p>
                                          <p className="text-sm font-bold text-slate-800">
                                             {lead.schedulingData?.surgeryDate ? new Date(lead.schedulingData.surgeryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                                          </p>
                                       </div>
                                       <div>
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">OT Slot Type</p>
                                          <p className="text-sm font-bold text-slate-800">
                                             {lead.schedulingData?.slotType || lead.schedulingData?.otSlot?.split('@')?.[0]?.trim() || "Morning"}
                                          </p>
                                       </div>
                                       <div>
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Specific Time</p>
                                          <p className="text-sm font-bold text-slate-800">
                                             {formatTime12(lead.schedulingData?.specificTime || lead.schedulingData?.otSlot?.split('@')?.[1]?.trim(), "TBD")}
                                          </p>
                                       </div>
                                    </div>

                                    {/* Clinical Team Assignment Card */}
                                    <div className="bg-white p-5 rounded-2xl border border-violet-100 shadow-sm space-y-4 text-left">
                                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Assigned Clinical Team</p>
                                       <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                          <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                                             <p className="text-[9px] font-black text-indigo-400 uppercase leading-tight">Surgeon</p>
                                             <p className="text-xs font-black text-indigo-900 truncate">
                                                {lead.surgeonType === 'other' && lead.assignedSurgeonOtherName
                                                   ? `Dr. ${lead.assignedSurgeonOtherName}`
                                                   : `Dr. ${lead.assignedSurgeon?.user?.firstName || 'Assigned'} ${lead.assignedSurgeon?.user?.lastName || 'Surgeon'}`}
                                             </p>
                                          </div>
                                          <div className="p-3 bg-purple-50/50 rounded-xl border border-purple-100">
                                             <p className="text-[9px] font-black text-purple-400 uppercase leading-tight">Anesthesiologist</p>
                                             <p className="text-xs font-black text-purple-900 truncate">
                                                {lead.anesthesiologistType === 'other' && lead.assignedanesthesiologistOtherName
                                                   ? `Dr. ${lead.assignedanesthesiologistOtherName}`
                                                   : `Dr. ${lead.assignedanesthesiologist?.user?.firstName || 'Assigned'} ${lead.assignedanesthesiologist?.user?.lastName || 'Anesthesiologist'}`}
                                             </p>
                                          </div>
                                          <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                                             <p className="text-[9px] font-black text-amber-400 uppercase leading-tight">Hospital Facility</p>
                                             <p className="text-xs font-black text-amber-900 truncate">{lead.assignedHospital?.hospitalName || 'Assigned Facility'}</p>
                                          </div>
                                       </div>
                                    </div>
                                 </div>
                              )}

                              {/* Read-Only Historical view for Payment Links Tab */}
                              {displayStatus === 'payment_pending' && !['scheduling', 'payment_pending'].includes(lead.status) && (
                                 <div className="bg-[#143E78] rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden space-y-6 animate-fadeIn pb-10 text-left">
                                    <div className="absolute top-0 right-0 p-8 opacity-10">
                                       <LayoutGrid className="w-24 h-24 rotate-12" />
                                    </div>
                                    <div className="relative z-10 space-y-6">
                                       <div className="flex justify-between items-start">
                                          <div>
                                             <h3 className="text-xl font-black uppercase tracking-widest">Surgical Settlement Confirmed</h3>
                                             <p className="text-blue-200 text-[10px] font-bold mt-1 uppercase tracking-widest">Final Bill Details</p>
                                          </div>
                                          <div className="bg-emerald-500 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-950/40">
                                             Settled
                                          </div>
                                       </div>

                                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                          <div className="space-y-4">
                                             <div className="flex justify-between items-center text-blue-100 font-bold px-1 border-b border-white/10 pb-2">
                                                <span className="text-[11px] uppercase tracking-widest">Doxez Platform Fee</span>
                                                <span className="text-sm bg-blue-800 px-3 py-1 rounded-lg border border-blue-700">{lead.billingDetails?.platformFee || 5}%</span>
                                             </div>
                                             <div className="flex justify-between items-center text-blue-100 font-bold px-1 border-b border-white/10 pb-2">
                                                <span className="text-[11px] uppercase tracking-widest">GST on Platform Fee</span>
                                                <span className="text-sm bg-blue-800 px-3 py-1 rounded-lg border border-blue-700">{lead.billingDetails?.gstPercent || 18}%</span>
                                             </div>
                                          </div>

                                          <div className="bg-white/5 rounded-3xl p-6 flex flex-col justify-center gap-3">
                                             <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase">
                                                <span>Procedure Amount</span>
                                                <span className="text-white text-xs">₹{(lead.billingDetails?.procedureAmount || 0).toLocaleString()}</span>
                                             </div>
                                             <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase">
                                                <span>Platform Booking Fee</span>
                                                <span className="text-white text-xs">₹{(lead.billingDetails?.platformFeeAmount || 0).toLocaleString()}</span>
                                             </div>
                                             <div className="flex justify-between items-center text-blue-300 text-[10px] font-black uppercase">
                                                <span>GST on Platform Fee</span>
                                                <span className="text-white text-xs">₹{(lead.billingDetails?.gstAmount || 0).toLocaleString()}</span>
                                             </div>

                                             <div className="mt-2 pt-4 border-t border-white/10 text-center">
                                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Total Paid by Hospital</p>
                                                <p className="text-3xl font-black text-emerald-400 drop-shadow-md">₹{(lead.billingDetails?.totalPayableAmount || 0).toLocaleString()}</p>
                                             </div>
                                          </div>
                                       </div>

                                       <div className="bg-white/5 p-4 rounded-2xl border border-white/5 flex items-center gap-3 text-left">
                                          <Activity className="w-5 h-5 text-emerald-400 flex-shrink-0 animate-pulse" />
                                          <div>
                                             <p className="text-[9px] font-black text-blue-200 uppercase tracking-widest leading-none">Payment Reference ID</p>
                                             <p className="text-xs font-bold text-white mt-1.5 font-mono">
                                                {lead.razorpayDetails?.lastPaymentId || lead.razorpayDetails?.paymentLinkId || 'MANUAL_OVERRIDE_SETTLED'}
                                             </p>
                                          </div>
                                       </div>
                                    </div>
                                 </div>
                              )}



                              {/* 💳 STAGE 7B: Payment Verification & OTP Release */}
                              {displayStatus === 'paid' && (
                                 <div className="space-y-8 animate-fadeIn">
                                    <div className="bg-emerald-50 rounded-[2.5rem] border border-emerald-100 p-8 shadow-sm">
                                       <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                                          <div className="flex items-center gap-6 text-center md:text-left flex-col md:flex-row">
                                             <div className="w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center text-white shadow-xl shadow-emerald-200">
                                                <ShieldCheck className="w-10 h-10" />
                                             </div>
                                             <div>
                                                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.2em] mb-1 block">
                                                   {isPastSurgeryCase
                                                      ? 'Past Surgery: Payment Confirmed'
                                                      : lead?.pastPaymentProof?.url
                                                         ? 'Payment Status: Receipt Uploaded'
                                                         : 'Payment Status: Confirmed'}
                                                </span>
                                                <h2 className="text-2xl font-black text-[#143E78] uppercase tracking-tighter">
                                                   {isPastSurgeryCase
                                                      ? 'Past Surgery Registered'
                                                      : lead?.pastPaymentProof?.url
                                                         ? 'Receipt Uploaded'
                                                         : 'Payment Confirmed'}
                                                </h2>
                                                <p className="text-xs font-bold text-slate-500 mt-1 max-w-[400px]">
                                                   {isPastSurgeryCase
                                                      ? 'Surgery was already completed in the past. Verification codes are automatically skipped.'
                                                      : lead?.pastPaymentProof?.url
                                                         ? 'Payment receipt has been uploaded. Please review the surgical details below and click to complete manual doctor verification.'
                                                         : 'Payment has been successfully received from the hospital. Please review the surgical details below and click to complete manual doctor verification.'}
                                                </p>

                                                <div className="mt-4 p-3 bg-white/60 border border-emerald-100 rounded-2xl flex items-center gap-4 animate-fadeIn">
                                                   <div>
                                                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Transaction ID</p>
                                                      <p className="text-[11px] font-black text-emerald-700 mt-1">
                                                         {lead.razorpayDetails?.lastPaymentId ||
                                                            lead.associatedSurgery?.paymentLinkDetails?.razorpayPaymentId ||
                                                            lead.razorpayDetails?.paymentLinkId ||
                                                            'MANUAL_SETTLEMENT'}
                                                      </p>
                                                   </div>
                                                   <div className="w-px h-8 bg-emerald-100" />
                                                   <div>
                                                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Confirmed Amount</p>
                                                      <p className="text-[11px] font-black text-[#143E78] mt-1">
                                                         ₹{(lead.razorpayDetails?.paidAmount || lead.associatedSurgery?.surgeryCost || lead.billingDetails?.totalPayableAmount || 0).toLocaleString()}
                                                      </p>
                                                   </div>
                                                </div>
                                             </div>
                                          </div>
                                          {updating ? (
                                             <div className="w-full md:w-auto px-10 py-5 bg-[#143E78] rounded-3xl relative overflow-hidden flex items-center justify-center gap-3 min-w-[200px]">
                                                {/* Shimmer sweep */}
                                                <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                                                {/* Skeleton blobs */}
                                                <div className="w-4 h-4 rounded-full bg-blue-500/60 animate-pulse" />
                                                <div className="h-3 w-44 rounded-full bg-blue-500/60 animate-pulse" />
                                             </div>
                                          ) : isStageReadOnly ? (
                                             <div className="w-full md:w-auto px-8 py-4 bg-emerald-100 text-emerald-800 font-black rounded-3xl uppercase text-xs tracking-widest border border-emerald-300 flex items-center justify-center gap-2">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Payment & Verification Confirmed
                                             </div>
                                          ) : isPastSurgeryCase ? (
                                             <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                                                <button
                                                   type="button"
                                                   onClick={async () => {
                                                      if (!id) return;
                                                      setSendingManualNotif(true);
                                                      try {
                                                         const res = await api.post(`/leads/${id}/verify-release-otps`);
                                                         toast.success(typeof res.data?.message === 'string' ? res.data.message : 'Surgery verification codes sent to doctors & hospital successfully!');
                                                      } catch (error) {
                                                         toast.error(typeof error.response?.data?.message === 'string' ? error.response.data.message : 'Failed to send verification codes');
                                                      } finally {
                                                         setSendingManualNotif(false);
                                                      }
                                                   }}
                                                   disabled={sendingManualNotif}
                                                   className="px-6 py-5 bg-indigo-700 hover:bg-indigo-800 text-white font-black rounded-3xl uppercase text-xs tracking-widest transition-all shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                                                >
                                                   {sendingManualNotif ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4 text-amber-400" />}
                                                   Send Verification Code
                                                </button>
                                                <button
                                                   onClick={handleVerifyAndReleaseOTPs}
                                                   disabled={updating}
                                                   className="px-10 py-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black rounded-3xl uppercase text-xs tracking-widest transition-all shadow-xl flex items-center justify-center gap-3 active:scale-95 cursor-pointer"
                                                >
                                                   <CheckCircle className="w-4 h-4 text-slate-950" /> Skip Verification & Move to Post-Op
                                                </button>
                                             </div>
                                          ) : (
                                             <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                                                <button
                                                   type="button"
                                                   onClick={handleVerifyAndReleaseOTPs}
                                                   disabled={updating}
                                                   className="w-full md:w-auto px-6 py-5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-black rounded-3xl uppercase text-xs tracking-widest transition-all border border-emerald-200 shadow-md flex items-center justify-center gap-2 active:scale-95 cursor-pointer disabled:opacity-50"
                                                >
                                                   {updating ? <Loader2 className="w-4 h-4 animate-spin text-emerald-600" /> : <UserCheck className="w-4 h-4 text-emerald-600" />}
                                                   <span>Manual Doctor Verification</span>
                                                </button>
                                             </div>
                                          )}
                                       </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                       {/* Surgeon Card */}
                                       <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-xl shadow-slate-200/40 flex flex-col justify-between hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
                                          <div>
                                             <div className="flex items-start gap-4 mb-4">
                                                <div className="w-14 h-14 shrink-0 bg-blue-50/80 rounded-[1.25rem] flex items-center justify-center text-blue-600 group-hover:scale-105 group-hover:bg-blue-100 transition-all">
                                                   <UserPlus className="w-7 h-7" />
                                                </div>
                                                <div className="flex flex-col min-w-0 pt-0.5 w-full">
                                                   <span className="w-max px-2.5 py-1 bg-blue-50/80 text-blue-600 rounded-md text-[8px] font-black uppercase tracking-widest mb-2 border border-blue-100/50">Surgeon</span>
                                                   <h3 className="text-[1.1rem] font-black text-[#143E78] uppercase tracking-tighter leading-tight break-words">
                                                      {lead.surgeonType === 'other' && lead.assignedSurgeonOtherName
                                                         ? `Dr. ${lead.assignedSurgeonOtherName}`
                                                         : (lead.assignedSurgeon?.user?.firstName ? `Dr. ${lead.assignedSurgeon.user.firstName} ${lead.assignedSurgeon.user.lastName}` : 'Unassigned')}
                                                   </h3>
                                                   <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1.5 break-words">
                                                      {lead.surgeonType === 'other'
                                                         ? (lead.assignedSurgeonOtherOCMR ? `External Doctor (OCMR: ${lead.assignedSurgeonOtherOCMR})` : 'External Doctor')
                                                         : (lead.assignedSurgeon?.specialization || 'Clinical Specialist')}
                                                   </p>
                                                </div>
                                             </div>
                                          </div>
                                          <div className="mt-4 pt-4 border-t border-slate-50 flex flex-col gap-2.5">
                                             {((lead.surgeonType === 'other' && lead.assignedSurgeonOtherPhone) || lead.assignedSurgeon?.user?.phone) && (
                                                <div className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors cursor-default">
                                                   <div className="w-8 h-8 shrink-0 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-500">
                                                      <PhoneCall className="w-4 h-4" />
                                                   </div>
                                                   <div className="flex flex-col min-w-0">
                                                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Phone</span>
                                                      <span className="text-xs font-bold text-slate-700 truncate">
                                                         {lead.surgeonType === 'other' ? lead.assignedSurgeonOtherPhone : lead.assignedSurgeon?.user?.phone}
                                                      </span>
                                                   </div>
                                                </div>
                                             )}
                                             {((lead.surgeonType === 'other' && lead.assignedSurgeonOtherEmail) || lead.assignedSurgeon?.user?.email) && (
                                                <div className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors cursor-default">
                                                   <div className="w-8 h-8 shrink-0 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500">
                                                      <Mail className="w-4 h-4" />
                                                   </div>
                                                   <div className="flex flex-col min-w-0 w-full">
                                                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Email</span>
                                                      <span className="text-xs font-bold text-slate-700 truncate" title={lead.surgeonType === 'other' ? lead.assignedSurgeonOtherEmail : lead.assignedSurgeon?.user?.email}>
                                                         {lead.surgeonType === 'other' ? lead.assignedSurgeonOtherEmail : lead.assignedSurgeon?.user?.email}
                                                      </span>
                                                   </div>
                                                </div>
                                             )}
                                          </div>
                                       </div>

                                       {/* anesthesiologist Card */}
                                       <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-xl shadow-slate-200/40 flex flex-col justify-between hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
                                          <div>
                                             <div className="flex items-start gap-4 mb-4">
                                                <div className="w-14 h-14 shrink-0 bg-indigo-50/80 rounded-[1.25rem] flex items-center justify-center text-indigo-600 group-hover:scale-105 group-hover:bg-indigo-100 transition-all">
                                                   <Stethoscope className="w-7 h-7" />
                                                </div>
                                                <div className="flex flex-col min-w-0 pt-0.5 w-full">
                                                   <span className="w-max px-2.5 py-1 bg-indigo-50/80 text-indigo-600 rounded-md text-[8px] font-black uppercase tracking-widest mb-2 border border-indigo-100/50">Anesthesiologist</span>
                                                   <h3 className="text-[1.1rem] font-black text-[#143E78] uppercase tracking-tighter leading-tight break-words">
                                                      {lead.anesthesiologistType === 'other' && lead.assignedanesthesiologistOtherName
                                                         ? `Dr. ${lead.assignedanesthesiologistOtherName}`
                                                         : (lead.assignedanesthesiologist?.user?.firstName ? `Dr. ${lead.assignedanesthesiologist.user.firstName} ${lead.assignedanesthesiologist.user.lastName}` : 'Unassigned')}
                                                   </h3>
                                                   <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1.5 break-words">
                                                      {lead.anesthesiologistType === 'other'
                                                         ? (lead.assignedanesthesiologistOtherOCMR ? `External Doctor (OCMR: ${lead.assignedanesthesiologistOtherOCMR})` : 'External Doctor')
                                                         : (lead.assignedanesthesiologist?.specialization || 'Clinical Anesthesia')}
                                                   </p>
                                                </div>
                                             </div>
                                          </div>
                                          <div className="mt-4 pt-4 border-t border-slate-50 flex flex-col gap-2.5">
                                             {((lead.anesthesiologistType === 'other' && lead.assignedanesthesiologistOtherPhone) || lead.assignedanesthesiologist?.user?.phone) && (
                                                <div className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors cursor-default">
                                                   <div className="w-8 h-8 shrink-0 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-500">
                                                      <PhoneCall className="w-4 h-4" />
                                                   </div>
                                                   <div className="flex flex-col min-w-0">
                                                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Phone</span>
                                                      <span className="text-xs font-bold text-slate-700 truncate">
                                                         {lead.anesthesiologistType === 'other' ? lead.assignedanesthesiologistOtherPhone : lead.assignedanesthesiologist?.user?.phone}
                                                      </span>
                                                   </div>
                                                </div>
                                             )}
                                             {((lead.anesthesiologistType === 'other' && lead.assignedanesthesiologistOtherEmail) || lead.assignedanesthesiologist?.user?.email) && (
                                                <div className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors cursor-default">
                                                   <div className="w-8 h-8 shrink-0 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500">
                                                      <Mail className="w-4 h-4" />
                                                   </div>
                                                   <div className="flex flex-col min-w-0 w-full">
                                                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Email</span>
                                                      <span className="text-xs font-bold text-slate-700 truncate" title={lead.anesthesiologistType === 'other' ? lead.assignedanesthesiologistOtherEmail : lead.assignedanesthesiologist?.user?.email}>
                                                         {lead.anesthesiologistType === 'other' ? lead.assignedanesthesiologistOtherEmail : lead.assignedanesthesiologist?.user?.email}
                                                      </span>
                                                   </div>
                                                </div>
                                             )}
                                          </div>
                                       </div>

                                       {/* Facility Card */}
                                       <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-xl shadow-slate-200/40 flex flex-col justify-between hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
                                          <div>
                                             <div className="flex items-start gap-4 mb-4">
                                                <div className="w-14 h-14 shrink-0 bg-emerald-50/80 rounded-[1.25rem] flex items-center justify-center text-emerald-600 group-hover:scale-105 group-hover:bg-emerald-100 transition-all">
                                                   <Building2 className="w-7 h-7" />
                                                </div>
                                                <div className="flex flex-col min-w-0 pt-0.5 w-full">
                                                   <span className="w-max px-2.5 py-1 bg-emerald-50/80 text-emerald-600 rounded-md text-[8px] font-black uppercase tracking-widest mb-2 border border-emerald-100/50">Facility</span>
                                                   <h3 className="text-[1.1rem] font-black text-[#143E78] uppercase tracking-tighter leading-tight break-words">
                                                      {lead.assignedHospital?.hospitalName || "Partner Facility"}
                                                   </h3>
                                                   <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1.5 break-words">{lead.assignedHospital?.address?.city || 'Selected Center'}</p>
                                                </div>
                                             </div>
                                          </div>
                                          <div className="mt-4 pt-4 border-t border-slate-50 flex flex-col gap-2.5">
                                             {(lead.assignedHospital?.contactPerson?.phone || lead.hospitalAlternateContact?.whatsapp) && (
                                                <div className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors cursor-default">
                                                   <div className="w-8 h-8 shrink-0 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-500">
                                                      <PhoneCall className="w-4 h-4" />
                                                   </div>
                                                   <div className="flex flex-col min-w-0">
                                                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Phone</span>
                                                      <span className="text-xs font-bold text-slate-700 truncate">{lead.hospitalAlternateContact?.whatsapp || lead.assignedHospital.contactPerson?.phone}</span>
                                                   </div>
                                                </div>
                                             )}
                                             {(lead.assignedHospital?.contactPerson?.email || lead.assignedHospital?.mailId) && (
                                                <div className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors cursor-default">
                                                   <div className="w-8 h-8 shrink-0 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500">
                                                      <Mail className="w-4 h-4" />
                                                   </div>
                                                   <div className="flex flex-col min-w-0 w-full">
                                                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Email</span>
                                                      <span className="text-xs font-bold text-slate-700 truncate" title={lead.assignedHospital.contactPerson?.email || lead.assignedHospital.mailId}>{lead.assignedHospital.contactPerson?.email || lead.assignedHospital.mailId}</span>
                                                   </div>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    </div>

                                    {/* Final Ready Box */}
                                    <div className="bg-[#143E78] rounded-[2.5rem] sm:rounded-[3rem] p-6 sm:p-10 text-white flex flex-col md:flex-row justify-between items-center gap-6 sm:gap-8 shadow-2xl relative overflow-hidden group">
                                       <div className="absolute top-0 right-0 p-8 sm:p-10 opacity-10 scale-150 transition-transform group-hover:rotate-12 pointer-events-none">
                                          <Clock className="w-32 h-32" />
                                       </div>
                                       <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-8 w-full">
                                          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-[2rem] flex items-center justify-center border border-white/20 flex-shrink-0">
                                             <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400" />
                                          </div>
                                          <div className="text-center sm:text-left flex-1 min-w-0">
                                             <span className="text-[8px] sm:text-[10px] font-black text-emerald-400 uppercase tracking-[0.3em] block mb-2 leading-none">Confirmed Surgical Slot</span>
                                             <h3 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight leading-tight whitespace-nowrap">
                                                {lead.schedulingData?.surgeryDate ? new Date(lead.schedulingData.surgeryDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Date TBD"}
                                             </h3>
                                             <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 sm:gap-4 mt-4">
                                                <div className="px-3 sm:px-4 py-1.5 bg-white/10 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-widest border border-white/10 flex items-center gap-2">
                                                   <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-blue-400 rounded-full animate-pulse" />
                                                   {lead.schedulingData?.otSlot || "Time TBD"}
                                                </div>
                                                <div className="px-3 sm:px-4 py-1.5 bg-emerald-500 text-white rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-950/20">
                                                   Verified Ready
                                                </div>
                                             </div>
                                          </div>
                                          <p className="text-[9px] sm:text-[11px] font-bold text-blue-200 uppercase tracking-tight leading-relaxed max-w-[280px] text-center sm:text-right">
                                             {isPastSurgeryCase
                                                ? 'Surgery was completed in the past. Verification codes are skipped.'
                                                : 'Case is verified & ready. Click above to complete manual doctor verification.'}
                                          </p>
                                       </div>
                                    </div>
                                 </div>
                              )}


                              {/* 🏥 STAGE 8: In-Surgery Protocol */}
                              {displayStatus === 'surgery' && (
                                 <div className="py-8 sm:py-12 bg-white rounded-[2rem] sm:rounded-[3rem] border border-blue-50 shadow-sm animate-fadeIn text-center space-y-6 sm:space-y-8">
                                    {((lead?.surgicalData?.surgicalReschedule?.isRescheduling && !lead?.surgicalData?.surgicalReschedule?.isPenaltyPaid) ||
                                       (lead?.surgicalReschedule?.isRescheduling && !lead?.surgicalReschedule?.isPenaltyPaid)) && (
                                          <div className="mx-6 p-5 bg-amber-50 rounded-2xl border-2 border-amber-200 text-amber-900 text-center space-y-3">
                                             <p className="text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2">
                                                <AlertTriangle className="w-5 h-5 text-amber-600 animate-pulse" />
                                                Reschedule Penalty Settlement Pending (₹{(lead.surgicalData?.surgicalReschedule?.penaltyAmount || lead.surgicalReschedule?.penaltyAmount || 0).toLocaleString()})
                                             </p>
                                             <p className="text-[10px] font-bold text-amber-700 uppercase">
                                                Penalty payment link sent to Hospital. Once the hospital completes payment, click the button below to verify and advance to Stage 6: OT Scheduling.
                                             </p>
                                             <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
                                                <button
                                                   type="button"
                                                   onClick={() => handleSyncPaymentStatus(false)}
                                                   disabled={updating}
                                                   className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-black rounded-xl text-[10px] uppercase tracking-widest shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                                                >
                                                   <RefreshCcw className={`w-3.5 h-3.5 ${updating ? 'animate-spin' : ''}`} />
                                                   {updating ? 'Checking Razorpay...' : 'Check / Sync Penalty Payment Status'}
                                                </button>
                                                <button
                                                   type="button"
                                                   onClick={handleConfirmPenaltyManually}
                                                   disabled={updating}
                                                   className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-[10px] uppercase tracking-widest shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                                                >
                                                   <CheckCircle className="w-3.5 h-3.5" />
                                                   Confirm Penalty Manually
                                                </button>
                                             </div>
                                          </div>
                                       )}
                                    <div className="space-y-3 sm:space-y-4">
                                       <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto text-white shadow-xl shadow-blue-200 animate-pulse">
                                          <Activity className="w-8 h-8 sm:w-10 sm:h-10" />
                                       </div>
                                       <div>
                                          <h3 className="text-xl sm:text-2xl font-black text-[#143E78] uppercase tracking-tighter">Surgery in Progress</h3>
                                          <p className="text-slate-400 font-black uppercase text-[8px] sm:text-[9px] tracking-[0.2em] mt-1">The patient is currently in surgery</p>
                                       </div>
                                    </div>

                                    {/* 🩺 Manual Doctor Verification Inline Form */}
                                    <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 text-left">
                                       <div className="bg-slate-50/80 rounded-[2rem] p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
                                          <div className="flex items-center gap-3 border-b border-slate-200/80 pb-4">
                                             <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-black">
                                                <UserCheck className="w-5 h-5" />
                                             </div>
                                             <div>
                                                <h4 className="text-base sm:text-lg font-black text-slate-900 uppercase tracking-tight">Manual Doctor Verification</h4>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Log Timings & Advance directly to Post-Op</p>
                                             </div>
                                          </div>

                                          <div className="space-y-4">
                                             {/* Operating Surgeon Arrival Time */}
                                             <div className="space-y-1.5">
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                                                   Operating Surgeon Hospital Arrival Time *
                                                </label>
                                                <input
                                                   type="datetime-local"
                                                   disabled={isStageReadOnly}
                                                   value={manualVerifyForm.surgeonArrivalTime}
                                                   onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, surgeonArrivalTime: e.target.value })}
                                                   className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                />
                                             </div>

                                             {/* Anesthesiologist Arrival Time */}
                                             {Boolean(lead?.assignedanesthesiologist || lead?.assignedanesthesiologistOtherName || lead?.anesthesiologistType === 'other') && (
                                                <div className="space-y-1.5">
                                                   <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                                                      Anesthesiologist Hospital Arrival Time *
                                                   </label>
                                                   <input
                                                      type="datetime-local"
                                                      disabled={isStageReadOnly}
                                                      value={manualVerifyForm.anesthesiologistArrivalTime}
                                                      onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, anesthesiologistArrivalTime: e.target.value })}
                                                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                   />
                                                </div>
                                             )}

                                             {/* Surgery Start Time */}
                                             <div className="space-y-1.5">
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                                                   Surgery Start Time *
                                                </label>
                                                <input
                                                   type="datetime-local"
                                                   disabled={isStageReadOnly}
                                                   value={manualVerifyForm.startTime}
                                                   onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, startTime: e.target.value })}
                                                   className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                />
                                             </div>

                                             {/* Surgery End Time */}
                                             <div className="space-y-1.5">
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                                                   Surgery End Time *
                                                </label>
                                                <input
                                                   type="datetime-local"
                                                   disabled={isStageReadOnly}
                                                   value={manualVerifyForm.endTime}
                                                   onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, endTime: e.target.value })}
                                                   className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                />
                                             </div>

                                             {/* Verification Remarks / Notes */}
                                             <div className="space-y-1.5">
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                                                   Verification Remarks / Notes
                                                </label>
                                                <textarea
                                                   rows={3}
                                                   disabled={isStageReadOnly}
                                                   placeholder="Enter notes (e.g. Both surgeon and anesthesiologist arrived at hospital and surgery started...)"
                                                   value={manualVerifyForm.notes}
                                                   onChange={(e) => setManualVerifyForm({ ...manualVerifyForm, notes: e.target.value })}
                                                   className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 transition-all resize-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                />
                                             </div>

                                             {/* Action Button: Verify & Move to Post-Op */}
                                             {!isStageReadOnly ? (
                                                <button
                                                   type="button"
                                                   onClick={handleManualDoctorVerify}
                                                   disabled={updating}
                                                   className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-emerald-200 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99]"
                                                >
                                                   {updating ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <CheckCircle className="w-4 h-4" />}
                                                   <span>Verify & Move to Post-Op</span>
                                                </button>
                                             ) : (
                                                <div className="w-full py-3.5 bg-emerald-100 text-emerald-800 rounded-2xl text-xs font-black uppercase tracking-wider text-center border border-emerald-200 flex items-center justify-center gap-2">
                                                   <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                                   <span>Surgery Timings Verified & Completed</span>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    </div>

                                    {!isStageReadOnly && (
                                       <div className="max-w-2xl mx-auto px-4 sm:px-6 mt-4 flex flex-col md:flex-row gap-2 sm:gap-3 w-full">
                                          <button
                                             onClick={() => {
                                                setRescheduleForm(prev => ({
                                                   ...prev,
                                                   newSurgeryDate: lead.schedulingData?.surgeryDate ? new Date(lead.schedulingData.surgeryDate).toISOString().split('T')[0] : '',
                                                   isAbortingView: false
                                                }));
                                                setShowRescheduleModal(true);
                                             }}

                                             className="flex-1 py-3 sm:py-4 bg-slate-100 hover:bg-slate-200 text-slate-500 font-black rounded-2xl sm:rounded-3xl uppercase text-[9px] sm:text-[10px] tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer"
                                          >
                                             <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Reschedule OT
                                          </button>
                                          <button
                                             onClick={() => {
                                                setRescheduleForm({
                                                   faultBy: '',
                                                   penaltyAmount: 0,
                                                   notes: '',
                                                   isAborted: false,
                                                   abortionFault: '',
                                                   abortionPenaltyPercentage: 0,
                                                   useAlternate: false,
                                                   alternateEmail: '',
                                                   alternateWhatsapp: '',
                                                   isAbortingView: true,
                                                   penaltyPercentage: 0,
                                                   abortReason: '',
                                                   abortFault: ''
                                                });
                                                setShowRescheduleModal(true);
                                             }}
                                             className="flex-1 py-3 sm:py-4 bg-red-50 hover:bg-red-100 text-red-500 font-black rounded-2xl sm:rounded-3xl uppercase text-[9px] sm:text-[10px] tracking-wider transition-all flex items-center justify-center gap-2 border border-red-100 cursor-pointer"
                                          >
                                             <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Abort Surgery
                                          </button>
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* Default Fallback for late stages */}
                              {/* 🛡️ STAGE 9: Post-Op Clinical Monitoring & Observation */}
                              {displayStatus === 'closure' && (
                                 <div className="space-y-8 animate-fadeIn">
                                    {/* Surgeon Comprehensive Bio Profile */}
                                    <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] border border-slate-100 p-5 sm:p-8 shadow-sm overflow-hidden relative">
                                       <div className="absolute top-0 right-0 p-8 sm:p-12 opacity-[0.03] pointer-events-none">
                                          <ShieldCheck className="w-40 h-40 sm:w-56 sm:h-56" />
                                       </div>
                                       <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start gap-6 sm:gap-8">
                                          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-7 w-full">
                                             <div className="relative flex-shrink-0">
                                                <div className="w-20 h-20 sm:w-28 sm:h-28 bg-blue-50 rounded-2xl sm:rounded-[2.5rem] flex items-center justify-center border-4 border-white shadow-xl overflow-hidden">
                                                   {lead.assignedSurgeon?.user?.profileImage ? (
                                                      <img src={lead.assignedSurgeon.user.profileImage} alt="Surgeon" className="w-full h-full object-cover" />
                                                   ) : (
                                                      <UserCheck className="w-8 h-8 sm:w-12 sm:h-12 text-blue-500" />
                                                   )}
                                                </div>
                                                <div className="absolute -bottom-1.5 -right-1.5 sm:-bottom-2 sm:-right-2 bg-emerald-500 text-white p-1.5 sm:p-2.5 rounded-lg sm:rounded-xl shadow-lg border-2 sm:border-4 border-white">
                                                   <Activity className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse" />
                                                </div>
                                             </div>
                                             <div className="text-center sm:text-left flex-1 min-w-0">
                                                <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2 sm:gap-3 mb-2.5">
                                                   <span className="px-3 sm:px-4 py-1.5 bg-blue-100 text-blue-600 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-widest leading-none">Operating Surgeon</span>
                                                </div>
                                                <h2 className="text-xl sm:text-3xl font-black text-[#143E78] tracking-tighter leading-tight truncate">
                                                   {(() => {
                                                      const rawName = lead.surgeonType === 'other' && lead.assignedSurgeonOtherName
                                                         ? lead.assignedSurgeonOtherName
                                                         : `${lead.assignedSurgeon?.user?.firstName || 'Assigned'} ${lead.assignedSurgeon?.user?.lastName || 'Surgeon'}`;
                                                      const clean = rawName.replace(/^(dr\.\s*)+/i, '').trim();
                                                      return `Dr. ${clean}`;
                                                   })()}
                                                </h2>
                                                <p className="text-[10px] sm:text-xs font-bold text-slate-500 mt-1">
                                                   {lead.surgeonType === 'other'
                                                      ? (lead.assignedSurgeonOtherOCMR ? `External Doctor • OCMR: ${lead.assignedSurgeonOtherOCMR}` : 'External Doctor')
                                                      : `${lead.assignedSurgeon?.qualification || 'MBBS'} • ${lead.assignedSurgeon?.specialization || 'Surgeon'}`}
                                                </p>

                                                <div className="flex justify-center sm:justify-start items-center gap-4 mt-3">
                                                   <div className="flex items-center gap-2 text-slate-400">
                                                      <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-500" />
                                                      <span className="text-[10px] sm:text-[11px] font-black text-slate-600 tracking-tight">
                                                         {(lead.surgeonType === 'other' ? lead.assignedSurgeonOtherPhone : lead.assignedSurgeon?.user?.phone) || 'No contact'}
                                                      </span>
                                                   </div>
                                                </div>
                                             </div>
                                          </div>
                                          <div className="flex flex-col gap-3 w-full lg:w-64 mt-2 sm:mt-0">
                                             <button
                                                onClick={() => {
                                                   const sPhone = lead.surgeonType === 'other' ? lead.assignedSurgeonOtherPhone : lead.assignedSurgeon?.user?.phone;
                                                   if (sPhone) window.location.href = `tel:${sPhone}`;
                                                }}
                                                className="w-full flex items-center justify-center gap-3 px-5 sm:px-6 py-3.5 sm:py-4 bg-[#143E78] text-white rounded-xl sm:rounded-2xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest hover:bg-blue-900 transition-all shadow-xl shadow-blue-900/10 cursor-pointer"
                                             >
                                                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Call Surgeon
                                             </button>
                                             <button
                                                onClick={() => navigate(`/appointments/create?fromLeadId=${id}&isPostOp=true&returnUrl=${encodeURIComponent('/leads/' + id)}`)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-emerald-600 text-white rounded-xl sm:rounded-2xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-900/10 cursor-pointer"
                                             >
                                                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Schedule Post-Op Visit
                                             </button>
                                          </div>
                                       </div>
                                    </div>

                                    {/* Monitoring Dashboard */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                                       <div className="bg-slate-900 rounded-[1.5rem] sm:rounded-[2rem] p-5 sm:p-7 text-white relative overflow-hidden">
                                          <div className="absolute top-0 right-0 p-8 sm:p-12 opacity-5 pointer-events-none">
                                             <Clock className="w-28 h-28 sm:w-36 sm:h-36" />
                                          </div>
                                          <div className="relative z-10">
                                             <div className="flex items-center gap-2.5 mb-5">
                                                <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                                   <Calendar className="w-4 h-4 text-white" />
                                                </div>
                                                <div>
                                                   <h4 className="text-sm sm:text-base font-black uppercase tracking-tighter leading-tight">Observation Tracker</h4>
                                                   <p className="text-[7px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest">Post-Op Monitoring Period</p>
                                                </div>
                                             </div>

                                             <div className="space-y-5">
                                                <div className="flex justify-between items-end">
                                                   <div>
                                                      <p className="text-[9px] font-black text-white/50 uppercase tracking-[0.2em] mb-1">Visits Progress</p>
                                                      <p className="text-2xl font-black whitespace-nowrap">{sortedMonitoringLogs?.length || 0}/3 Required</p>
                                                   </div>
                                                   <div className="text-right">
                                                      <span className="px-2.5 py-1 bg-amber-500/20 text-amber-500 rounded-full text-[8px] font-black uppercase border border-amber-500/30 whitespace-nowrap">Action Required</span>
                                                   </div>
                                                </div>

                                                <div className="h-3 w-full bg-white/5 rounded-full overflow-hidden p-0.5 border border-white/5">
                                                   <div
                                                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-1000"
                                                      style={{ width: `${Math.min(((sortedMonitoringLogs?.length || 0) / 3) * 100, 100)}%` }}
                                                   />
                                                </div>

                                                <div className="grid grid-cols-3 gap-2 pt-1">
                                                   {[0, 1, 2].map(index => {
                                                      const log = sortedMonitoringLogs[index];
                                                      const hasLog = !!log;
                                                      return (
                                                         <div key={index} className={`text-center p-2 rounded-xl border-2 transition-all ${hasLog ? 'bg-blue-600/20 border-blue-500/30 text-blue-400' : 'bg-white/5 border-white/5 text-white/30'}`}>
                                                            <p className="text-[9px] font-black uppercase mb-1">Day {index + 1}</p>
                                                            {hasLog ? (
                                                               <div className="flex flex-col items-center gap-1">
                                                                  <CheckCircle2 className="w-3.5 h-3.5 mx-auto" />
                                                                  <span className="text-[9px] font-black text-white block mt-0.5">
                                                                     {new Date(log.visitDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                                  </span>
                                                                  <span className="text-[8px] font-black text-blue-300 block bg-blue-500/20 px-1.5 py-0.5 rounded-full mt-0.5">
                                                                     {log.startTime}-{log.endTime}
                                                                  </span>
                                                               </div>
                                                            ) : <Circle className="w-3.5 h-3.5 mx-auto mt-1" />}
                                                         </div>
                                                      );
                                                   })}
                                                </div>
                                             </div>
                                          </div>
                                       </div>

                                       <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] border border-slate-100 p-5 sm:p-7 shadow-sm flex flex-col h-full">
                                          <div className="flex justify-between items-center mb-5 gap-3">
                                             <div>
                                                <h4 className="text-[11px] sm:text-xs font-black text-[#143E78] uppercase tracking-widest">Visit History</h4>
                                                <p className="text-[8px] sm:text-[9px] font-bold text-slate-400 mt-0.5">Finalize after 3 entries</p>
                                             </div>
                                             {!isStageReadOnly && (
                                                <button
                                                   onClick={() => {
                                                      setMonitoringLog({ visitDate: '', startTime: '', endTime: '', notes: '', logId: null });
                                                      setShowAddMonitoringLogModal(true);
                                                   }}
                                                   className="p-2.5 sm:p-3 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all shadow-sm active:scale-95"
                                                >
                                                   <Plus className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                                                </button>
                                             )}
                                          </div>

                                          <div className="space-y-2.5 flex-1 overflow-y-auto max-h-[250px] pr-2 custom-scrollbar">
                                             {lead.surgicalData?.postOpMonitoring?.logs?.length > 0 ? (
                                                [...lead.surgicalData.postOpMonitoring.logs].reverse().map((log, idx) => (
                                                   <div key={log._id || idx} className="group p-3 sm:p-4 bg-slate-50/50 rounded-xl border border-slate-100 flex items-center justify-between gap-2.5">
                                                      <div className="flex items-center gap-2.5 sm:gap-3 overflow-hidden">
                                                         <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center text-emerald-500 shadow-sm border border-slate-50 flex-shrink-0">
                                                            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                                                         </div>
                                                         <div className="min-w-0">
                                                            <p className="text-[9px] sm:text-[10px] font-black text-[#143E78] uppercase truncate">
                                                               {new Date(log.visitDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • {log.startTime}-{log.endTime}
                                                            </p>
                                                            <p className="text-[8px] sm:text-[9px] font-medium text-slate-500 mt-0.5 truncate">{log.notes || 'Routine checkup'}</p>
                                                         </div>
                                                      </div>
                                                      {!isStageReadOnly && (
                                                         <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                                            <button
                                                               onClick={() => {
                                                                  setMonitoringLog({
                                                                     visitDate: new Date(log.visitDate).toISOString().split('T')[0],
                                                                     startTime: log.startTime,
                                                                     endTime: log.endTime,
                                                                     notes: log.notes,
                                                                     logId: log._id
                                                                  });
                                                                  setShowAddMonitoringLogModal(true);
                                                               }}
                                                               className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                               title="Edit Log"
                                                            >
                                                               <Edit className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                               onClick={() => handleDeleteMonitoringLog(log._id)}
                                                               className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                               title="Delete Log"
                                                            >
                                                               <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                         </div>
                                                      )}
                                                   </div>
                                                ))
                                             ) : (
                                                <div className="flex-1 flex flex-col items-center justify-center py-10 opacity-30">
                                                   <ClipboardList className="w-10 h-10 mb-2" />
                                                   <p className="text-[9px] font-black uppercase tracking-widest">Click + to start reporting (3 days)</p>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    </div>

                                    {/* Finalize Button */}
                                    {sortedMonitoringLogs?.length >= 3 && (
                                       <div className="mt-8 flex justify-end">
                                          {!isStageReadOnly ? (
                                             <button
                                                onClick={handleFinalizePostOp}
                                                className="flex items-center gap-2 px-8 py-4 bg-[#143E78] text-white rounded-2xl hover:bg-[#1c4b8e] transition-all font-black text-[11px] uppercase tracking-widest shadow-lg shadow-blue-900/20 active:scale-95 group"
                                             >
                                                <span>Go to Next Stage</span>
                                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                             </button>
                                          ) : (
                                             <div className="flex items-center gap-2 px-8 py-4 bg-emerald-50 text-emerald-700 font-black text-[11px] uppercase tracking-widest rounded-2xl border border-emerald-200">
                                                <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Post-Op Monitoring Period Complete
                                             </div>
                                          )}
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* 🏁 STAGE 10: Financial Lifecycle (Payout) */}
                              {displayStatus === 'payout_processing' && (
                                 <div className="space-y-8 pb-32 animate-fadeIn">
                                    {/* 📋 Section 1: Deal Summary (Top Panel) */}
                                    <div className="bg-[#143E78] rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 text-white relative overflow-hidden shadow-2xl">
                                       <div className="absolute top-0 right-0 p-8 sm:p-12 opacity-10 pointer-events-none">
                                          <Wallet className="w-48 h-48 sm:w-64 sm:h-64 rotate-12" />
                                       </div>
                                       <div className="relative z-10">
                                          <div className="flex flex-col sm:flex-row justify-between items-start gap-6 sm:gap-0 mb-8 sm:mb-10">
                                             <div>
                                                <span className="px-3 sm:px-4 py-1.5 bg-white/10 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] mb-3 sm:mb-4 inline-block border border-white/10">Stage 10: Financial Lifecycle</span>
                                                <h2 className="text-2xl sm:text-4xl font-black tracking-tighter uppercase leading-none">Deal Settlement</h2>
                                                <div className="mt-3 space-y-1">
                                                   <p className="text-white/60 text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                                                      <span className="text-white/40">BOOKING ID:</span> {lead.bookingId}
                                                   </p>
                                                   <p className="text-white/60 text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                                                      <span className="text-white/40">PAYOUT ID:</span> <span className="text-emerald-400 bg-emerald-900/30 px-2 py-0.5 rounded-md border border-emerald-500/20">{lead.payoutData?.masterPayoutId || 'GENERATING...'}</span>
                                                   </p>
                                                </div>
                                             </div>
                                             <div className="sm:text-right flex flex-col sm:items-end gap-2">
                                                <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">Status</p>
                                                <div className="flex flex-wrap items-center justify-end gap-2">
                                                   <span className="px-4 sm:px-6 py-2 bg-emerald-500 text-white rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20">
                                                      {lead.payoutData?.finalizedAt ? 'Settled & Completed' : 'Payout Pending'}
                                                   </span>
                                                   {isPastSurgeryCase && !isPayoutLocked && (
                                                      <button
                                                         type="button"
                                                         onClick={handleSendPayoutNotification}
                                                         disabled={isSendingPayoutNotif}
                                                         className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-slate-950 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-black uppercase tracking-widest shadow-lg flex items-center gap-2 transition-all cursor-pointer disabled:opacity-50"
                                                      >
                                                         {isSendingPayoutNotif ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-950" /> : <Send className="w-3.5 h-3.5 text-slate-950" />}
                                                         Send Payout Email
                                                      </button>
                                                   )}
                                                </div>
                                             </div>
                                          </div>

                                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
                                             <div className="space-y-1 min-w-0">
                                                <p className="text-[8px] sm:text-[10px] font-black text-white/30 uppercase tracking-widest leading-none">Patient</p>
                                                <p className="text-[11px] sm:text-sm font-black uppercase truncate">{lead.patientName}</p>
                                             </div>
                                             <div className="space-y-1 min-w-0">
                                                <p className="text-[8px] sm:text-[10px] font-black text-white/30 uppercase tracking-widest leading-none">Hospital</p>
                                                <p className="text-[11px] sm:text-sm font-black uppercase truncate">{lead.assignedHospital?.hospitalName || 'N/A'}</p>
                                             </div>
                                             <div className="space-y-1 min-w-0">
                                                <p className="text-[8px] sm:text-[10px] font-black text-white/30 uppercase tracking-widest leading-none">Surgery</p>
                                                <p className="text-[11px] sm:text-sm font-black uppercase truncate">{lead.treatmentRequired || 'N/A'}</p>
                                             </div>
                                             <div className="space-y-1 min-w-0">
                                                <p className="text-[8px] sm:text-[10px] font-black text-white/30 uppercase tracking-widest leading-none">Settlement Date</p>
                                                <p className="text-[11px] sm:text-sm font-black uppercase truncate">
                                                   {lead.payoutData?.finalizedAt ? new Date(lead.payoutData.finalizedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Pending'}
                                                </p>
                                             </div>
                                          </div>
                                       </div>
                                    </div>

                                    <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 sm:gap-8">
                                       {/* Left Column (Transactions, Configuration & Stakeholder Payouts) */}
                                       <div className="lg:col-span-8 space-y-6 sm:space-y-8">
                                          {/* 💳 Section 2: Payment History */}
                                          <div className="bg-white rounded-3xl sm:rounded-[3rem] p-6 sm:p-10 shadow-sm border border-slate-100 animate-fadeIn">
                                             <div className="flex items-center gap-4 mb-6 sm:mb-8 pb-5 border-b border-slate-100">
                                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-tr from-blue-500 to-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                                                   <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />
                                                </div>
                                                <div>
                                                   <h3 className="text-base sm:text-lg font-black text-[#143E78] uppercase tracking-tighter leading-none">Payment History</h3>
                                                   <p className="text-[8px] sm:text-[10px] font-bold text-slate-400 mt-1.5 uppercase tracking-widest">Transaction Records</p>
                                                </div>
                                             </div>

                                             <div className="space-y-4">
                                                {/* FINANCIAL SUMMARY BREAKDOWN */}
                                                {lead.billingDetails && (
                                                   <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                                                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 transition-all hover:bg-slate-100/50">
                                                         <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Procedure Amount</p>
                                                         <p className="text-xs font-black text-slate-700 mt-1">₹{(lead.billingDetails.procedureAmount || 0).toLocaleString('en-IN')}</p>
                                                      </div>
                                                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 transition-all hover:bg-slate-100/50">
                                                         <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Platform Booking Fee</p>
                                                         <p className="text-xs font-black text-slate-700 mt-1">₹{(lead.billingDetails.platformFeeAmount || 0).toLocaleString('en-IN')}</p>
                                                      </div>
                                                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 transition-all hover:bg-slate-100/50">
                                                         <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">GST Amount</p>
                                                         <p className="text-xs font-black text-slate-700 mt-1">₹{(lead.billingDetails.gstAmount || 0).toLocaleString('en-IN')}</p>
                                                      </div>
                                                      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-4 py-3 rounded-2xl text-white shadow-md shadow-emerald-500/10 flex flex-col justify-center">
                                                         <p className="text-[8px] font-black text-emerald-100 uppercase tracking-widest opacity-80">Total Collected</p>
                                                         <p className="text-sm font-black mt-0.5">₹{(lead.billingDetails.totalPayableAmount || 0).toLocaleString('en-IN')}</p>
                                                      </div>
                                                   </div>
                                                )}

                                                <div className="overflow-x-auto custom-scrollbar pb-2">
                                                   <table className="w-full min-w-[400px] sm:min-w-0">
                                                      <thead>
                                                         <tr className="border-b border-slate-100">
                                                            <th className="text-left py-4 text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Payment Info</th>
                                                            <th className="text-left py-4 text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Method</th>
                                                            <th className="text-left py-4 text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                                            <th className="text-right py-4 text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Amount</th>
                                                         </tr>
                                                      </thead>
                                                      <tbody className="divide-y divide-slate-50">
                                                         {payments.length > 0 ? (
                                                            payments.map((pay) => (
                                                               <tr key={pay._id} className="group border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-all">
                                                                  <td className="py-4 sm:py-5">
                                                                     <p className="text-[10px] sm:text-xs uppercase group-hover:text-blue-600 transition-colors">
                                                                        Surgery Payment ({pay.description || 'Transaction'})
                                                                     </p>
                                                                     <p className="text-[8px] sm:text-[10px] font-mono text-slate-400 mt-1 truncate max-w-[150px] sm:max-w-none">
                                                                        ID: {pay.razorpayPaymentId || pay.transactionId || pay._id}
                                                                     </p>
                                                                  </td>
                                                                  <td className="py-4 sm:py-5">
                                                                     <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-[8px] sm:text-[10px] font-black uppercase tracking-wider border border-slate-200/50">
                                                                        {pay.paymentMethod || 'Online'}
                                                                     </span>
                                                                  </td>
                                                                  <td className="py-4 sm:py-5">
                                                                     <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-wider border ${pay.status === 'completed' || pay.status === 'captured' || pay.status === 'paid'
                                                                        ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                                        : pay.status === 'failed'
                                                                           ? 'bg-red-50 text-red-600 border-red-100'
                                                                           : 'bg-amber-50 text-amber-600 border-amber-100'
                                                                        }`}>
                                                                        <div className={`w-1.5 h-1.5 rounded-full ${pay.status === 'completed' || pay.status === 'captured' || pay.status === 'paid'
                                                                           ? 'bg-emerald-505'
                                                                           : pay.status === 'failed'
                                                                              ? 'bg-red-500'
                                                                              : 'bg-amber-500'
                                                                           }`} /> {pay.status}
                                                                     </span>
                                                                  </td>
                                                                  <td className="py-4 sm:py-5 text-right">
                                                                     <p className="text-sm font-black text-[#143E78]">₹{(pay.amount || 0).toLocaleString('en-IN')}</p>
                                                                  </td>
                                                               </tr>
                                                            ))
                                                         ) : (
                                                            <tr className="group border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-all">
                                                               <td className="py-4 sm:py-5">
                                                                  <p className="text-[10px] sm:text-xs uppercase group-hover:text-blue-600 transition-colors">Surgery Package (Full)</p>
                                                                  <p className="text-[8px] sm:text-[10px] font-mono text-slate-400 mt-1 truncate max-w-[100px] sm:max-w-none">ID: {lead.razorpayDetails?.paymentId || 'MANUAL-REF'}</p>
                                                               </td>
                                                               <td className="py-4 sm:py-5">
                                                                  <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-[8px] sm:text-[10px] font-black uppercase tracking-wider border border-slate-200/50">Razorpay</span>
                                                               </td>
                                                               <td className="py-4 sm:py-5">
                                                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-wider">
                                                                     <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Success
                                                                  </span>
                                                               </td>
                                                               <td className="py-4 sm:py-5 text-right">
                                                                  <p className="text-sm font-black text-[#143E78]">₹{(lead.billingDetails?.totalPayableAmount || 0).toLocaleString('en-IN')}</p>
                                                               </td>
                                                            </tr>
                                                         )}
                                                         {lead.surgicalReschedule?.penaltyAmount > 0 && (
                                                            <tr className="group hover:bg-slate-50/50 transition-all">
                                                               <td className="py-4 sm:py-5">
                                                                  <p className="text-xs font-black text-red-500 uppercase">Rescheduling Penalty</p>
                                                                  <p className="text-[8px] sm:text-[10px] font-medium text-slate-400 mt-1">Fault: {lead.surgicalReschedule.faultBy}</p>
                                                               </td>
                                                               <td className="py-4 sm:py-5">
                                                                  <span className="px-3 py-1 bg-red-50 text-red-600 rounded-lg text-[8px] sm:text-[10px] font-black uppercase tracking-wider border border-red-100/50">Internal</span>
                                                               </td>
                                                               <td className="py-4 sm:py-5">
                                                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-600 border border-red-100 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-wider">Deducted</span>
                                                               </td>
                                                               <td className="py-4 sm:py-5 text-right text-red-500 font-black">
                                                                  - ₹{lead.surgicalReschedule.penaltyAmount.toLocaleString('en-IN')}
                                                               </td>
                                                            </tr>
                                                         )}
                                                         {lead.payoutData?.refundDetails?.amount > 0 && (
                                                            <tr className="group hover:bg-slate-50/50 transition-all">
                                                               <td className="py-4 sm:py-5">
                                                                  <p className="text-xs font-black text-amber-500 uppercase">Hospital Refund</p>
                                                                  <p className="text-[8px] sm:text-[10px] font-mono text-slate-400 mt-1">TXN: {lead.payoutData.refundDetails.transactionId || 'Pending'}</p>
                                                               </td>
                                                               <td className="py-4 sm:py-5">
                                                                  <span className="px-3 py-1 bg-amber-50 text-amber-600 rounded-lg text-[8px] sm:text-[10px] font-black uppercase tracking-wider border border-amber-100/50">{lead.payoutData.refundDetails.paymentMode}</span>
                                                               </td>
                                                               <td className="py-5">
                                                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 border border-amber-100 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-wider">Processed</span>
                                                               </td>
                                                               <td className="py-5 text-right text-amber-500 font-black">
                                                                  ₹{lead.payoutData.refundDetails.amount.toLocaleString('en-IN')}
                                                               </td>
                                                            </tr>
                                                         )}
                                                      </tbody>
                                                   </table>
                                                </div>
                                             </div>
                                          </div>

                                          {/* ⚠️ Section 3: Abort Case Handling (Shown only if applicable) */}
                                          {lead.surgicalReschedule?.isAbort && (
                                             <div className="bg-gradient-to-br from-red-50 to-rose-50/30 border-2 border-red-100 rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 relative overflow-hidden shadow-sm">
                                                <div className="absolute top-0 right-0 p-8 sm:p-12 opacity-5 pointer-events-none">
                                                   <AlertCircle className="w-32 h-32 sm:w-48 sm:h-48 text-red-500" />
                                                </div>
                                                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 relative z-10 text-center sm:text-left">
                                                   <div className="w-16 h-16 bg-gradient-to-tr from-red-500 to-rose-600 text-white rounded-3xl flex items-center justify-center shadow-xl shadow-red-500/20 flex-shrink-0">
                                                      <XCircle className="w-8 h-8" />
                                                   </div>
                                                   <div className="flex-1 min-w-0">
                                                      <h3 className="text-xl sm:text-2xl font-black text-red-600 uppercase tracking-tighter">Case Aborted</h3>
                                                      <p className="text-[10px] sm:text-xs font-bold text-red-500/60 uppercase tracking-widest mt-1">Financial Penalty Applicable</p>
                                                      <div className="mt-6 sm:mt-8 space-y-4">
                                                         <div className="grid grid-cols-2 gap-6 bg-white/60 rounded-2xl p-4 border border-red-100/50">
                                                            <div>
                                                               <p className="text-[8px] sm:text-[10px] font-black text-red-400 uppercase tracking-widest opacity-80">Fault By</p>
                                                               <p className="text-xs sm:text-sm font-black text-red-600 uppercase mt-1">{lead.surgicalReschedule.faultBy}</p>
                                                            </div>
                                                            <div>
                                                               <p className="text-[8px] sm:text-[10px] font-black text-red-400 uppercase tracking-widest opacity-80">Penalty Amount</p>
                                                               <p className="text-xs sm:text-sm font-black text-red-600 uppercase mt-1">₹{lead.surgicalReschedule.penaltyAmount.toLocaleString('en-IN')}</p>
                                                            </div>
                                                         </div>
                                                         <div>
                                                            <p className="text-[8px] sm:text-[10px] font-black text-red-400 uppercase tracking-widest opacity-80 ml-1">Abort Reason</p>
                                                            <p className="text-[10px] sm:text-xs font-medium text-red-700 mt-2 bg-white/70 p-4 rounded-2xl italic leading-relaxed border border-red-100/30">"{lead.surgicalReschedule.notes || 'No notes provided.'}"</p>
                                                         </div>
                                                      </div>
                                                   </div>
                                                </div>
                                             </div>
                                          )}

                                          {user.role !== 'customer_care' && user.role !== 'cc_head' && (
                                             <>
                                                {/* ⚙️ Section: Payout Configuration (WIDE VIEW) */}
                                                <div className="bg-white rounded-3xl sm:rounded-[3rem] p-6 sm:p-10 border border-slate-100 shadow-sm space-y-8 animate-fadeIn">
                                                   <div className="flex items-center gap-4 pb-5 border-b border-slate-100">
                                                      <div className="w-12 h-12 bg-gradient-to-tr from-slate-700 to-slate-800 text-white rounded-2xl flex items-center justify-center shadow-lg">
                                                         <Settings className="w-6 h-6 animate-spin-slow" />
                                                      </div>
                                                      <div>
                                                         <h3 className="text-lg font-black text-[#143E78] uppercase tracking-tighter leading-none">Payout Config.</h3>
                                                         <p className="text-[9px] font-bold text-slate-400 mt-1.5 uppercase tracking-widest">Adjust Distribution Percentages & Deductions</p>
                                                      </div>
                                                   </div>

                                                   <div className="space-y-6">

                                                      {/* Doctors Share Section - Two Rows Grid */}
                                                      {(() => {
                                                         const isSurgeonPartner = (lead.surgeonType || (lead.assignedSurgeonOtherName ? 'other' : 'partner')) === 'partner';
                                                         const isAnesthesiologistPartner = (lead.anesthesiologistType || (lead.assignedanesthesiologistOtherName ? 'other' : 'partner')) === 'partner';
                                                         const isBothExternal = !isSurgeonPartner && !isAnesthesiologistPartner;

                                                         if (isBothExternal) {
                                                            return (
                                                               <div className="p-5 bg-amber-50/80 border border-amber-200/80 rounded-2xl space-y-1">
                                                                  <div className="flex items-center gap-2 text-amber-900 font-black text-xs uppercase tracking-wider">
                                                                     <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                                                     External Surgical Team (Hospital Provided)
                                                                  </div>
                                                                  <p className="text-[11px] font-bold text-amber-700">
                                                                     Both Surgeon and Anesthesiologist are external doctors. Please upload payment settlement proof below.
                                                                  </p>
                                                               </div>
                                                            );
                                                         }

                                                         return (
                                                            <div className="p-5 sm:p-6 bg-blue-50/20 border border-blue-100/50 rounded-2xl">
                                                               <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-4 px-1">Doctors Share & Deductions</p>
                                                               <div className="space-y-4">
                                                                  {/* Row 1: Surgeon % & Anesthesia % */}
                                                                  <div className="grid grid-cols-2 gap-4">
                                                                     {isSurgeonPartner ? (
                                                                        <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                                           <label className="text-[8px] sm:text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2">Surgeon %</label>
                                                                           <input type="number" value={payoutConfig.surgeon} onChange={(e) => setPayoutConfig({ ...payoutConfig, surgeon: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white transition-all" />
                                                                        </div>
                                                                     ) : (
                                                                        <div className="bg-slate-100/80 p-4 rounded-xl border border-slate-200 flex items-center justify-center text-center">
                                                                           <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">External Surgeon — No DOXEZ Share</span>
                                                                        </div>
                                                                     )}

                                                                     {isAnesthesiologistPartner ? (
                                                                        <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                                           <label className="text-[8px] sm:text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2">Anesthesia %</label>
                                                                           <input type="number" value={payoutConfig.anesthesiologist} onChange={(e) => setPayoutConfig({ ...payoutConfig, anesthesiologist: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white transition-all" />
                                                                        </div>
                                                                     ) : (
                                                                        <div className="bg-slate-100/80 p-4 rounded-xl border border-slate-200 flex items-center justify-center text-center">
                                                                           <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">External Anesthesiologist — No DOXEZ Share</span>
                                                                        </div>
                                                                     )}
                                                                  </div>

                                                                  {/* Row 2: Surgeon TDS % & Anesth. TDS % */}
                                                                  <div className="grid grid-cols-2 gap-4">
                                                                     {isSurgeonPartner ? (
                                                                        <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                                           <label className="text-[8px] sm:text-[9px] font-black text-rose-500 uppercase tracking-widest block mb-2">Surgeon TDS %</label>
                                                                           <input type="number" value={payoutConfig.surgeonTds} onChange={(e) => setPayoutConfig({ ...payoutConfig, surgeonTds: Number(e.target.value) })} className="w-full bg-rose-50/20 border border-rose-100 rounded-lg px-3 py-2.5 text-xs font-bold text-rose-700 outline-none focus:border-rose-500 focus:bg-white transition-all" />
                                                                        </div>
                                                                     ) : <div />}

                                                                     {isAnesthesiologistPartner ? (
                                                                        <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                                           <label className="text-[8px] sm:text-[9px] font-black text-rose-500 uppercase tracking-widest block mb-2">Anesth. TDS %</label>
                                                                           <input type="number" disabled={isPayoutLocked} value={payoutConfig.anesthesiologistTds} onChange={(e) => setPayoutConfig({ ...payoutConfig, anesthesiologistTds: Number(e.target.value) })} className="w-full bg-rose-50/20 border border-rose-100 rounded-lg px-3 py-2.5 text-xs font-bold text-rose-700 outline-none focus:border-rose-500 focus:bg-white transition-all disabled:bg-slate-100 disabled:cursor-not-allowed" />
                                                                        </div>
                                                                     ) : <div />}
                                                                  </div>
                                                               </div>
                                                            </div>
                                                         );
                                                      })()}

                                                      {/* Platform & Partner Section - 2 Columns Grid */}
                                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                         <div className="p-5 sm:p-6 bg-indigo-50/10 border border-indigo-100/30 rounded-2xl">
                                                            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-3 px-1">Platform Distribution</p>
                                                            <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                               <label className="text-[8px] sm:text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2">Platform Booking Fee %</label>
                                                               <input type="number" disabled={isPayoutLocked} value={payoutConfig.doxezRetention} onChange={(e) => setPayoutConfig({ ...payoutConfig, doxezRetention: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 focus:bg-white transition-all disabled:bg-slate-100 disabled:cursor-not-allowed" />
                                                            </div>
                                                         </div>

                                                         <div className="p-5 sm:p-6 bg-amber-50/10 border border-amber-100/30 rounded-2xl">
                                                            <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-3 px-1">Partner Commission</p>
                                                            <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                               <label className="text-[8px] sm:text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2">Lead Partner %</label>
                                                               <input type="number" disabled={isPayoutLocked} value={payoutConfig.leadPartner} onChange={(e) => setPayoutConfig({ ...payoutConfig, leadPartner: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-700 outline-none focus:border-amber-500 focus:bg-white transition-all disabled:bg-slate-100 disabled:cursor-not-allowed" />
                                                            </div>
                                                         </div>
                                                      </div>

                                                      {/* Incentives Section - 2 Columns Grid */}
                                                      {((lead.surgeonType || (lead.assignedSurgeonOtherName ? 'other' : 'partner')) === 'partner' || (lead.anesthesiologistType || (lead.assignedanesthesiologistOtherName ? 'other' : 'partner')) === 'partner') && (
                                                         <div className="p-5 sm:p-6 bg-emerald-50/20 border border-emerald-100/40 rounded-2xl">
                                                            <div className="flex items-center justify-between mb-4 px-1">
                                                               <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Special Incentives</p>
                                                               <span className="text-[7.5px] text-emerald-600 bg-emerald-100/50 px-2 py-0.5 rounded-md font-extrabold border border-emerald-200/50">DEDUCTED FROM DOXEZ</span>
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                               {(lead.surgeonType || (lead.assignedSurgeonOtherName ? 'other' : 'partner')) === 'partner' ? (
                                                                  <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                                     <label className="text-[8px] sm:text-[9px] font-black text-emerald-600 uppercase tracking-widest block mb-2">Surgeon Incentive (₹)</label>
                                                                     <input type="number" disabled={isPayoutLocked} value={payoutConfig.surgeonIncentive || ''} onChange={(e) => setPayoutConfig({ ...payoutConfig, surgeonIncentive: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-700 outline-none focus:border-emerald-500 focus:bg-white transition-all disabled:bg-slate-100 disabled:cursor-not-allowed" placeholder="0" />
                                                                  </div>
                                                               ) : <div />}
                                                               {(lead.anesthesiologistType || (lead.assignedanesthesiologistOtherName ? 'other' : 'partner')) === 'partner' ? (
                                                                  <div className="bg-white p-4 rounded-xl border border-slate-100">
                                                                     <label className="text-[8px] sm:text-[9px] font-black text-emerald-600 uppercase tracking-widest block mb-2">Anesthesiologist Incentive (₹)</label>
                                                                     <input type="number" disabled={isPayoutLocked} value={payoutConfig.anesthesiologistIncentive || ''} onChange={(e) => setPayoutConfig({ ...payoutConfig, anesthesiologistIncentive: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-700 outline-none focus:border-emerald-500 focus:bg-white transition-all disabled:bg-slate-100 disabled:cursor-not-allowed" placeholder="0" />
                                                                  </div>
                                                               ) : <div />}
                                                            </div>
                                                         </div>
                                                      )}
                                                   </div>
                                                </div>

                                                {/* 💰 Section 4: Clinical Payouts (SIDE-BY-SIDE CARDS) */}
                                                <div className="bg-white rounded-3xl sm:rounded-[3rem] p-6 sm:p-10 border border-slate-100 shadow-sm space-y-8 animate-fadeIn">
                                                   <div className="flex items-center gap-4 pb-5 border-b border-slate-100">
                                                      <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center border border-indigo-100">
                                                         <UserCheck className="w-5 h-5 animate-pulse" />
                                                      </div>
                                                      <div>
                                                         <h3 className="text-lg font-black text-[#143E78] uppercase tracking-tighter leading-none">Clinical Payouts</h3>
                                                         <p className="text-[9px] font-bold text-slate-400 mt-1.5 uppercase tracking-widest">Stakeholder Settlements & Proof of Payments</p>
                                                      </div>
                                                   </div>

                                                   <div className="space-y-6">
                                                      <div className="space-y-6">
                                                         {(() => {
                                                            const isSurgeonPartner = (lead.surgeonType || (lead.assignedSurgeonOtherName ? 'other' : 'partner')) === 'partner';
                                                            const isAnesthesiologistPartner = (lead.anesthesiologistType || (lead.assignedanesthesiologistOtherName ? 'other' : 'partner')) === 'partner';

                                                            const surgeonHasProof = Boolean(
                                                               payoutForm.surgeonPayoutDoc ||
                                                               lead.payoutData?.doctorPayouts?.find(p => p.role === 'surgeon')?.payoutProofUrl
                                                            );

                                                            const anesthesiologistHasProof = Boolean(
                                                               payoutForm.anesthesiologistPayoutDoc ||
                                                               lead.payoutData?.doctorPayouts?.find(p => p.role === 'anesthesiologist')?.payoutProofUrl
                                                            );

                                                            return (
                                                               <div className="grid grid-cols-1 gap-6 xl:gap-8">
                                                                  {/* Operating Surgeon Card */}
                                                                  <div className="bg-slate-50/40 rounded-2xl p-5 border border-slate-100 space-y-4 transition-all hover:bg-slate-50 flex flex-col justify-between">
                                                                     <div>
                                                                        <div className="flex justify-between items-start gap-4 mb-3">
                                                                           <div className="min-w-0">
                                                                              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                                                                                 Operating Surgeon {isSurgeonPartner ? '(Partner)' : '(External)'}
                                                                              </p>
                                                                              <p className="text-sm font-extrabold text-[#143E78] uppercase truncate leading-tight">
                                                                                 {isSurgeonPartner
                                                                                    ? (lead.assignedSurgeon?.user?.fullName || 'Doctor')
                                                                                    : (lead.assignedSurgeonOtherName || 'External Doctor')}
                                                                              </p>
                                                                              {!isSurgeonPartner && (
                                                                                 <div className="mt-0.5 space-y-0.5">
                                                                                    {lead.assignedSurgeonOtherOCMR && (
                                                                                       <p className="text-[10px] font-bold text-slate-400">OCMR: {lead.assignedSurgeonOtherOCMR}</p>
                                                                                    )}
                                                                                    {lead.assignedSurgeonOtherPhone && (
                                                                                       <p className="text-[10px] font-bold text-slate-400">Phone: {lead.assignedSurgeonOtherPhone}</p>
                                                                                    )}
                                                                                    {lead.assignedSurgeonOtherEmail && (
                                                                                       <p className="text-[10px] font-bold text-slate-400">Email: {lead.assignedSurgeonOtherEmail}</p>
                                                                                    )}
                                                                                 </div>
                                                                              )}
                                                                           </div>
                                                                           {!isPayoutLocked && (
                                                                              <button
                                                                                 onClick={() => document.getElementById('surgeon_payout_file').click()}
                                                                                 className={`px-3 py-2 rounded-xl flex items-center gap-1.5 transition-all shrink-0 font-black text-[9px] uppercase ${surgeonHasProof
                                                                                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100'
                                                                                    : 'bg-white text-slate-400 hover:text-[#143E78] border border-slate-200 shadow-sm'
                                                                                    }`}
                                                                                 title="Upload Payout Proof"
                                                                              >
                                                                                 {surgeonHasProof ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                                                                 <span>{surgeonHasProof ? 'Uploaded' : 'Upload'}</span>
                                                                              </button>
                                                                           )}
                                                                           <input id="surgeon_payout_file" type="file" hidden onChange={(e) => setPayoutForm({ ...payoutForm, surgeonPayoutDoc: e.target.files[0] })} />
                                                                        </div>

                                                                        {isSurgeonPartner ? (
                                                                           <div className="space-y-1.5">
                                                                              <label className="text-[9px] font-black text-slate-600 uppercase tracking-wider block">
                                                                                 Custom / Agreed Payment (₹)
                                                                              </label>
                                                                              <div className="relative">
                                                                                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">₹</div>
                                                                                 <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    disabled={isPayoutLocked}
                                                                                    value={payoutForm.surgeonAmount ?? (Math.round((payoutConfig.baseAmount || 0) * (payoutConfig.surgeon / 100)) + (payoutConfig.surgeonIncentive || 0))}
                                                                                    onChange={(e) => setPayoutForm({ ...payoutForm, surgeonAmount: parseFloat(e.target.value) || 0, _isSurgeonManual: true })}
                                                                                    className="w-full bg-white border border-slate-200 rounded-xl py-2.5 pl-8 pr-4 text-sm font-extrabold text-slate-800 outline-none focus:border-indigo-500 transition-all text-right shadow-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                                                 />
                                                                              </div>
                                                                           </div>
                                                                        ) : (
                                                                           <div className="space-y-3">
                                                                              <div className="bg-amber-50/60 border border-amber-100/80 rounded-xl p-2.5 text-center">
                                                                                 <span className="text-[9px] font-extrabold text-amber-800 uppercase tracking-wider">Hospital Provided — External Doctor</span>
                                                                              </div>
                                                                              <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-sm space-y-1.5">
                                                                                 <label className="text-[9px] font-black text-slate-600 uppercase tracking-wider block">
                                                                                    Manual Agreed Payment (₹)
                                                                                 </label>
                                                                                 <div className="relative">
                                                                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">₹</div>
                                                                                    <input
                                                                                       type="number"
                                                                                       min="0"
                                                                                       disabled={isPayoutLocked}
                                                                                       placeholder="0"
                                                                                       value={payoutForm.surgeonAmount ?? 0}
                                                                                       onChange={(e) => setPayoutForm({ ...payoutForm, surgeonAmount: parseFloat(e.target.value) || 0, _isSurgeonManual: true })}
                                                                                       className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-7 pr-3 text-xs font-extrabold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white transition-all text-right shadow-inner disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                                                    />
                                                                                 </div>
                                                                                 <p className="text-[8px] font-medium text-slate-400">Enter manually agreed payout amount for database record upon mutual discussion.</p>
                                                                              </div>
                                                                           </div>
                                                                        )}

                                                                        {/* TDS Breakdown with Toggle (X) Cross Button — Surgeon */}
                                                                        {(() => {
                                                                           const isTdsDisabled = Boolean(payoutForm.disableSurgeonTds);
                                                                           const gross = isSurgeonPartner
                                                                              ? (payoutForm.surgeonAmount ?? (Math.round((payoutConfig.baseAmount || 0) * (payoutConfig.surgeon / 100)) + (payoutConfig.surgeonIncentive || 0)))
                                                                              : (payoutForm.surgeonAmount ?? 0);
                                                                           const tdsRate = isTdsDisabled ? 0 : (payoutConfig.surgeonTds || 10);
                                                                           const tds = Math.round(gross * (tdsRate / 100));
                                                                           const net = gross - tds;
                                                                           return (
                                                                              <div className='bg-indigo-50/40 rounded-xl px-4 py-3 border border-indigo-100 space-y-2 mt-3'>
                                                                                 <div className='flex justify-between items-center'>
                                                                                    <span className='text-[10px] font-black text-slate-500 tracking-wider'>Gross Amount</span>
                                                                                    <span className='text-xs font-bold text-[#143E78]'>₹{gross.toLocaleString('en-IN')}</span>
                                                                                 </div>
                                                                                 <div className='flex justify-between items-center'>
                                                                                    <span className={`text-[9px] font-black uppercase tracking-widest ${isTdsDisabled ? 'line-through text-slate-400' : 'text-red-400'}`}>
                                                                                       TDS Deduction ({isTdsDisabled ? '0%' : `${payoutConfig.surgeonTds || 10}%`})
                                                                                    </span>
                                                                                    <div className='flex items-center gap-2'>
                                                                                       <span className={`text-xs font-bold ${isTdsDisabled ? 'text-slate-400' : 'text-red-500'}`}>
                                                                                          {isTdsDisabled ? '₹0' : `- ₹${tds.toLocaleString('en-IN')}`}
                                                                                       </span>
                                                                                       {!isPayoutLocked && (
                                                                                          <button
                                                                                             type="button"
                                                                                             onClick={() => setPayoutForm({ ...payoutForm, disableSurgeonTds: !isTdsDisabled })}
                                                                                             className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black transition-all cursor-pointer ${isTdsDisabled
                                                                                                ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                                                                                                : 'bg-red-100 text-red-600 hover:bg-red-200'
                                                                                                }`}
                                                                                             title={isTdsDisabled ? "Re-enable TDS Deduction" : "Remove / Disable TDS Deduction"}
                                                                                          >
                                                                                             {isTdsDisabled ? '✓' : '✕'}
                                                                                          </button>
                                                                                       )}
                                                                                    </div>
                                                                                 </div>
                                                                                 <div className='flex justify-between items-center border-t border-indigo-100 pt-2'>
                                                                                    <span className='text-[9px] font-black text-emerald-600 uppercase tracking-widest'>Net Payable</span>
                                                                                    <span className='text-sm font-extrabold text-emerald-600'>₹{net.toLocaleString('en-IN')}</span>
                                                                                 </div>
                                                                              </div>
                                                                           );
                                                                        })()}
                                                                     </div>
                                                                     <div className="mt-4">
                                                                        {payoutForm.surgeonPayoutDoc && (
                                                                           <div className="flex items-center gap-2 text-emerald-600 animate-fadeIn bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100">
                                                                              <FileText className="w-4 h-4" />
                                                                              <span className="text-[9px] font-black uppercase truncate">Staged: {payoutForm.surgeonPayoutDoc.name}</span>
                                                                           </div>
                                                                        )}
                                                                        {lead.payoutData?.doctorPayouts?.find(p => p.role === 'surgeon')?.payoutProofUrl && !payoutForm.surgeonPayoutDoc && (
                                                                           <a
                                                                              href={lead.payoutData.doctorPayouts.find(p => p.role === 'surgeon').payoutProofUrl}
                                                                              target="_blank"
                                                                              rel="noopener noreferrer"
                                                                              className="flex items-center gap-2 text-blue-600 bg-blue-50 p-2.5 rounded-xl border border-blue-100 hover:bg-blue-100/75 transition-all animate-fadeIn"
                                                                           >
                                                                              <ExternalLink className="w-4 h-4" />
                                                                              <span className="text-[9px] font-black uppercase">View Uploaded Proof</span>
                                                                           </a>
                                                                        )}
                                                                     </div>
                                                                  </div>

                                                                  {/* Anesthesiologist Card */}
                                                                  <div className="bg-slate-50/40 rounded-2xl p-5 border border-slate-100 space-y-4 transition-all hover:bg-slate-50 flex flex-col justify-between">
                                                                     <div>
                                                                        <div className="flex justify-between items-start gap-4 mb-3">
                                                                           <div className="min-w-0">
                                                                              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                                                                                 Anesthesiologist {isAnesthesiologistPartner ? '(Partner)' : '(External)'}
                                                                              </p>
                                                                              <p className="text-sm font-extrabold text-[#143E78] uppercase truncate leading-tight">
                                                                                 {isAnesthesiologistPartner
                                                                                    ? (lead.assignedanesthesiologist?.user?.fullName || 'Doctor')
                                                                                    : (lead.assignedanesthesiologistOtherName || 'External Doctor')}
                                                                              </p>
                                                                              {!isAnesthesiologistPartner && (
                                                                                 <div className="mt-0.5 space-y-0.5">
                                                                                    {lead.assignedanesthesiologistOtherOCMR && (
                                                                                       <p className="text-[10px] font-bold text-slate-400">OCMR: {lead.assignedanesthesiologistOtherOCMR}</p>
                                                                                    )}
                                                                                    {lead.assignedanesthesiologistOtherPhone && (
                                                                                       <p className="text-[10px] font-bold text-slate-400">Phone: {lead.assignedanesthesiologistOtherPhone}</p>
                                                                                    )}
                                                                                    {lead.assignedanesthesiologistOtherEmail && (
                                                                                       <p className="text-[10px] font-bold text-slate-400">Email: {lead.assignedanesthesiologistOtherEmail}</p>
                                                                                    )}
                                                                                 </div>
                                                                              )}
                                                                           </div>
                                                                           {!isPayoutLocked && (
                                                                              <button
                                                                                 onClick={() => document.getElementById('anesthesiologist_payout_file').click()}
                                                                                 className={`px-3 py-2 rounded-xl flex items-center gap-1.5 transition-all shrink-0 font-black text-[9px] uppercase ${anesthesiologistHasProof
                                                                                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100'
                                                                                    : 'bg-white text-slate-400 hover:text-[#143E78] border border-slate-200 shadow-sm'
                                                                                    }`}
                                                                                 title="Upload Payout Proof"
                                                                              >
                                                                                 {anesthesiologistHasProof ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                                                                 <span>{anesthesiologistHasProof ? 'Uploaded' : 'Upload'}</span>
                                                                              </button>
                                                                           )}
                                                                           <input id="anesthesiologist_payout_file" type="file" hidden onChange={(e) => setPayoutForm({ ...payoutForm, anesthesiologistPayoutDoc: e.target.files[0] })} />
                                                                        </div>

                                                                        {isAnesthesiologistPartner ? (
                                                                           <div className="space-y-1.5">
                                                                              <label className="text-[9px] font-black text-slate-600 uppercase tracking-wider block">
                                                                                 Custom / Agreed Payment (₹)
                                                                              </label>
                                                                              <div className="relative">
                                                                                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">₹</div>
                                                                                 <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    disabled={isPayoutLocked}
                                                                                    value={payoutForm.anesthesiologistAmount ?? (Math.round((payoutConfig.baseAmount || 0) * (payoutConfig.anesthesiologist / 100)) + (payoutConfig.anesthesiologistIncentive || 0))}
                                                                                    onChange={(e) => setPayoutForm({ ...payoutForm, anesthesiologistAmount: parseFloat(e.target.value) || 0, _isAnesthesiologistManual: true })}
                                                                                    className="w-full bg-white border border-slate-200 rounded-xl py-2.5 pl-8 pr-4 text-sm font-extrabold text-slate-800 outline-none focus:border-purple-500 transition-all text-right shadow-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                                                 />
                                                                              </div>
                                                                           </div>
                                                                        ) : (
                                                                           <div className="space-y-3">
                                                                              <div className="bg-amber-50/60 border border-amber-100/80 rounded-xl p-2.5 text-center">
                                                                                 <span className="text-[9px] font-extrabold text-amber-800 uppercase tracking-wider">Hospital Provided — External Doctor</span>
                                                                              </div>
                                                                              <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-sm space-y-1.5">
                                                                                 <label className="text-[9px] font-black text-slate-600 uppercase tracking-wider block">
                                                                                    Manual Agreed Payment (₹)
                                                                                 </label>
                                                                                 <div className="relative">
                                                                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">₹</div>
                                                                                    <input
                                                                                       type="number"
                                                                                       min="0"
                                                                                       disabled={isPayoutLocked}
                                                                                       placeholder="0"
                                                                                       value={payoutForm.anesthesiologistAmount ?? 0}
                                                                                       onChange={(e) => setPayoutForm({ ...payoutForm, anesthesiologistAmount: parseFloat(e.target.value) || 0, _isAnesthesiologistManual: true })}
                                                                                       className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-7 pr-3 text-xs font-extrabold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white transition-all text-right shadow-inner disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                                                    />
                                                                                 </div>
                                                                                 <p className="text-[8px] font-medium text-slate-400">Enter manually agreed payout amount for database record upon mutual discussion.</p>
                                                                              </div>
                                                                           </div>
                                                                        )}

                                                                        {/* TDS Breakdown with Toggle (X) Cross Button — Anesthesiologist */}
                                                                        {(() => {
                                                                           const isTdsDisabled = Boolean(payoutForm.disableAnesthesiologistTds);
                                                                           const gross = isAnesthesiologistPartner
                                                                              ? (payoutForm.anesthesiologistAmount ?? (Math.round((payoutConfig.baseAmount || 0) * (payoutConfig.anesthesiologist / 100)) + (payoutConfig.anesthesiologistIncentive || 0)))
                                                                              : (payoutForm.anesthesiologistAmount ?? 0);
                                                                           const tdsRate = isTdsDisabled ? 0 : (payoutConfig.anesthesiologistTds || 10);
                                                                           const tds = Math.round(gross * (tdsRate / 100));
                                                                           const net = gross - tds;
                                                                           return (
                                                                              <div className="bg-purple-50/40 rounded-xl px-4 py-3 border border-purple-100 space-y-2 mt-3">
                                                                                 <div className="flex justify-between items-center">
                                                                                    <span className="text-[10px] font-black text-slate-500 tracking-wider">Gross Amount</span>
                                                                                    <span className="text-xs font-bold text-[#143E78]">₹{gross.toLocaleString('en-IN')}</span>
                                                                                 </div>
                                                                                 <div className="flex justify-between items-center">
                                                                                    <span className={`text-[9px] font-black uppercase tracking-widest ${isTdsDisabled ? 'line-through text-slate-400' : 'text-red-400'}`}>
                                                                                       TDS Deduction ({isTdsDisabled ? '0%' : `${payoutConfig.anesthesiologistTds || 10}%`})
                                                                                    </span>
                                                                                    <div className="flex items-center gap-2">
                                                                                       <span className={`text-xs font-bold ${isTdsDisabled ? 'text-slate-400' : 'text-red-500'}`}>
                                                                                          {isTdsDisabled ? '₹0' : `- ₹${tds.toLocaleString('en-IN')}`}
                                                                                       </span>
                                                                                       {!isPayoutLocked && (
                                                                                          <button
                                                                                             type="button"
                                                                                             onClick={() => setPayoutForm({ ...payoutForm, disableAnesthesiologistTds: !isTdsDisabled })}
                                                                                             className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black transition-all cursor-pointer ${isTdsDisabled
                                                                                                ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                                                                                                : 'bg-red-100 text-red-600 hover:bg-red-200'
                                                                                                }`}
                                                                                             title={isTdsDisabled ? "Re-enable TDS Deduction" : "Remove / Disable TDS Deduction"}
                                                                                          >
                                                                                             {isTdsDisabled ? '✓' : '✕'}
                                                                                          </button>
                                                                                       )}
                                                                                    </div>
                                                                                 </div>
                                                                                 <div className="flex justify-between items-center border-t border-purple-100 pt-2">
                                                                                    <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Net Payable</span>
                                                                                    <span className="text-sm font-extrabold text-emerald-600">₹{net.toLocaleString('en-IN')}</span>
                                                                                 </div>
                                                                              </div>
                                                                           );
                                                                        })()}
                                                                     </div>
                                                                     <div className="mt-4">
                                                                        {payoutForm.anesthesiologistPayoutDoc && (
                                                                           <div className="flex items-center gap-2 text-emerald-600 animate-fadeIn bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100">
                                                                              <FileText className="w-4 h-4" />
                                                                              <span className="text-[9px] font-black uppercase truncate">Staged: {payoutForm.anesthesiologistPayoutDoc.name}</span>
                                                                           </div>
                                                                        )}
                                                                        {lead.payoutData?.doctorPayouts?.find(p => p.role === 'anesthesiologist')?.payoutProofUrl && !payoutForm.anesthesiologistPayoutDoc && (
                                                                           <a
                                                                              href={lead.payoutData.doctorPayouts.find(p => p.role === 'anesthesiologist').payoutProofUrl}
                                                                              target="_blank"
                                                                              rel="noopener noreferrer"
                                                                              className="flex items-center gap-2 text-blue-600 bg-blue-50 p-2.5 rounded-xl border border-blue-100 hover:bg-blue-100/75 transition-all animate-fadeIn"
                                                                           >
                                                                              <ExternalLink className="w-4 h-4" />
                                                                              <span className="text-[9px] font-black uppercase">View Uploaded Proof</span>
                                                                           </a>
                                                                        )}
                                                                     </div>
                                                                  </div>
                                                               </div>
                                                            );
                                                         })()}
                                                      </div>

                                                      {!isPayoutLocked && (
                                                         <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                                            <button
                                                               onClick={() => handleUpdatePayout()}
                                                               disabled={isSubmittingPayout}
                                                               className="flex-1 py-4 bg-gradient-to-r from-[#143E78] to-blue-800 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:from-blue-900 hover:to-indigo-900 transition-all shadow-lg shadow-blue-900/25 active:scale-[0.98] disabled:opacity-50"
                                                            >
                                                               {isSubmittingPayout ? 'Processing...' : 'Save Payout Details'}
                                                            </button>
                                                            {isPastSurgeryCase && (
                                                               <button
                                                                  type="button"
                                                                  onClick={handleSendPayoutNotification}
                                                                  disabled={isSendingPayoutNotif}
                                                                  className="px-6 py-4 bg-amber-400 hover:bg-amber-300 text-slate-950 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                                               >
                                                                  {isSendingPayoutNotif ? <Loader2 className="w-4 h-4 animate-spin text-slate-950" /> : <Send className="w-4 h-4 text-slate-950" />}
                                                                  Send Payout Email
                                                               </button>
                                                            )}
                                                         </div>
                                                      )}
                                                   </div>
                                                </div>
                                             </>
                                          )}
                                       </div>

                                       {/* Right Column (Live Summary Sidebar & Actions) */}
                                       <div className="lg:col-span-4 space-y-6 sm:space-y-8 animate-fadeIn">
                                          {user.role === 'customer_care' || user.role === 'cc_head' ? (
                                             <div className="bg-slate-50 rounded-3xl border border-slate-100 p-6 sm:p-8 text-center relative overflow-hidden shadow-sm">
                                                <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
                                                   <Lock className="w-full h-full -rotate-12 translate-x-1/4 scale-150 text-slate-900" />
                                                </div>
                                                <div className="relative z-10">
                                                   <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm border border-slate-100">
                                                      <Lock className="w-8 h-8 text-slate-400" />
                                                   </div>
                                                   <h3 className="text-lg font-black text-[#143E78] uppercase tracking-tighter">View-Only Access</h3>
                                                   <p className="text-xs font-medium text-slate-500 mt-4 leading-relaxed">
                                                      Financial actions are restricted to <strong>Accountants</strong> and <strong>Admins</strong> only.
                                                      You can view the settlement status below but cannot modify payout details.
                                                   </p>
                                                   <div className="mt-8 p-4 bg-white/50 rounded-2xl border border-slate-100/50">
                                                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Locked Stage</p>
                                                      <div className="w-full bg-slate-200 h-2 rounded-full mt-3 overflow-hidden">
                                                         <div className="bg-[#143E78] h-full w-2/3 rounded-full animate-pulse" />
                                                      </div>
                                                   </div>
                                                </div>
                                             </div>
                                          ) : (
                                             <>
                                                {/* 📊 FINAL SETTLEMENT BREAKUP SUMMARY CARD */}
                                                <div className="bg-[#0B132B] rounded-[2rem] p-5 sm:p-6 shadow-2xl border border-slate-800 relative overflow-hidden before:absolute before:top-0 before:left-0 before:w-1.5 before:h-full before:bg-emerald-500">
                                                   <div className="absolute top-0 right-0 p-8 sm:p-12 opacity-5 pointer-events-none">
                                                      <Activity className="w-24 h-24 sm:w-32 sm:h-32 text-white" />
                                                   </div>
                                                   <div className="relative z-10">
                                                      <div className="flex items-center gap-3 mb-4 border-b border-white/5 pb-4">
                                                         <div className="p-2 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                                                            <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
                                                         </div>
                                                         <div>
                                                            <h4 className="text-sm font-black text-white uppercase tracking-tighter">Payment Summary</h4>
                                                            <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Real-time Settlement Breakup</p>
                                                         </div>
                                                      </div>

                                                      {(() => {
                                                         const isSurgeonPartner = (lead.surgeonType || (lead.assignedSurgeonOtherName ? 'other' : 'partner')) === 'partner';
                                                         const isAnesthesiologistPartner = (lead.anesthesiologistType || (lead.assignedanesthesiologistOtherName ? 'other' : 'partner')) === 'partner';

                                                         const base = payoutConfig.baseAmount || 0;
                                                         const sInc = isSurgeonPartner ? (payoutConfig.surgeonIncentive || 0) : 0;
                                                         const aInc = isAnesthesiologistPartner ? (payoutConfig.anesthesiologistIncentive || 0) : 0;

                                                         const sBase = isSurgeonPartner ? Math.round(base * (payoutConfig.surgeon / 100)) : 0;
                                                         const aBase = isAnesthesiologistPartner ? Math.round(base * (payoutConfig.anesthesiologist / 100)) : 0;

                                                         const leadComm = Math.round(base * (payoutConfig.leadPartner / 100));

                                                         const doxezBase = Math.round(base * (payoutConfig.doxezRetention / 100));
                                                         const doxezCollection = doxezBase - sInc - aInc - leadComm;

                                                         const sGross = sBase + sInc;
                                                         const aGross = aBase + aInc;

                                                         const doxezFinalRetention = doxezBase - sGross - aGross - leadComm;
                                                         const hospitalShare = base - (lead?.billingDetails?.totalPayableAmount || doxezBase);

                                                         return (
                                                            <div className="space-y-2">
                                                               {/* Base cost */}
                                                               <div className="flex justify-between items-center py-2 px-3 bg-white/5 rounded-lg border border-white/5">
                                                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Base Surgical Cost</span>
                                                                  <span className="text-xs font-black text-white">₹{base.toLocaleString('en-IN')}</span>
                                                               </div>

                                                               {/* Hospital Share Total */}
                                                               <div className="flex flex-col p-3 bg-gradient-to-br from-emerald-950/40 to-teal-950/20 rounded-lg border border-emerald-500/20 mb-1">
                                                                  <div className="flex justify-between items-center">
                                                                     <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">Hospital Share</span>
                                                                     <span className="text-sm font-black text-emerald-400">₹{hospitalShare.toLocaleString('en-IN')}</span>
                                                                  </div>
                                                                  <p className="text-[7px] font-medium text-slate-500 mt-1">Remaining Amount to Collect from Deal</p>
                                                               </div>

                                                               {/* Platform Booking Fee */}
                                                               <div className="flex justify-between items-center py-2 px-3 bg-white/5 rounded-lg border border-white/5">
                                                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Platform Booking Fee</span>
                                                                  <span className="text-xs font-black text-white">₹{(lead?.billingDetails?.platformFeeAmount || 0).toLocaleString('en-IN')}</span>
                                                               </div>

                                                               {/* GST Amount */}
                                                               <div className="flex justify-between items-center py-2 px-3 bg-white/5 rounded-lg border border-white/5">
                                                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Platform GST</span>
                                                                  <span className="text-xs font-black text-white">₹{(lead?.billingDetails?.gstAmount || 0).toLocaleString('en-IN')}</span>
                                                               </div>

                                                               {/* Surgeon Card — Shown only if Partner Surgeon */}
                                                               {isSurgeonPartner && (
                                                                  <div className="py-2 px-3 bg-white/5 rounded-lg border border-white/5 space-y-1.5">
                                                                     <div className="flex justify-between items-center">
                                                                        <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">Surgeon Share</span>
                                                                        <span className="text-[11px] font-black text-blue-400">₹{sGross.toLocaleString('en-IN')}</span>
                                                                     </div>
                                                                     <div className="flex justify-between items-center text-[8px] text-slate-500 font-semibold border-t border-white/5 pt-1">
                                                                        <span>Net (After TDS)</span>
                                                                        <span>₹{(sGross - Math.round(sGross * (payoutConfig.surgeonTds / 100))).toLocaleString('en-IN')}</span>
                                                                     </div>
                                                                  </div>
                                                               )}

                                                               {/* Anesthesia Card — Shown only if Partner Anesthesiologist */}
                                                               {isAnesthesiologistPartner && (
                                                                  <div className="py-2 px-3 bg-white/5 rounded-lg border border-white/5 space-y-1.5">
                                                                     <div className="flex justify-between items-center">
                                                                        <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">Anesthesia Share</span>
                                                                        <span className="text-[11px] font-black text-purple-400">₹{aGross.toLocaleString('en-IN')}</span>
                                                                     </div>
                                                                     <div className="flex justify-between items-center text-[8px] text-slate-500 font-semibold border-t border-white/5 pt-1">
                                                                        <span>Net (After TDS)</span>
                                                                        <span>₹{(aGross - Math.round(aGross * (payoutConfig.anesthesiologistTds / 100))).toLocaleString('en-IN')}</span>
                                                                     </div>
                                                                  </div>
                                                               )}

                                                               {/* Doxez Retention */}
                                                               <div className="py-2 px-3 bg-white/5 rounded-lg border border-white/5 flex justify-between items-center text-[9px] font-semibold text-slate-400">
                                                                  <div className="flex flex-col">
                                                                     <span className="text-indigo-400 font-black uppercase">Doxez Share ({payoutConfig.doxezRetention}%)</span>
                                                                     {(sInc > 0 || aInc > 0 || leadComm > 0) && <span className="text-[7px] text-slate-500 block mt-0.5">After Special Deductions</span>}
                                                                  </div>
                                                                  <span className="text-indigo-400 font-black">₹{doxezCollection.toLocaleString('en-IN')}</span>
                                                               </div>

                                                               {/* Lead Partner */}
                                                               <div className="py-2 px-3 bg-white/5 rounded-lg border border-white/5 flex justify-between items-center text-[9px] font-semibold text-slate-400">
                                                                  <div className="flex flex-col">
                                                                     <span className="text-amber-400 font-black uppercase">Partner ({payoutConfig.leadPartner}%)</span>
                                                                     <span className="text-[7px] text-slate-500 block mt-0.5 truncate max-w-[120px]">{lead?.leadGeneratedByName || 'Unknown Partner'}</span>
                                                                  </div>
                                                                  <span className="text-amber-400 font-black">₹{leadComm.toLocaleString('en-IN')}</span>
                                                               </div>
                                                            </div>
                                                         );
                                                      })()}
                                                   </div>
                                                </div>

                                                {/* 💸 Section 6: Hospital Refund */}
                                                {lead?.surgicalReschedule?.isAborted && !isPayoutLocked && (
                                                   <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-3xl p-5 shadow-xl shadow-emerald-700/10 text-white">
                                                      <div className="flex items-center gap-3 mb-5 border-b border-white/10 pb-4">
                                                         <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center border border-white/20">
                                                            <RefreshCcw className="w-5 h-5 animate-spin-slow text-white" />
                                                         </div>
                                                         <h4 className="text-xs font-black uppercase tracking-widest">Hospital Refund</h4>
                                                      </div>
                                                      <div className="space-y-4">
                                                         <div className="space-y-2">
                                                            <p className="text-[9px] font-black uppercase tracking-widest opacity-70 ml-1">Refund Amount</p>
                                                            <input
                                                               type="number"
                                                               value={payoutForm.refundDetails?.amount || 0}
                                                               onChange={(e) => setPayoutForm({ ...payoutForm, refundDetails: { ...payoutForm.refundDetails, amount: Number(e.target.value) } })}
                                                               className="w-full bg-white/10 border border-white/25 rounded-xl px-4 py-2.5 text-white text-sm font-bold outline-none focus:bg-white/20 focus:border-white/40 focus:ring-2 focus:ring-white/10"
                                                            />
                                                         </div>
                                                         <div className="space-y-2">
                                                            <p className="text-[9px] font-black uppercase tracking-widest opacity-70 ml-1">Transaction ID</p>
                                                            <input
                                                               type="text"
                                                               value={payoutForm.refundDetails?.transactionId || ''}
                                                               onChange={(e) => setPayoutForm({ ...payoutForm, refundDetails: { ...payoutForm.refundDetails, transactionId: e.target.value } })}
                                                               className="w-full bg-white/10 border border-white/25 rounded-xl px-4 py-2.5 text-white text-sm font-bold outline-none focus:bg-white/20 focus:border-white/40 focus:ring-2 focus:ring-white/10"
                                                            />
                                                         </div>
                                                         <button
                                                            onClick={() => handleUpdatePayout()}
                                                            className="w-full py-3.5 bg-white text-emerald-700 hover:bg-emerald-50 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl active:scale-[0.98]"
                                                         >
                                                            Process Refund
                                                         </button>
                                                      </div>
                                                   </div>
                                                )}

                                                <div className="pt-4 animate-fadeIn">
                                                   {!isPayoutLocked ? (
                                                      <button
                                                         onClick={() => {
                                                            setShowFinalizeModal(true);
                                                         }}
                                                         disabled={isFinalizingDeal}
                                                         className="w-full py-4 sm:py-5 px-2 sm:px-4 bg-slate-950 hover:bg-slate-900 text-white rounded-2xl text-[7px] sm:text-[10px] md:text-xs font-black uppercase tracking-normal sm:tracking-widest transition-all shadow-xl shadow-slate-950/20 flex items-center justify-center gap-2 sm:gap-3 group active:scale-[0.98] disabled:opacity-50"
                                                      >
                                                         <ShieldCheck className="w-3 h-3 sm:w-5 sm:h-5 text-emerald-500 shrink-0" />
                                                         <span className="whitespace-nowrap">
                                                            {isFinalizingDeal ? 'Processing...' : 'Mark as Completed'}
                                                         </span>
                                                         <ArrowRight className="w-2 h-2 sm:w-4 sm:h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0 hidden sm:block" />
                                                      </button>
                                                   ) : (
                                                      <div className="w-full py-4 sm:py-5 px-4 bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 rounded-2xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2">
                                                         <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                                         <span>Deal Finalized & Payout Complete</span>
                                                      </div>
                                                   )}
                                                </div>
                                             </>
                                          )}
                                       </div>
                                    </div>


                                 </div>
                              )}

                              {displayStatus === 'completed' && (
                                 <div className="space-y-8 animate-fadeIn">
                                    <div className="bg-emerald-50 rounded-[3rem] border-2 border-emerald-100 p-8 sm:p-12 text-center relative overflow-hidden shadow-2xl shadow-emerald-100/50">
                                       <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none">
                                          <Trophy className="w-64 h-64 rotate-12" />
                                       </div>
                                       <div className="relative z-10 space-y-6">
                                          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-[2rem] flex items-center justify-center mx-auto shadow-xl border border-emerald-100">
                                             <div className="w-12 h-12 sm:w-14 sm:h-14 bg-emerald-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-100">
                                                <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8" />
                                             </div>
                                          </div>
                                          <div className="space-y-2">
                                             <h2 className="text-3xl sm:text-5xl font-black text-emerald-900 uppercase tracking-tighter leading-none">Deal <br className="sm:hidden" /> Finalized</h2>
                                             <div className="max-w-md mx-auto p-6 sm:p-8 bg-white rounded-[2.5rem] border border-emerald-100 shadow-sm mt-6">
                                                <div className="grid grid-cols-3 gap-4 text-left border-b border-emerald-50 pb-6 mb-6">
                                                   <div>
                                                      <p className="text-[8px] sm:text-[10px] font-black text-emerald-800/40 uppercase tracking-widest leading-tight">Finalization<br />Date</p>
                                                      <p className="text-xs sm:text-sm font-black text-emerald-900 mt-2">{new Date(lead.payoutData?.finalizedAt || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                                   </div>
                                                   <div>
                                                      <p className="text-[8px] sm:text-[10px] font-black text-emerald-800/40 uppercase tracking-widest leading-tight">Booking<br />ID</p>
                                                      <p className="text-xs sm:text-sm font-black text-emerald-900 mt-2">{lead.bookingId || lead.leadId || lead._id?.slice(-8).toUpperCase()}</p>
                                                   </div>
                                                   <div>
                                                      <p className="text-[8px] sm:text-[10px] font-black text-emerald-800/40 uppercase tracking-widest leading-tight">Payout<br />Reference</p>
                                                      <p className="text-xs sm:text-sm font-black text-emerald-900 mt-2 font-mono">{lead.payoutData?.masterPayoutId || 'N/A'}</p>
                                                   </div>
                                                </div>
                                                {lead.payoutData?.doctorPayouts?.length > 0 && (
                                                   <div className="text-left space-y-4">
                                                      <p className="text-[8px] sm:text-[10px] font-black text-emerald-800/40 uppercase tracking-widest">Settlement Record</p>
                                                      <div className="space-y-4">
                                                         {lead.payoutData.doctorPayouts.filter(p => p.amount > 0).map((p, idx) => {
                                                            const gross = Number(p.amount);
                                                            const tds = Math.round(gross * 0.10);
                                                            const net = gross - tds;
                                                            return (
                                                               <div key={idx} className="bg-slate-50/50 p-4 rounded-3xl border border-slate-100 space-y-3 shadow-inner">
                                                                  {/* Header: Role & File Link */}
                                                                  <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                                                                     <div>
                                                                        <span className={`px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${p.role === 'surgeon' ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-purple-50 text-purple-600 border border-purple-100'}`}>
                                                                           {p.role}
                                                                        </span>
                                                                        <p className="text-xs font-black text-slate-800 mt-1">
                                                                           {p.doctorName || (p.role === 'surgeon' ? 'Surgeon' : 'Anesthetist')}
                                                                        </p>
                                                                     </div>
                                                                     {p.payoutProofUrl ? (
                                                                        <button
                                                                           onClick={() => handleFilePreview({ url: p.payoutProofUrl, name: `${p.role}_payout_proof` })}
                                                                           className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl transition-all border border-emerald-100"
                                                                        >
                                                                           <FileText className="w-3.5 h-3.5" />
                                                                           <span className="text-[9px] font-black uppercase tracking-widest font-black">Receipt PDF</span>
                                                                        </button>
                                                                     ) : (
                                                                        <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-lg uppercase">
                                                                           No Receipt
                                                                        </span>
                                                                     )}
                                                                  </div>

                                                                  {/* Breakdown */}
                                                                  <div className="grid grid-cols-3 gap-2 text-center bg-white p-2.5 rounded-2xl border border-slate-100">
                                                                     <div>
                                                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-tight">Gross</p>
                                                                        <p className="text-xs font-black text-slate-700 mt-0.5">₹{gross.toLocaleString('en-IN')}</p>
                                                                     </div>
                                                                     <div>
                                                                        <p className="text-[8px] font-black text-red-400 uppercase tracking-tight">TDS (10%)</p>
                                                                        <p className="text-xs font-black text-red-600 mt-0.5">₹{tds.toLocaleString('en-IN')}</p>
                                                                     </div>
                                                                     <div className="border-l border-slate-100">
                                                                        <p className="text-[8px] font-black text-emerald-500 uppercase tracking-tight">Net Paid</p>
                                                                        <p className="text-xs font-black text-emerald-600 mt-0.5">₹{net.toLocaleString('en-IN')}</p>
                                                                     </div>
                                                                  </div>
                                                               </div>
                                                            );
                                                         })}
                                                      </div>
                                                   </div>
                                                )}
                                                {/* Download Invoice */}
                                                <div className="pt-5 mt-2">
                                                   <button
                                                      onClick={() => generateDealInvoicePDF(lead, payments)}
                                                      className="w-full flex items-center justify-center gap-3 py-4 bg-[#143E78] hover:bg-[#1c4b8e] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-900/20 active:scale-95"
                                                   >
                                                      <Download className="w-4 h-4" />
                                                      Download Invoice PDF
                                                   </button>
                                                </div>
                                             </div>
                                          </div>
                                       </div>
                                       <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                                          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-3 px-8 py-4 bg-emerald-600 text-white rounded-full font-black uppercase text-[10px] tracking-widest hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-200">
                                             <ArrowLeft className="w-4 h-4" /> Return to Dashboard
                                          </button>
                                       </div>
                                    </div>

                                    <div className="bg-white rounded-[2.5rem] border border-slate-100 p-10 shadow-sm overflow-hidden relative group">
                                       <div className="flex items-center gap-4 mb-8">
                                          <div className="w-12 h-12 bg-slate-50 text-slate-500 rounded-2xl flex items-center justify-center">
                                             <FileText className="w-6 h-6" />
                                          </div>
                                          <div>
                                             <h3 className="text-lg font-black text-[#143E78] uppercase tracking-tighter leading-none">Archived Records</h3>
                                             <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-widest">Historical Data Vault</p>
                                          </div>
                                       </div>
                                       <p className="text-xs text-slate-500 font-medium leading-relaxed">
                                          This deal was finalized after clinical intake and payout settlements. All medical logs,
                                          financial transaction proofs, and stakeholder notes are now archived for your administrative records.
                                       </p>
                                    </div>
                                 </div>
                              )}

                           </>
                        )}
                     </div>
                  )}
               </div>
            </div>
            {/* 🏥 ADD/EDIT MONITORING LOG MODAL */}
            {showAddMonitoringLogModal && createPortal(
               <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100000] flex items-center justify-center p-4 overflow-y-auto">
                  <div className="bg-white w-full max-w-md rounded-[2rem] sm:rounded-[3rem] overflow-hidden shadow-2xl animate-zoomIn border border-white/20 my-auto">
                     <div className="bg-[#143E78] p-6 sm:p-8 text-white relative overflow-hidden flex justify-between items-center">
                        <div className="absolute top-0 right-0 p-8 opacity-10">
                           <ClipboardList className="w-20 h-20 sm:w-24 sm:h-24 rotate-12" />
                        </div>
                        <div className="relative z-10 flex items-center gap-3 sm:gap-4">
                           <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                              {monitoringLog.logId ? <Edit className="w-6 h-6 sm:w-8 h-8" /> : <Plus className="w-6 h-6 sm:w-8 h-8" />}
                           </div>
                           <div>
                              <h3 className="text-lg sm:text-xl font-black uppercase tracking-tighter text-white">
                                 {monitoringLog.logId ? 'Edit Doctor Visit' : 'Record Doctor Visit'}
                              </h3>
                              <p className="text-[9px] sm:text-[10px] font-bold text-white/40 uppercase tracking-widest mt-0.5">Log visit details & patient notes</p>
                           </div>
                        </div>
                        <button onClick={() => setShowAddMonitoringLogModal(false)} className="relative z-20 p-2 hover:bg-white/10 rounded-full transition-all">
                           <X className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                     </div>

                     <div className="p-6 sm:p-8 space-y-5 sm:space-y-6 bg-slate-50/50">
                        <div className="space-y-2">
                           <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Date of Visit</label>
                           <input
                              type="date"
                              min={isPastSurgeryCase ? undefined : todayDateStr}
                              value={monitoringLog.visitDate}
                              onChange={(e) => setMonitoringLog({ ...monitoringLog, visitDate: e.target.value })}
                              className="w-full px-5 sm:px-6 py-3.5 sm:py-4 bg-white rounded-xl sm:rounded-2xl border-2 border-slate-100 focus:border-blue-500 outline-none font-bold text-sm transition-all shadow-sm"
                           />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                           <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Start Time</label>
                              <input
                                 type="time"
                                 value={monitoringLog.startTime}
                                 onChange={(e) => setMonitoringLog({ ...monitoringLog, startTime: e.target.value })}
                                 className="w-full px-5 sm:px-6 py-3.5 sm:py-4 bg-white rounded-xl sm:rounded-2xl border-2 border-slate-100 focus:border-blue-500 outline-none font-bold text-sm transition-all shadow-sm"
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">End Time</label>
                              <input
                                 type="time"
                                 value={monitoringLog.endTime}
                                 onChange={(e) => setMonitoringLog({ ...monitoringLog, endTime: e.target.value })}
                                 className="w-full px-5 sm:px-6 py-3.5 sm:py-4 bg-white rounded-xl sm:rounded-2xl border-2 border-slate-100 focus:border-blue-500 outline-none font-bold text-sm transition-all shadow-sm"
                              />
                           </div>
                        </div>

                        <div className="space-y-2">
                           <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Observation Notes (Optional)</label>
                           <textarea
                              rows="3"
                              value={monitoringLog.notes}
                              onChange={(e) => setMonitoringLog({ ...monitoringLog, notes: e.target.value })}
                              placeholder="Any specific clinical observations or patient feedback..."
                              className="w-full px-5 sm:px-6 py-3.5 sm:py-4 bg-white rounded-xl sm:rounded-2xl border-2 border-slate-100 focus:border-blue-500 outline-none font-medium text-sm transition-all shadow-sm"
                           />
                        </div>

                        <div className="flex gap-4 pt-4">
                           <button
                              onClick={() => setShowAddMonitoringLogModal(false)}
                              className="flex-1 py-3.5 sm:py-4 text-slate-400 font-black uppercase text-[10px] tracking-widest hover:text-slate-600 transition-all"
                           >
                              Discard
                           </button>
                           <button
                              onClick={handleAddMonitoringLog}
                              disabled={isSubmittingLog}
                              className="flex-[2] py-3.5 sm:py-4 bg-[#143E78] text-white font-black rounded-xl sm:rounded-2xl uppercase text-[10px] tracking-widest hover:bg-blue-900 transition-all shadow-xl shadow-blue-900/10 active:scale-95 disabled:opacity-50"
                           >
                              {isSubmittingLog ? 'Saving...' : (monitoringLog.logId ? 'Update Log' : 'Save Visit Log')}
                           </button>
                        </div>
                     </div>
                  </div>
               </div>,
               document.body
            )
            }

            {/* 🔄 RESCHEDULE OT MODAL */}
            {
               showRescheduleModal && createPortal(
                  <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100000] flex items-center justify-center p-4 overflow-y-auto">
                     <div className="bg-white w-full max-w-lg rounded-[2rem] sm:rounded-[3rem] overflow-hidden shadow-2xl animate-zoomIn border border-white/20 my-auto flex flex-col max-h-[90vh]">
                        <div className="bg-slate-900 p-5 text-white relative overflow-hidden shrink-0 flex justify-between items-center">
                           <div className="absolute top-0 right-0 p-8 opacity-10">
                              <RefreshCcw className="w-24 h-24 rotate-12" />
                           </div>
                           <div className="relative z-10 flex items-center gap-4">
                              <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                                 <Calendar className="w-8 h-8" />
                              </div>
                              <div>
                                 <h3 className="text-xl font-black uppercase tracking-tighter">Reschedule Surgery</h3>
                                 <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Determine fault and re-initialize pipeline</p>
                              </div>
                           </div>
                           <button onClick={() => setShowRescheduleModal(false)} className="relative z-20 p-2 hover:bg-white/10 rounded-full transition-all">
                              <X className="w-6 h-6" />
                           </button>
                        </div>

                        {!rescheduleForm.isAbortingView ? (
                           <div className="p-5 space-y-4 bg-slate-50/50 overflow-y-auto">
                              {/* STICKY FAULT BUTTONS - won't scroll */}
                              <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm pb-3 space-y-3">
                                 <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] block text-center">
                                    Determine Fault Responsibility
                                 </label>
                                 <div className="grid grid-cols-3 gap-3">
                                    {['Patient', 'Hospital', 'Doctor'].map(fault => (
                                       <button
                                          key={fault}
                                          onClick={() => setRescheduleForm({ ...rescheduleForm, faultBy: fault })}
                                          className={`py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all border-2 ${rescheduleForm.faultBy === fault ? 'bg-[#143E78] text-white border-[#143E78] shadow-lg shadow-blue-900/20' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}
                                       >
                                          {fault}
                                       </button>
                                    ))}
                                 </div>
                              </div>

                              {/* REST OF CONTENT scrolls normally below */}

                              {rescheduleForm.faultBy === 'Hospital' && (
                                 <div className="space-y-6 bg-amber-50/50 p-6 rounded-[2.5rem] border-2 border-dashed border-amber-200">
                                    <div className="space-y-4">
                                       <label className="text-[11px] font-black text-amber-700 uppercase tracking-[0.2em] ml-1 flex items-center gap-2">
                                          <AlertCircle className="w-5 h-5" /> Penalty Amount (₹ Optional)
                                       </label>
                                       <div className="relative">
                                          <div className="absolute left-6 top-1/2 -translate-y-1/2 text-2xl font-black text-amber-300">₹</div>
                                          <input
                                             type="number"
                                             min="0"
                                             value={rescheduleForm.penaltyAmount}
                                             onChange={(e) => setRescheduleForm({ ...rescheduleForm, penaltyAmount: e.target.value })}
                                             className="w-full pl-12 pr-8 py-6 bg-white border-2 border-amber-500 rounded-3xl text-3xl font-black text-[#143E78] outline-none transition-all shadow-inner placeholder:text-amber-100"
                                             placeholder="Enter Amount (Optional)"
                                          />
                                       </div>
                                       <p className="text-[9px] font-bold text-amber-600 uppercase tracking-widest text-center px-4">
                                          Leave blank if no penalty. If entered, a payment link will be generated for the Hospital.
                                       </p>
                                    </div>

                                    <div className="pt-6 border-t border-slate-50 space-y-4">
                                       <div className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl">
                                          <div>
                                             <p className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Alternative Notification?</p>
                                             <p className="text-[8px] font-bold text-slate-400 uppercase">Send link to custom email/number</p>
                                          </div>
                                          <button
                                             onClick={() => setRescheduleForm({ ...rescheduleForm, useAlternate: !rescheduleForm.useAlternate })}
                                             className={`px-4 py-2 rounded-full text-[9px] font-black uppercase transition-all ${rescheduleForm.useAlternate ? "bg-orange-600 text-white shadow-lg" : "bg-slate-200 text-slate-500"}`}
                                          >
                                             {rescheduleForm.useAlternate ? "Active" : "Disabled"}
                                          </button>
                                       </div>

                                       {rescheduleForm.useAlternate && (
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 animate-fadeIn">
                                             <div className="space-y-1">
                                                <label className="text-[8px] font-black text-slate-400 uppercase ml-2">Alternate Email</label>
                                                <input
                                                   type="email"
                                                   placeholder="billing@hospital.com"
                                                   value={rescheduleForm.alternateEmail}
                                                   onChange={(e) => setRescheduleForm({ ...rescheduleForm, alternateEmail: e.target.value })}
                                                   className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold focus:bg-white outline-none"
                                                />
                                             </div>
                                             <div className="space-y-1">
                                                <label className="text-[8px] font-black text-slate-400 uppercase ml-2">Alternate WhatsApp</label>
                                                <input
                                                   type="text"
                                                   placeholder="10-digit number"
                                                   value={rescheduleForm.alternateWhatsapp}
                                                   onChange={(e) => setRescheduleForm({ ...rescheduleForm, alternateWhatsapp: e.target.value })}
                                                   className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold focus:bg-white outline-none"
                                                />
                                             </div>
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              )}

                              <div className="space-y-4 animate-fadeIn">
                                 <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-blue-500" /> New Surgery Date
                                 </label>
                                 <input
                                    type="date" min={(isPastSurgeryCase || lead?.isManualEntry) ? undefined : todayDateStr}
                                    value={rescheduleForm.newSurgeryDate}
                                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, newSurgeryDate: e.target.value })}
                                    className="w-full p-5 bg-white border-2 border-slate-100 rounded-[2rem] text-sm font-black text-[#143E78] outline-none focus:border-blue-500 transition-all"
                                 />
                              </div>

                              <div className="space-y-3">
                                 <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Internal Log / Reason</label>
                                 <textarea
                                    value={rescheduleForm.notes}
                                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, notes: e.target.value })}
                                    className="w-full p-6 bg-white border-2 border-slate-100 rounded-[2rem] text-xs font-bold text-slate-600 outline-none focus:border-[#143E78] transition-all min-h-[100px]"
                                    placeholder="Describe why the surgery slot is changing..."
                                 />
                              </div>

                              <div className="pt-4 flex flex-col gap-3">
                                 {isRescheduling ? (
                                    <div className="w-full py-5 bg-[#143E78] rounded-[2rem] relative overflow-hidden flex items-center justify-center gap-3">
                                       {/* Shimmer sweep */}
                                       <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                                       {/* Skeleton blobs */}
                                       <div className="w-4 h-4 rounded-full bg-blue-500/60 animate-pulse" />
                                       <div className="h-3 w-44 rounded-full bg-blue-500/60 animate-pulse" />
                                    </div>
                                 ) : (
                                    <button
                                       onClick={handleRescheduleSurgery}
                                       disabled={isRescheduling || !rescheduleForm.faultBy}
                                       className="w-full py-5 bg-[#143E78] text-white font-black rounded-[2rem] uppercase text-xs tracking-[0.2em] hover:bg-slate-900 transition-all shadow-xl shadow-blue-900/20 disabled:opacity-50 flex items-center justify-center gap-3"
                                    >
                                       <ShieldCheck className="w-4 h-4" />
                                       {rescheduleForm.faultBy === 'Hospital' ? 'Send Penalty Link & Reschedule' : 'Confirm Direct Reschedule'}
                                    </button>
                                 )}

                                 <button
                                    onClick={() => setRescheduleForm({ ...rescheduleForm, isAbortingView: true })}
                                    className="w-full py-4 bg-slate-100 text-slate-500 font-black rounded-[2rem] uppercase text-[10px] tracking-widest hover:bg-red-50 hover:text-red-600 transition-all border border-slate-200"
                                 >
                                    Switch to Abortion View
                                 </button>
                                 <button
                                    onClick={() => setShowRescheduleModal(false)}
                                    className="w-full py-2 text-slate-400 font-bold uppercase text-[9px] tracking-widest hover:text-slate-600"
                                 >
                                    Close Modal
                                 </button>
                              </div>
                           </div>
                        ) : (
                           <div className="p-5 space-y-4 bg-slate-50/50 overflow-y-auto">
                              <div className="space-y-4">
                                 <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Who is responsible for Abortion?</label>
                                 <div className="grid grid-cols-3 gap-3">
                                    {['Patient', 'Hospital', 'Doctor'].map(fault => (
                                       <button
                                          key={fault}
                                          onClick={() => setRescheduleForm({ ...rescheduleForm, abortFault: fault })}
                                          className={`py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all border-2 ${rescheduleForm.abortFault === fault ? 'bg-red-600 text-white border-red-600 shadow-lg shadow-red-200' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}
                                       >
                                          {fault}
                                       </button>
                                    ))}
                                 </div>
                              </div>

                              {rescheduleForm.abortFault === 'Hospital' && (
                                 <div className="space-y-4 animate-slideDown p-6 bg-red-50 rounded-3xl border border-red-100">
                                    <label className="text-[11px] font-black text-red-600 uppercase tracking-[0.2em] ml-1">Penalty Percentage (%)</label>
                                    <div className="relative group">
                                       <input
                                          type="number"
                                          value={rescheduleForm.penaltyPercentage}
                                          onChange={(e) => setRescheduleForm({ ...rescheduleForm, penaltyPercentage: e.target.value })}
                                          className="w-full pr-12 pl-6 py-4 bg-white border-2 border-red-100 rounded-2xl text-xl font-black text-red-900 outline-none focus:border-red-500 transition-all"
                                          placeholder="0"
                                       />
                                       <div className="absolute right-6 top-1/2 -translate-y-1/2 text-xl font-black text-red-300">%</div>
                                    </div>
                                    <p className="text-[9px] font-bold text-red-400 uppercase tracking-widest leading-relaxed">Lead will move to Payout stage for penalty settlement.</p>
                                 </div>
                              )}

                              <div className="space-y-4">
                                 <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Reason for Abortion</label>
                                 <textarea
                                    value={rescheduleForm.abortReason}
                                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, abortReason: e.target.value })}
                                    className="w-full p-6 bg-white border-2 border-red-100 rounded-3xl text-xs font-bold text-slate-600 outline-none focus:border-red-500 transition-all min-h-[100px]"
                                    placeholder="Describe why the surgery is being aborted permanently..."
                                 />
                              </div>

                              <div className="pt-4 flex flex-col gap-3">
                                 <button
                                    onClick={handleAbortSurgery}
                                    disabled={isRescheduling}
                                    className="w-full py-5 bg-red-600 text-white font-black rounded-[2rem] uppercase text-xs tracking-widest hover:bg-red-700 transition-all shadow-xl shadow-red-900/20 disabled:opacity-50 flex items-center justify-center gap-3"
                                 >
                                    {isRescheduling ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                                    Confirm Permanent Abortion
                                 </button>
                                 <button
                                    onClick={() => setRescheduleForm({ ...rescheduleForm, isAbortingView: false })}
                                    className="w-full py-4 text-slate-400 font-black uppercase text-[10px] tracking-widest hover:text-slate-600 transition-all"
                                 >
                                    Go Back to Reschedule
                                 </button>
                              </div>
                           </div>
                        )}
                     </div>
                  </div>,
                  document.body
               )
            }

            {/* 🖼️ DOCUMENT PREVIEW MODAL */}
            {
               previewFile && createPortal(
                  <div onClick={() => setPreviewFile(null)} className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[100000] flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto">
                     <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-5xl rounded-2xl sm:rounded-[2.5rem] overflow-hidden shadow-2xl animate-zoomIn flex flex-col h-[94vh] sm:h-[90vh] max-h-[96vh] my-auto">
                        <div className="p-4 sm:p-6 border-b flex justify-between items-center bg-white sticky top-0 z-10">
                           <div className="flex items-center gap-3 sm:gap-4">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#143E78]/5 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#143E78] shrink-0">
                                 {previewFile.isPDF ? <FileText className="w-5 h-5 sm:w-6 sm:h-6" /> : <Image className="w-5 h-5 sm:w-6 sm:h-6" />}
                              </div>
                              <div className="min-w-0">
                                 <h3 className="text-base sm:text-lg font-black text-[#143E78] uppercase tracking-tight sm:tracking-tighter truncate">Document Preview</h3>
                                 <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 max-w-[200px] sm:max-w-[300px] truncate">{previewFile.name}</p>
                              </div>
                           </div>
                           <button onClick={() => setPreviewFile(null)} className="p-2.5 sm:p-3 bg-slate-100 hover:bg-red-50 hover:text-red-500 rounded-xl sm:rounded-2xl transition-all group shrink-0">
                              <X className="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-90 transition-transform" />
                           </button>
                        </div>
                        <div className="flex-1 overflow-hidden p-2 sm:p-6 bg-slate-100 flex items-center justify-center min-h-0">
                           {previewFile.isPDF ? (
                              <iframe src={`${previewFile.url}#toolbar=0`} className="w-full h-full rounded-xl sm:rounded-2xl border-none bg-white shadow-sm" title="PDF Preview" />
                           ) : (
                              <div className="relative group max-h-full flex items-center justify-center overflow-auto">
                                 <img src={previewFile.url} alt="Preview" className="max-w-full max-h-full object-contain rounded-xl sm:rounded-2xl shadow-2xl border-2 sm:border-4 border-white" />
                              </div>
                           )}
                        </div>
                        <div className="p-4 sm:p-6 border-t bg-white flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 shrink-0">
                           <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black text-slate-400 uppercase">File Info:</span>
                              <span className="text-[10px] font-bold text-[#143E78] bg-blue-50 px-2 py-1 rounded">
                                 {previewFile.type || (previewFile.url?.endsWith('.pdf') ? 'application/pdf' : 'image/generic')}
                              </span>
                           </div>
                           <button onClick={() => {
                              const link = document.createElement('a');
                              link.href = previewFile.url;
                              link.download = previewFile.name || 'document';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                           }} className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-[#143E78] text-white font-black rounded-xl sm:rounded-2xl shadow-xl shadow-blue-900/20 uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-95 transition-all">
                              <Download className="w-4 h-4" /> Download Original File
                           </button>
                        </div>
                     </div>
                  </div>,
                  document.body
               )
            }

            {/* 🔄 REOPEN LEAD CHOICE MODAL */}
            {
               showRevertModal && createPortal(
                  <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100000] flex items-center justify-center p-4 overflow-y-auto">
                     <div className="bg-white w-full max-w-lg rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-zoomIn my-auto border border-slate-100">
                        <div className="bg-gradient-to-br from-indigo-600 to-[#143E78] p-6 sm:p-8 text-white relative flex justify-between items-start">
                           <div className="space-y-1">
                              <div className="w-12 h-12 bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center mb-3">
                                 <RefreshCcw className="w-6 h-6 text-indigo-200" />
                              </div>
                              <h2 className="text-xl font-black uppercase tracking-tight">Reopen Lost Case</h2>
                              <p className="text-xs font-medium text-indigo-200">Choose which stage to restore this lead to:</p>
                           </div>
                           <button onClick={() => setShowRevertModal(false)} className="p-2 hover:bg-white/10 rounded-xl text-white/70 hover:text-white transition-all">
                              <X className="w-6 h-6" />
                           </button>
                        </div>

                        <div className="p-6 sm:p-8 space-y-4">
                           {/* Button 1: Reopen from Beginning */}
                           <button
                              onClick={async () => {
                                 setUpdating(true);
                                 try {
                                    await api.put(`/leads/${id}/reopen`, { mode: 'start' });
                                    toast.success('Lead reopened from Stage 1: Intake');
                                    setShowRevertModal(false);
                                    await fetchLead();
                                 } catch (err) {
                                    toast.error(err.response?.data?.message || 'Failed to reopen lead');
                                 } finally {
                                    setUpdating(false);
                                 }
                              }}
                              disabled={updating}
                              className="w-full text-left p-5 bg-slate-50 hover:bg-indigo-50/80 border-2 border-slate-200 hover:border-indigo-500 rounded-2xl transition-all group flex items-start gap-4 shadow-sm cursor-pointer"
                           >
                              <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 group-hover:border-indigo-500 text-slate-600 group-hover:text-indigo-600 flex items-center justify-center font-black shrink-0 transition-all shadow-sm">
                                 <RotateCcw className="w-5 h-5" />
                              </div>
                              <div className="space-y-1 flex-1">
                                 <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-black text-slate-900 group-hover:text-indigo-950 uppercase tracking-wide">
                                       1. Reopen from Beginning
                                    </h4>
                                    <span className="text-[10px] font-black uppercase bg-slate-200 group-hover:bg-indigo-200 text-slate-700 group-hover:text-indigo-900 px-2.5 py-0.5 rounded-md">
                                       Stage 1
                                    </span>
                                 </div>
                                 <p className="text-xs font-bold text-indigo-600">Stage 1: Intake</p>
                                 <p className="text-[11px] font-medium text-slate-500 group-hover:text-slate-700 leading-relaxed pt-1">
                                    Start fresh from Stage 1 (Intake). Re-evaluate patient intake and workflow from scratch.
                                 </p>
                              </div>
                           </button>

                           {/* Button 2: Reopen from Lost Stage */}
                           {lead?.previousStatus && lead.previousStatus !== 'new' && (
                              <button
                                 onClick={async () => {
                                    setUpdating(true);
                                    try {
                                       await api.put(`/leads/${id}/reopen`, { mode: 'previous' });
                                       toast.success(`Lead restored to ${getStageLabelForStatus(lead.previousStatus)}`);
                                       setShowRevertModal(false);
                                       await fetchLead();
                                    } catch (err) {
                                       toast.error(err.response?.data?.message || 'Failed to reopen lead');
                                    } finally {
                                       setUpdating(false);
                                    }
                                 }}
                                 disabled={updating}
                                 className="w-full text-left p-5 bg-indigo-50/50 hover:bg-indigo-100/70 border-2 border-indigo-200 hover:border-indigo-600 rounded-2xl transition-all group flex items-start gap-4 shadow-sm cursor-pointer"
                              >
                                 <div className="w-11 h-11 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black shrink-0 shadow-md shadow-indigo-200 transition-all">
                                    <FastForward className="w-5 h-5" />
                                 </div>
                                 <div className="space-y-1 flex-1">
                                    <div className="flex items-center justify-between">
                                       <h4 className="text-sm font-black text-indigo-950 uppercase tracking-wide">
                                          2. Reopen from Lost Stage
                                       </h4>
                                       <span className="text-[10px] font-black uppercase bg-indigo-600 text-white px-2.5 py-0.5 rounded-md shadow-xs">
                                          Saved Stage
                                       </span>
                                    </div>
                                    <p className="text-xs font-bold text-indigo-700">
                                       {getStageLabelForStatus(lead.previousStatus)}
                                    </p>
                                    <p className="text-[11px] font-medium text-indigo-900/80 leading-relaxed pt-1">
                                       Restore lead directly back to where it was lost ({getStageLabelForStatus(lead.previousStatus)}).
                                    </p>
                                 </div>
                              </button>
                           )}

                           <button
                              onClick={() => setShowRevertModal(false)}
                              className="w-full py-3.5 text-slate-400 font-black uppercase text-xs tracking-widest hover:text-slate-600 transition-all mt-2 cursor-pointer"
                           >
                              Cancel
                           </button>
                        </div>
                     </div>
                  </div>,
                  document.body
               )
            }

            {/* 🏥 HOSPITAL CHANGE MODAL */}
            {
               showHospitalChange && createPortal(
                  <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100000] flex items-center justify-center p-4 overflow-y-auto">
                     <div className="bg-white w-full max-w-lg rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-zoomIn max-h-[90vh] flex flex-col my-auto">
                        <div className="bg-[#143E78] p-5 text-white flex justify-between items-center shrink-0">
                           <div>
                              <h2 className="text-xl font-black uppercase tracking-tighter">Assign Hospital</h2>
                              <p className="text-[10px] font-bold text-indigo-300 uppercase tracking-widest mt-1">Choose hospital for treatment</p>
                           </div>
                           <button onClick={() => setShowHospitalChange(false)} className="p-2 hover:bg-white/10 rounded-full transition-all">
                              <X className="w-6 h-6" />
                           </button>
                        </div>
                        <div className="p-8 space-y-6">
                           <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                              {hospitals.map((h) => (
                                 <button
                                    key={h._id}
                                    onClick={async () => {
                                       try {
                                          setUpdating(true);
                                          await api.patch(`/leads/${id}`, { assignedHospital: h._id });

                                          const isChange = lead.assignedHospital && (lead.assignedHospital._id || lead.assignedHospital) !== h._id;
                                          const updatedLead = { ...lead, assignedHospital: h };
                                          setLead(updatedLead);
                                          setHospitalProcess('bed_check');
                                          setShowHospitalChange(false);

                                          if (isChange) {
                                             try {
                                                await api.post(`/leads/${id}/notify-hospital-change`, {
                                                   hospitalName: h.hospitalName,
                                                   location: h.address?.city || 'Primary Center'
                                                });
                                                toast.success(`Hospital updated and patient notified: ${h.hospitalName}`);
                                             } catch (err) {
                                                console.error("Notification failed", err);
                                             }
                                          } else {
                                             toast.success(`Assigned to ${h.hospitalName}`);
                                          }
                                       } catch (error) {
                                          toast.error('Failed to assign hospital. Please try again.');
                                       } finally {
                                          setUpdating(false);
                                       }
                                    }}
                                    className={`w-full p-4 rounded-2xl border-2 flex items-center justify-between transition-all ${lead.assignedHospital?._id === h._id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-50 hover:border-[#143E78]'}`}
                                 >
                                    <div className="flex items-center gap-4 text-left">
                                       <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                                          <Building2 className="w-5 h-5 text-slate-400" />
                                       </div>
                                       <div>
                                          <p className="text-xs font-black text-[#143E78]">{h.hospitalName}</p>
                                          <p className="text-[9px] font-bold text-slate-400 uppercase">{h.address?.city || 'Primary Center'}</p>
                                       </div>
                                    </div>
                                    {lead.assignedHospital?._id === h._id && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                                 </button>
                              ))}
                           </div>
                           <button onClick={() => setShowHospitalChange(false)} className="w-full py-4 bg-slate-100 text-slate-500 font-black rounded-2xl uppercase tracking-widest">Close</button>
                        </div>
                     </div>
                  </div>,
                  document.body
               )}

            {showCCReassignModal && createPortal(
               <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 overflow-y-auto">
                  <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl flex flex-col max-h-[90vh] my-auto animate-zoomIn">
                     <div className="bg-[#143E78] rounded-t-[2rem] px-6 py-5 text-white shrink-0 relative overflow-hidden flex justify-between items-center">
                        <div className="relative z-10">
                           <h3 className="text-xl font-black uppercase tracking-tighter">Reassign Agent</h3>
                           <p className="text-blue-200 text-[10px] font-bold mt-1 uppercase tracking-widest">Select new Customer Care owner</p>
                        </div>
                        <button onClick={() => setShowCCReassignModal(false)} className="relative z-20 p-2 hover:bg-white/10 rounded-full transition-all">
                           <X className="w-6 h-6" />
                        </button>
                     </div>
                     <div className="p-6 overflow-y-auto space-y-3">
                        {agents.length > 0 ? (
                           agents.map(agent => (
                              <button
                                 key={agent._id}
                                 onClick={() => handleReassignCC(agent._id)}
                                 className={`w-full p-4 flex items-center justify-between rounded-2xl border-2 transition-all ${lead.assignedCustomerCare?._id === agent._id ? 'bg-indigo-50 border-indigo-600' : 'bg-slate-50 border-transparent hover:border-slate-200'}`}
                              >
                                 <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs ${lead.assignedCustomerCare?._id === agent._id ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                       {agent.firstName?.[0]}{agent.lastName?.[0]}
                                    </div>
                                    <div className="text-left">
                                       <p className="font-black text-slate-800 text-sm uppercase">{agent.firstName} {agent.lastName}</p>
                                       <p className="text-[10px] font-bold text-slate-400">{agent.email}</p>
                                    </div>
                                 </div>
                                 {lead.assignedCustomerCare?._id === agent._id && (
                                    <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                                 )}
                              </button>
                           ))
                        ) : (
                           <div className="py-10 text-center opacity-40">
                              <UserPlus className="w-10 h-10 mx-auto mb-2" />
                              <p className="text-xs font-black uppercase tracking-widest">No Active Agents Found</p>
                           </div>
                        )}
                     </div>
                  </div>
               </div>,
               document.body
            )}

            {/* 🛡️ DEAL FINALIZE CONFIRMATION MODAL */}
            {showFinalizeModal && createPortal(
               <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100000] flex items-center justify-center p-4 overflow-y-auto">
                  <div className="bg-white w-full max-w-md rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-zoomIn my-auto">
                     <div className="bg-[#143E78] p-8 text-white flex flex-col items-center text-center gap-4 relative">
                        <button onClick={() => setShowFinalizeModal(false)} className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-all">
                           <X className="w-6 h-6" />
                        </button>
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                           <ShieldCheck className="w-8 h-8 text-emerald-400" />
                        </div>
                        <div>
                           <h2 className="text-xl font-black uppercase tracking-tighter">Confirm Deal Finalization</h2>
                           <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest mt-1">This action is permanent and irreversible</p>
                        </div>
                     </div>
                     <div className="p-8 space-y-6">
                        <p className="text-sm font-bold text-slate-500 text-center leading-relaxed">
                           Are you sure you want to finalize this deal? All financial payouts, TDS calculations, and hospital settlements will be locked as completed.
                        </p>
                        <div className="flex flex-col gap-3">
                           <button onClick={handleFinalizeDeal} disabled={isFinalizingDeal} className="w-full py-5 bg-[#143E78] text-white font-black rounded-2xl shadow-lg uppercase text-xs tracking-[0.2em] transition-all hover:scale-[1.02]">
                              {isFinalizingDeal ? 'Finalizing...' : 'Yes, Finalize Deal'}
                           </button>
                           <button onClick={() => setShowFinalizeModal(false)} className="w-full py-4 text-slate-400 font-black uppercase text-[10px] tracking-widest hover:text-slate-600 transition-all">
                              Cancel
                           </button>
                        </div>
                     </div>
                  </div>
               </div>,
               document.body
            )}

            {/* Quick List Modals */}
            <DoctorListPopup
               isOpen={showDocListModal}
               onClose={() => setShowDocListModal(false)}
               doctors={doctors}
               onSelect={(docId) => {
                  setAssignmentForm(prev => ({ ...prev, assignedSurgeon: docId }));
                  setShowDocListModal(false);
               }}
            />

            <HospitalListPopup
               isOpen={showHospListModal}
               onClose={() => setShowHospListModal(false)}
               hospitals={hospitals}
               onSelect={(hospId) => {
                  setAssignmentForm(prev => ({ ...prev, assignedHospital: hospId }));
                  setShowHospListModal(false);
               }}
            />

            <AvailabilityPopup
               isOpen={showAvailModal}
               onClose={() => setShowAvailModal(false)}
               doctors={doctors}
               onRefresh={fetchDoctorsAndHospitals}
            />

            {/* 📞 QUICK CALL FOLLOW-UP STATUS MODAL */}
            {
               showQuickDispModal && latestCall && createPortal(
                  <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4">
                     <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
                        <div className="bg-violet-700 p-5 text-white flex justify-between items-center shrink-0">
                           <div className="flex items-center gap-2.5">
                              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                                 <PhoneCall className="w-5 h-5" />
                              </div>
                              <div>
                                 <h2 className="text-base font-bold">Update Follow-up Status</h2>
                                 <p className="text-[11px] text-violet-200">{lead?.patientName} • {lead?.patientPhone}</p>
                              </div>
                           </div>
                           <button
                              type="button"
                              onClick={() => setShowQuickDispModal(false)}
                              className="p-1.5 hover:bg-white/20 rounded-lg text-white/80 hover:text-white transition-all cursor-pointer"
                           >
                              <X className="w-5 h-5" />
                           </button>
                        </div>

                        <div className="p-5 space-y-4">
                           <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1.5">
                              <div className="flex items-center justify-between">
                                 <span className="text-[11px] font-semibold text-slate-500">Exotel Telephony Status:</span>
                                 <span className="font-bold text-slate-700">{getLightStatusBadge(latestCall.status).label}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                 <span className="text-[11px] font-semibold text-slate-500">Call Time:</span>
                                 <span className="text-slate-600">
                                    {new Date(latestCall.createdAt || latestCall.startTime).toLocaleString('en-IN', {
                                       day: '2-digit', month: 'short', year: 'numeric',
                                       hour: '2-digit', minute: '2-digit', hour12: true
                                    })}
                                 </span>
                              </div>
                           </div>

                           <form onSubmit={handleSaveQuickDisp} className="space-y-4">
                              <div>
                                 <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                                    Follow-up Status <span className="text-rose-500">*</span>
                                 </label>
                                 <select
                                    value={quickDispForm.disposition}
                                    onChange={e => setQuickDispForm({ ...quickDispForm, disposition: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold bg-white text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                    required
                                 >
                                    <option value="callback_completed">🟢 Callback Completed / Resolved</option>
                                    <option value="follow_up_needed">🔵 Follow-up Needed (Call Later)</option>
                                    <option value="appointment_booked">🟣 Appointment Booked</option>
                                    <option value="not_interested">⚪ Not Interested</option>
                                    <option value="invalid_number">🔴 Invalid / Wrong Phone Number</option>
                                    <option value="pending">🟡 Pending Callback</option>
                                 </select>
                              </div>

                              <div>
                                 <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                                    Call Reason / Inquiry
                                 </label>
                                 <input
                                    type="text"
                                    placeholder="e.g. Surgery consultation, Pricing query"
                                    value={quickDispForm.callReason}
                                    onChange={e => setQuickDispForm({ ...quickDispForm, callReason: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-violet-500"
                                 />
                              </div>

                              <div>
                                 <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                                    Follow-up Notes & Remarks
                                 </label>
                                 <textarea
                                    rows={3}
                                    placeholder="e.g. Spoke to patient on 9 Sept. Confirmed details and callback scheduled."
                                    value={quickDispForm.dispositionNotes}
                                    onChange={e => setQuickDispForm({ ...quickDispForm, dispositionNotes: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-violet-500 resize-none"
                                 />
                              </div>

                              <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
                                 <button
                                    type="button"
                                    disabled={isSavingQuickDisp}
                                    onClick={() => setShowQuickDispModal(false)}
                                    className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-all cursor-pointer"
                                 >
                                    Cancel
                                 </button>
                                 <button
                                    type="submit"
                                    disabled={isSavingQuickDisp}
                                    className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-violet-200 flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                                 >
                                    {isSavingQuickDisp ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                                    <span>Save Status</span>
                                 </button>
                              </div>
                           </form>
                        </div>
                     </div>
                  </div>,
                  document.body
               )
            }

            {/* 🧮 OT COST CALCULATOR MODAL */}
            {showCalculatorModal && createPortal(
               <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-3 sm:p-6 overflow-y-auto">
                  <div className="bg-white w-full max-w-[96vw] xl:max-w-7xl 2xl:max-w-[1550px] rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl flex flex-col max-h-[94vh] my-auto animate-zoomIn border border-slate-100">
                     {/* Modal Header */}
                     <div className="bg-[#143E78] rounded-t-[2rem] px-8 py-5 text-white shrink-0 relative overflow-hidden flex justify-between items-center text-left">
                        <div className="relative z-10 text-left">
                           <div className="flex flex-wrap items-center gap-3">
                              <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-2">
                                 <Calculator className="w-5.5 h-5.5 text-emerald-400" />
                                 {lead?.assignedHospital?.hospitalName || lead?.assignedHospital?.name || lead?.assignedHospital?.user?.fullName || "Hospital"} — Cost Calculator
                              </h3>
                              {(lead?.assignedHospital?.phone || lead?.assignedHospital?.contactPerson?.phone || lead?.assignedHospital?.user?.phone) && (
                                 <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-full text-[10px] font-black uppercase tracking-wider shadow-inner">
                                    <Phone className="w-3 h-3 text-emerald-400" />
                                    {lead?.assignedHospital?.phone || lead?.assignedHospital?.contactPerson?.phone || lead?.assignedHospital?.user?.phone}
                                 </span>
                              )}
                           </div>
                           <p className="text-blue-200 text-xs font-bold mt-1 uppercase tracking-widest">
                              Empower billing alignment by hospital items and utility deductions for {lead?.assignedHospital?.hospitalName || lead?.assignedHospital?.name || lead?.assignedHospital?.user?.fullName || "hospital"}
                           </p>
                        </div>
                        <button onClick={() => setShowCalculatorModal(false)} className="relative z-20 p-2 hover:bg-white/10 rounded-full transition-all cursor-pointer">
                           <X className="w-6 h-6" />
                        </button>
                     </div>

                     {/* Modal Body */}
                     <div className="p-6 sm:p-8 overflow-y-auto space-y-8 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 text-left">
                        {/* Section 1: Base Hospital Bill Items */}
                        <div className="space-y-4 col-span-1 lg:col-span-7 xl:col-span-7">
                           <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-2.5 gap-2">
                              <div>
                                 <h4 className="text-base sm:text-lg font-black text-[#143E78] uppercase tracking-wider flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-blue-500" />
                                    Hospital Base Payment Structure
                                 </h4>
                                 <p className="text-xs font-bold text-slate-400 mt-0.5">
                                    Only rows with rates filled in will be applied to the bill.
                                 </p>
                              </div>
                              <div className="flex items-center gap-2">
                                 <button
                                    type="button"
                                    onClick={handleApplyHospitalTemplate}
                                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-black px-3.5 py-2 rounded-xl uppercase tracking-wider flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                                 >
                                    <Sparkles className="w-3.5 h-3.5 text-emerald-200" /> Auto-Fill Rates
                                 </button>
                                 <button
                                    type="button"
                                    onClick={() => setCalcBaseItems([...calcBaseItems, { name: '', rate: '', days: '', hours: '', amount: '' }])}
                                    className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 font-black px-3.5 py-2 rounded-xl uppercase tracking-wider flex items-center gap-1.5 border border-blue-200/80 transition-all cursor-pointer"
                                 >
                                    <Plus className="w-4 h-4" /> Add Row
                                 </button>
                              </div>
                           </div>
                           <div className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b pb-1.5">
                              <span className="flex-1 min-w-0">Item</span>
                              <span className="w-28 text-center">Hospital Rate</span>
                              <span className="w-20 text-right">Rate (₹)</span>
                              <span className="w-14 text-center">Days</span>
                              <span className="w-14 text-center">Hours</span>
                              <span className="w-20 text-right">Total</span>
                              <span className="w-7"></span>
                           </div>
                           <div className="space-y-2.5 max-h-[520px] overflow-y-auto overflow-x-hidden pr-1">
                              {calcBaseItems.map((item, idx) => (
                                 <div key={`base-${idx}`} className="flex items-center gap-1.5 bg-slate-50/90 hover:bg-slate-50 p-2 rounded-xl border border-slate-200/70 shadow-2xs transition-all">
                                    <div className="flex-1 min-w-0">
                                       <input
                                          type="text"
                                          value={item.name}
                                          onChange={(e) => {
                                             const newList = [...calcBaseItems];
                                             newList[idx].name = e.target.value;
                                             setCalcBaseItems(newList);
                                          }}
                                          className="w-full bg-white border border-slate-200 rounded-lg p-1.5 px-2.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-[#143E78]"
                                          placeholder="Description..."
                                       />
                                    </div>
                                    {(() => {
                                       const official = getOfficialRateInfo(item.name);
                                       return (
                                          <div className="w-28 text-center shrink-0">
                                             {official ? (
                                                <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200/80 rounded-lg text-[10px] font-extrabold inline-flex items-center justify-center gap-0.5 whitespace-nowrap shadow-2xs">
                                                   <span>₹{official.rate.toLocaleString()}</span>
                                                   <span className="text-[8.5px] text-blue-500 font-bold uppercase">({official.unitType === 'Fixed Amount' ? 'Fixed' : official.unitType})</span>
                                                </span>
                                             ) : (
                                                <span className="text-xs font-bold text-slate-300">—</span>
                                             )}
                                          </div>
                                       );
                                    })()}
                                    <div className="flex items-center gap-1.5 shrink-0">
                                       <input
                                          type="text"
                                          inputMode="numeric"
                                          value={item.rate}
                                          onChange={(e) => {
                                             const newList = [...calcBaseItems];
                                             const raw = e.target.value.replace(/[^0-9]/g, '');
                                             newList[idx].rate = raw;
                                             const dNum = newList[idx].days === '' ? 1 : (Number(newList[idx].days) || 1);
                                             const hNum = newList[idx].hours === '' ? 1 : (Number(newList[idx].hours) || 1);
                                             newList[idx].amount = (Number(raw) * dNum * hNum).toString();
                                             setCalcBaseItems(newList);
                                          }}
                                          className="w-20 bg-white border border-slate-200 rounded-lg p-1.5 px-2 text-xs font-bold text-slate-800 text-right focus:outline-none focus:border-[#143E78]"
                                          placeholder="Rate (₹)"
                                       />
                                       <input
                                          type="text"
                                          inputMode="numeric"
                                          value={item.days}
                                          onChange={(e) => {
                                             const newList = [...calcBaseItems];
                                             const raw = e.target.value.replace(/[^0-9]/g, '');
                                             newList[idx].days = raw;
                                             const rateNum = Number(newList[idx].rate) || 0;
                                             const d = raw === '' ? 1 : Number(raw);
                                             const h = newList[idx].hours === '' ? 1 : (Number(newList[idx].hours) || 1);
                                             newList[idx].amount = (rateNum * d * h).toString();
                                             setCalcBaseItems(newList);
                                          }}
                                          className="w-14 bg-white border border-slate-200 rounded-lg p-1.5 px-1 text-xs font-bold text-slate-800 text-center focus:outline-none focus:border-[#143E78]"
                                          placeholder="Days"
                                       />
                                       <input
                                          type="text"
                                          inputMode="numeric"
                                          value={item.hours}
                                          onChange={(e) => {
                                             const newList = [...calcBaseItems];
                                             const raw = e.target.value.replace(/[^0-9]/g, '');
                                             newList[idx].hours = raw;
                                             const rateNum = Number(newList[idx].rate) || 0;
                                             const d = newList[idx].days === '' ? 1 : (Number(newList[idx].days) || 1);
                                             const h = raw === '' ? 1 : Number(raw);
                                             newList[idx].amount = (rateNum * d * h).toString();
                                             setCalcBaseItems(newList);
                                          }}
                                          className="w-14 bg-white border border-slate-200 rounded-lg p-1.5 px-1 text-xs font-bold text-slate-800 text-center focus:outline-none focus:border-[#143E78]"
                                          placeholder="Hours"
                                       />
                                       <div className="w-20 text-right text-xs font-black text-slate-700 px-1 whitespace-nowrap">
                                          ₹{Number(item.amount || 0).toLocaleString()}
                                       </div>
                                       <button
                                          type="button"
                                          onClick={() => {
                                             const newList = calcBaseItems.filter((_, i) => i !== idx);
                                             setCalcBaseItems(newList);
                                          }}
                                          className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                                       >
                                          <Trash2 className="w-4 h-4" />
                                       </button>
                                    </div>
                                 </div>
                              ))}
                              {calcBaseItems.length === 0 && calcDeductedItems.filter(d => (d.name || d.rate || d.amount)).length === 0 && (
                                 <p className="text-xs text-slate-400 font-bold italic py-4 text-center">No base items added yet.</p>
                              )}

                              {/* Mirrored Deduction Items under Base Hospital Charges (Read-Only) */}
                              {calcDeductedItems.filter(d => (d.name || d.rate || d.amount)).map((deductItem, dIdx) => (
                                 <div key={`mirrored-deduct-${dIdx}`} className="flex items-center gap-1.5 bg-amber-50/70 p-2 rounded-xl border border-amber-200/60">
                                    <div className="flex-1 min-w-0 bg-amber-50 border border-amber-200/80 rounded-lg p-1.5 px-2.5 text-xs font-bold text-amber-950 flex items-center justify-between">
                                       <span className="truncate">{deductItem.name || 'Deduction Item'}</span>
                                       <span className="text-[8.5px] font-black uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded tracking-wider ml-1">Deduction</span>
                                    </div>
                                    <div className="w-28 text-center text-xs font-bold text-amber-700 italic shrink-0">Deduction</div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                       <input
                                          type="text"
                                          readOnly
                                          disabled
                                          value={deductItem.rate}
                                          className="w-20 bg-amber-50 border border-amber-200/80 rounded-lg p-1.5 px-2 text-xs font-bold text-amber-950 text-right cursor-not-allowed opacity-90"
                                          placeholder="Rate (₹)"
                                       />
                                       <input
                                          type="text"
                                          readOnly
                                          disabled
                                          value={deductItem.days}
                                          className="w-14 bg-amber-50 border border-amber-200/80 rounded-lg p-1.5 px-1 text-xs font-bold text-amber-950 text-center cursor-not-allowed opacity-90"
                                          placeholder="Days"
                                       />
                                       <input
                                          type="text"
                                          readOnly
                                          disabled
                                          value={deductItem.hours}
                                          className="w-14 bg-amber-50 border border-amber-200/80 rounded-lg p-1.5 px-1 text-xs font-bold text-amber-950 text-center cursor-not-allowed opacity-90"
                                          placeholder="Hours"
                                       />
                                       <div className="w-20 text-right text-xs font-black text-amber-950 px-1 whitespace-nowrap">
                                          ₹{Number(deductItem.amount || 0).toLocaleString()}
                                       </div>
                                       <div className="w-7 flex items-center justify-center text-amber-500">
                                          <Lock className="w-3.5 h-3.5" />
                                       </div>
                                    </div>
                                 </div>
                              ))}
                           </div>
                        </div>

                        {/* Section 2: Deductions / Other Utilities */}
                        <div className="space-y-4 col-span-1 lg:col-span-5 xl:col-span-5 text-left">
                           <div className="flex justify-between items-center border-b pb-2.5">
                              <h4 className="text-base sm:text-lg font-black text-amber-600 uppercase tracking-wider flex items-center gap-2">
                                 <AlertTriangle className="w-5 h-5 text-amber-500" />
                                 Deductions (Other Utilities)
                              </h4>
                              <button
                                 onClick={() => setCalcDeductedItems([...calcDeductedItems, { name: '', rate: '', days: '', hours: '', amount: '' }])}
                                 className="text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 font-black px-3.5 py-2 rounded-xl uppercase tracking-wider flex items-center gap-1.5 border border-amber-200/80 transition-all cursor-pointer"
                              >
                                 <Plus className="w-4 h-4" /> Add Row
                              </button>
                           </div>
                           <div className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b pb-1.5">
                              <span className="flex-1 min-w-0">Item</span>
                              <span className="w-20 text-right">Rate (₹)</span>
                              <span className="w-14 text-center">Days</span>
                              <span className="w-14 text-center">Hours</span>
                              <span className="w-20 text-right">Total</span>
                              <span className="w-7"></span>
                           </div>
                           <div className="space-y-2.5 max-h-[520px] overflow-y-auto overflow-x-hidden pr-1">
                              {calcDeductedItems.map((item, idx) => (
                                 <div key={`deduct-${idx}`} className="flex items-center gap-1.5 bg-slate-50/90 hover:bg-slate-50 p-2 rounded-xl border border-slate-200/70 shadow-2xs transition-all">
                                    <input
                                       type="text"
                                       value={item.name}
                                       onChange={(e) => {
                                          const newList = [...calcDeductedItems];
                                          newList[idx].name = e.target.value;
                                          setCalcDeductedItems(newList);
                                       }}
                                       className="flex-1 min-w-0 bg-white border border-slate-200 rounded-lg p-1.5 px-2.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-amber-500"
                                       placeholder="Description..."
                                    />
                                    <input
                                       type="text"
                                       inputMode="numeric"
                                       value={item.rate}
                                       onChange={(e) => {
                                          const newList = [...calcDeductedItems];
                                          const raw = e.target.value.replace(/[^0-9]/g, '');
                                          newList[idx].rate = raw;
                                          const dNum = newList[idx].days === '' ? 1 : (Number(newList[idx].days) || 1);
                                          const hNum = newList[idx].hours === '' ? 1 : (Number(newList[idx].hours) || 1);
                                          newList[idx].amount = (Number(raw) * dNum * hNum).toString();
                                          setCalcDeductedItems(newList);
                                       }}
                                       className="w-20 bg-white border border-slate-200 rounded-lg p-1.5 px-2 text-xs font-bold text-slate-800 text-right focus:outline-none focus:border-amber-500"
                                       placeholder="Rate (₹)"
                                    />
                                    <input
                                       type="text"
                                       inputMode="numeric"
                                       value={item.days}
                                       onChange={(e) => {
                                          const newList = [...calcDeductedItems];
                                          const raw = e.target.value.replace(/[^0-9]/g, '');
                                          newList[idx].days = raw;
                                          const rateNum = Number(newList[idx].rate) || 0;
                                          const d = raw === '' ? 1 : Number(raw);
                                          const h = newList[idx].hours === '' ? 1 : (Number(newList[idx].hours) || 1);
                                          newList[idx].amount = (rateNum * d * h).toString();
                                          setCalcDeductedItems(newList);
                                       }}
                                       className="w-14 bg-white border border-slate-200 rounded-lg p-1.5 px-1 text-xs font-bold text-slate-800 text-center focus:outline-none focus:border-amber-500"
                                       placeholder="Days"
                                    />
                                    <input
                                       type="text"
                                       inputMode="numeric"
                                       value={item.hours}
                                       onChange={(e) => {
                                          const newList = [...calcDeductedItems];
                                          const raw = e.target.value.replace(/[^0-9]/g, '');
                                          newList[idx].hours = raw;
                                          const rateNum = Number(newList[idx].rate) || 0;
                                          const d = newList[idx].days === '' ? 1 : (Number(newList[idx].days) || 1);
                                          const h = raw === '' ? 1 : Number(raw);
                                          newList[idx].amount = (rateNum * d * h).toString();
                                          setCalcDeductedItems(newList);
                                       }}
                                       className="w-14 bg-white border border-slate-200 rounded-lg p-1.5 px-1 text-xs font-bold text-slate-800 text-center focus:outline-none focus:border-amber-500"
                                       placeholder="Hours"
                                    />
                                    <div className="w-20 text-right text-xs font-black text-slate-600 px-1 whitespace-nowrap">
                                       ₹{Number(item.amount || 0).toLocaleString()}
                                    </div>
                                    <button
                                       type="button"
                                       onClick={() => {
                                          const newList = calcDeductedItems.filter((_, i) => i !== idx);
                                          setCalcDeductedItems(newList);
                                       }}
                                       className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                                    >
                                       <Trash2 className="w-4 h-4" />
                                    </button>
                                 </div>
                              ))}
                              {calcDeductedItems.length === 0 && (
                                 <p className="text-xs text-slate-400 font-bold italic py-4 text-center">No deductions added (Optional).</p>
                              )}
                           </div>
                        </div>
                     </div>

                     {/* Modal Summary & Footer Actions */}
                     <div className="p-8 bg-slate-50 rounded-b-[2rem] border-t shrink-0 flex flex-col md:flex-row justify-between items-center gap-6">
                        {(() => {
                           const userBaseSum = calcBaseItems.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
                           const deductSum = calcDeductedItems.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
                           const grossBaseTotal = userBaseSum + deductSum;
                           const netEstimatedCost = userBaseSum;

                           return (
                              <>
                                 {/* Cost Summary Box */}
                                 <div className="flex flex-wrap md:flex-nowrap gap-6 text-left w-full md:w-auto">
                                    <div>
                                       <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Total Surgical Package</span>
                                       <span className="text-md font-black text-slate-700">
                                          ₹{grossBaseTotal.toLocaleString()}
                                       </span>
                                    </div>
                                    <div className="border-t pt-2 md:border-t-0 md:pt-0 md:border-l md:pl-6">
                                       <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Total Deductions</span>
                                       <span className="text-md font-black text-amber-600">
                                          - ₹{deductSum.toLocaleString()}
                                       </span>
                                    </div>
                                    <div className="border-t pt-2 md:border-t-0 md:pt-0 md:border-l md:pl-6 bg-blue-50/50 px-4 py-1.5 rounded-2xl border border-blue-100/50">
                                       <span className="text-[10px] font-black text-[#143E78] uppercase tracking-widest block">Total Estimated Cost</span>
                                       <span className="text-lg font-black text-[#143E78]">
                                          ₹{netEstimatedCost.toLocaleString()}
                                       </span>
                                    </div>
                                 </div>

                                 {/* Modal Action Buttons */}
                                 <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0 justify-end">
                                    <button
                                       type="button"
                                       onClick={handleRemoveCalculator}
                                       className="flex-1 md:flex-initial px-5 py-3.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-black rounded-xl uppercase text-[10px] tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs"
                                       title="Remove applied calculation and reset billing amount"
                                    >
                                       <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                       Remove Applied
                                    </button>
                                    <button
                                       type="button"
                                       onClick={() => setShowCalculatorModal(false)}
                                       className="flex-1 md:flex-initial px-6 py-3.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-black rounded-xl uppercase text-[10px] tracking-wider transition-all cursor-pointer"
                                    >
                                       Cancel
                                    </button>
                                    <button
                                       type="button"
                                       onClick={() => {
                                          handleApplyCalculator(netEstimatedCost);
                                       }}
                                       className="flex-1 md:flex-initial px-8 py-3.5 bg-[#143E78] hover:bg-blue-900 text-white font-black rounded-xl uppercase text-[10px] tracking-wider transition-all shadow-lg cursor-pointer"
                                    >
                                       Apply
                                    </button>
                                 </div>
                              </>
                           );
                        })()}
                     </div>
                  </div>
               </div>,
               document.body
            )}
            {/* 🧮 MANUAL CALCULATOR MODAL */}
            {showManualCalculatorModal && createPortal(
               <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 overflow-y-auto">
                  <div className="bg-white w-full max-w-4xl rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl flex flex-col max-h-[92vh] my-auto animate-zoomIn">
                     {/* Modal Header */}
                     <div className="bg-[#143E78] rounded-t-[2rem] px-8 py-5 text-white shrink-0 relative overflow-hidden flex justify-between items-center text-left">
                        <div className="relative z-10 text-left">
                           <div className="flex flex-wrap items-center gap-3">
                              <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-2">
                                 <Calculator className="w-5 h-5 text-amber-400" />
                                 {lead?.assignedHospital?.hospitalName || lead?.assignedHospital?.name || lead?.assignedHospital?.user?.fullName || "Hospital"} Hospital Cost
                              </h3>
                              {(lead?.assignedHospital?.phone || lead?.assignedHospital?.contactPerson?.phone || lead?.assignedHospital?.user?.phone) && (
                                 <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-400/20 text-amber-300 border border-amber-400/30 rounded-full text-[10px] font-black uppercase tracking-wider shadow-inner">
                                    <Phone className="w-3 h-3 text-amber-400" />
                                    {lead?.assignedHospital?.phone || lead?.assignedHospital?.contactPerson?.phone || lead?.assignedHospital?.user?.phone}
                                 </span>
                              )}
                           </div>
                           <p className="text-blue-200 text-[10px] font-bold mt-1 uppercase tracking-widest">
                              Add dynamic charges and exemption amounts to calculate Surgical Procedure Cost.
                           </p>
                        </div>
                        <button onClick={() => setShowManualCalculatorModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-all text-white cursor-pointer">
                           <X className="w-6 h-6" />
                        </button>
                     </div>

                     {/* Modal Body */}
                     <div className="p-6 sm:p-8 overflow-y-auto space-y-6 flex-1 text-left">
                        <div className="flex justify-between items-center border-b pb-3">
                           <div>
                              <h4 className="text-sm font-black text-[#143E78] uppercase tracking-wider flex items-center gap-2">
                                 <FileText className="w-4 h-4 text-blue-600" />
                                 Custom Charges
                              </h4>
                              <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">
                                 Enter charge description and amount
                              </p>
                           </div>
                           <button
                              type="button"
                              onClick={addManualCalcItem}
                              className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl text-[10px] font-black uppercase flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
                           >
                              <Plus className="w-4 h-4" /> Add Field
                           </button>
                        </div>

                        {/* Items List */}
                        <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                           {manualCalcItems.length === 0 ? (
                              <div className="p-8 text-center bg-slate-50/80 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 font-bold uppercase text-xs">
                                 No custom charge fields added yet. Click "+ ADD FIELD" above to add your custom charge.
                              </div>
                           ) : (
                              manualCalcItems.map((item, index) => (
                                 <div key={item.id || index} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl transition-all hover:border-slate-300">
                                    {/* Type Selector (+ Add or - Exclude) */}
                                    <select
                                       value={item.type}
                                       onChange={(e) => updateManualCalcItem(item.id, 'type', e.target.value)}
                                       className={`px-3 py-2.5 text-xs font-black rounded-xl border outline-none cursor-pointer ${item.type === 'add' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-rose-50 text-rose-700 border-rose-300'
                                          }`}
                                    >
                                       <option value="add">+ Add Charge (₹)</option>
                                       <option value="deduct">- Exemption / Less (₹)</option>
                                    </select>

                                    {/* Charge Description */}
                                    <input
                                       type="text"
                                       value={item.name}
                                       onChange={(e) => updateManualCalcItem(item.id, 'name', e.target.value)}
                                       placeholder="Charge Name (e.g. Surgery Fee, Bed Charge, Ayushman Exemption)"
                                       className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-amber-500"
                                    />

                                    {/* Amount Input */}
                                    <div className="relative w-36 shrink-0">
                                       <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-black text-slate-400 text-xs">₹</span>
                                       <input
                                          type="text"
                                          inputMode="numeric"
                                          value={item.amount}
                                          onChange={(e) => updateManualCalcItem(item.id, 'amount', e.target.value.replace(/[^0-9]/g, ''))}
                                          placeholder="0"
                                          className="w-full pl-8 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-800 outline-none focus:border-amber-500 text-right"
                                       />
                                    </div>

                                    {/* Remove Item */}
                                    <button
                                       type="button"
                                       onClick={() => removeManualCalcItem(item.id)}
                                       className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all cursor-pointer"
                                       title="Remove Field"
                                    >
                                       <Trash2 className="w-4 h-4" />
                                    </button>
                                 </div>
                              ))
                           )}
                        </div>

                        {/* Live Calculation Summary Banner */}
                        {(() => {
                           const netAmount = manualCalcItems.reduce((acc, item) => {
                              const val = Number(item.amount) || 0;
                              return item.type === 'deduct' ? acc - val : acc + val;
                           }, 0);
                           const finalVal = Math.max(0, netAmount);

                           return (
                              <div className="bg-[#143E78] p-5 rounded-2xl text-white flex justify-between items-center shadow-lg">
                                 <div>
                                    <span className="text-[10px] font-black uppercase text-amber-400 tracking-widest block">Total Surgical Amount</span>
                                    <span className="text-[10px] font-bold text-blue-200">Sum of charges minus exemptions</span>
                                 </div>
                                 <span className="text-3xl font-black text-emerald-400">₹{finalVal.toLocaleString()}</span>
                              </div>
                           );
                        })()}
                     </div>

                     {/* Modal Footer Actions */}
                     <div className="p-6 bg-slate-50 rounded-b-[2rem] border-t shrink-0 flex flex-wrap justify-end gap-3">
                        <button
                           type="button"
                           onClick={handleRemoveManualCalculator}
                           className="px-5 py-3 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-black rounded-xl uppercase text-[10px] tracking-wider transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                           title="Remove applied calculation and reset billing amount"
                        >
                           <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                           Remove Applied
                        </button>
                        <button
                           type="button"
                           onClick={() => setShowManualCalculatorModal(false)}
                           className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-black rounded-xl uppercase text-[10px] tracking-wider transition-all cursor-pointer"
                        >
                           Cancel
                        </button>
                        <button
                           type="button"
                           onClick={handleApplyManualCalculator}
                           className="px-8 py-3 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black rounded-xl uppercase text-[10px] tracking-wider transition-all shadow-lg cursor-pointer flex items-center gap-2"
                        >
                           <CheckCircle className="w-4 h-4 text-slate-950" /> Apply Manual Calculation
                        </button>
                     </div>
                  </div>
               </div>,
               document.body
            )}

            {/* 🛑 MARK AS LOST MODAL */}
            {showNotInterestedModal && (
               <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-fadeIn">
                  <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-6 animate-scaleUp">
                     <div className="flex justify-between items-start border-b border-slate-100 pb-4">
                        <div className="flex items-center gap-3">
                           <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-700 flex items-center justify-center font-black">
                              <XCircle className="w-5 h-5" />
                           </div>
                           <div>
                              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Mark Lead as Lost</h3>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Move to Terminal Lost State</p>
                           </div>
                        </div>
                        <button onClick={() => setShowNotInterestedModal(false)} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-all">
                           <X className="w-5 h-5" />
                        </button>
                     </div>

                     <div className="space-y-3">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Reason for Loss <span className="text-rose-500">*</span></label>
                        <textarea
                           value={notInterestedReason}
                           onChange={(e) => setNotInterestedReason(e.target.value)}
                           placeholder="Enter reason for marking this lead as lost..."
                           className="w-full h-28 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                        ></textarea>
                     </div>

                     <div className="flex gap-3 pt-2">
                        <button
                           onClick={() => setShowNotInterestedModal(false)}
                           className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-black uppercase tracking-wider rounded-xl transition-all"
                        >
                           Cancel
                        </button>
                        <button
                           onClick={handleDirectMarkLost}
                           disabled={updating || !notInterestedReason.trim()}
                           className="flex-1 py-3 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-rose-200"
                        >
                           {updating ? 'Processing...' : 'Confirm Mark Lost'}
                        </button>
                     </div>
                  </div>
               </div>
            )}
         </div>
      </div>
   );
};

export default LeadDetail;
